/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Plus,
  Download,
  FileDown,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  X,
  Pencil,
  Trash2,
  StickyNote,
  UserCheck,
  UserMinus,
  MoreHorizontal,
  Search,
  Copy,
  Factory,
  Package,
  Signature,
  Truck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Kanban, List, CalendarDays } from 'lucide-react';
import api from '../services/api';
import { getStockLevelStyle } from '../utils/stockUtils';
import './AdminDashboard.css';
import AdminDashboardTabs from './AdminDashboardTabs';
import AdminDashboardHeader from './AdminDashboardHeader';
import { DEFAULT_DASHBOARD_TABS, BUILTIN_TAB_LABEL_OVERRIDES } from './domains/settings/dashboardTabs';
import { toLocalDateKey, toLocalMonthKey } from './domains/orders/dateUtils';
import {
  RAW_TRANSACTION_ACTIONS,
  RAW_TRANSACTION_ACTION_MAP,
  RAW_TRANSACTION_FILTER_ACTIONS,
  getRawTransactionActionLabel,
  isLegacyTransactionField
} from './domains/inventory/constants';
import { normalizeProductRawMaterialEntry } from './domains/products/helpers';
import PipelineTransitionModal from './domains/orders/PipelineTransitionModal';
import PipelineHistoryModal from './domains/orders/PipelineHistoryModal';
import WelcomeModal from './onboarding/WelcomeModal';
import GuidedTour from './onboarding/GuidedTour';
import OnboardingChecklist from './onboarding/OnboardingChecklist';
import { buildDashboardChecklist } from './onboarding/checklistDefinitions';
import { buildDashboardTourSteps } from './onboarding/tourDefinitions';
import OrderDetailsModal from './domains/orders/OrderDetailsModal';
import ConfirmDialogModal from './domains/inventory/ConfirmDialogModal';
import NotesModal from './domains/settings/NotesModal';
import useOfflineQueue from './adminDashboard/features/offlineQueue/useOfflineQueue';
import OfflineQueueBanner from './adminDashboard/features/offlineQueue/OfflineQueueBanner';
import OfflineQueueModal from './adminDashboard/features/offlineQueue/OfflineQueueModal';
import useOrders from './adminDashboard/features/orders/useOrders';
import useInventory from './adminDashboard/features/inventory/useInventory';

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_err) {
    return 'UTC';
  }
};

const getDateKeyForTimeZone = (timeZone, date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || getBrowserTimeZone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (!year || !month || !day) return toLocalDateKey(date);
    return `${year}-${month}-${day}`;
  } catch (_err) {
    return toLocalDateKey(date);
  }
};

const STAFF_ATTENDANCE_STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'half_day', label: 'Half Day' },
  { value: 'leave', label: 'Leave' }
];

const DEFAULT_RAW_TRANSACTION_SCHEMA = {
  quantity_unit: 'Kg',
  include_date: true,
  include_notes: true,
  fields: [
    { key: 'type', label: 'Transaction Type', type: 'dropdown', required: true, options: ['in', 'out'] },
    { key: 'quantity', label: 'Quantity', type: 'number', required: true, options: [] }
  ],
  stock_field_key: 'quantity',
  type_field_key: 'type',
  grouping_keys: []
};

const DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA = {
  ...DEFAULT_RAW_TRANSACTION_SCHEMA,
  quantity_unit: 'Pcs',
  grouping_keys: []
};

const FINISHED_GOODS_ACTION_LABELS = {
  in: 'Stock In',
  out: 'Stock Out',
  hold: 'Hold'
};
const DEFAULT_SETTINGS = {
  stock_threshold_critical: '50',
  stock_threshold_low: '100',
  stock_threshold_medium: '200',
  font_size: 'medium',
  color_indicators_enabled: 'true',
  orders_enabled: 'true',
  material_pipeline: JSON.stringify([]),
  raw_materials: JSON.stringify([]),
  product_schema: JSON.stringify({ fields: [] }),
  staff_schema: JSON.stringify({ fields: [] }),
  customer_schema: JSON.stringify({ fields: [] }),
  contractor_schema: JSON.stringify({ fields: [] }),
  user_roles: JSON.stringify({ roles: [] }),
  transaction_schema: JSON.stringify(DEFAULT_RAW_TRANSACTION_SCHEMA),
  raw_material_transaction_schema: JSON.stringify(DEFAULT_RAW_TRANSACTION_SCHEMA),
  finished_goods_transaction_schema: JSON.stringify(DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA),
  challan_number_format: 'calendar',
  challan_branding_logo_url: '',
  challan_branding_legal_name: '',
  challan_branding_address: '',
  challan_branding_gst: '',
  challan_branding_pan: '',
  challan_branding_contact: '',
  challan_branding_generated_by: 'Generated by Inventory Management Software',
  challan_branding_show_qr: 'true'
};

const ROLE_PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete'];
const ROLE_SECTION_GROUP_ORDER = ['inventory', 'analytics', 'products', 'orders', 'people', 'system', 'custom'];
const ROLE_SECTION_GROUP_LABELS = {
  inventory: 'Inventory',
  analytics: 'Analytics',
  products: 'Products',
  orders: 'Orders',
  people: 'People',
  system: 'System',
  custom: 'Custom'
};
const RAW_OUTBOUND_ACTIONS = RAW_TRANSACTION_ACTIONS.filter((action) => action.type === 'out');
const CHALLAN_LAYOUT_DEFAULTS = {
  internal: {
    include_unit_cost: true,
    include_estimated_cost: true,
    include_product_id: true,
    include_order_ids: true,
    include_qr: false
  },
  customer: {
    include_unit_cost: false,
    include_estimated_cost: false,
    include_product_id: false,
    include_order_ids: false,
    include_qr: true
  }
};
const normalizeToggleBoolean = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const safe = String(value || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'on'].includes(safe)) return true;
  if (['false', '0', 'no', 'n', 'off'].includes(safe)) return false;
  return fallback;
};
const normalizeChallanOutputPreferences = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const layout = source.layout === 'customer' ? 'customer' : 'internal';
  const defaults = CHALLAN_LAYOUT_DEFAULTS[layout];
  return {
    layout,
    include_unit_cost: normalizeToggleBoolean(source.include_unit_cost, defaults.include_unit_cost),
    include_estimated_cost: normalizeToggleBoolean(source.include_estimated_cost, defaults.include_estimated_cost),
    include_product_id: normalizeToggleBoolean(source.include_product_id, defaults.include_product_id),
    include_order_ids: normalizeToggleBoolean(source.include_order_ids, defaults.include_order_ids),
    include_qr: normalizeToggleBoolean(source.include_qr, defaults.include_qr)
  };
};

const createPermissionSet = (seed = {}) => ({
  view: seed.view === true,
  create: seed.create === true,
  edit: seed.edit === true,
  delete: seed.delete === true
});

const hasAnyPermission = (permissionSet) =>
  Boolean(permissionSet?.view || permissionSet?.create || permissionSet?.edit || permissionSet?.delete);

const normalizePermissionSectionId = (sectionId) => {
  const safe = String(sectionId || '').trim();
  if (!safe) return '';
  if (safe.startsWith('settings_')) return 'settings';
  return safe;
};

const getRoleSectionGroupId = (sectionId) => {
  const safe = normalizePermissionSectionId(sectionId);
  if (['stock', 'add', 'transactions', 'raw_materials'].includes(safe)) return 'inventory';
  if (['insights'].includes(safe)) return 'analytics';
  if (['product_add', 'products'].includes(safe)) return 'products';
  if (['order', 'orders', 'pipeline_board'].includes(safe)) return 'orders';
  if (['staff', 'customers', 'contractors'].includes(safe)) return 'people';
  if (['settings', 'settings_users'].includes(safe)) return 'system';
  return 'custom';
};

const parseTransactionSchemaValue = (value, fallbackSchema = DEFAULT_RAW_TRANSACTION_SCHEMA) => {
  if (!value) return fallbackSchema;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    const defaultFields = fallbackSchema.fields || DEFAULT_RAW_TRANSACTION_SCHEMA.fields;
    return {
      quantity_unit: parsed.quantity_unit || fallbackSchema.quantity_unit || 'Kg',
      include_date: parsed.include_date !== false,
      include_notes: parsed.include_notes !== false,
      fields: Array.isArray(parsed.fields) && parsed.fields.length > 0 ? parsed.fields : defaultFields,
      stock_field_key: parsed.stock_field_key || fallbackSchema.stock_field_key || 'quantity',
      type_field_key: parsed.type_field_key || fallbackSchema.type_field_key || 'type',
      grouping_keys: Array.isArray(parsed.grouping_keys) ? parsed.grouping_keys : []
    };
  } catch (_err) {
    return fallbackSchema;
  }
};

const ONBOARDING_TOUR_VERSION = 1;

const getDefaultChecklistState = () => ({
  hidden: false,
  expanded: true,
  dismissed_once: false
});

const normalizeChecklistState = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return {
    hidden: Boolean(source.hidden),
    expanded: source.expanded !== false,
    dismissed_once: Boolean(source.dismissed_once)
  };
};

const normalizeOnboardingState = (value = {}) => ({
  tour_version: Number(value?.tour_version) || ONBOARDING_TOUR_VERSION,
  welcome_dismissed_at: value?.welcome_dismissed_at || null,
  tour_completed_at: value?.tour_completed_at || null,
  tour_skipped_at: value?.tour_skipped_at || null,
  tour_snoozed_until: value?.tour_snoozed_until || null,
  checklist_state: normalizeChecklistState(value?.checklist_state || getDefaultChecklistState())
});

function AdminDashboard({ user, onLogout, onUserUpdate }) {
  const initialTimeZone = user?.timezone || getBrowserTimeZone();
  const initialDateKey = getDateKeyForTimeZone(initialTimeZone);
  const canReturnToSuperAdmin = Boolean(localStorage.getItem('superadmin_token'));
  const [currentUserTimeZone, setCurrentUserTimeZone] = useState(initialTimeZone);
  const [activeTab, setActiveTab] = useState('stock');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    } catch (_err) {
      return false;
    }
  });
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pipelineTransitioningIds, setPipelineTransitioningIds] = useState([]);
  const [pipelineSearch, setPipelineSearch] = useState('');
  const [pipelineAssigneeFilter, setPipelineAssigneeFilter] = useState('all');
  const [pipelineOverdueOnly, setPipelineOverdueOnly] = useState(false);
  const [pipelineBlockedOnly, setPipelineBlockedOnly] = useState(false);
  const [pipelineUnassignedOnly, setPipelineUnassignedOnly] = useState(false);
  const [pipelineView, setPipelineView] = useState('board');
  const [pipelineCalendarCursor, setPipelineCalendarCursor] = useState(toLocalMonthKey());
  const [expandedCalendarDayKey, setExpandedCalendarDayKey] = useState(null);
  const [selectedPipelineOrderIds, setSelectedPipelineOrderIds] = useState([]);
  const [pipelineTransitionDialog, setPipelineTransitionDialog] = useState(null);
  const [pipelineTransitionForm, setPipelineTransitionForm] = useState({ reason: '', note: '', blocked_reason: '' });
  const [pipelineHistoryOrder, setPipelineHistoryOrder] = useState(null);
  const [pipelineHistoryRows, setPipelineHistoryRows] = useState([]);
  const [pipelineHistoryLoading, setPipelineHistoryLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pipelinePreset, setPipelinePreset] = useState('all');
  const [pipelineActionMenuOpenId, setPipelineActionMenuOpenId] = useState(null);
  const [pipelineEditingAssigneeId, setPipelineEditingAssigneeId] = useState(null);
  const [notesModalTarget, setNotesModalTarget] = useState(null);
  const [notesModalText, setNotesModalText] = useState('');
  const [orderNotesTarget, setOrderNotesTarget] = useState(null);
  const [orderNotesText, setOrderNotesText] = useState('');
  const [productNotesTarget, setProductNotesTarget] = useState(null);
  const [productNotesText, setProductNotesText] = useState('');
  const [, setColors] = useState([]);
  const [stock, setStock] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [onboardingDataLoaded, setOnboardingDataLoaded] = useState(false);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [onboardingState, setOnboardingState] = useState(() => normalizeOnboardingState());
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [setupCardHidden, setSetupCardHidden] = useState(false);
  const [setupCardExpanded, setSetupCardExpanded] = useState(true);
  const [setupCardDismissedOnce, setSetupCardDismissedOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: '',
    skill_category: '',
    id_type: '',
    id_value: '',
    is_active: true
  });
  const [staffCustomValues, setStaffCustomValues] = useState({});
  const [staffIdCache, setStaffIdCache] = useState({});
  const [staffSearch, setStaffSearch] = useState('');
  const [staffStatusFilter, setStaffStatusFilter] = useState('all');
  const [staffNotesFilter, setStaffNotesFilter] = useState('all');
  const [staffSortMode, setStaffSortMode] = useState('recent');
  const [viewingStaff, setViewingStaff] = useState(null);
  const [showStaffAttendanceModal, setShowStaffAttendanceModal] = useState(false);
  const [staffAttendanceDate, setStaffAttendanceDate] = useState(initialDateKey);
  const [staffAttendanceEntries, setStaffAttendanceEntries] = useState({});
  const [staffAttendanceLoading, setStaffAttendanceLoading] = useState(false);
  const [staffAttendanceSaving, setStaffAttendanceSaving] = useState(false);
  const [showStaffAttendanceViewModal, setShowStaffAttendanceViewModal] = useState(false);
  const [staffAttendanceViewDate, setStaffAttendanceViewDate] = useState(initialDateKey);
  const [staffAttendanceViewRows, setStaffAttendanceViewRows] = useState([]);
  const [staffAttendanceViewLoading, setStaffAttendanceViewLoading] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerCustomValues, setCustomerCustomValues] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerStatusFilter, setCustomerStatusFilter] = useState('all');
  const [customerNotesFilter, setCustomerNotesFilter] = useState('all');
  const [customerSortMode, setCustomerSortMode] = useState('recent');
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [showCreateContractor, setShowCreateContractor] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [contractorCustomValues, setContractorCustomValues] = useState({});
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorStatusFilter, setContractorStatusFilter] = useState('all');
  const [contractorNotesFilter, setContractorNotesFilter] = useState('all');
  const [contractorSortMode, setContractorSortMode] = useState('recent');
  const [viewingContractor, setViewingContractor] = useState(null);
  const [materialType] = useState('custom');
  const [stockSearch, setStockSearch] = useState('');
  const [stockSort, setStockSort] = useState('none');
  const [insightsRange, setInsightsRange] = useState('30d');
  const [insightsDeadStockDays, setInsightsDeadStockDays] = useState('60');
  const [insightsRawMaterialFilter, setInsightsRawMaterialFilter] = useState('');
  const [insightsCharacteristicFilter, setInsightsCharacteristicFilter] = useState('');
  const stockSummaryExpanded = true;
  const [stockOverviewMode, setStockOverviewMode] = useState('raw_materials');
  const [includeSetupOnlyStock, setIncludeSetupOnlyStock] = useState(true);
  const [stockFilters, setStockFilters] = useState({});
  const [success, setSuccess] = useState('');
  const [customLists, setCustomLists] = useState([]);
  const [customListInputs, setCustomListInputs] = useState({});
  const [editingListItems, setEditingListItems] = useState({});
  const [transactionValues, setTransactionValues] = useState({});
  const [transactionDate, setTransactionDate] = useState(initialDateKey);
  const [transactionNotes, setTransactionNotes] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFormStates, setCustomFormStates] = useState({});
  const [viewingNote, setViewingNote] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [orderCostBreakdown, setOrderCostBreakdown] = useState(null);
  const [orderCostBreakdownLoading, setOrderCostBreakdownLoading] = useState(false);
  const [orderCostForm, setOrderCostForm] = useState({
    labor_cost: '',
    overhead_cost: '',
    freight_cost: '',
    misc_cost: '',
    tax_amount: '',
    discount_amount: ''
  });
  const [cancelOrderTarget, setCancelOrderTarget] = useState(null);
  const [completeOrderTarget, setCompleteOrderTarget] = useState(null);
  const [lowStockModal, setLowStockModal] = useState(null);
  const [lowStockOverrideLoading, setLowStockOverrideLoading] = useState(false);
  const [csvImportModal, setCsvImportModal] = useState(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [rawMaterialsImportModal, setRawMaterialsImportModal] = useState(null);
  const [rawMaterialsImportLoading, setRawMaterialsImportLoading] = useState(false);
  const [holdOverviewOpen, setHoldOverviewOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(25);
  const [productCatalog, setProductCatalog] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productMaterialFilter, setProductMaterialFilter] = useState('all');
  const [productHasRawFilter, setProductHasRawFilter] = useState('all');
  const [productViewMode, setProductViewMode] = useState('cards');
  const [productListSort, setProductListSort] = useState({ key: 'name', direction: 'asc' });
  const [expandedProductListIds, setExpandedProductListIds] = useState([]);
  const [expandedProductBomIds, setExpandedProductBomIds] = useState([]);
  const [orderForm, setOrderForm] = useState({
    order_number: '',
    buyer_name: '',
    delivery_date: initialDateKey
  });
  const [orderProducts, setOrderProducts] = useState([{ product_id: '', quantity: 1 }]);
  const [orderFormErrors, setOrderFormErrors] = useState({});
  const [orderNumberLoading, setOrderNumberLoading] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderStageFilter, setOrderStageFilter] = useState('all');
  const [orderBuyerFilter, setOrderBuyerFilter] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [orderSort, setOrderSort] = useState('created_desc');
  const [productFormName, setProductFormName] = useState('');
  const [productFormValues, setProductFormValues] = useState({});
  const [newUserRoleName, setNewUserRoleName] = useState('');
  const [newUserRoleParent, setNewUserRoleParent] = useState('');
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleAccessExpanded, setRoleAccessExpanded] = useState(false);
  const [productRawMaterials, setProductRawMaterials] = useState([]);
  const [productAddStep, setProductAddStep] = useState(1);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editProductValues, setEditProductValues] = useState({});
  const [editProductRawMaterialCount, setEditProductRawMaterialCount] = useState(0);
  const [editProductRawMaterials, setEditProductRawMaterials] = useState([]);
  const [settingsTab, setSettingsTab] = useState('system');
  const [settingsSearch, setSettingsSearch] = useState('');
  const [manageListCategoryFilter, setManageListCategoryFilter] = useState('all');
  const [selectedManageListKey, setSelectedManageListKey] = useState('');
  const [editingTransactionValues, setEditingTransactionValues] = useState({});
  const [editingTransactionDate, setEditingTransactionDate] = useState('');
  const [editingTransactionNotes, setEditingTransactionNotes] = useState('');
  const [editingTransactionLedgerMode, setEditingTransactionLedgerMode] = useState('raw_material');
  const [editingRawMaterialAction, setEditingRawMaterialAction] = useState(RAW_TRANSACTION_ACTIONS[0].key);
  const [editingRawMaterialType, setEditingRawMaterialType] = useState('');
  const [editingRawMaterialCharacteristics, setEditingRawMaterialCharacteristics] = useState({});
  const [editingRawMaterialCost, setEditingRawMaterialCost] = useState('');
  const [editingFinishedGoodsAction, setEditingFinishedGoodsAction] = useState('in');
  const [editingFinishedGoodsProductId, setEditingFinishedGoodsProductId] = useState('');
  const [newPipelineStage, setNewPipelineStage] = useState('');
  const [newRawMaterial, setNewRawMaterial] = useState({ name: '', unitLabel: '', consumeStage: '' });
  const [selectedRawMaterialIndex, setSelectedRawMaterialIndex] = useState(0);
  const [newRawMaterialCategory, setNewRawMaterialCategory] = useState({ name: '', mandatory: false });
  const [rawMaterialAction, setRawMaterialAction] = useState(RAW_TRANSACTION_ACTIONS[0].key);
  const [rawMaterialType, setRawMaterialType] = useState('');
  const [finishedGoodsAction, setFinishedGoodsAction] = useState('in');
  const [finishedGoodsProductId, setFinishedGoodsProductId] = useState('');
  const [rawMaterialCharacteristics, setRawMaterialCharacteristics] = useState({});
  const [rawMaterialCost, setRawMaterialCost] = useState('');
  const [rawMaterialLinkedOrderNumber, setRawMaterialLinkedOrderNumber] = useState('');
  const [movementHasChallan, setMovementHasChallan] = useState(false);
  const [movementChallanType, setMovementChallanType] = useState('delivery');
  const [movementChallanOrderNumber, setMovementChallanOrderNumber] = useState('');
  const [movementChallanPartyName, setMovementChallanPartyName] = useState('');
  const [movementChallanPartyContact, setMovementChallanPartyContact] = useState('');
  const [movementChallanPartyAddress, setMovementChallanPartyAddress] = useState('');
  const [movementChallanTransporterName, setMovementChallanTransporterName] = useState('');
  const [movementChallanVehicleNumber, setMovementChallanVehicleNumber] = useState('');
  const [movementChallanLrNumber, setMovementChallanLrNumber] = useState('');
  const [movementChallanDriverName, setMovementChallanDriverName] = useState('');
  const [movementChallanDriverPhone, setMovementChallanDriverPhone] = useState('');
  const [movementChallanDispatchDateTime, setMovementChallanDispatchDateTime] = useState('');
  const [movementChallanSignatureData, setMovementChallanSignatureData] = useState('');
  const [movementChallanCustomDetails, setMovementChallanCustomDetails] = useState([{ key: '', value: '' }]);
  const [, setChallanActionLoadingId] = useState(null);
  const [challanOutputDialog, setChallanOutputDialog] = useState(null);
  const [challanOutputLayout, setChallanOutputLayout] = useState('internal');
  const [challanOutputIncludeUnitCost, setChallanOutputIncludeUnitCost] = useState(true);
  const [challanOutputIncludeEstimatedCost, setChallanOutputIncludeEstimatedCost] = useState(true);
  const [challanOutputIncludeProductId, setChallanOutputIncludeProductId] = useState(true);
  const [challanOutputIncludeOrderIds, setChallanOutputIncludeOrderIds] = useState(true);
  const [challanOutputIncludeQr, setChallanOutputIncludeQr] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [transactionLedgerMode, setTransactionLedgerMode] = useState('raw_material');
  const [transactionRawMaterialFilter, setTransactionRawMaterialFilter] = useState('');
  const [transactionRawActionFilter, setTransactionRawActionFilter] = useState('all');
  const [transactionFinishedProductFilter, setTransactionFinishedProductFilter] = useState('');
  const [transactionDateFromFilter, setTransactionDateFromFilter] = useState('');
  const [transactionDateToFilter, setTransactionDateToFilter] = useState('');
  const [transactionRawCharacteristicFilters, setTransactionRawCharacteristicFilters] = useState({});
  const [transactionActionMenuOpenId, setTransactionActionMenuOpenId] = useState(null);
  const [rawMaterialStockType, setRawMaterialStockType] = useState('');
  const [rawMaterialStockData, setRawMaterialStockData] = useState([]);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [tourTooltipStyle, setTourTooltipStyle] = useState({ top: 80, left: 80 });


  const PASSWORD_REQUIREMENTS_TEXT = 'At least 8 characters, including uppercase, lowercase, number, and special character.';
  const isStrongPassword = (value) => {
    const pwd = String(value || '').trim();
    if (pwd.length < 8) return false;
    return /[A-Z]/.test(pwd)
      && /[a-z]/.test(pwd)
      && /\d/.test(pwd)
      && /[^A-Za-z0-9]/.test(pwd);
  };

  const handleReturnToSuperAdmin = () => {
    const token = localStorage.getItem('superadmin_token');
    const refreshToken = localStorage.getItem('superadmin_refresh_token');
    const superUser = localStorage.getItem('superadmin_user');
    if (token && superUser) {
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(superUser);
      } catch (_err) {
        parsedUser = null;
      }
      api.setAuthSession({
        token,
        refreshToken,
        user: parsedUser
      });
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_refresh_token');
      localStorage.removeItem('superadmin_user');
      window.location.href = '/superadmin';
    }
  };
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const FONT_SIZE_OPTIONS = [
    { value: 'small', label: 'Small', iconLabel: 'A' },
    { value: 'medium', label: 'Medium', iconLabel: 'A' },
    { value: 'large', label: 'Large', iconLabel: 'A' }
  ];
  const getNormalizedFontSize = (value) => {
    const allowed = new Set(FONT_SIZE_OPTIONS.map((option) => option.value));
    return allowed.has(value) ? value : 'medium';
  };
  const loadRequestRef = useRef({
    id: 0,
    controller: null,
    inFlight: false
  });
  const rawMaterialStockRequestIdRef = useRef(0);
  const tourTargetRef = useRef(null);
  const tourNavTargetRef = useRef(null);
  const [formData, setFormData] = useState({
    color_id: '',
    material_type: 'custom',
    type: 'in',
    quantity: '',
    date: initialDateKey,
    notes: '',
    challan_no: '',
    total_weight: '',
  });
  const [, setPadItemCount] = useState(1);
  const [padItems, setPadItems] = useState([{ pad_type_id: '', quantity: '' }]);
  const ordersEnabled = true;
  const selectedFontSize = getNormalizedFontSize(settings?.font_size);
  const isAdminUser = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'superadmin';
  const parseUserRolesSetting = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      const roles = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.roles)
          ? parsed.roles
          : [];
      return roles
        .map((role) => ({
          name: String(role?.name || '').trim(),
          inherits_from: role?.inherits_from ? String(role.inherits_from).trim() : '',
          permissions: (() => {
            const map = {};
            if (role?.permissions && typeof role.permissions === 'object' && !Array.isArray(role.permissions)) {
              Object.keys(role.permissions).forEach((sectionKey) => {
                const normalizedSection = normalizePermissionSectionId(sectionKey);
                if (!normalizedSection) return;
                map[normalizedSection] = createPermissionSet(role.permissions[sectionKey] || {});
              });
            }
            const legacyTabs = Array.isArray(role?.tabs) ? role.tabs.filter(Boolean) : [];
            legacyTabs.forEach((tabId) => {
              const normalizedSection = normalizePermissionSectionId(tabId);
              if (!normalizedSection || map[normalizedSection]) return;
              map[normalizedSection] = createPermissionSet({
                view: true,
                create: true,
                edit: true,
                delete: true
              });
            });
            return map;
          })()
        }))
        .filter((role) => role.name && role.name.toLowerCase() !== 'admin' && role.name.toLowerCase() !== 'user');
    } catch (_err) {
      return [];
    }
  };
  const formatRoleLabel = (value) =>
    String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  const dashboardTabs = useMemo(() => {
    if (!settings?.dashboard_tabs) return DEFAULT_DASHBOARD_TABS;
    try {
      const parsed = JSON.parse(settings.dashboard_tabs);
      if (!Array.isArray(parsed)) return DEFAULT_DASHBOARD_TABS;
      const normalized = parsed
        .filter((tab) => tab && tab.id)
        .map((tab) => ({
          id: tab.id,
          label: BUILTIN_TAB_LABEL_OVERRIDES[tab.id] || tab.label || tab.id,
          visible: tab.visible !== false,
          mode: tab.mode === 'custom_form' ? 'custom_form' : 'builtin',
          fields: Array.isArray(tab.fields) ? tab.fields : []
        }));
      const existingIds = new Set(normalized.map((tab) => tab.id));
      const merged = [...normalized];
      DEFAULT_DASHBOARD_TABS.forEach((tab) => {
        if (!existingIds.has(tab.id)) merged.push(tab);
      });
      return merged;
    } catch (_err) {
      return DEFAULT_DASHBOARD_TABS;
    }
  }, [settings?.dashboard_tabs]);
  const userRoleDefinitions = useMemo(
    () => parseUserRolesSetting(settings?.user_roles),
    [settings?.user_roles]
  );
  const roleOptions = useMemo(() => {
    const roleSet = new Set(['admin', 'owner', 'user']);
    userRoleDefinitions.forEach((role) => {
      if (role?.name) roleSet.add(role.name);
    });
    users.forEach((usr) => {
      if (usr?.role) roleSet.add(usr.role);
    });
    return Array.from(roleSet);
  }, [userRoleDefinitions, users]);
  const roleTabOptions = useMemo(
    () => dashboardTabs.filter((tab) => tab.visible !== false).map((tab) => ({ id: tab.id, label: tab.label || formatRoleLabel(tab.id) })),
    [dashboardTabs]
  );
  const roleSectionGroups = useMemo(() => {
    const grouped = roleTabOptions.reduce((acc, tab) => {
      const groupId = getRoleSectionGroupId(tab.id);
      if (!acc[groupId]) acc[groupId] = [];
      acc[groupId].push(tab);
      return acc;
    }, {});
    return ROLE_SECTION_GROUP_ORDER
      .filter((groupId) => Array.isArray(grouped[groupId]) && grouped[groupId].length > 0)
      .map((groupId) => ({
        id: groupId,
        label: ROLE_SECTION_GROUP_LABELS[groupId] || formatRoleLabel(groupId),
        sections: grouped[groupId]
      }));
  }, [roleTabOptions]);
  const roleNameOptions = useMemo(
    () => userRoleDefinitions.map((role) => role.name).filter(Boolean),
    [userRoleDefinitions]
  );
  const roleAssignmentCounts = useMemo(() => {
    const counts = {};
    (users || []).forEach((usr) => {
      const roleName = String(usr?.role || '').trim();
      if (!roleName) return;
      counts[roleName] = (counts[roleName] || 0) + 1;
    });
    return counts;
  }, [users]);
  const filteredRoleDefinitions = useMemo(() => {
    const query = String(roleSearchQuery || '').trim().toLowerCase();
    if (!query) return userRoleDefinitions;
    return userRoleDefinitions.filter((role) => {
      const roleName = String(role?.name || '').toLowerCase();
      const parent = String(role?.inherits_from || '').toLowerCase();
      return roleName.includes(query) || parent.includes(query);
    });
  }, [roleSearchQuery, userRoleDefinitions]);
  const selectedRoleIndex = useMemo(
    () => userRoleDefinitions.findIndex((role) => role.name === selectedRoleName),
    [userRoleDefinitions, selectedRoleName]
  );
  const selectedRoleDefinition = selectedRoleIndex >= 0 ? userRoleDefinitions[selectedRoleIndex] : null;
  const resolvedRolePermissionsMap = useMemo(() => {
    const roleMap = new Map(
      userRoleDefinitions
        .filter((role) => role?.name)
        .map((role) => [role.name.toLowerCase(), role])
    );
    const cache = new Map();
    const resolveRolePermissions = (roleName, stack = new Set()) => {
      const key = String(roleName || '').toLowerCase();
      if (!key || !roleMap.has(key)) return {};
      if (cache.has(key)) return cache.get(key);
      if (stack.has(key)) return {};
      stack.add(key);
      const role = roleMap.get(key);
      const parentName = String(role?.inherits_from || '').trim().toLowerCase();
      const parentPermissions = parentName ? resolveRolePermissions(parentName, stack) : {};
      const merged = { ...parentPermissions };
      const ownPermissions = role?.permissions && typeof role.permissions === 'object' ? role.permissions : {};
      Object.keys(ownPermissions).forEach((section) => {
        merged[section] = createPermissionSet(ownPermissions[section] || {});
      });
      cache.set(key, merged);
      return merged;
    };
    const map = {};
    userRoleDefinitions.forEach((role) => {
      map[role.name] = resolveRolePermissions(role.name);
    });
    return map;
  }, [userRoleDefinitions]);
  const roleTabMap = useMemo(() => {
    const map = {};
    Object.keys(resolvedRolePermissionsMap).forEach((roleName) => {
      const permissions = resolvedRolePermissionsMap[roleName] || {};
      map[roleName] = Object.keys(permissions).filter((section) => hasAnyPermission(permissions[section]));
    });
    return map;
  }, [resolvedRolePermissionsMap]);

  const orderedTabs = useMemo(() => {
    const filtered = dashboardTabs.filter((tab) => tab.visible !== false);
    const withoutOrders = ordersEnabled
      ? filtered
      : filtered.filter((tab) => tab.id !== 'order' && tab.id !== 'orders');
    const withOrders = (() => {
      if (!ordersEnabled) return withoutOrders;
      const existingIds = new Set(withoutOrders.map((tab) => tab.id));
      const required = DEFAULT_DASHBOARD_TABS.filter((tab) => tab.id === 'order' || tab.id === 'orders');
      const merged = [...withoutOrders];
      required.forEach((tab) => {
        if (!existingIds.has(tab.id)) merged.push(tab);
      });
      return merged;
    })();
    if (!isAdminUser) {
      const allowedTabs = roleTabMap[user?.role];
      if (Array.isArray(allowedTabs) && allowedTabs.length > 0) {
        const allowedSet = new Set(allowedTabs);
        const filteredTabs = withOrders.filter((tab) => allowedSet.has(tab.id));
        return filteredTabs.length ? filteredTabs : withOrders;
      }
      const restrictedTabs = withOrders.filter(
        (tab) => tab.id !== 'staff' && tab.id !== 'customers' && tab.id !== 'contractors' && tab.id !== 'settings'
      );
      return restrictedTabs.length ? restrictedTabs : withOrders;
    }
    return withOrders.length ? withOrders : DEFAULT_DASHBOARD_TABS;
  }, [dashboardTabs, ordersEnabled, isAdminUser, roleTabMap, user?.role]);

  const currentTabConfig = orderedTabs.find((tab) => tab.id === activeTab);
  const visibleTabIds = useMemo(() => new Set(orderedTabs.map((tab) => tab.id)), [orderedTabs]);
  const canAccessUsersTab = isAdminUser;
  const canAccessStaffTab = isAdminUser || visibleTabIds.has('staff');
  const canAccessCustomersTab = isAdminUser || visibleTabIds.has('customers');
  const canAccessContractorsTab = isAdminUser || visibleTabIds.has('contractors');
  const shouldPrefetchOnboardingData = !onboardingDataLoaded;
  const shouldPrefetchOnboardingUsers = shouldPrefetchOnboardingData && isAdminUser && visibleTabIds.has('settings');
  const shouldPrefetchOnboardingOrders = shouldPrefetchOnboardingData
    && ordersEnabled
    && (visibleTabIds.has('order') || visibleTabIds.has('orders') || visibleTabIds.has('pipeline_board'));
  const shouldPrefetchOnboardingProducts = shouldPrefetchOnboardingData
    && (visibleTabIds.has('order') || visibleTabIds.has('products') || visibleTabIds.has('product_add') || visibleTabIds.has('add'));
  const onboardingActions = useMemo(() => ({
    openRawMaterials: () => {
      setActiveTab('settings');
      setSettingsTab('rawMaterials');
    },
    openManageLists: () => {
      setActiveTab('settings');
      setSettingsTab('customLists');
    },
    openSystemSettings: () => {
      setActiveTab('settings');
      setSettingsTab('system');
    },
    openUserAccess: () => {
      setActiveTab('settings');
      setSettingsTab('users');
    },
    openProductAdd: () => setActiveTab('product_add'),
    openAddMovement: () => setActiveTab('add'),
    openCreateOrder: () => setActiveTab('order'),
    openWorkflow: () => setActiveTab('pipeline_board'),
    openStock: () => setActiveTab('stock'),
    openOrders: () => setActiveTab('orders'),
    openTransactions: () => setActiveTab('transactions')
  }), []);
  const applyChecklistState = useCallback((value) => {
    const next = normalizeChecklistState(value);
    setSetupCardHidden(next.hidden);
    setSetupCardExpanded(next.expanded);
    setSetupCardDismissedOnce(next.dismissed_once);
  }, []);
  const loadOnboardingState = useCallback(async () => {
    try {
      const data = await api.getOnboardingMe();
      const nextState = normalizeOnboardingState(data?.state);
      setOnboardingState(nextState);
      applyChecklistState(nextState.checklist_state);
    } catch (_err) {
      const nextState = normalizeOnboardingState();
      setOnboardingState(nextState);
      applyChecklistState(nextState.checklist_state);
    } finally {
      setOnboardingLoaded(true);
    }
  }, [applyChecklistState]);
  const persistOnboardingState = useCallback(async (patch) => {
    setOnboardingSaving(true);
    try {
      const data = await api.updateOnboardingMe(patch);
      const nextState = normalizeOnboardingState(data?.state);
      setOnboardingState(nextState);
      applyChecklistState(nextState.checklist_state);
      return nextState;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setOnboardingSaving(false);
    }
  }, [applyChecklistState]);
  const closeTour = useCallback(() => {
    setIsTourOpen(false);
    if (tourTargetRef.current) {
      tourTargetRef.current.classList.remove('tour-target-active');
      tourTargetRef.current = null;
    }
    if (tourNavTargetRef.current) {
      tourNavTargetRef.current.classList.remove('tour-target-nav-active');
      tourNavTargetRef.current = null;
    }
  }, []);
  const startTour = useCallback(() => {
    setWelcomeModalOpen(false);
    setTourStepIndex(0);
    setIsTourOpen(true);
  }, []);
  const handleWelcomeStart = useCallback(() => {
    const dismissedAt = new Date().toISOString();
    persistOnboardingState({
      tour_version: ONBOARDING_TOUR_VERSION,
      welcome_dismissed_at: dismissedAt,
      tour_skipped_at: null,
      tour_snoozed_until: null
    }).catch(() => {});
    startTour();
  }, [persistOnboardingState, startTour]);
  const handleWelcomeLater = useCallback(() => {
    const snoozeUntil = new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString();
    setWelcomeModalOpen(false);
    persistOnboardingState({
      tour_version: ONBOARDING_TOUR_VERSION,
      tour_snoozed_until: snoozeUntil
    }).catch(() => {});
  }, [persistOnboardingState]);
  const handleWelcomeSkip = useCallback(() => {
    const skippedAt = new Date().toISOString();
    setWelcomeModalOpen(false);
    persistOnboardingState({
      tour_version: ONBOARDING_TOUR_VERSION,
      welcome_dismissed_at: skippedAt,
      tour_skipped_at: skippedAt,
      tour_completed_at: null,
      tour_snoozed_until: null
    }).catch(() => {});
  }, [persistOnboardingState]);
  const rawMaterialTransactionSchema = useMemo(() => {
    const source = settings?.raw_material_transaction_schema || settings?.transaction_schema;
    return parseTransactionSchemaValue(source, DEFAULT_RAW_TRANSACTION_SCHEMA);
  }, [settings?.raw_material_transaction_schema, settings?.transaction_schema]);
  const finishedGoodsTransactionSchema = useMemo(() => {
    const source = settings?.finished_goods_transaction_schema || settings?.transaction_schema;
    return parseTransactionSchemaValue(source, DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA);
  }, [settings?.finished_goods_transaction_schema, settings?.transaction_schema]);
  const transactionSchema = transactionLedgerMode === 'finished_goods'
    ? finishedGoodsTransactionSchema
    : rawMaterialTransactionSchema;
  const productSchema = useMemo(() => {
    if (!settings?.product_schema) {
      return { fields: [] };
    }
    try {
      const parsed = JSON.parse(settings.product_schema);
      return {
        fields: Array.isArray(parsed.fields) ? parsed.fields : []
      };
    } catch (_err) {
      return { fields: [] };
    }
  }, [settings?.product_schema]);
  const staffSchema = useMemo(() => {
    if (!settings?.staff_schema) {
      return { fields: [] };
    }
    try {
      const parsed = JSON.parse(settings.staff_schema);
      return {
        fields: Array.isArray(parsed.fields) ? parsed.fields : []
      };
    } catch (_err) {
      return { fields: [] };
    }
  }, [settings?.staff_schema]);
  const customerSchema = useMemo(() => {
    if (!settings?.customer_schema) {
      return { fields: [] };
    }
    try {
      const parsed = JSON.parse(settings.customer_schema);
      return {
        fields: Array.isArray(parsed.fields) ? parsed.fields : []
      };
    } catch (_err) {
      return { fields: [] };
    }
  }, [settings?.customer_schema]);
  const contractorSchema = useMemo(() => {
    if (!settings?.contractor_schema) {
      return { fields: [] };
    }
    try {
      const parsed = JSON.parse(settings.contractor_schema);
      return {
        fields: Array.isArray(parsed.fields) ? parsed.fields : []
      };
    } catch (_err) {
      return { fields: [] };
    }
  }, [settings?.contractor_schema]);

  useEffect(() => {
    const hasRaw = Boolean(settings?.raw_material_transaction_schema);
    const hasFinished = Boolean(settings?.finished_goods_transaction_schema);
    if (hasRaw && hasFinished) return;
    const legacy = settings?.transaction_schema
      ? parseTransactionSchemaValue(settings.transaction_schema, DEFAULT_RAW_TRANSACTION_SCHEMA)
      : DEFAULT_RAW_TRANSACTION_SCHEMA;
    const nextRaw = parseTransactionSchemaValue(legacy, DEFAULT_RAW_TRANSACTION_SCHEMA);
    const nextFinished = parseTransactionSchemaValue(legacy, DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA);
    setSettings((prev) => ({
      ...prev,
      raw_material_transaction_schema: prev.raw_material_transaction_schema || JSON.stringify(nextRaw),
      finished_goods_transaction_schema: prev.finished_goods_transaction_schema || JSON.stringify(nextFinished)
    }));
  }, [
    settings?.raw_material_transaction_schema,
    settings?.finished_goods_transaction_schema,
    settings?.transaction_schema
  ]);
  const toListKey = useCallback((label) =>
    `rm_${String(label || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')}`, []);

  const normalizeRawMaterialCharacteristics = useCallback((characteristics, rawMaterialName = '') => {
    if (!Array.isArray(characteristics)) return [];
    const seen = new Set();
    return characteristics
      .map((item) => {
        const name = String(item?.name || item?.label || '').trim();
        if (!name) return null;
        const normalized = name.toLowerCase();
        if (seen.has(normalized)) return null;
        seen.add(normalized);
        const listKeyRaw = String(item?.list_key || '').trim();
        const fallbackKey = rawMaterialName ? toListKey(`${rawMaterialName}_${name}`) : toListKey(name);
        return {
          name,
          list_key: listKeyRaw || fallbackKey,
          mandatory: Boolean(item?.mandatory)
        };
      })
      .filter(Boolean);
  }, [toListKey]);

  const rawMaterials = useMemo(() => {
    const normalizeEntry = (item) => {
      const name = String(item?.name || item?.label || '').trim();
      if (!name) return null;
      const characteristics = normalizeRawMaterialCharacteristics(
        item?.characteristics
        || (Array.isArray(item?.subcategories) && item.subcategories.length > 0
          ? [{ name: 'Type', list_key: toListKey(`${name}_type`), mandatory: true }]
          : []),
        name
      );
      return {
        name,
        unit_label: String(item?.unit_label || item?.unitLabel || '').trim(),
        consume_stage: String(item?.consume_stage || item?.consumeStage || '').trim(),
        characteristics,
        is_main: Boolean(item?.is_main),
        archived: Boolean(item?.archived)
      };
    };

    const source = (() => {
      if (!settings?.raw_materials) return [];
      if (Array.isArray(settings.raw_materials)) return settings.raw_materials;
      try {
        const parsed = JSON.parse(settings.raw_materials);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_err) {
        return [];
      }
    })();

    const normalized = source.map(normalizeEntry).filter(Boolean);
    if (normalized.length > 0 && !normalized.some((item) => item.is_main)) {
      normalized[0].is_main = true;
    }
    return normalized;
  }, [settings?.raw_materials, normalizeRawMaterialCharacteristics, toListKey]);

  const rawMaterialOptions = useMemo(() => (
    rawMaterials
      .filter((item) => !item.archived)
      .map((item) => ({
        name: item.name,
        unit_label: item.unit_label || '',
        consume_stage: item.consume_stage || '',
        characteristics: Array.isArray(item.characteristics) ? item.characteristics : [],
        is_main: Boolean(item.is_main),
        archived: Boolean(item.archived)
      }))
      .filter((item) => item.name)
  ), [rawMaterials]);
  const findRawMaterialByName = useCallback(
    (name) => rawMaterials.find((item) => item.name === name) || null,
    [rawMaterials]
  );
  const getRawMaterialSelectOptions = useCallback(
    (selectedName = '') => {
      if (!selectedName) return rawMaterialOptions;
      const selectedMaterial = findRawMaterialByName(selectedName);
      if (!selectedMaterial || !selectedMaterial.archived || rawMaterialOptions.some((item) => item.name === selectedName)) {
        return rawMaterialOptions;
      }
      return [
        ...rawMaterialOptions,
        {
          name: selectedMaterial.name,
          unit_label: selectedMaterial.unit_label || '',
          consume_stage: selectedMaterial.consume_stage || '',
          characteristics: Array.isArray(selectedMaterial.characteristics) ? selectedMaterial.characteristics : [],
          is_main: Boolean(selectedMaterial.is_main),
          archived: true
        }
      ];
    },
    [findRawMaterialByName, rawMaterialOptions]
  );
  const rawMaterialUnitByName = useMemo(
    () => rawMaterials.reduce((acc, item) => {
      const name = String(item?.name || '').trim();
      if (!name) return acc;
      acc[name] = String(item?.unit_label || '').trim();
      return acc;
    }, {}),
    [rawMaterials]
  );
  const csvImportCharacteristicHeaders = useMemo(() => {
    const seen = new Set();
    const headers = [];
    rawMaterials.forEach((material) => {
      (material.characteristics || []).forEach((characteristic) => {
        const name = String(characteristic?.name || '').trim();
        const key = name.toLowerCase();
        if (!name || seen.has(key)) return;
        seen.add(key);
        headers.push(name);
      });
    });
    return headers;
  }, [rawMaterials]);
  const pipelineStages = useMemo(() => {
    if (!settings?.material_pipeline) return [];
    if (Array.isArray(settings.material_pipeline)) {
      return settings.material_pipeline.filter((item) => typeof item === 'string' && item.trim());
    }
    try {
      const parsed = JSON.parse(settings.material_pipeline);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => typeof item === 'string' && item.trim());
    } catch (_err) {
      return [];
    }
  }, [settings?.material_pipeline]);
  

  const settingsSearchQuery = settingsSearch.trim().toLowerCase();
  const matchesSettingsSearch = (value) =>
    !settingsSearchQuery || String(value || '').toLowerCase().includes(settingsSearchQuery);
  const matchesSettingsSection = (label, keywords = []) =>
    !settingsSearchQuery || [label, ...keywords].some((item) => matchesSettingsSearch(item));
  const thresholdCritical = parseFloat(settings.stock_threshold_critical);
  const thresholdLow = parseFloat(settings.stock_threshold_low);
  const thresholdMedium = parseFloat(settings.stock_threshold_medium);
  const thresholdsValid = !isNaN(thresholdCritical)
    && !isNaN(thresholdLow)
    && !isNaN(thresholdMedium)
    && thresholdCritical < thresholdLow
    && thresholdLow < thresholdMedium;
  const settingsDirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);
  const rawQuantityUnit = rawMaterialTransactionSchema.quantity_unit || 'Kg';
  const finishedGoodsQuantityUnit = finishedGoodsTransactionSchema.quantity_unit || 'Pcs';
  const quantityUnit = rawQuantityUnit;
  const rawMaterialTransactionFields = useMemo(
    () => (rawMaterialTransactionSchema.fields || []).filter((field) => !isLegacyTransactionField(field)),
    [rawMaterialTransactionSchema.fields]
  );
  const transactionFields = useMemo(
    () => (transactionSchema.fields || []).filter((field) => !isLegacyTransactionField(field)),
    [transactionSchema.fields]
  );
  const groupingFields = useMemo(
    () => rawMaterialTransactionFields.filter((field) => (rawMaterialTransactionSchema.grouping_keys || []).includes(field.key)),
    [rawMaterialTransactionFields, rawMaterialTransactionSchema.grouping_keys]
  );
  const transactionStockFieldKey = transactionSchema.stock_field_key || 'quantity';
  const transactionTypeFieldKey = transactionSchema.type_field_key || 'type';
  const transactionStockField = useMemo(
    () => transactionFields.find((field) => field.key === transactionStockFieldKey) || {
      key: transactionStockFieldKey,
      label: 'Quantity',
      type: 'number',
      required: true
    },
    [transactionFields, transactionStockFieldKey]
  );
  const transactionExtraFields = useMemo(
    () => transactionFields.filter(
      (field) => field.key !== transactionStockFieldKey && field.key !== transactionTypeFieldKey
    ),
    [transactionFields, transactionStockFieldKey, transactionTypeFieldKey]
  );

  const dropdownFields = useMemo(() => {
    const staffBaseFields = [
      { key: 'staff_skill_category', label: 'Staff Skill Category', type: 'dropdown', required: true },
      { key: 'staff_id_type', label: 'Staff ID Type', type: 'dropdown', required: true }
    ];
    const merged = [
      ...(rawMaterialTransactionFields || []),
      ...((finishedGoodsTransactionSchema.fields || []).filter((field) => !isLegacyTransactionField(field))),
      ...(productSchema.fields || []),
      ...(staffSchema.fields || []),
      ...(customerSchema.fields || []),
      ...(contractorSchema.fields || []),
      ...staffBaseFields
    ];
    const dropdowns = merged.filter((field) => field && field.type === 'dropdown' && field.key);
    const unique = [];
    const seen = new Set();
    dropdowns.forEach((field) => {
      if (seen.has(field.key)) return;
      seen.add(field.key);
      unique.push(field);
    });
    return unique;
  }, [
    rawMaterialTransactionFields,
    finishedGoodsTransactionSchema.fields,
    staffSchema.fields,
    customerSchema.fields,
    contractorSchema.fields,
    productSchema.fields
  ]);

  const customListMap = useMemo(() => {
    const map = {};
    customLists.forEach((list) => {
      map[list.list_key] = list;
    });
    return map;
  }, [customLists]);

  const manageListCatalog = useMemo(() => {
    const sectionConfigs = [
      { id: 'transaction', title: 'Transaction Lists', usage: 'Transactions', fields: rawMaterialTransactionFields || [] },
      {
        id: 'finished-goods',
        title: 'Finished Goods Lists',
        usage: 'Finished Goods',
        fields: ((finishedGoodsTransactionSchema.fields || []).filter((field) => !isLegacyTransactionField(field)))
      },
      { id: 'product', title: 'Product Lists', usage: 'Products', fields: productSchema.fields || [] },
      { id: 'staff', title: 'Staff Lists', usage: 'Staff', fields: staffSchema.fields || [] },
      { id: 'customer', title: 'Customer Lists', usage: 'Customers', fields: customerSchema.fields || [] },
      { id: 'contractor', title: 'Contractor Lists', usage: 'Contractors', fields: contractorSchema.fields || [] },
      {
        id: 'raw-material',
        title: 'Raw Material Value Lists',
        usage: 'Raw Materials',
        fields: rawMaterials.flatMap((material) =>
          (material.characteristics || []).map((characteristic) => ({
            key: characteristic.list_key,
            label: `${material.name} - ${characteristic.name}`,
            type: 'dropdown',
            required: characteristic.mandatory
          }))
        )
      },
      {
        id: 'staff-base',
        title: 'Staff Core Lists',
        usage: 'Staff Core',
        fields: [
          { key: 'staff_skill_category', label: 'Staff Skill Category', type: 'dropdown', required: true },
          { key: 'staff_id_type', label: 'Staff ID Type', type: 'dropdown', required: true }
        ]
      }
    ];

    const byKey = new Map();
    sectionConfigs.forEach((section) => {
      section.fields
        .filter((field) => field?.type === 'dropdown' && field.key)
        .forEach((field) => {
          const existing = byKey.get(field.key);
          if (!existing) {
            byKey.set(field.key, {
              key: field.key,
              label: field.label || field.key,
              required: Boolean(field.required),
              sectionId: section.id,
              sectionTitle: section.title,
              usages: [section.usage]
            });
            return;
          }
          const mergedUsages = Array.from(new Set([...(existing.usages || []), section.usage]));
          byKey.set(field.key, {
            ...existing,
            label: existing.label || field.label || field.key,
            required: existing.required || Boolean(field.required),
            usages: mergedUsages
          });
        });
    });

    return Array.from(byKey.values()).sort((a, b) => {
      if (a.sectionTitle !== b.sectionTitle) return a.sectionTitle.localeCompare(b.sectionTitle);
      return a.label.localeCompare(b.label);
    });
  }, [
    rawMaterialTransactionFields,
    finishedGoodsTransactionSchema.fields,
    productSchema.fields,
    staffSchema.fields,
    customerSchema.fields,
    contractorSchema.fields,
    rawMaterials
  ]);

  const getDropdownOptions = (field) => {
    const list = customListMap[field.key];
    if (list && Array.isArray(list.items) && list.items.length > 0) {
      return list.items.map((item) => item.value);
    }
    if (Array.isArray(field.options) && field.options.length > 0) {
      return field.options;
    }
    return [];
  };

  const getCharacteristicValues = (listKey) => {
    const list = customListMap[listKey];
    if (!list || !Array.isArray(list.items)) return [];
    return list.items.map((item) => item.value).filter(Boolean);
  };

  const normalizeRawMaterialTransactionCharacteristics = (rawMaterialName, characteristicValues = {}) => {
    const selectedMaterial = findRawMaterialByName(rawMaterialName);
    if (!selectedMaterial) return {};
    const output = {};
    (selectedMaterial.characteristics || []).forEach((characteristic) => {
      const name = String(characteristic?.name || '').trim();
      if (!name) return;
      output[name] = String(characteristicValues?.[name] || '').trim();
    });
    return output;
  };

  const getCharacteristicSelectionKey = (characteristicValues = {}) => {
    const normalizedEntries = Object.entries(characteristicValues || {})
      .map(([key, value]) => [String(key || '').trim(), String(value || '').trim()])
      .filter(([key, value]) => key && value)
      .sort(([left], [right]) => left.localeCompare(right));
    return JSON.stringify(Object.fromEntries(normalizedEntries));
  };

  const formatCharacteristicSelectionLabel = (characteristicValues = {}, emptyLabel = 'No characteristics') => {
    const normalizedEntries = Object.entries(characteristicValues || {})
      .map(([key, value]) => [String(key || '').trim(), String(value || '').trim()])
      .filter(([key, value]) => key && value)
      .sort(([left], [right]) => left.localeCompare(right));
    if (normalizedEntries.length === 0) return emptyLabel;
    return normalizedEntries.map(([key, value]) => `${key}: ${value}`).join(' | ');
  };

  const getTransactionRawMaterialContext = (transaction) => {
    const custom = transaction?.custom_fields && typeof transaction.custom_fields === 'object'
      ? transaction.custom_fields
      : {};
    const rawMaterialName = String(custom.raw_material_type || '').trim();
    const finishedProductName = String(custom.finished_product_name || '').trim();
    const baseCharacteristics = custom.raw_material_characteristics && typeof custom.raw_material_characteristics === 'object'
      ? custom.raw_material_characteristics
      : {};
    const normalizedCharacteristics = {};
    Object.entries(baseCharacteristics).forEach(([key, value]) => {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey) return;
      normalizedCharacteristics[normalizedKey] = String(value || '').trim();
    });
    if (Object.keys(normalizedCharacteristics).length === 0) {
      const legacySubcategory = String(custom.raw_material_subcategory || '').trim();
      if (legacySubcategory) {
        normalizedCharacteristics.Type = legacySubcategory;
      }
    }
    return {
      rawMaterialName,
      finishedProductName,
      action: String(custom.raw_material_action || custom.finished_goods_action || '').trim(),
      unitCost: Number(custom.raw_material_cost || custom.finished_goods_unit_cost || 0),
      characteristics: normalizedCharacteristics
    };
  };

  const getTransactionActionLabel = (transaction, rawContext = getTransactionRawMaterialContext(transaction)) => {
    if (rawContext.finishedProductName && !rawContext.rawMaterialName) {
      const normalizedType = String(transaction?.type || '').trim().toLowerCase();
      return FINISHED_GOODS_ACTION_LABELS[normalizedType] || String(transaction?.type || '-').toUpperCase();
    }
    return getRawTransactionActionLabel(rawContext.action, transaction?.type);
  };

  const getRawMaterialStockBalance = (rawMaterialName, characteristicValues = {}) => {
    const normalizedTarget = normalizeRawMaterialTransactionCharacteristics(rawMaterialName, characteristicValues);
    const selectedMaterial = findRawMaterialByName(rawMaterialName);
    if (!selectedMaterial) return 0;
    const characteristicNames = (selectedMaterial.characteristics || [])
      .map((item) => String(item?.name || '').trim())
      .filter(Boolean);
    return transactions.reduce((total, transaction) => {
      const context = getTransactionRawMaterialContext(transaction);
      if (context.rawMaterialName !== rawMaterialName) return total;
      const matches = characteristicNames.every(
        (name) => String(context.characteristics?.[name] || '') === String(normalizedTarget?.[name] || '')
      );
      if (!matches) return total;
      const quantity = Number(transaction.quantity || 0);
      if (!Number.isFinite(quantity)) return total;
      return transaction.type === 'in' ? total + quantity : total - quantity;
    }, 0);
  };

  const goToManageList = (field) => {
    if (!field?.key || !isAdminUser) return;
    setSelectedManageListKey(field.key);
    setManageListCategoryFilter('all');
    setSettingsSearch(field.label || field.key);
    setSettingsTab('customLists');
    setActiveTab('settings');
  };

  const renderDropdownLabel = (field) => (
    <span className="label-with-info">
      <span>{field.label || field.key}{field.required ? ' *' : ''}</span>
      {isAdminUser && field?.key && (
        <button
          type="button"
          className="info-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToManageList(field);
          }}
          title="Manage list"
          aria-label={`Manage list for ${field.label || field.key}`}
        >
          i
        </button>
      )}
    </span>
  );

  const updateTransactionValue = (key, value) => {
    setTransactionValues((prev) => ({ ...prev, [key]: value }));
  };

  const getCurrentTimeZone = () => currentUserTimeZone || getBrowserTimeZone();
  const getTodayDateKey = () => getDateKeyForTimeZone(getCurrentTimeZone());

  useEffect(() => {
    setCurrentUserTimeZone(user?.timezone || getBrowserTimeZone());
  }, [user?.timezone]);

  useEffect(() => {
    try {
      localStorage.setItem('admin_sidebar_collapsed', isSidebarCollapsed ? 'true' : 'false');
    } catch (_err) {
      // Ignore storage write errors.
    }
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const todayDateKey = getDateKeyForTimeZone(currentUserTimeZone || getBrowserTimeZone());
    const nextValues = {};
    transactionFields.forEach((field) => {
      if (field.type === 'checkbox') {
        nextValues[field.key] = false;
      } else if (field.type === 'date') {
        nextValues[field.key] = todayDateKey;
      } else {
        nextValues[field.key] = '';
      }
    });
    setTransactionValues(nextValues);
    setTransactionDate(todayDateKey);
    setTransactionNotes('');
  }, [transactionFields, currentUserTimeZone]);
  const orderedTabIds = orderedTabs.map((tab) => tab.id);

  const updateCustomFieldValue = (tabId, key, value) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [tabId]: {
        ...(prev[tabId] || {}),
        [key]: value
      }
    }));
  };

  const getCustomFieldValues = (tabId) => customFieldValues[tabId] || {};
  const getBaseFormState = () => ({
    type: 'in',
    quantity: '',
    date: getTodayDateKey(),
    notes: ''
  });

  const getCustomFormState = (tabId) => customFormStates[tabId] || getBaseFormState();

  const updateCustomFormState = (tabId, updates) => {
    setCustomFormStates((prev) => ({
      ...prev,
      [tabId]: {
        ...(prev[tabId] || getBaseFormState()),
        ...updates
      }
    }));
  };

  const buildCustomFieldsPayload = (fields, values) => {
    const payload = {};
    for (const field of fields) {
      const value = values[field.key];
      if (field.required && (value === undefined || value === '' || value === null)) {
        throw new Error(`"${field.label}" is required.`);
      }
      if (field.type === 'checkbox') {
        payload[field.key] = Boolean(value);
      } else {
        payload[field.key] = value ?? '';
      }
    }
    return payload;
  };

  const handleSubmitCustomForm = async (tab) => {
    setError('');
    setSuccess('');
    const formState = getCustomFormState(tab.id);
    try {
      const customFieldsPayload = buildCustomFieldsPayload(tab.fields || [], getCustomFieldValues(tab.id));
      await api.addTransaction({
        material_type: 'custom',
        type: formState.type,
        quantity: parseFloat(formState.quantity),
        date: formState.date,
        notes: formState.notes,
        custom_fields: customFieldsPayload,
        entry_source: tab.id
      });
      setSuccess('Transaction added successfully!');
      updateCustomFormState(tab.id, getBaseFormState());
      setCustomFieldValues((prev) => ({ ...prev, [tab.id]: {} }));
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    return () => {
      if (loadRequestRef.current.controller) {
        loadRequestRef.current.controller.abort();
      }
    };
  }, []);


  useEffect(() => {
    if (!orderedTabIds.includes(activeTab)) {
      setActiveTab(orderedTabs[0]?.id || 'stock');
    }
  }, [orderedTabIds, orderedTabs, activeTab]);


  useEffect(() => {
    setSelectedPipelineOrderIds((prev) => prev.filter((id) => orders.some((order) => order.id === id)));
  }, [orders]);


  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!viewingOrder?.id) {
      setOrderCostBreakdown(null);
      setOrderCostBreakdownLoading(false);
      return;
    }
    let isMounted = true;
    setOrderCostBreakdownLoading(true);
    api.getOrderCostBreakdown(viewingOrder.id)
      .then((data) => {
        if (!isMounted) return;
        setOrderCostBreakdown(data || null);
        const manual = data?.manual_components || {};
        setOrderCostForm({
          labor_cost: String(manual.labor_cost ?? ''),
          overhead_cost: String(manual.overhead_cost ?? ''),
          freight_cost: String(manual.freight_cost ?? ''),
          misc_cost: String(manual.misc_cost ?? ''),
          tax_amount: String(manual.tax_amount ?? ''),
          discount_amount: String(manual.discount_amount ?? '')
        });
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message);
      })
      .finally(() => {
        if (isMounted) setOrderCostBreakdownLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [viewingOrder?.id]);

  useEffect(() => {
    if (!canAccessUsersTab && settingsTab === 'users') {
      setSettingsTab('system');
    }
  }, [canAccessUsersTab, settingsTab]);

  useEffect(() => {
    try {
      const key = `role_access_expanded_${String(user?.company_id || 'default')}`;
      const saved = localStorage.getItem(key);
      if (saved === 'true') setRoleAccessExpanded(true);
      else if (saved === 'false') setRoleAccessExpanded(false);
    } catch (_err) {
      // Ignore localStorage read errors.
    }
  }, [user?.company_id]);

  useEffect(() => {
    try {
      const key = `role_access_expanded_${String(user?.company_id || 'default')}`;
      localStorage.setItem(key, roleAccessExpanded ? 'true' : 'false');
    } catch (_err) {
      // Ignore localStorage write errors.
    }
  }, [roleAccessExpanded, user?.company_id]);

  useEffect(() => {
    if (!roleNameOptions.length) {
      if (selectedRoleName) setSelectedRoleName('');
      return;
    }
    if (!selectedRoleName || !roleNameOptions.includes(selectedRoleName)) {
      setSelectedRoleName(roleNameOptions[0]);
    }
  }, [roleNameOptions, selectedRoleName]);

  useEffect(() => {
    if (manageListCatalog.length === 0) {
      if (selectedManageListKey) setSelectedManageListKey('');
      return;
    }
    const exists = manageListCatalog.some((entry) => entry.key === selectedManageListKey);
    if (!exists) {
      setSelectedManageListKey(manageListCatalog[0].key);
    }
  }, [manageListCatalog, selectedManageListKey]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      setTransactionsPage(1);
    }
  }, [activeTab]);

  const handleSetInventoryMovementMode = useCallback((mode) => {
    setTransactionLedgerMode(mode);
    if (mode === 'raw_material') {
      setTransactionFinishedProductFilter('');
      if (!transactionRawMaterialFilter) {
        setTransactionRawMaterialFilter(rawMaterialOptions[0]?.name || '');
      }
      return;
    }
    setTransactionFinishedProductFilter('');
    setTransactionRawCharacteristicFilters({});
  }, [rawMaterialOptions, transactionRawMaterialFilter]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [
    transactionLedgerMode,
    transactionTypeFilter,
    transactionRawMaterialFilter,
    transactionRawActionFilter,
    transactionFinishedProductFilter,
    transactionDateFromFilter,
    transactionDateToFilter,
    transactionRawCharacteristicFilters,
    transactionsPageSize
  ]);

  useEffect(() => {
    setRawMaterialCharacteristics({});
  }, [rawMaterialType]);

  useEffect(() => {
    if (!movementHasChallan) return;
    if (materialType === 'pad' && String(formData.type || '').toLowerCase() !== 'out') {
      setFormData((prev) => ({ ...prev, type: 'out' }));
    }
    if (transactionLedgerMode === 'finished_goods' && finishedGoodsAction !== 'out') {
      setFinishedGoodsAction('out');
      return;
    }
    if (transactionLedgerMode === 'raw_material') {
      const actionConfig = RAW_TRANSACTION_ACTION_MAP[rawMaterialAction];
      if (!actionConfig || actionConfig.type !== 'out') {
        setRawMaterialAction(RAW_OUTBOUND_ACTIONS[0]?.key || rawMaterialAction);
      }
    }
  }, [
    movementHasChallan,
    materialType,
    formData.type,
    transactionLedgerMode,
    finishedGoodsAction,
    rawMaterialAction
  ]);

  useEffect(() => {
    if (!Array.isArray(rawMaterialOptions) || rawMaterialOptions.length === 0) {
      if (transactionRawMaterialFilter) setTransactionRawMaterialFilter('');
      return;
    }
    if (transactionLedgerMode !== 'raw_material') return;
    const exists = rawMaterialOptions.some((item) => item.name === transactionRawMaterialFilter);
    if (!exists) {
      setTransactionRawMaterialFilter(rawMaterialOptions[0].name);
    }
  }, [rawMaterialOptions, transactionRawMaterialFilter, transactionLedgerMode]);

  useEffect(() => {
    if (activeTab === 'order' && !orderForm.order_number) {
      loadNextOrderNumber();
    }
  }, [activeTab, orderForm.order_number]);

  useEffect(() => {
    let isMounted = true;
    const requestId = rawMaterialStockRequestIdRef.current + 1;
    rawMaterialStockRequestIdRef.current = requestId;

    const loadRawMaterialStock = async () => {
      if (!rawMaterialStockType) {
        setRawMaterialStockData([]);
        return;
      }
      try {
        const data = await api.getRawMaterialStock(rawMaterialStockType);
        if (!isMounted || requestId !== rawMaterialStockRequestIdRef.current) return;
        setRawMaterialStockData(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted || requestId !== rawMaterialStockRequestIdRef.current) return;
        setError(err.message);
      }
    };
    loadRawMaterialStock();

    return () => {
      isMounted = false;
    };
  }, [rawMaterialStockType]);

  useEffect(() => {
    if (rawMaterials.length === 0) {
      setSelectedRawMaterialIndex(0);
      return;
    }
    if (selectedRawMaterialIndex >= rawMaterials.length) {
      setSelectedRawMaterialIndex(0);
    }
  }, [rawMaterials, selectedRawMaterialIndex]);

  useEffect(() => {
    if (!Array.isArray(rawMaterialOptions) || rawMaterialOptions.length === 0) {
      if (rawMaterialStockType) setRawMaterialStockType('');
      return;
    }
    const defaultMaterialName = rawMaterialOptions.find((item) => item.is_main)?.name || rawMaterialOptions[0].name;
    if (!rawMaterialStockType) {
      if (defaultMaterialName) setRawMaterialStockType(defaultMaterialName);
      return;
    }
    const exists = rawMaterialOptions.some((item) => item.name === rawMaterialStockType);
    if (exists) return;
    if (defaultMaterialName) {
      setRawMaterialStockType(defaultMaterialName);
    }
  }, [rawMaterialOptions, rawMaterialStockType]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      material_type: 'custom',
    }));
  }, [materialType]);

  const setRawMaterialsInSettings = (nextMaterials) => {
    setSettings({
      ...settings,
      raw_materials: JSON.stringify(nextMaterials)
    });
  };

  const updateProductFormValue = (key, value) => {
    setProductFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const updateProductRawMaterial = (index, updates) => {
    setProductRawMaterials((prev) =>
      prev.map((entry, idx) => (idx === index ? { ...entry, ...updates } : entry))
    );
  };

  const handleAddProductRawMaterialEntry = () => {
    setProductRawMaterials((prev) => ([
      ...prev,
      { raw_material_name: '', characteristics: {}, required_qty: '' }
    ]));
  };

  const handleRemoveProductRawMaterialEntry = (index) => {
    setProductRawMaterials((prev) => prev.filter((_, idx) => idx !== index));
  };

  const resetProductCreateForm = () => {
    setProductFormName('');
    setProductFormValues({});
    setProductRawMaterials([]);
    setProductAddStep(1);
  };

  const handleCreateProduct = async () => {
    const trimmedName = productFormName.trim();
    if (!trimmedName) {
      setError('Product name is required.');
      return;
    }
    const missing = (productSchema.fields || []).find((field) => {
      if (!field?.required) return false;
      const value = productFormValues[field.key];
      if (field.type === 'checkbox') return value !== true;
      if (value === undefined || value === null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    });
    if (missing) {
      setError(`"${missing.label || missing.key}" is required.`);
      return;
    }
    const normalizedRawMaterials = productRawMaterials.map((entry) => normalizeProductRawMaterialEntry(entry));
    if (normalizedRawMaterials.length > 0) {
      const missingRaw = productRawMaterials.some((entry) => {
        const normalized = normalizeProductRawMaterialEntry(entry);
        if (!normalized.raw_material_name || !(parseFloat(normalized.required_qty) > 0)) return true;
        const selectedRawMaterial = rawMaterials.find((item) => item.name === normalized.raw_material_name);
        if (!selectedRawMaterial) return true;
        const requiredCharacteristics = (selectedRawMaterial.characteristics || []).filter((item) => item.mandatory);
        return requiredCharacteristics.some((char) => !String(normalized.characteristics?.[char.name] || '').trim());
      });
      if (missingRaw) {
        setError('Select raw material and quantity. Fill all mandatory characteristics for each entry.');
        return;
      }
    }
    try {
      const payload = {
        name: trimmedName,
        fields: {
          ...productFormValues,
          raw_materials: normalizedRawMaterials
        }
      };
      if (!navigator.onLine) {
        enqueueOfflineAction('product_create', payload);
        setSuccess('You are offline. Product saved and will sync when your connection returns.');
        resetProductCreateForm();
        return;
      }
      await api.createProduct(payload);
      setSuccess('Product added successfully!');
      resetProductCreateForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openOrderNotes = (order) => {
    setOrderNotesTarget(order);
    setOrderNotesText(order?.notes || '');
  };

  const handleSaveOrderNotes = async () => {
    if (!orderNotesTarget) return;
    const notes = orderNotesText.trim();
    if (!navigator.onLine) {
      enqueueOfflineAction('order_note_update', { id: orderNotesTarget.id, notes });
      setOrders((prev) => prev.map((item) => (item.id === orderNotesTarget.id ? { ...item, notes } : item)));
      setSuccess('You are offline. Order notes saved and will sync when your connection returns.');
      setOrderNotesTarget(null);
      return;
    }
    try {
      const updated = await api.updateOrderNotes(orderNotesTarget.id, notes);
      setOrders((prev) => prev.map((item) => (item.id === orderNotesTarget.id ? { ...item, notes: updated.notes } : item)));
      setSuccess('Order notes saved.');
      setOrderNotesTarget(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveOrderCostComponents = async () => {
    if (!viewingOrder?.id) return;
    if (!navigator.onLine) {
      setError('Device is offline. Please reconnect to save landed cost inputs.');
      return;
    }
    try {
      const payload = {
        labor_cost: Number(orderCostForm.labor_cost || 0),
        overhead_cost: Number(orderCostForm.overhead_cost || 0),
        freight_cost: Number(orderCostForm.freight_cost || 0),
        misc_cost: Number(orderCostForm.misc_cost || 0),
        tax_amount: Number(orderCostForm.tax_amount || 0),
        discount_amount: Number(orderCostForm.discount_amount || 0)
      };
      const updated = await api.updateOrderCostComponents(viewingOrder.id, payload);
      setOrderCostBreakdown(updated || null);
      setSuccess('Order landed cost inputs saved.');
      await loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const openProductNotes = (product) => {
    setProductNotesTarget(product);
    setProductNotesText(product?.notes || '');
  };

  const handleSaveProductNotes = async () => {
    if (!productNotesTarget) return;
    const notes = productNotesText.trim();
    if (!navigator.onLine) {
      enqueueOfflineAction('product_note_update', { id: productNotesTarget.id, notes });
      setProductCatalog((prev) => prev.map((item) => (item.id === productNotesTarget.id ? { ...item, notes } : item)));
      setSuccess('You are offline. Product notes saved and will sync when your connection returns.');
      setProductNotesTarget(null);
      return;
    }
    try {
      const updated = await api.updateProductNotes(productNotesTarget.id, notes);
      setProductCatalog((prev) => prev.map((item) => (item.id === productNotesTarget.id ? { ...item, notes: updated.notes } : item)));
      setSuccess('Product notes saved.');
      setProductNotesTarget(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const openNotesModal = (type, entity, label) => {
    setNotesModalTarget({ type, entity, label });
    setNotesModalText(entity?.notes || '');
  };

  const handleSaveNotesModal = async () => {
    if (!notesModalTarget?.entity) return;
    const notes = notesModalText.trim();
    const { type, entity } = notesModalTarget;
    try {
      if (type === 'staff') {
        const updated = await api.updateStaffNotes(entity.id, notes);
        setStaff((prev) => prev.map((item) => (item.id === entity.id ? { ...item, notes: updated.notes } : item)));
      } else if (type === 'customer') {
        const updated = await api.updateCustomerNotes(entity.id, notes);
        setCustomers((prev) => prev.map((item) => (item.id === entity.id ? { ...item, notes: updated.notes } : item)));
      } else if (type === 'contractor') {
        const updated = await api.updateContractorNotes(entity.id, notes);
        setContractors((prev) => prev.map((item) => (item.id === entity.id ? { ...item, notes: updated.notes } : item)));
      } else if (type === 'transaction') {
        const updated = await api.updateTransactionNotes(entity.id, notes);
        setTransactions((prev) => prev.map((item) => (item.id === entity.id ? { ...item, notes: updated.notes } : item)));
      }
      setSuccess('Notes saved.');
      setNotesModalTarget(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const parseProductPayload = (product) => {
    try {
      return JSON.parse(product?.payload || '{}');
    } catch (_err) {
      return {};
    }
  };

  const handleStartEditProduct = (product) => {
    const payload = parseProductPayload(product);
    const fields = payload.fields && typeof payload.fields === 'object' ? payload.fields : {};
    const existingRawMaterials = Array.isArray(fields.raw_materials)
      ? fields.raw_materials.map((entry) => normalizeProductRawMaterialEntry(entry))
      : [];

    const nextValues = { ...fields };
    delete nextValues.raw_materials;

    setEditingProduct(product);
    setEditProductName(product?.name || '');
    setEditProductValues(nextValues);
    setEditProductRawMaterialCount(existingRawMaterials.length);
    setEditProductRawMaterials(existingRawMaterials);
  };

  const updateEditProductRawMaterial = (index, updates) => {
    setEditProductRawMaterials((prev) =>
      prev.map((entry, idx) => (idx === index ? { ...entry, ...updates } : entry))
    );
  };

  const handleSaveEditedProduct = async () => {
    if (!editingProduct?.id) return;
    const trimmedName = editProductName.trim();
    if (!trimmedName) {
      setError('Product name is required.');
      return;
    }
    const missing = (productSchema.fields || []).find((field) => {
      if (!field?.required) return false;
      const value = editProductValues[field.key];
      if (field.type === 'checkbox') return value !== true;
      if (value === undefined || value === null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    });
    if (missing) {
      setError(`"${missing.label || missing.key}" is required.`);
      return;
    }
    if (editProductRawMaterialCount > 0) {
      const missingRaw = editProductRawMaterials.some((entry) => {
        const normalized = normalizeProductRawMaterialEntry(entry);
        if (!normalized.raw_material_name || !(parseFloat(normalized.required_qty) > 0)) return true;
        const selectedRawMaterial = rawMaterials.find((item) => item.name === normalized.raw_material_name);
        if (!selectedRawMaterial) return true;
        const requiredCharacteristics = (selectedRawMaterial.characteristics || []).filter((item) => item.mandatory);
        return requiredCharacteristics.some((char) => !String(normalized.characteristics?.[char.name] || '').trim());
      });
      if (missingRaw) {
        setError('Select raw material and quantity. Fill all mandatory characteristics for each entry.');
        return;
      }
    }

    try {
      await api.updateProduct(editingProduct.id, {
        name: trimmedName,
        fields: {
          ...editProductValues,
          raw_materials: editProductRawMaterialCount > 0
            ? editProductRawMaterials.map((entry) => normalizeProductRawMaterialEntry(entry))
            : []
        }
      });
      setSuccess('Product updated successfully!');
      setEditingProduct(null);
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await api.deleteProduct(id);
      setSuccess('Product deleted.');
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const requestDeleteProduct = (product) => {
    openConfirmDialog({
      type: 'product-delete',
      id: product.id,
      title: 'Delete product?',
      message: `Delete "${product.name}"? This action cannot be undone.`,
      actionLabel: 'Delete Product'
    });
  };

  const requestDuplicateProduct = (product) => {
    openConfirmDialog({
      type: 'product-duplicate',
      id: product.id,
      title: 'Duplicate product?',
      message: `Create a copy of "${product.name}"?`,
      actionLabel: 'Duplicate Product'
    });
  };

  const handleDuplicateProduct = async (product) => {
    const fields = product?.parsedPayload?.fields && typeof product.parsedPayload.fields === 'object'
      ? product.parsedPayload.fields
      : {};
    const baseName = String(product?.name || 'Product').trim() || 'Product';
    try {
      await api.createProduct({
        name: `${baseName} (Copy)`,
        fields
      });
      setSuccess(`Duplicated "${baseName}".`);
      await loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleProductBomExpanded = (productId) => {
    setExpandedProductBomIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };

  const persistRawMaterialsSetting = async (nextMaterials) => {
    const serialized = JSON.stringify(nextMaterials);
    setSettings((prev) => ({
      ...prev,
      raw_materials: serialized
    }));
    try {
      await api.updateSettings({ raw_materials: serialized });
      setSavedSettings((prev) => ({
        ...prev,
        raw_materials: serialized
      }));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const addRawMaterial = async () => {
    const name = newRawMaterial.name.trim();
    const unitLabel = newRawMaterial.unitLabel.trim();
    const consumeStage = newRawMaterial.consumeStage.trim();
    if (!name) return;
    if (rawMaterials.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      setError('Raw material name must be unique.');
      return;
    }
    const hasMain = rawMaterials.some((item) => item.is_main);
    const nextMaterials = [
      ...rawMaterials,
      { name, unit_label: unitLabel, consume_stage: consumeStage, characteristics: [], is_main: !hasMain }
    ];
    const persisted = await persistRawMaterialsSetting(nextMaterials);
    if (!persisted) return;
    setSelectedRawMaterialIndex(nextMaterials.length - 1);
    setNewRawMaterial({ name: '', unitLabel: '', consumeStage: '' });
  };

  const updateRawMaterial = (index, updates) => {
    const next = updates.is_main
      ? rawMaterials.map((item, idx) =>
        idx === index ? { ...item, ...updates, is_main: true } : { ...item, is_main: false }
      )
      : rawMaterials.map((item, idx) => (idx === index ? { ...item, ...updates } : item));
    setRawMaterialsInSettings(next);
  };

  const toggleRawMaterialArchived = async (index) => {
    const target = rawMaterials[index];
    if (!target) return;
    const next = rawMaterials.map((item, idx) =>
      idx === index ? { ...item, archived: !item.archived } : item
    );
    if (!next.some((item) => item.is_main && !item.archived)) {
      const firstActiveIndex = next.findIndex((item) => !item.archived);
      if (firstActiveIndex >= 0) {
        next.forEach((item, idx) => {
          item.is_main = idx === firstActiveIndex;
        });
      }
    }
    const persisted = await persistRawMaterialsSetting(next);
    if (!persisted) return;
    setSuccess(target.archived ? `"${target.name}" restored.` : `"${target.name}" archived.`);
  };

  const updateRawMaterialCharacteristics = (index, nextCharacteristics) => {
    const rawMaterialName = rawMaterials[index]?.name || '';
    updateRawMaterial(index, {
      characteristics: normalizeRawMaterialCharacteristics(nextCharacteristics, rawMaterialName)
    });
  };

  const addRawMaterialCategory = async (index) => {
    const name = newRawMaterialCategory.name.trim();
    if (!name) return;
    const material = rawMaterials[index];
    if (!material) return;
    const next = Array.isArray(material.characteristics)
      ? [...material.characteristics]
      : [];
    if (next.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      setError(`"${name}" already exists for this raw material.`);
      return;
    }
    next.push({
      name,
      list_key: toListKey(`${material.name || 'material'}_${name}`),
      mandatory: Boolean(newRawMaterialCategory.mandatory)
    });
    const normalized = normalizeRawMaterialCharacteristics(next, material.name || '');
    const nextMaterials = rawMaterials.map((item, itemIndex) =>
      itemIndex === index ? { ...item, characteristics: normalized } : item
    );
    const persisted = await persistRawMaterialsSetting(nextMaterials);
    if (!persisted) return;
    setNewRawMaterialCategory({ name: '', mandatory: false });
  };

  const updateRawMaterialCategory = (index, categoryIndex, updates) => {
    const nextName = String(updates?.name || '').trim();
    if (nextName) {
      const duplicateExists = (rawMaterials[index]?.characteristics || []).some(
        (item, idx) => idx !== categoryIndex && item.name.toLowerCase() === nextName.toLowerCase()
      );
      if (duplicateExists) {
        setError(`"${nextName}" already exists for this raw material.`);
        return;
      }
    }
    const next = Array.isArray(rawMaterials[index]?.characteristics)
      ? rawMaterials[index].characteristics.map((item, idx) =>
        idx === categoryIndex
          ? {
            ...item,
            ...updates,
            list_key: toListKey(`${rawMaterials[index]?.name || 'material'}_${updates?.name || item.name}`)
          }
          : item
      )
      : [];
    updateRawMaterialCharacteristics(index, next);
  };

  const removeRawMaterialCategory = (index, categoryIndex) => {
    const raw = rawMaterials[index];
    const target = raw?.characteristics?.[categoryIndex];
    if (!raw || !target) return;
    const inUse = productCatalog.some((product) => {
      let payload = {};
      try {
        payload = JSON.parse(product?.payload || '{}');
      } catch (_err) {
        payload = {};
      }
      const rawList = Array.isArray(payload?.fields?.raw_materials) ? payload.fields.raw_materials : [];
      return rawList.some((entry) => {
        const sameMaterial =
          String(entry?.raw_material_name || entry?.type || '').trim().toLowerCase() === raw.name.toLowerCase();
        const selections = entry?.characteristics && typeof entry.characteristics === 'object'
          ? entry.characteristics
          : {};
        return sameMaterial && String(selections[target.name] || '').trim() !== '';
      });
    });
    if (inUse) {
      setError(`Cannot delete characteristic "${target.name}" because it is used in products.`);
      return;
    }
    const next = Array.isArray(rawMaterials[index]?.characteristics)
      ? rawMaterials[index].characteristics.filter((_, idx) => idx !== categoryIndex)
      : [];
    updateRawMaterialCharacteristics(index, next);
  };

  const loadData = useCallback(async ({ silent = false } = {}) => {
    const nextRequestId = loadRequestRef.current.id + 1;
    if (loadRequestRef.current.controller) {
      loadRequestRef.current.controller.abort();
    }
    const controller = new AbortController();
    loadRequestRef.current = { id: nextRequestId, controller, inFlight: true };

    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const requestOptions = { signal: controller.signal };
      const promises = [
        api.getTransactions(materialType, requestOptions),
        api.getColors(requestOptions),
        api.getStock(requestOptions),
        api.getSettings(requestOptions),
        api.getCustomLists(requestOptions),
      ];
      let rawMaterialStockIndex = null;
      if (rawMaterialStockType) {
        rawMaterialStockIndex = promises.push(api.getRawMaterialStock(rawMaterialStockType, requestOptions)) - 1;
      }
      
      let usersIndex = null;
      if (
        activeTab === 'users'
        || activeTab === 'pipeline_board'
        || (activeTab === 'settings' && (settingsTab === 'users' || settingsTab === 'system') && canAccessUsersTab)
        || shouldPrefetchOnboardingUsers
      ) {
        usersIndex = promises.push(api.getUsers(requestOptions)) - 1;
      }
      let ordersIndex = null;
      if (
        ordersEnabled
        && (
          activeTab === 'order'
          || activeTab === 'orders'
          || activeTab === 'insights'
          || activeTab === 'pipeline_board'
          || (activeTab === 'stock' && stockOverviewMode === 'finished_goods')
          || shouldPrefetchOnboardingOrders
        )
      ) {
        ordersIndex = promises.push(api.getOrders(requestOptions)) - 1;
      }
      let productsIndex = null;
      if (
        activeTab === 'order'
        || activeTab === 'products'
        || activeTab === 'product_add'
        || activeTab === 'add'
        || (activeTab === 'stock' && stockOverviewMode === 'finished_goods')
        || shouldPrefetchOnboardingProducts
      ) {
        productsIndex = promises.push(api.getProducts(requestOptions)) - 1;
      }
      let staffIndex = null;
      if (canAccessStaffTab && activeTab === 'staff') {
        staffIndex = promises.push(api.getStaff(requestOptions)) - 1;
      }
      let customersIndex = null;
      if (canAccessCustomersTab && (activeTab === 'order' || activeTab === 'customers')) {
        customersIndex = promises.push(api.getCustomers(requestOptions)) - 1;
      }
      let contractorsIndex = null;
      if (canAccessContractorsTab && activeTab === 'contractors') {
        contractorsIndex = promises.push(api.getContractors(requestOptions)) - 1;
      }

      const requestNames = ['transactions', 'colors', 'stock', 'settings', 'custom lists'];
      if (rawMaterialStockIndex !== null) requestNames[rawMaterialStockIndex] = 'raw material stock';
      if (usersIndex !== null) requestNames[usersIndex] = 'users';
      if (ordersIndex !== null) requestNames[ordersIndex] = 'orders';
      if (productsIndex !== null) requestNames[productsIndex] = 'products';
      if (staffIndex !== null) requestNames[staffIndex] = 'staff';
      if (customersIndex !== null) requestNames[customersIndex] = 'customers';
      if (contractorsIndex !== null) requestNames[contractorsIndex] = 'contractors';

      const settled = await Promise.allSettled(promises);
      if (nextRequestId !== loadRequestRef.current.id) {
        return;
      }
      const failedRequests = [];
      const values = settled.map((result, index) => {
        if (result.status === 'fulfilled') return result.value;
        if (result.reason?.name !== 'AbortError') {
          failedRequests.push(requestNames[index] || `request #${index + 1}`);
        }
        return undefined;
      });

      if (values[0] !== undefined) {
        setTransactions(Array.isArray(values[0]) ? values[0] : []);
      }
      if (values[1] !== undefined) {
        setColors(Array.isArray(values[1]) ? values[1] : []);
      }
      if (values[2] !== undefined) {
        setStock(Array.isArray(values[2]) ? values[2] : []);
      }
      if (values[3] !== undefined) {
        const mergedSettings = { ...DEFAULT_SETTINGS, ...(values[3] || {}) };
        setSettings(mergedSettings);
        setSavedSettings(mergedSettings);
      }
      if (values[4] !== undefined) {
        setCustomLists(Array.isArray(values[4]) ? values[4] : []);
      }

      if (usersIndex !== null && values[usersIndex] !== undefined) {
        setUsers(Array.isArray(values[usersIndex]) ? values[usersIndex] : []);
      }
      if (ordersIndex !== null && values[ordersIndex] !== undefined) {
        setOrders(Array.isArray(values[ordersIndex]) ? values[ordersIndex] : []);
      }
      if (productsIndex !== null && values[productsIndex] !== undefined) {
        setProductCatalog(Array.isArray(values[productsIndex]) ? values[productsIndex] : []);
      }
      if (shouldPrefetchOnboardingData) {
        setOnboardingDataLoaded(true);
      }
      if (staffIndex !== null && values[staffIndex] !== undefined) {
        setStaff(Array.isArray(values[staffIndex]) ? values[staffIndex] : []);
      }
      if (customersIndex !== null && values[customersIndex] !== undefined) {
        setCustomers(Array.isArray(values[customersIndex]) ? values[customersIndex] : []);
      }
      if (contractorsIndex !== null && values[contractorsIndex] !== undefined) {
        setContractors(Array.isArray(values[contractorsIndex]) ? values[contractorsIndex] : []);
      }
      if (rawMaterialStockIndex !== null) {
        if (values[rawMaterialStockIndex] !== undefined) {
          setRawMaterialStockData(Array.isArray(values[rawMaterialStockIndex]) ? values[rawMaterialStockIndex] : []);
        }
      } else {
        setRawMaterialStockData([]);
      }

      if (failedRequests.length > 0) {
        setError(`Could not load: ${failedRequests.join(', ')}. Showing last known data.`);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      if (nextRequestId === loadRequestRef.current.id) {
        loadRequestRef.current = { ...loadRequestRef.current, inFlight: false };
        if (!silent) {
          setLoading(false);
        }
      }
    }
  }, [
    materialType,
    rawMaterialStockType,
    shouldPrefetchOnboardingData,
    shouldPrefetchOnboardingOrders,
    shouldPrefetchOnboardingProducts,
    shouldPrefetchOnboardingUsers,
    activeTab,
    settingsTab,
    canAccessUsersTab,
    ordersEnabled,
    stockOverviewMode,
    canAccessStaffTab,
    canAccessCustomersTab,
    canAccessContractorsTab
  ]);

  // Tour state is defined later once the onboarding context is assembled.
  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    loadOnboardingState();
  }, [loadOnboardingState]);

  // Tour state is defined later once the onboarding context is assembled.
  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Tour state is defined later once the onboarding context is assembled.
  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    const refreshableTabs = new Set(['pipeline_board', 'orders', 'stock', 'transactions', 'insights']);
    if (!refreshableTabs.has(activeTab)) return undefined;
    const intervalMs = activeTab === 'pipeline_board' ? 30000 : 45000;
    const timer = setInterval(() => {
      loadData({ silent: true });
    }, intervalMs);
    return () => clearInterval(timer);
  }, [activeTab, loadData]);

  const handleDeleteColor = async (id) => {
    try {
      await api.deleteColor(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.password.trim()) return;
    if (!isStrongPassword(newUser.password)) {
      setError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }

    try {
      await api.addUser(newUser.username, newUser.password, newUser.role);
      setNewUser({ username: '', password: '', role: 'user' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditUser = (usr) => {
    setEditingUser({
      id: usr.id,
      username: usr.username,
      password: '', // Leave empty, will only update if filled
      role: usr.role,
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    if (editingUser.password && editingUser.password.trim() && !isStrongPassword(editingUser.password)) {
      setError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }

    try {
      const response = await api.updateUser(
        editingUser.id,
        editingUser.username,
        editingUser.password, // Can be empty
        editingUser.role
      );
      if (editingUser.id === user.id && response?.user) {
        api.setAuthSession({
          token: response.token,
          refreshToken: api.getRefreshToken(),
          user: response.user
        });
        if (typeof onUserUpdate === 'function') {
          onUserUpdate(response.user);
        }
      }
      setEditingUser(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.updateSettings(settings);
      setSuccess('Settings updated successfully!');
      setSavedSettings(settings);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const buildSettingsWithUserRoles = (roles, baseSettings = settings) => {
    const normalizedRoles = roles
      .map((role) => {
        const name = String(role?.name || '').trim();
        if (!name) return null;
        const inheritsFrom = String(role?.inherits_from || '').trim();
        const permissions = {};
        const sourcePermissions = role?.permissions && typeof role.permissions === 'object' ? role.permissions : {};
        Object.keys(sourcePermissions).forEach((sectionKey) => {
          const normalizedSection = normalizePermissionSectionId(sectionKey);
          if (!normalizedSection) return;
          const set = createPermissionSet(sourcePermissions[sectionKey] || {});
          permissions[normalizedSection] = set;
        });
        const tabs = Object.keys(permissions).filter((section) => hasAnyPermission(permissions[section]));
        return {
          name,
          inherits_from: inheritsFrom || undefined,
          permissions,
          tabs
        };
      })
      .filter(Boolean);
    return {
      ...baseSettings,
      user_roles: JSON.stringify({ roles: normalizedRoles })
    };
  };

  const persistUserRoles = async (roles, successMessage = 'Role access updated.') => {
    const nextSettings = buildSettingsWithUserRoles(roles, settings);
    setSettings(nextSettings);
    try {
      await api.updateSettings(nextSettings);
      setSavedSettings(nextSettings);
      setSuccess(successMessage);
      loadData({ silent: true });
      return true;
    } catch (err) {
      setError(`Role changes not saved: ${err.message}`);
      return false;
    }
  };

  const handleAddUserRole = async () => {
    const roleName = newUserRoleName.trim();
    if (!roleName) {
      setError('Role name is required.');
      return;
    }
    const reserved = ['admin', 'owner', 'user', 'superadmin'];
    if (reserved.includes(roleName.toLowerCase())) {
      setError('This role name is reserved.');
      return;
    }
    if (userRoleDefinitions.some((role) => role.name.toLowerCase() === roleName.toLowerCase())) {
      setError('Role already exists.');
      return;
    }
    if (newUserRoleParent && newUserRoleParent.toLowerCase() === roleName.toLowerCase()) {
      setError('Role cannot inherit from itself.');
      return;
    }
    const nextRoles = [
      ...userRoleDefinitions,
      {
        name: roleName,
        inherits_from: newUserRoleParent || undefined,
        permissions: {}
      }
    ];
    const saved = await persistUserRoles(nextRoles, 'Role added and saved.');
    if (!saved) return;
    setNewUserRoleName('');
    setNewUserRoleParent('');
    setSelectedRoleName(roleName);
  };

  const handleRemoveUserRole = async (index) => {
    const removedRole = userRoleDefinitions[index];
    const removedName = String(removedRole?.name || '').trim().toLowerCase();
    const next = userRoleDefinitions
      .filter((_, idx) => idx !== index)
      .map((role) => {
        if (removedName && String(role?.inherits_from || '').trim().toLowerCase() === removedName) {
          return { ...role, inherits_from: '' };
        }
        return role;
      });
    const saved = await persistUserRoles(next, 'Role removed and saved.');
    if (!saved) return;
    if (selectedRoleName === removedRole?.name) {
      setSelectedRoleName(next[0]?.name || '');
    }
  };

  const handleToggleUserRolePermission = async (roleIndex, tabId, action, basePermissionSet = null) => {
    const next = userRoleDefinitions.map((role, index) => {
      if (index !== roleIndex) return role;
      const currentPermissions = role?.permissions && typeof role.permissions === 'object'
        ? role.permissions
        : {};
      const currentSet = createPermissionSet(basePermissionSet || currentPermissions[tabId] || {});
      const nextSet = { ...currentSet, [action]: !currentSet[action] };
      const nextPermissions = { ...currentPermissions };
      if (hasAnyPermission(nextSet)) nextPermissions[tabId] = nextSet;
      else delete nextPermissions[tabId];
      return { ...role, permissions: nextPermissions };
    });
    await persistUserRoles(next, 'Role permissions saved.');
  };

  const handleSetRoleInheritance = async (roleIndex, parentRoleName) => {
    const targetRole = userRoleDefinitions[roleIndex];
    const targetName = String(targetRole?.name || '').trim().toLowerCase();
    const parentName = String(parentRoleName || '').trim();
    if (parentName && parentName.toLowerCase() === targetName) {
      setError('Role cannot inherit from itself.');
      return;
    }
    const next = userRoleDefinitions.map((role, index) => (
      index === roleIndex
        ? { ...role, inherits_from: parentName }
        : role
    ));
    await persistUserRoles(next, 'Role inheritance saved.');
  };

  const handleClearRolePermissionOverride = async (roleIndex, tabId) => {
    const next = userRoleDefinitions.map((role, index) => {
      if (index !== roleIndex) return role;
      const currentPermissions = role?.permissions && typeof role.permissions === 'object'
        ? role.permissions
        : {};
      const nextPermissions = { ...currentPermissions };
      delete nextPermissions[tabId];
      return { ...role, permissions: nextPermissions };
    });
    await persistUserRoles(next, 'Role override cleared.');
  };

  const handleApplyRoleBulkPermissions = async (roleIndex, mode) => {
    const next = userRoleDefinitions.map((role, index) => {
      if (index !== roleIndex) return role;
      const currentPermissions = role?.permissions && typeof role.permissions === 'object'
        ? role.permissions
        : {};
      const nextPermissions = { ...currentPermissions };
      roleTabOptions.forEach((tab) => {
        const tabId = tab.id;
        const currentSet = createPermissionSet(resolvedRolePermissionsMap[role.name]?.[tabId] || {});
        if (mode === 'grant_view') {
          nextPermissions[tabId] = { ...currentSet, view: true };
          return;
        }
        if (mode === 'grant_crud') {
          nextPermissions[tabId] = {
            view: true,
            create: true,
            edit: true,
            delete: true
          };
          return;
        }
        if (mode === 'clear_all') {
          nextPermissions[tabId] = {
            view: false,
            create: false,
            edit: false,
            delete: false
          };
        }
      });
      return { ...role, permissions: nextPermissions };
    });
    const message = mode === 'grant_view'
      ? 'Granted view access for all sections.'
      : mode === 'grant_crud'
        ? 'Granted full CRUD access for all sections.'
        : 'Cleared all section permissions.';
    await persistUserRoles(next, message);
  };

  const handleDeleteUser = async (id) => {
    try {
      await api.deleteUser(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetStaffForm = (overrides = {}) => {
    setStaffForm({
      name: '',
      skill_category: '',
      id_type: '',
      id_value: '',
      is_active: true,
      ...overrides
    });
    setStaffCustomValues({});
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const customFields = buildCustomFieldsPayload(staffSchema.fields || [], staffCustomValues);
      await api.addStaff({
        name: staffForm.name,
        skill_category: staffForm.skill_category,
        id_type: staffForm.id_type,
        id_value: staffForm.id_value,
        custom_fields: customFields
      });
      setSuccess('Staff member added.');
      setShowCreateStaff(false);
      resetStaffForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStaff = (member) => {
    setEditingStaff(member);
    const cachedId = staffIdCache[member.id] || '';
    resetStaffForm({
      name: member.name || '',
      skill_category: member.skill_category || '',
      id_type: member.id_type || '',
      id_value: cachedId,
      is_active: member.is_active !== false
    });
    setStaffCustomValues(member.custom_fields || {});
  };

  const handleViewStaff = (member) => {
    setViewingStaff(member);
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    if (!editingStaff) return;
    setError('');
    if (editingStaff.id_masked && !staffForm.id_value) {
      setError('Reveal the ID value before saving.');
      return;
    }
    try {
      const customFields = buildCustomFieldsPayload(staffSchema.fields || [], staffCustomValues);
      await api.updateStaff(editingStaff.id, {
        name: staffForm.name,
        skill_category: staffForm.skill_category,
        id_type: staffForm.id_type,
        id_value: staffForm.id_value,
        is_active: staffForm.is_active,
        custom_fields: customFields
      });
      setSuccess('Staff updated.');
      setEditingStaff(null);
      resetStaffForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevealStaffId = async (memberId) => {
    if (staffIdCache[memberId]) {
      setStaffForm((prev) => ({ ...prev, id_value: staffIdCache[memberId] }));
      return;
    }
    try {
      const result = await api.revealStaffId(memberId);
      const value = result?.id_value || '';
      setStaffIdCache((prev) => ({ ...prev, [memberId]: value }));
      setStaffForm((prev) => ({ ...prev, id_value: value }));
      setSuccess('ID revealed.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteStaff = async (id) => {
    try {
      await api.deleteStaff(id);
      setSuccess('Staff deleted.');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateStaffStatus = async (id, isActive) => {
    try {
      await api.updateStaffStatus(id, isActive);
      setSuccess(isActive ? 'Staff marked active.' : 'Staff marked unemployed.');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const customFields = buildCustomFieldsPayload(customerSchema.fields || [], customerCustomValues);
      await api.addCustomer({ custom_fields: customFields });
      setSuccess('Customer added.');
      setShowCreateCustomer(false);
      setCustomerCustomValues({});
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerCustomValues(customer.custom_fields || {});
  };

  const handleViewCustomer = (customer) => {
    setViewingCustomer(customer);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    setError('');
    try {
      const customFields = buildCustomFieldsPayload(customerSchema.fields || [], customerCustomValues);
      await api.updateCustomer(editingCustomer.id, {
        custom_fields: customFields,
        is_active: editingCustomer.is_active !== false
      });
      setSuccess('Customer updated.');
      setEditingCustomer(null);
      setCustomerCustomValues({});
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCustomer = async (id) => {
    try {
      await api.deleteCustomer(id);
      setSuccess('Customer deleted.');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddContractor = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const customFields = buildCustomFieldsPayload(contractorSchema.fields || [], contractorCustomValues);
      await api.addContractor({ custom_fields: customFields });
      setSuccess('Contractor added.');
      setShowCreateContractor(false);
      setContractorCustomValues({});
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditContractor = (contractor) => {
    setEditingContractor(contractor);
    setContractorCustomValues(contractor.custom_fields || {});
  };

  const handleViewContractor = (contractor) => {
    setViewingContractor(contractor);
  };

  const handleUpdateContractor = async (e) => {
    e.preventDefault();
    if (!editingContractor) return;
    setError('');
    try {
      const customFields = buildCustomFieldsPayload(contractorSchema.fields || [], contractorCustomValues);
      await api.updateContractor(editingContractor.id, {
        custom_fields: customFields,
        is_active: editingContractor.is_active !== false
      });
      setSuccess('Contractor updated.');
      setEditingContractor(null);
      setContractorCustomValues({});
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteContractor = async (id) => {
    try {
      await api.deleteContractor(id);
      setSuccess('Contractor deleted.');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnlockUser = async (username) => {
    try {
      await api.unlockUser(username);
      setSuccess(`User "${username}" unlocked`);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTimezoneChange = async (id, timezone) => {
    try {
      await api.updateUserTimezone(id, timezone);
      if (Number(id) === Number(user?.id)) {
        setCurrentUserTimeZone(timezone || getBrowserTimeZone());
        try {
          const parsedUser = api.getCurrentUser();
          if (parsedUser && Number(parsedUser.id) === Number(id)) {
            api.setCurrentUser({ ...parsedUser, timezone });
          }
        } catch (_err) {
          // no-op: localStorage sync is best-effort
        }
      }
      setSuccess('Timezone updated successfully');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const parseUtcTimestamp = (value) => {
    if (!value) return null;
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const hasTz = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
    return new Date(hasTz ? normalized : `${normalized}Z`);
  };

  const formatDateTime = (value, timeZone) => {
    if (!value) return '-';
    const date = parseUtcTimestamp(value) || new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const formatDateOnly = (value, timeZone) => {
    if (!value) return '-';
    const parsedValue = String(value);
    const date = parsedValue.includes('T')
      ? parseUtcTimestamp(parsedValue) || new Date(parsedValue)
      : new Date(`${parsedValue}T00:00:00Z`);
    if (!date || Number.isNaN(date.getTime())) return '-';
    const normalizedTimeZone = parsedValue.includes('T') ? timeZone : 'UTC';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: normalizedTimeZone,
      dateStyle: 'medium',
    }).format(date);
  };

  const formatInsightsCurrency = (value) => {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await api.deleteTransaction(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const parseOrderPayload = useCallback((payload) => {
    if (!payload) return null;
    if (typeof payload === 'object') return payload;
    try {
      return JSON.parse(payload);
    } catch (_err) {
      return null;
    }
  }, []);
  const stageColorMap = useMemo(() => {
    const stages = pipelineStages.length > 0
      ? pipelineStages
      : Array.from(new Set(orders.map((order) => order.pipeline_stage).filter(Boolean)));
    const count = stages.length || 1;
    const map = {};
    stages.forEach((stage, idx) => {
      const hue = (idx * 360) / count;
      map[stage] = {
        bg: `hsl(${hue} 70% 90%)`,
        fg: `hsl(${hue} 40% 30%)`
      };
    });
    return map;
  }, [pipelineStages, orders]);

  const getStageColors = (stage) => {
    const normalized = String(stage || '').trim().toLowerCase();
    if (normalized === 'completed') return { bg: '#e3f6ec', fg: '#1f7a3a' };
    if (normalized === 'cancelled' || normalized === 'canceled') return { bg: '#ffe4e6', fg: '#b42318' };
    return stageColorMap[stage] || { bg: '#f1f3f5', fg: '#495057' };
  };

  const getOrderStatusLabel = (order) => {
    const statusRaw = String(order?.status || '').trim();
    const normalized = statusRaw.toLowerCase();
    if (normalized === 'completed' || normalized === 'cancelled' || normalized === 'canceled') {
      return statusRaw;
    }
    return order?.pipeline_stage || 'In Progress';
  };

  const handleCancelOrder = async (order) => {
    if (!order) return;
    setCancelOrderTarget(order);
  };

  const confirmCancelOrder = async () => {
    if (!cancelOrderTarget) return;
    setError('');
    setSuccess('');
    try {
      await api.transitionOrderPipelineStage(cancelOrderTarget.id, 'cancel');
      setSuccess(`Order #${cancelOrderTarget.order_number} cancelled.`);
      setViewingOrder(null);
      setCancelOrderTarget(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCompleteOrder = (order) => {
    if (!order) return;
    setCompleteOrderTarget(order);
  };

  const confirmCompleteOrder = async () => {
    if (!completeOrderTarget) return;
    setError('');
    setSuccess('');
    try {
      await api.transitionOrderPipelineStage(completeOrderTarget.id, 'complete');
      setSuccess(`Order #${completeOrderTarget.order_number} completed.`);
      setViewingOrder(null);
      setCompleteOrderTarget(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Bulk actions removed for now.

  const buildOrderCsv = (order) => {
    const payload = parseOrderPayload(order?.payload) || {};
    const products = Array.isArray(payload.products) ? payload.products : [];
    const rows = [];
    rows.push(['Order Number', order?.order_number || ''].join(','));
    rows.push(['Buyer', order?.buyer_name || ''].join(','));
    rows.push(['Status', getOrderStatusLabel(order)].join(','));
    rows.push(['Delivery Date', payload.delivery_date || ''].join(','));
    rows.push([]);
    rows.push(['Product', 'Quantity', 'Raw Material', 'Subcategory', 'Per Unit Required', 'Total Required'].join(','));
    products.forEach((product) => {
      const productName = product.product_name || 'Unnamed Product';
      const quantity = Number(product.quantity || 0);
      const items = Array.isArray(product.items) ? product.items : [];
      if (items.length === 0) {
        rows.push([productName, quantity, '', '', '', ''].join(','));
        return;
      }
      items.forEach((item) => {
        const rawType = item.raw_material_name || item.raw_material_type || item.type || '';
        const characteristicText = Object.entries(item.characteristics || {})
          .map(([key, value]) => `${key}: ${value}`)
          .join(' | ');
        const perUnit = Number(item.required_qty || item.required_kg || 0);
        const totalReq = perUnit * (Number.isFinite(quantity) ? quantity : 0);
        rows.push([productName, quantity, rawType, characteristicText, perUnit, totalReq].join(','));
      });
    });
    return rows.map((row) => (Array.isArray(row) ? row.join(',') : row)).join('\n');
  };

  const handleDownloadOrderCsv = (order) => {
    const csvContent = buildOrderCsv(order);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const orderNo = order?.order_number ? `_${order.order_number}` : '';
    link.setAttribute('href', url);
    link.setAttribute('download', `Order${orderNo}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReplaceOrder = (order) => {
    const payload = parseOrderPayload(order?.payload) || {};
    const products = Array.isArray(payload.products) ? payload.products : [];
    setOrderForm((prev) => ({
      ...prev,
      buyer_name: payload.buyer_name || order?.buyer_name || '',
      delivery_date: payload.delivery_date || prev.delivery_date
    }));
    setOrderProducts(
      products.length > 0
        ? products.map((product) => ({
            product_id: product.product_id || '',
            quantity: Math.max(1, parseInt(product.quantity, 10) || 1)
          }))
        : [{ product_id: '', quantity: 1 }]
    );
    setViewingOrder(null);
    setActiveTab('order');
  };

  const openConfirmDialog = (payload) => {
    setConfirmDialog(payload);
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(null);
  };

  const executeConfirmAction = async () => {
    if (!confirmDialog) return;

    try {
      if (confirmDialog.type === 'color') {
        await handleDeleteColor(confirmDialog.id);
      } else if (confirmDialog.type === 'user') {
        await handleDeleteUser(confirmDialog.id);
      } else if (confirmDialog.type === 'staff-delete') {
        await handleDeleteStaff(confirmDialog.id);
      } else if (confirmDialog.type === 'staff-status') {
        await handleUpdateStaffStatus(confirmDialog.id, confirmDialog.is_active);
      } else if (confirmDialog.type === 'customer-delete') {
        await handleDeleteCustomer(confirmDialog.id);
      } else if (confirmDialog.type === 'contractor-delete') {
        await handleDeleteContractor(confirmDialog.id);
      } else if (confirmDialog.type === 'transaction') {
        await handleDeleteTransaction(confirmDialog.id);
      } else if (confirmDialog.type === 'product-delete') {
        await handleDeleteProduct(confirmDialog.id);
      } else if (confirmDialog.type === 'product-duplicate') {
        const productToDuplicate = productCatalogWithPayload.find(
          (product) => String(product.id) === String(confirmDialog.id)
        );
        if (!productToDuplicate) {
          throw new Error('Product not found for duplication.');
        }
        await handleDuplicateProduct(productToDuplicate);
      } else if (confirmDialog.type === 'unlock') {
        await handleUnlockUser(confirmDialog.id);
      } else if (confirmDialog.type === 'custom-list-item-delete') {
        try {
          await api.deleteCustomListItem(confirmDialog.id);
          setSuccess('List value deleted.');
          loadData();
        } catch (err) {
          setError(err.message);
        }
      }
    } finally {
      closeConfirmDialog();
    }
  };

  const handleEditTransaction = (transaction) => {
    const custom = transaction.custom_fields || {};
    const nextValues = {};
    transactionFields.forEach((field) => {
      if (field.key === transactionSchema.stock_field_key) {
        nextValues[field.key] = transaction.quantity ?? '';
        return;
      }
      if (field.key === transactionSchema.type_field_key) {
        nextValues[field.key] = transaction.type ?? '';
        return;
      }
      if (field.type === 'checkbox') {
        nextValues[field.key] = Boolean(custom[field.key]);
        return;
      }
      nextValues[field.key] = custom[field.key] ?? '';
    });
    const rawMaterialName = String(custom.raw_material_type || '').trim();
    const rawAction = String(custom.raw_material_action || transaction.type || RAW_TRANSACTION_ACTIONS[0].key).toLowerCase();
    const rawCost = custom.raw_material_cost;
    const finishedProductId = custom.finished_product_id !== undefined && custom.finished_product_id !== null
      ? String(custom.finished_product_id)
      : '';
    const finishedProductName = String(custom.finished_product_name || '').trim();
    const finishedAction = String(custom.finished_goods_action || transaction.type || 'in').toLowerCase();
    const isFinishedGoods = Boolean(finishedProductId || finishedProductName);
    const matchedFinishedProduct = finishedGoodsOptions.find(
      (item) => String(item.id) === finishedProductId || item.name === finishedProductName
    );

    setEditingTransactionLedgerMode(isFinishedGoods ? 'finished_goods' : 'raw_material');
    setEditingRawMaterialAction(rawAction || RAW_TRANSACTION_ACTIONS[0].key);
    setEditingRawMaterialType(rawMaterialName);
    setEditingRawMaterialCharacteristics(
      normalizeRawMaterialTransactionCharacteristics(rawMaterialName, custom.raw_material_characteristics)
    );
    setEditingRawMaterialCost(
      rawCost !== undefined && rawCost !== null && rawCost !== '' ? String(rawCost) : ''
    );
    setEditingFinishedGoodsAction(finishedAction || 'in');
    setEditingFinishedGoodsProductId(matchedFinishedProduct ? String(matchedFinishedProduct.id) : finishedProductId);

    setEditingTransactionValues(nextValues);
    const normalizedDate = (transaction.date || getTodayDateKey()).split('T')[0];
    setEditingTransactionDate(normalizedDate);
    setEditingTransactionNotes(transaction.notes || '');
    setEditingTransaction({
      ...transaction,
      date: normalizedDate,
    });
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!editingTransaction) return;

    try {
      const customFieldsPayload = {
        ...((editingTransaction?.custom_fields && typeof editingTransaction.custom_fields === 'object')
          ? editingTransaction.custom_fields
          : {}),
        ...buildCustomFieldsPayload(transactionExtraFields, editingTransactionValues)
      };
      const existingCustom = editingTransaction?.custom_fields && typeof editingTransaction.custom_fields === 'object'
        ? editingTransaction.custom_fields
        : {};
      const rawMaterialKeys = ['raw_material_type', 'raw_material_characteristics', 'raw_material_action', 'raw_material_cost', 'raw_material_subcategory'];
      const finishedGoodsKeys = ['finished_product_id', 'finished_product_name', 'finished_goods_action', 'finished_goods_unit_cost'];
      const stockFieldKey = transactionSchema.stock_field_key || 'quantity';
      const typeFieldKey = transactionSchema.type_field_key || 'type';
      let normalizedType = String(editingTransactionValues[typeFieldKey] || editingTransaction.type || '').toLowerCase();
      let stockQuantity = parseFloat(editingTransactionValues[stockFieldKey]);
      if (Number.isNaN(stockQuantity)) {
        throw new Error('Stock decider field must be a number.');
      }
      if (stockQuantity <= 0) {
        throw new Error('Stock decider field must be greater than 0.');
      }

      if (editingTransactionLedgerMode === 'finished_goods') {
        const selectedProduct = finishedGoodsOptions.find((item) => String(item.id) === String(editingFinishedGoodsProductId));
        if (!selectedProduct) {
          throw new Error('Finished product is required.');
        }
        normalizedType = String(editingFinishedGoodsAction || normalizedType || 'in').toLowerCase();
        if (!['in', 'out', 'hold'].includes(normalizedType)) {
          throw new Error('Transaction type must be In, Out, or Hold.');
        }
        rawMaterialKeys.forEach((key) => delete customFieldsPayload[key]);
        customFieldsPayload.finished_product_id = selectedProduct.id;
        customFieldsPayload.finished_product_name = selectedProduct.name;
        customFieldsPayload.finished_goods_action = normalizedType;
        customFieldsPayload.finished_goods_unit_cost = Number(existingCustom.finished_goods_unit_cost || 0);
      } else {
        const actionConfig = RAW_TRANSACTION_ACTION_MAP[editingRawMaterialAction];
        if (!actionConfig) {
          throw new Error('Select a valid raw material action.');
        }
        if (!editingRawMaterialType) {
          throw new Error('Raw material is required.');
        }
        const selectedMaterial = findRawMaterialByName(editingRawMaterialType);
        if (!selectedMaterial) {
          throw new Error('Select a valid raw material.');
        }
        const selectedCharacteristics = {};
        (selectedMaterial.characteristics || []).forEach((characteristic) => {
          const name = String(characteristic?.name || '').trim();
          if (!name) return;
          const value = String(editingRawMaterialCharacteristics?.[name] || '').trim();
          if (characteristic.mandatory && !value) {
            throw new Error(`"${name}" is mandatory for ${selectedMaterial.name}.`);
          }
          selectedCharacteristics[name] = value;
        });
        const unitCost = Number(editingRawMaterialCost);
        if (!Number.isFinite(unitCost) || unitCost <= 0) {
          throw new Error('Raw material unit cost is required.');
        }
        normalizedType = actionConfig.type;
        finishedGoodsKeys.forEach((key) => delete customFieldsPayload[key]);
        customFieldsPayload.raw_material_type = editingRawMaterialType;
        customFieldsPayload.raw_material_characteristics = selectedCharacteristics;
        customFieldsPayload.raw_material_action = actionConfig.key;
        customFieldsPayload.raw_material_cost = unitCost;
        const firstCharacteristic = (selectedMaterial.characteristics || [])[0];
        customFieldsPayload.raw_material_subcategory = firstCharacteristic?.name
          ? (selectedCharacteristics[firstCharacteristic.name] || '')
          : '';
      }

      if (!['in', 'out', 'hold'].includes(normalizedType)) {
        throw new Error('Transaction type must be In, Out, or Hold.');
      }

      const effectiveDate = transactionSchema.include_date !== false
        ? editingTransactionDate
        : getTodayDateKey();
      const effectiveNotes = transactionSchema.include_notes !== false ? editingTransactionNotes : '';

      await api.updateTransaction(editingTransaction.id, {
        material_type: 'custom',
        type: normalizedType,
        quantity: stockQuantity,
        date: effectiveDate,
        notes: effectiveNotes,
        custom_fields: customFieldsPayload
      });
      setEditingTransaction(null);
      setEditingTransactionValues({});
      setEditingTransactionDate('');
      setEditingTransactionNotes('');
      setEditingRawMaterialAction(RAW_TRANSACTION_ACTIONS[0].key);
      setEditingRawMaterialType('');
      setEditingRawMaterialCharacteristics({});
      setEditingRawMaterialCost('');
      setEditingFinishedGoodsAction('in');
      setEditingFinishedGoodsProductId('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const getSettingsSummaryItems = () => {
    if (settingsTab === 'system') {
      const brandingConfigured = Boolean(
        String(settings.challan_branding_legal_name || '').trim()
        || String(settings.challan_branding_address || '').trim()
        || String(settings.challan_branding_contact || '').trim()
      );
      return [
        {
          label: 'Color Indicators',
          value: settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true ? 'On' : 'Off',
          tone: settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Font Size',
          value: FONT_SIZE_OPTIONS.find((option) => option.value === selectedFontSize)?.label || 'Medium',
          tone: 'is-neutral'
        },
        {
          label: 'Thresholds',
          value: thresholdsValid ? 'Valid' : 'Needs Fix',
          tone: thresholdsValid ? 'is-positive' : 'is-warning'
        },
        {
          label: 'Pipeline Steps',
          value: `${pipelineStages.length}`,
          tone: pipelineStages.length > 0 ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Challan Format',
          value: settings.challan_number_format === 'financial_year' ? 'Financial Year' : 'Calendar Year',
          tone: 'is-neutral'
        },
        {
          label: 'Challan Branding',
          value: brandingConfigured ? 'Configured' : 'Basic',
          tone: brandingConfigured ? 'is-positive' : 'is-neutral'
        }
      ];
    }
    if (settingsTab === 'customLists') {
      return [
        {
          label: 'Dropdown Fields',
          value: `${dropdownFields.length}`,
          tone: dropdownFields.length > 0 ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Active Lists',
          value: `${customLists.length}`,
          tone: customLists.length > 0 ? 'is-positive' : 'is-neutral'
        }
      ];
    }
    if (settingsTab === 'rawMaterials') {
      const mainMaterial = rawMaterials.find((item) => item.is_main)?.name || 'Not set';
      const characteristicCount = rawMaterials.reduce(
        (total, item) => total + (Array.isArray(item.characteristics) ? item.characteristics.length : 0),
        0
      );
      return [
        {
          label: 'Material Types',
          value: `${rawMaterials.length}`,
          tone: rawMaterials.length > 0 ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Main Material',
          value: mainMaterial,
          tone: mainMaterial === 'Not set' ? 'is-warning' : 'is-positive'
        },
        {
          label: 'Characteristics',
          value: `${characteristicCount}`,
          tone: characteristicCount > 0 ? 'is-positive' : 'is-neutral'
        }
      ];
    }
    if (settingsTab === 'users') {
      const adminCount = users.filter((usr) => usr.role === 'admin' || usr.role === 'owner').length;
      const lockedCount = users.filter((usr) => {
        if (!usr.locked_until) return false;
        const lockedUntil = new Date(usr.locked_until);
        return !isNaN(lockedUntil.getTime()) && lockedUntil > new Date();
      }).length;
      return [
        {
          label: 'Total Users',
          value: `${users.length}`,
          tone: users.length > 0 ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Admins / Owners',
          value: `${adminCount}`,
          tone: adminCount > 0 ? 'is-positive' : 'is-neutral'
        },
        {
          label: 'Locked Accounts',
          value: `${lockedCount}`,
          tone: lockedCount > 0 ? 'is-warning' : 'is-positive'
        }
      ];
    }
    return [];
  };

  const renderSettingsSummary = () => {
    const items = getSettingsSummaryItems();
    if (items.length === 0) return null;
    return (
      <div className="settings-summary">
        <div className="summary-cards settings-summary-cards">
          {items.map((item) => (
            <div key={item.label} className={`summary-card settings-summary-card ${item.tone || ''}`}>
              <div className="summary-value">{item.value}</div>
              <div className="summary-label">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSettingsHeader = ({ title, description, dirty, actions }) => (
    <div className="settings-header">
      <div className="settings-header-row">
        <div className="settings-header-text">
          <h2>{title}</h2>
          <p className="helper-text">{description}</p>
        </div>
        <div className="settings-header-status">
          <span className={`settings-status-pill ${dirty ? 'dirty' : 'saved'}`}>
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </span>
        </div>
      </div>
      <div className="settings-header-actions">
        <div className="settings-search">
          <span className="settings-search-icon" aria-hidden="true">
            <Search size={14} />
          </span>
          <input
            type="text"
            value={settingsSearch}
            onChange={(e) => setSettingsSearch(e.target.value)}
            placeholder="Search settings"
          />
          {settingsSearch && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSettingsSearch('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {actions}
      </div>
    </div>
  );

  const handleAddCustomListItem = async (listKey) => {
    const value = (customListInputs[listKey] || '').trim();
    if (!value) return;
    setError('');
    setSuccess('');
    try {
      if (!customListMap[listKey]) {
        const listEntry = manageListCatalog.find((entry) => entry.key === listKey);
        await api.createCustomList(listKey, listEntry?.label || listKey);
      }
      await api.addCustomListItem(listKey, value);
      setCustomListInputs((prev) => ({ ...prev, [listKey]: '' }));
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateCustomListItem = async (itemId, value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setError('');
    setSuccess('');
    try {
      await api.updateCustomListItem(itemId, trimmed);
      setEditingListItems((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCustomListItem = async (itemId) => {
    openConfirmDialog({
      type: 'custom-list-item-delete',
      id: itemId,
      title: 'Delete list value?',
      message: 'This will remove the value from the list.'
    });
  };

  const buildChallanCustomDetailsPayload = () => {
    const payload = {};
    movementChallanCustomDetails.forEach((row) => {
      const key = String(row?.key || '').trim();
      if (!key) return;
      payload[key] = String(row?.value || '').trim();
    });
    return payload;
  };

  const buildChallanOutputPreferencesPayload = () => ({
    layout: challanOutputLayout === 'customer' ? 'customer' : 'internal',
    include_unit_cost: challanOutputIncludeUnitCost === true,
    include_estimated_cost: challanOutputIncludeEstimatedCost === true,
    include_product_id: challanOutputIncludeProductId === true,
    include_order_ids: challanOutputIncludeOrderIds === true,
    include_qr: challanOutputIncludeQr === true
  });

  const applyChallanOutputPreferences = (preferences) => {
    const safe = normalizeChallanOutputPreferences(preferences);
    setChallanOutputLayout(safe.layout);
    setChallanOutputIncludeUnitCost(safe.include_unit_cost);
    setChallanOutputIncludeEstimatedCost(safe.include_estimated_cost);
    setChallanOutputIncludeProductId(safe.include_product_id);
    setChallanOutputIncludeOrderIds(safe.include_order_ids);
    setChallanOutputIncludeQr(safe.include_qr);
  };

  const buildMovementChallanPayload = () => ({
    type: movementChallanType,
    order_number: movementChallanOrderNumber,
    party_name: movementChallanPartyName,
    party_contact: movementChallanPartyContact,
    party_address: movementChallanPartyAddress,
    transporter_name: movementChallanTransporterName,
    vehicle_number: movementChallanVehicleNumber,
    lr_number: movementChallanLrNumber,
    driver_name: movementChallanDriverName,
    driver_phone: movementChallanDriverPhone,
    dispatch_datetime: movementChallanDispatchDateTime,
    signature_data: movementChallanSignatureData,
    custom_details: buildChallanCustomDetailsPayload()
  });

  const handleUploadChallanSignature = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setMovementChallanSignatureData(result);
    };
    reader.readAsDataURL(file);
  };

  const openChallanPrintPopup = async (challanId, options = {}) => {
    const shouldAutoPrint = options.autoPrint !== false;
    const printPreferences = normalizeChallanOutputPreferences(options.printPreferences);
    const popup = window.open('', '_blank');
    if (!popup) {
      setError('Popup blocked. Please allow popups and try again.');
      return false;
    }
    popup.document.open();
    popup.document.write(`<!DOCTYPE html><html><head><title>Preparing challan...</title></head><body style="font-family: Arial, sans-serif; padding: 24px; color: #1f2937;">Preparing challan print preview...</body></html>`);
    popup.document.close();
    try {
      const html = await api.getChallanPrintHtml(challanId, printPreferences);
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      popup.focus();
      if (shouldAutoPrint) {
        setTimeout(() => popup.print(), 150);
      }
      return true;
    } catch (err) {
      if (!popup.closed) popup.close();
      setError(err.message);
      return false;
    }
  };

  const openChallanOutputDialog = (challan, intent = 'print') => {
    const challanId = Number(challan?.id);
    if (!Number.isFinite(challanId)) return;
    const basePreferences = normalizeChallanOutputPreferences(challan?.print_preferences);
    applyChallanOutputPreferences(basePreferences);
    setChallanOutputDialog({
      challanId,
      challanNumber: String(challan?.challan_number || '').trim(),
      intent: intent === 'download' ? 'download' : 'print'
    });
  };

  const handleConfirmChallanOutput = async () => {
    if (!challanOutputDialog?.challanId) return;
    const target = { ...challanOutputDialog };
    const preferences = buildChallanOutputPreferencesPayload();
    setChallanOutputDialog(null);
    const opened = await openChallanPrintPopup(target.challanId, {
      autoPrint: true,
      printPreferences: preferences
    });
    if (opened) {
      if (target.intent === 'download') {
        setSuccess('Print dialog opened with selected challan options. Choose "Save as PDF" to download.');
      }
    }
  };

  const handlePrintChallan = (challan) => {
    openChallanOutputDialog(challan, 'print');
  };

  const handleDownloadChallanPdf = (challan) => {
    openChallanOutputDialog(challan, 'download');
  };

  const handleCancelChallan = async (challan) => {
    const challanId = Number(challan?.id);
    if (!Number.isFinite(challanId)) return;
    const reason = window.prompt(`Cancel challan ${challan?.challan_number || ''}. Reason (optional):`, '') || '';
    try {
      setChallanActionLoadingId(challanId);
      await api.cancelChallan(challanId, reason);
      setSuccess(`Challan ${challan?.challan_number || ''} cancelled and reversed.`);
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setChallanActionLoadingId(null);
    }
  };

  const handleReissueChallan = async (challan) => {
    const challanId = Number(challan?.id);
    if (!Number.isFinite(challanId)) return;
    const shouldProceed = window.confirm(`Reissue challan ${challan?.challan_number || ''} as a new challan?`);
    if (!shouldProceed) return;
    try {
      setChallanActionLoadingId(challanId);
      const response = await api.reissueChallan(challanId, { date: getTodayDateKey() });
      setSuccess(response?.message || 'Challan reissued successfully.');
      loadData({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setChallanActionLoadingId(null);
    }
  };

  const resetTransactionFormState = () => {
    setTransactionValues({});
    setTransactionDate(getTodayDateKey());
    setTransactionNotes('');
    setRawMaterialAction(RAW_TRANSACTION_ACTIONS[0].key);
    setRawMaterialType('');
    setFinishedGoodsAction('in');
    setFinishedGoodsProductId('');
    setRawMaterialCharacteristics({});
    setRawMaterialCost('');
    setRawMaterialLinkedOrderNumber('');
    setMovementHasChallan(false);
    setMovementChallanType('delivery');
    setMovementChallanOrderNumber('');
    setMovementChallanPartyName('');
    setMovementChallanPartyContact('');
    setMovementChallanPartyAddress('');
    setMovementChallanTransporterName('');
    setMovementChallanVehicleNumber('');
    setMovementChallanLrNumber('');
    setMovementChallanDriverName('');
    setMovementChallanDriverPhone('');
    setMovementChallanDispatchDateTime('');
    setMovementChallanSignatureData('');
    setMovementChallanCustomDetails([{ key: '', value: '' }]);
    setCustomFieldValues((prev) => ({ ...prev, add: {} }));
    setPadItemCount(1);
    setPadItems([{ pad_type_id: '', quantity: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (materialType === 'pad') {
        const items = padItems.map((item) => ({
          pad_type_id: item.pad_type_id,
          quantity: parseInt(item.quantity, 10)
        }));
        const payload = {
          material_type: 'pad',
          type: formData.type,
          date: formData.date,
          notes: formData.notes,
          challan_no: formData.challan_no,
          total_weight: parseFloat(formData.total_weight),
          items
        };
        if (movementHasChallan) {
          if (String(formData.type || '').toLowerCase() !== 'out') {
            throw new Error('Delivery or Material Issue challan requires outbound movement. Choose Stock Out / outbound action.');
          }
          payload.challan = buildMovementChallanPayload();
        }
        if (!navigator.onLine) {
          enqueueOfflineAction('transaction_create', payload);
          setSuccess('You are offline. Transaction saved and will sync when your connection returns.');
          resetTransactionFormState();
          return;
        }
        await api.addTransaction(payload);
      } else {
        const effectiveDate = transactionSchema.include_date !== false
          ? transactionDate
          : getTodayDateKey();
        const effectiveNotes = transactionSchema.include_notes !== false ? transactionNotes : '';
        const stockQuantity = parseFloat(transactionValues[transactionStockFieldKey]);
        if (Number.isNaN(stockQuantity) || stockQuantity <= 0) {
          throw new Error('Stock decider field must be a number.');
        }
        let payload = null;
        if (transactionLedgerMode === 'finished_goods') {
          const selectedProduct = finishedGoodsOptions.find((item) => String(item.id) === String(finishedGoodsProductId));
          if (!selectedProduct) {
            throw new Error('Finished product is required.');
          }
          const customFieldsPayload = buildCustomFieldsPayload(transactionExtraFields, transactionValues);
          customFieldsPayload.finished_product_id = selectedProduct.id;
          customFieldsPayload.finished_product_name = selectedProduct.name;
          customFieldsPayload.finished_goods_action = finishedGoodsAction;
          customFieldsPayload.finished_goods_unit_cost = 0;
          payload = {
            type: finishedGoodsAction,
            quantity: stockQuantity,
            date: effectiveDate,
            notes: effectiveNotes,
            custom_fields: customFieldsPayload,
            entry_source: 'add'
          };
          if (movementHasChallan) {
            if (finishedGoodsAction !== 'out') {
              throw new Error('Delivery or Material Issue challan requires outbound movement. Choose Stock Out / outbound action.');
            }
            payload.challan = buildMovementChallanPayload();
          }
        } else {
          const actionConfig = RAW_TRANSACTION_ACTION_MAP[rawMaterialAction];
          if (!actionConfig) {
            throw new Error('Select a valid transaction action.');
          }
          if (!rawMaterialType) {
            throw new Error('Raw material is required.');
          }
          const selectedMaterial = rawMaterialOptions.find((item) => item.name === rawMaterialType);
          if (!selectedMaterial) {
            throw new Error('Select a valid raw material.');
          }
          const selectedCharacteristics = {};
          (selectedMaterial.characteristics || []).forEach((characteristic) => {
            const name = String(characteristic?.name || '').trim();
            if (!name) return;
            const value = String(rawMaterialCharacteristics?.[name] || '').trim();
            if (characteristic.mandatory && !value) {
              throw new Error(`"${name}" is mandatory for ${selectedMaterial.name}.`);
            }
            selectedCharacteristics[name] = value;
          });
          if (!rawMaterialCost || Number.isNaN(parseFloat(rawMaterialCost)) || parseFloat(rawMaterialCost) <= 0) {
            throw new Error('Raw material unit cost is required.');
          }
          const customFieldsPayload = buildCustomFieldsPayload(transactionExtraFields, transactionValues);
          customFieldsPayload.raw_material_type = rawMaterialType;
          customFieldsPayload.raw_material_characteristics = selectedCharacteristics;
          customFieldsPayload.raw_material_action = actionConfig.key;
          customFieldsPayload.raw_material_cost = parseFloat(rawMaterialCost);
          const linkedOrderNumber = String(rawMaterialLinkedOrderNumber || '').trim();
          if (actionConfig.type === 'in' && linkedOrderNumber) {
            customFieldsPayload.linked_order_number = linkedOrderNumber;
          }
          const firstCharacteristic = (selectedMaterial.characteristics || [])[0];
          if (firstCharacteristic?.name) {
            customFieldsPayload.raw_material_subcategory = selectedCharacteristics[firstCharacteristic.name] || '';
          }
          const normalizedType = actionConfig.type;
          if (normalizedType === 'out') {
            const availableStock = getRawMaterialStockBalance(rawMaterialType, selectedCharacteristics);
            if (stockQuantity > availableStock) {
              throw new Error(`Insufficient stock for this raw material combination. Available: ${availableStock.toFixed(2)}`);
            }
          }
          payload = {
            type: normalizedType,
            quantity: stockQuantity,
            date: effectiveDate,
            notes: effectiveNotes,
            custom_fields: customFieldsPayload,
            entry_source: 'add'
          };
          if (movementHasChallan) {
            if (normalizedType !== 'out') {
              throw new Error('Delivery or Material Issue challan requires outbound movement. Choose Stock Out / outbound action.');
            }
            payload.challan = buildMovementChallanPayload();
          }
        }
        if (!navigator.onLine) {
          enqueueOfflineAction('transaction_create', payload);
          setSuccess('You are offline. Transaction saved and will sync when your connection returns.');
          resetTransactionFormState();
          return;
        }
        await api.addTransaction(payload);
      }
      setSuccess('Transaction added successfully!');
      resetTransactionFormState();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let insideQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];
      const nextChar = line[index + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          current += '"';
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values;
  };

  const downloadRawMaterialsCsv = () => {
    if (rawMaterials.length === 0) {
      setError('No raw materials to export.');
      return;
    }

    const headers = ['name', 'unit_label', 'consume_stage', 'is_main', 'archived', 'characteristics'];
    const rows = rawMaterials.map((item) => [
      escapeCsvValue(item.name),
      escapeCsvValue(item.unit_label || ''),
      escapeCsvValue(item.consume_stage || ''),
      escapeCsvValue(item.is_main ? 'true' : 'false'),
      escapeCsvValue(item.archived ? 'true' : 'false'),
      escapeCsvValue(
        (item.characteristics || [])
          .map((entry) => `${entry.name}${entry.mandatory ? '|required' : '|optional'}`)
          .join('; ')
      )
    ].join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Raw_Materials_${getTodayDateKey()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const normalizeCsvBoolean = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return ['true', 'yes', '1', 'required'].includes(normalized);
  };

  const buildRawMaterialsImportTemplate = () => {
    const headers = [
      'name',
      'unit_label',
      'consume_stage',
      'is_main',
      'archived',
      'characteristic_1_name',
      'characteristic_1_required',
      'characteristic_2_name',
      'characteristic_2_required',
      'characteristic_3_name',
      'characteristic_3_required'
    ];
    const sampleRows = [
      [
        escapeCsvValue('Primary Material'),
        escapeCsvValue('Kg'),
        escapeCsvValue('Production'),
        escapeCsvValue('true'),
        escapeCsvValue('false'),
        escapeCsvValue('Grade'),
        escapeCsvValue('true'),
        escapeCsvValue('Specification'),
        escapeCsvValue('false'),
        escapeCsvValue('Finish'),
        escapeCsvValue('false')
      ].join(','),
      [
        escapeCsvValue('Packaging'),
        escapeCsvValue('Pcs'),
        escapeCsvValue('Packing'),
        escapeCsvValue('false'),
        escapeCsvValue('false'),
        escapeCsvValue('Type'),
        escapeCsvValue('true'),
        escapeCsvValue(''),
        escapeCsvValue(''),
        escapeCsvValue(''),
        escapeCsvValue('')
      ].join(',')
    ];
    return [headers.join(','), ...sampleRows].join('\n');
  };

  const downloadRawMaterialsTemplate = () => {
    const csvContent = buildRawMaterialsImportTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'Raw_Materials_Import_Template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openRawMaterialsImportModal = () => {
    setRawMaterialsImportModal({
      fileName: '',
      csvText: '',
      showTemplate: false,
      summary: null,
      rows: [],
      importMode: 'block',
      result: null
    });
  };

  const handleRawMaterialsImportFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setRawMaterialsImportModal((prev) => (
        prev
          ? {
            ...prev,
            fileName: file.name,
            csvText: text,
            summary: null,
            rows: [],
            result: null
          }
          : prev
      ));
    } catch (_err) {
      setError('Unable to read CSV file.');
    } finally {
      event.target.value = '';
    }
  };

  const buildRawMaterialsImportPreview = () => {
    if (!rawMaterialsImportModal?.csvText?.trim()) {
      throw new Error('Please choose a CSV file first.');
    }

    const lines = rawMaterialsImportModal.csvText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      throw new Error('CSV must include a header row and at least one raw material row.');
    }

    const originalHeaders = parseCsvLine(lines[0]).map((header) => String(header || '').trim());
    const headers = originalHeaders.map((header) => header.toLowerCase());
    const requiredHeaders = ['name', 'unit_label'];
    const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing CSV columns: ${missingHeaders.join(', ')}`);
    }

    const characteristicSlots = headers.reduce((acc, header, index) => {
      const match = header.match(/^characteristic_(\d+)_(name|required)$/);
      if (!match) return acc;
      const slotNumber = Number(match[1]);
      const field = match[2];
      const existing = acc.get(slotNumber) || {};
      existing[field] = {
        header,
        index
      };
      acc.set(slotNumber, existing);
      return acc;
    }, new Map());

    const fileDuplicateCounts = new Map();
    const previewRows = lines.slice(1).map((line, rowIndex) => {
      const values = parseCsvLine(line);
      const row = headers.reduce((acc, header, index) => {
        acc[header] = values[index] || '';
        return acc;
      }, {});

      const name = String(row.name || '').trim();
      const unitLabel = String(row.unit_label || '').trim();
      const consumeStage = String(row.consume_stage || '').trim();
      const isMain = normalizeCsvBoolean(row.is_main);
      const archived = normalizeCsvBoolean(row.archived);

      const multiColumnCharacteristics = Array.from(characteristicSlots.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, slot]) => {
          const characteristicName = String(values[slot?.name?.index] || '').trim();
          if (!characteristicName) return null;
          return {
            name: characteristicName,
            mandatory: normalizeCsvBoolean(values[slot?.required?.index] || '')
          };
        })
        .filter(Boolean);

      const legacyCharacteristics = String(row.characteristics || '')
        .split(';')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => {
          const [rawName, rawMode] = entry.split('|').map((part) => String(part || '').trim());
          return {
            name: rawName,
            mandatory: normalizeCsvBoolean(rawMode)
          };
        });

      const material = {
        name,
        unit_label: unitLabel,
        consume_stage: consumeStage,
        is_main: isMain,
        archived,
        characteristics: normalizeRawMaterialCharacteristics(
          multiColumnCharacteristics.length > 0 ? multiColumnCharacteristics : legacyCharacteristics,
          name
        )
      };

      const errors = [];
      if (!name) {
        errors.push('Material name is required.');
      }
      if (!unitLabel) {
        errors.push('Unit label is required.');
      }
      if (material.characteristics.some((item) => !String(item?.name || '').trim())) {
        errors.push('Every characteristic must have a name.');
      }

      const matchIndex = rawMaterials.findIndex((item) => (
        String(item?.name || '').trim().toLowerCase() === name.toLowerCase()
        && String(item?.unit_label || '').trim().toLowerCase() === unitLabel.toLowerCase()
      ));
      const existingMaterial = matchIndex >= 0 ? rawMaterials[matchIndex] : null;
      const fileKey = `${name.toLowerCase()}__${unitLabel.toLowerCase()}`;
      fileDuplicateCounts.set(fileKey, (fileDuplicateCounts.get(fileKey) || 0) + 1);

      return {
        id: `raw-material-import-row-${rowIndex + 2}`,
        rowNumber: rowIndex + 2,
        material,
        existingMaterial,
        matchIndex,
        errors,
        decision: errors.length > 0
          ? 'skip'
          : (existingMaterial ? 'review' : 'create')
      };
    }).map((row) => {
      const duplicateKey = `${row.material.name.toLowerCase()}__${row.material.unit_label.toLowerCase()}`;
      if (row.material.name && row.material.unit_label && (fileDuplicateCounts.get(duplicateKey) || 0) > 1) {
        return {
          ...row,
          errors: [...row.errors, 'This CSV contains duplicate rows for the same material name and unit.'],
          decision: 'skip'
        };
      }
      return row;
    });

    const summary = {
      total_rows: previewRows.length,
      valid_rows: previewRows.filter((row) => row.errors.length === 0).length,
      error_rows: previewRows.filter((row) => row.errors.length > 0).length,
      duplicate_rows: previewRows.filter((row) => row.existingMaterial).length,
      new_rows: previewRows.filter((row) => !row.existingMaterial && row.errors.length === 0).length,
      needs_review_rows: previewRows.filter((row) => row.decision === 'review').length
    };

    return {
      rows: previewRows,
      summary
    };
  };

  const handleRawMaterialsImportPreview = async () => {
    try {
      setRawMaterialsImportLoading(true);
      setError('');
      const result = buildRawMaterialsImportPreview();
      setRawMaterialsImportModal((prev) => (
        prev
          ? {
            ...prev,
            rows: result.rows,
            summary: result.summary,
            result: null
          }
          : prev
      ));
    } catch (err) {
      setError(err.message);
      setRawMaterialsImportModal((prev) => (
        prev
          ? {
            ...prev,
            rows: [],
            summary: null,
            result: null
          }
          : prev
      ));
    } finally {
      setRawMaterialsImportLoading(false);
    }
  };

  const updateRawMaterialsImportRowDecision = (rowId, decision) => {
    setRawMaterialsImportModal((prev) => {
      if (!prev) return prev;
      const nextRows = prev.rows.map((row) => (
        row.id === rowId
          ? { ...row, decision }
          : row
      ));
      const nextSummary = prev.summary
        ? {
          ...prev.summary,
          needs_review_rows: nextRows.filter((row) => row.decision === 'review').length
        }
        : prev.summary;
      return {
        ...prev,
        rows: nextRows,
        summary: nextSummary,
        result: null
      };
    });
  };

  const handleRawMaterialsImportCommit = async () => {
    if (!rawMaterialsImportModal?.rows?.length) {
      setError('Preview the CSV before importing.');
      return;
    }

    const unresolvedRows = rawMaterialsImportModal.rows.filter((row) => row.errors.length === 0 && row.decision === 'review');
    if (unresolvedRows.length > 0) {
      setError('Choose update or skip for all matched rows before importing.');
      return;
    }

    const invalidRows = rawMaterialsImportModal.rows.filter((row) => row.errors.length > 0);
    if (rawMaterialsImportModal.importMode === 'block' && invalidRows.length > 0) {
      setError('Remove or skip CSV errors first, or switch to "Import valid rows only".');
      return;
    }

    try {
      setRawMaterialsImportLoading(true);
      setError('');
      let nextMaterials = rawMaterials.map((item) => ({
        ...item,
        characteristics: normalizeRawMaterialCharacteristics(item.characteristics || [], item.name || '')
      }));
      let explicitMainIndex = null;
      const result = {
        created: 0,
        updated: 0,
        restored: 0,
        skipped: 0,
        invalid: 0
      };

      rawMaterialsImportModal.rows.forEach((row) => {
        if (row.errors.length > 0) {
          result.invalid += 1;
          result.skipped += 1;
          return;
        }

        if (row.decision === 'skip') {
          result.skipped += 1;
          return;
        }

        if (row.decision === 'create') {
          const createdMaterial = {
            ...row.material,
            characteristics: normalizeRawMaterialCharacteristics(row.material.characteristics || [], row.material.name || '')
          };
          nextMaterials = [...nextMaterials, createdMaterial];
          if (createdMaterial.is_main && !createdMaterial.archived) {
            explicitMainIndex = nextMaterials.length - 1;
          }
          result.created += 1;
          return;
        }

        if (!['update', 'restore_update'].includes(row.decision) || row.matchIndex < 0) {
          result.skipped += 1;
          return;
        }

        const previousMaterial = nextMaterials[row.matchIndex];
        if (!previousMaterial) {
          result.skipped += 1;
          return;
        }

        const updatedMaterial = {
          ...previousMaterial,
          ...row.material,
          archived: row.decision === 'restore_update' ? false : row.material.archived,
          characteristics: normalizeRawMaterialCharacteristics(row.material.characteristics || [], row.material.name || '')
        };
        nextMaterials = nextMaterials.map((item, idx) => (
          idx === row.matchIndex ? updatedMaterial : item
        ));
        if (updatedMaterial.is_main && !updatedMaterial.archived) {
          explicitMainIndex = row.matchIndex;
        }
        if (previousMaterial.archived && row.decision === 'restore_update') {
          result.restored += 1;
        }
        result.updated += 1;
      });

      if (explicitMainIndex !== null) {
        nextMaterials = nextMaterials.map((item, idx) => ({
          ...item,
          is_main: idx === explicitMainIndex
        }));
      }

      if (!nextMaterials.some((item) => item.is_main && !item.archived)) {
        const firstActiveIndex = nextMaterials.findIndex((item) => !item.archived);
        if (firstActiveIndex >= 0) {
          nextMaterials = nextMaterials.map((item, idx) => ({
            ...item,
            is_main: idx === firstActiveIndex
          }));
        }
      }

      const persisted = await persistRawMaterialsSetting(nextMaterials);
      if (!persisted) return;

      setSelectedRawMaterialIndex(0);
      setRawMaterialsImportModal((prev) => (
        prev
          ? {
            ...prev,
            result
          }
          : prev
      ));
      setSuccess(`Raw materials import completed. ${result.created} created, ${result.updated} updated, ${result.skipped} skipped.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setRawMaterialsImportLoading(false);
    }
  };

  const handleExportStock = (stockData, options = {}) => {
    if (!stockData || stockData.length === 0) {
      setError('No stock data to export');
      return;
    }

    const mode = options.mode || 'main';
    const selectedMaterialForExport = rawMaterialOptions.find((item) => item.name === rawMaterialStockType);
    const exportCharacteristicNames = Array.isArray(selectedMaterialForExport?.characteristics)
      ? selectedMaterialForExport.characteristics
        .map((item) => String(item?.name || '').trim())
        .filter(Boolean)
      : [];
    const headers =
      mode === 'raw_material'
        ? [
          'Combination',
          ...exportCharacteristicNames,
          `Current Stock (${rawMaterialUnitLabel})`
        ]
        : mode === 'finished_goods'
          ? [
            'Product',
            `Current Stock (${finishedGoodsQuantityUnit})`,
            'Completed Orders'
          ]
        : [
          ...groupingFields.map((field) => field.label),
          `Current Stock (${quantityUnit})`
        ];

    const rows =
      mode === 'raw_material'
        ? stockData.map((item) => [
          `"${item.group?.label ?? 'Unassigned'}"`,
          ...exportCharacteristicNames.map((name) => `"${item.group?.characteristics?.[name] || ''}"`),
          item.current_stock
        ].join(','))
        : mode === 'finished_goods'
          ? stockData.map((item) => [
            `"${item.group?.product_name || 'Unnamed Product'}"`,
            item.current_stock,
            item.completed_orders || 0
          ].join(','))
        : stockData.map((item) => [
          ...groupingFields.map((field) => `"${item.group?.[field.key] ?? 'Unassigned'}"`),
          item.current_stock
        ].join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = getTodayDateKey();
    link.setAttribute('href', url);
    const suffix = mode === 'raw_material'
      ? `_${rawMaterialStockType}`
      : (mode === 'finished_goods' ? '_finished_goods' : '');
    link.setAttribute('download', `Stock${suffix}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Stock data exported successfully!');
  };

  const handleExportProductsCsv = () => {
    if (!filteredProductCatalog || filteredProductCatalog.length === 0) {
      setError('No product data to export');
      return;
    }

    const headers = [
      'Name',
      ...(productSchema.fields || []).filter((field) => field.key !== 'raw_materials').map((field) => field.label || field.key),
      'Raw Materials',
      'Created'
    ];

    const rows = filteredProductCatalog.map((product) => {
      const fields = product.parsedPayload?.fields || {};
      const rawMaterialsList = Array.isArray(fields.raw_materials)
        ? fields.raw_materials
          .map((entry) => {
            const rawName = entry?.raw_material_name || entry?.type;
            if (!rawName) return '';
            const characteristicText = Object.entries(entry?.characteristics || {})
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            const base = characteristicText ? `${rawName} (${characteristicText})` : rawName;
            const qty = entry.required_qty ? ` (${entry.required_qty})` : '';
            return `${base}${qty}`;
          })
          .filter(Boolean)
        : [];

      const dynamicFieldValues = (productSchema.fields || [])
        .filter((field) => field.key !== 'raw_materials')
        .map((field) => {
          const value = fields[field.key];
          if (field.type === 'checkbox') return value ? 'Yes' : 'No';
          if (value === undefined || value === null || value === '') return '—';
          return field.type === 'number' && field.unit ? `${value} ${field.unit}` : String(value);
        });

      return [
        escapeCsvValue(product.name),
        ...dynamicFieldValues.map(escapeCsvValue),
        escapeCsvValue(rawMaterialsList.join('; ')),
        escapeCsvValue(product.created_at ? formatDateOnly(product.created_at, getCurrentTimeZone()) : '—')
      ].join(',');
    });

    const csvContent = [headers.map(escapeCsvValue).join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = getTodayDateKey();
    link.setAttribute('href', url);
    link.setAttribute('download', `Products_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Product data exported successfully!');
  };

  const handleExportTransactionsCsv = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      setError('No transaction data to export');
      return;
    }

    const headers = [
      ...transactionFields.map((field) => field.label),
      ...transactionLedgerCharacteristics.map((characteristic) => characteristic.name),
      'Material / Product',
      'Action',
      'Unit Cost',
      ...(transactionSchema.include_date !== false ? ['Date'] : []),
      ...(transactionSchema.include_notes !== false ? ['Notes'] : []),
      'Created By'
    ];

    const rows = filteredTransactions.map((transaction) => {
      const fieldValues = transactionFields.map((field) => {
        if (field.key === transactionSchema.type_field_key) return String(transaction.type || '-').toUpperCase();
        if (field.key === transactionSchema.stock_field_key) return transaction.quantity;
        const value = transaction.custom_fields?.[field.key];
        return value !== undefined && value !== '' ? String(value) : '-';
      });

      const rawContext = getTransactionRawMaterialContext(transaction);
      const characteristicValues = transactionLedgerCharacteristics.map(
        (characteristic) => rawContext.characteristics?.[characteristic.name] || '-'
      );

      return [
        ...fieldValues,
        ...characteristicValues,
        rawContext.rawMaterialName || rawContext.finishedProductName || '-',
        getTransactionActionLabel(transaction, rawContext),
        rawContext.unitCost > 0 ? rawContext.unitCost.toFixed(2) : '-',
        ...(transactionSchema.include_date !== false ? [formatDateOnly(transaction.date, getCurrentTimeZone())] : []),
        ...(transactionSchema.include_notes !== false ? [transaction.notes || '-'] : []),
        transaction.created_by_name || transaction.created_by_username || transaction.created_by || '-'
      ].map(escapeCsvValue).join(',');
    });

    const csvContent = [headers.map(escapeCsvValue).join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = getTodayDateKey();
    link.setAttribute('href', url);
    link.setAttribute('download', `Transactions_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Transaction data exported successfully!');
  };

  const filteredStock = stock
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      const groupValues = Object.values(item.group || {}).join(' ').toLowerCase();
      return groupValues.includes(query);
    })
    .filter((item) =>
      groupingFields.every((field) => {
        const selected = stockFilters[field.key];
        if (!selected) return true;
        return (item.group?.[field.key] || 'Unassigned') === selected;
      })
    )
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return 0;
    });

  const filteredRawMaterialStock = rawMaterialStockData
    .filter((item) => includeSetupOnlyStock || !item?.no_movement_yet)
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      const label = item.group?.label || '';
      const characteristicText = Object.values(item.group?.characteristics || {}).join(' ');
      return `${label} ${characteristicText}`.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return 0;
    });

  const getFinishedGoodsStockKey = (item = {}) => {
    const productId = Number(item?.product_id);
    if (Number.isInteger(productId)) return `id:${productId}`;
    const productName = String(item?.product_name || '').trim();
    return productName ? `name:${productName.toLowerCase()}` : '';
  };

  const finishedGoodsStockData = orders.reduce((acc, order) => {
    const normalizedStatus = String(order?.status || '').trim().toLowerCase();
    if (normalizedStatus !== 'completed') return acc;
    const payload = parseOrderPayload(order?.payload) || {};
    const products = Array.isArray(payload.products) ? payload.products : [];
    products.forEach((product, index) => {
      const quantity = Number(product?.quantity || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) return;
      const rawName = String(product?.product_name || '').trim();
      const productName = rawName || `Product ${index + 1}`;
      const key = getFinishedGoodsStockKey({ product_id: product?.product_id, product_name: productName });
      if (!key) return;
      const existing = acc.get(key) || {
        group: { product_name: productName, product_id: product?.product_id || null },
        total_in: 0,
        total_out: 0,
        total_hold: 0,
        current_stock: 0,
        completed_orders: 0,
        no_movement_yet: false
      };
      existing.group.product_name = productName;
      existing.group.product_id = product?.product_id || existing.group.product_id || null;
      existing.total_in += quantity;
      existing.current_stock += quantity;
      existing.completed_orders += 1;
      existing.no_movement_yet = false;
      acc.set(key, existing);
    });
    return acc;
  }, new Map());

  transactions.forEach((transaction) => {
    const custom = transaction?.custom_fields && typeof transaction.custom_fields === 'object'
      ? transaction.custom_fields
      : {};
    const productId = Number(custom.finished_product_id);
    const productName = String(custom.finished_product_name || '').trim();
    const key = getFinishedGoodsStockKey({ product_id: productId, product_name: productName });
    if (!key) return;
    const quantity = Number(transaction.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    const existing = finishedGoodsStockData.get(key) || {
      group: { product_name: productName || 'Unnamed Product', product_id: Number.isInteger(productId) ? productId : null },
      total_in: 0,
      total_out: 0,
      total_hold: 0,
      current_stock: 0,
      completed_orders: 0,
      no_movement_yet: false
    };
    if (productName) existing.group.product_name = productName;
    if (Number.isInteger(productId)) existing.group.product_id = productId;
    const type = String(transaction.type || '').toLowerCase();
    if (type === 'in') {
      existing.total_in += quantity;
      existing.current_stock += quantity;
    } else if (type === 'out') {
      existing.total_out += quantity;
      existing.current_stock -= quantity;
    } else if (type === 'hold') {
      existing.total_hold += quantity;
      existing.current_stock -= quantity;
    }
    existing.no_movement_yet = false;
    finishedGoodsStockData.set(key, existing);
  });

  productCatalog.forEach((product) => {
    const key = getFinishedGoodsStockKey({ product_id: product?.id, product_name: product?.name });
    if (!key) return;
    const existing = finishedGoodsStockData.get(key);
    if (existing) {
      if (!existing.group?.product_name && product?.name) existing.group.product_name = String(product.name).trim();
      if (!existing.group?.product_id && Number.isInteger(Number(product?.id))) {
        existing.group.product_id = Number(product.id);
      }
      return;
    }
    finishedGoodsStockData.set(key, {
      group: {
        product_name: String(product?.name || '').trim() || 'Unnamed Product',
        product_id: Number.isInteger(Number(product?.id)) ? Number(product.id) : null
      },
      total_in: 0,
      total_out: 0,
      total_hold: 0,
      current_stock: 0,
      completed_orders: 0,
      no_movement_yet: true
    });
  });

  const filteredFinishedGoodsStock = Array.from(finishedGoodsStockData.values())
    .filter((item) => includeSetupOnlyStock || !item?.no_movement_yet)
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      return String(item.group?.product_name || '').toLowerCase().includes(query);
    })
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return String(a.group?.product_name || '').localeCompare(String(b.group?.product_name || ''));
    });

  const usersById = useMemo(
    () => users.reduce((acc, item) => ({ ...acc, [String(item.id)]: item }), {}),
    [users]
  );

  const pipelineOrdersByStage = useMemo(() => {
    const grouped = pipelineStages.reduce((acc, stage) => ({ ...acc, [stage]: [] }), {});
    const fallbackStage = pipelineStages[0] || null;
    orders
      .filter((order) => !['Completed', 'Cancelled'].includes(order.status || ''))
      .forEach((order) => {
        const stage = order.pipeline_stage || fallbackStage;
        if (stage && grouped[stage]) {
          grouped[stage].push(order);
        }
      });
    Object.keys(grouped).forEach((stage) => {
      grouped[stage].sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return bTime - aTime;
      });
    });
    return grouped;
  }, [orders, pipelineStages]);

  const getStageAgeDays = (order) => {
    const enteredAt = new Date(order.stage_entered_at || order.updated_at || order.created_at || '');
    if (Number.isNaN(enteredAt.getTime())) return 0;
    const diff = Date.now() - enteredAt.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  };

  const filteredPipelineOrdersByStage = useMemo(() => {
    const query = pipelineSearch.toLowerCase().trim();
    const result = {};
    pipelineStages.forEach((stage) => {
      const stageOrders = pipelineOrdersByStage[stage] || [];
      result[stage] = stageOrders.filter((order) => {
        if (pipelineAssigneeFilter !== 'all' && String(order.assigned_user_id || '') !== pipelineAssigneeFilter) {
          return false;
        }
        if (pipelineOverdueOnly && getStageAgeDays(order) < 7) {
          return false;
        }
        if (pipelineBlockedOnly && !order.blocked_reason) {
          return false;
        }
        if (pipelineUnassignedOnly && order.assigned_user_id) {
          return false;
        }
        if (!query) return true;
        return (
          String(order.order_number || '').includes(query)
          || String(order.buyer_name || '').toLowerCase().includes(query)
        );
      });
    });
    return result;
  }, [pipelineStages, pipelineOrdersByStage, pipelineSearch, pipelineAssigneeFilter, pipelineOverdueOnly, pipelineBlockedOnly, pipelineUnassignedOnly]);

  const filteredPipelineOrders = useMemo(() => {
    const flat = pipelineStages.flatMap((stage) => (filteredPipelineOrdersByStage[stage] || []).map((order) => ({
      ...order,
      _stageName: stage,
      _deliveryDate: parseOrderPayload(order?.payload)?.delivery_date || order.delivery_date || null
    })));
    flat.sort((a, b) => {
      const aDate = a._deliveryDate ? new Date(`${a._deliveryDate}T00:00:00Z`).getTime() : Number.MAX_SAFE_INTEGER;
      const bDate = b._deliveryDate ? new Date(`${b._deliveryDate}T00:00:00Z`).getTime() : Number.MAX_SAFE_INTEGER;
      if (aDate !== bDate) return aDate - bDate;
      return Number(a.order_number || 0) - Number(b.order_number || 0);
    });
    return flat;
  }, [pipelineStages, filteredPipelineOrdersByStage, parseOrderPayload]);

  const pipelineCalendarMonthStart = useMemo(() => {
    const [yearRaw, monthRaw] = String(pipelineCalendarCursor || '').split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    if (!year || !month || month < 1 || month > 12) {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return new Date(year, month - 1, 1);
  }, [pipelineCalendarCursor]);

  const pipelineCalendarMonthLabel = useMemo(() => (
    new Intl.DateTimeFormat('en-US', {
      timeZone: currentUserTimeZone || getBrowserTimeZone(),
      month: 'long',
      year: 'numeric'
    }).format(pipelineCalendarMonthStart)
  ), [pipelineCalendarMonthStart, currentUserTimeZone]);

  const pipelineCalendarWeekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const pipelineCalendarOrdersByDate = useMemo(() => {
    const map = {};
    filteredPipelineOrders.forEach((order) => {
      if (!order._deliveryDate) return;
      const key = String(order._deliveryDate);
      if (!map[key]) map[key] = [];
      map[key].push(order);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => Number(a.order_number || 0) - Number(b.order_number || 0));
    });
    return map;
  }, [filteredPipelineOrders]);

  const pipelineUnscheduledOrders = useMemo(
    () => filteredPipelineOrders.filter((order) => !order._deliveryDate),
    [filteredPipelineOrders]
  );

  const pipelineCalendarDays = useMemo(() => {
    const firstOfMonth = pipelineCalendarMonthStart;
    const firstWeekday = firstOfMonth.getDay();
    const firstGridDay = new Date(
      firstOfMonth.getFullYear(),
      firstOfMonth.getMonth(),
      1 - firstWeekday
    );
    const todayKey = getDateKeyForTimeZone(currentUserTimeZone || getBrowserTimeZone());
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const date = new Date(firstGridDay.getFullYear(), firstGridDay.getMonth(), firstGridDay.getDate() + i);
      const key = toLocalDateKey(date);
      days.push({
        key,
        date,
        dayNumber: date.getDate(),
        inCurrentMonth: date.getMonth() === firstOfMonth.getMonth(),
        isToday: key === todayKey,
        orders: pipelineCalendarOrdersByDate[key] || []
      });
    }
    return days;
  }, [pipelineCalendarMonthStart, pipelineCalendarOrdersByDate, currentUserTimeZone]);

  const transitionSingleOrder = async (order, action, options = {}) => {
    if (!order?.id) return;
    const previousOrder = order;
    const fallbackStage = pipelineStages[0] || null;
    const currentStage = previousOrder.pipeline_stage || fallbackStage;
    const currentIndex = pipelineStages.indexOf(currentStage);
    let optimisticStatus = previousOrder.status || 'On Hold';
    let optimisticStage = currentStage;
    let optimisticAssignee = previousOrder.assigned_user_id || null;
    let optimisticBlockedReason = previousOrder.blocked_reason || null;
    let optimisticDeliveryDate = parseOrderPayload(previousOrder?.payload)?.delivery_date || previousOrder.delivery_date || null;

    if (action === 'next' && currentIndex >= 0 && currentIndex < pipelineStages.length - 1) {
      optimisticStage = pipelineStages[currentIndex + 1];
    } else if (action === 'back' && currentIndex > 0) {
      optimisticStage = pipelineStages[currentIndex - 1];
    } else if (action === 'set_stage' && options.target_stage) {
      optimisticStage = options.target_stage;
    } else if (action === 'complete') {
      optimisticStatus = 'Completed';
    } else if (action === 'cancel') {
      optimisticStatus = 'Cancelled';
    } else if (action === 'assign') {
      optimisticAssignee = options.assigned_user_id || null;
    } else if (action === 'block') {
      optimisticBlockedReason = options.blocked_reason || 'Blocked';
    } else if (action === 'unblock') {
      optimisticBlockedReason = null;
    } else if (action === 'reschedule') {
      const nextDeliveryDate = typeof options.delivery_date === 'string' ? options.delivery_date.trim() : '';
      optimisticDeliveryDate = nextDeliveryDate || null;
    }

    setPipelineTransitioningIds((prev) => [...prev, order.id]);
    setOrders((prev) => prev.map((item) => (
      item.id === order.id
        ? (() => {
            const payload = parseOrderPayload(item?.payload) || {};
            return {
              ...item,
              pipeline_stage: optimisticStage,
              status: optimisticStatus,
              assigned_user_id: optimisticAssignee,
              blocked_reason: optimisticBlockedReason,
              delivery_date: optimisticDeliveryDate,
              payload: JSON.stringify({
                ...payload,
                delivery_date: optimisticDeliveryDate
              })
            };
          })()
        : item
    )));
    try {
      const updated = await api.transitionOrderPipelineStage(order.id, action, options);
      setOrders((prev) => prev.map((item) => (item.id === order.id ? { ...item, ...updated } : item)));
      return { ok: true };
    } catch (err) {
      setOrders((prev) => prev.map((item) => (item.id === order.id ? previousOrder : item)));
      setError(err.message);
      return { ok: false };
    } finally {
      setPipelineTransitioningIds((prev) => prev.filter((id) => id !== order.id));
    }
  };

  const openPipelineTransitionDialog = (order, action, stageOverride = null, extraOptions = {}) => {
    setPipelineTransitionDialog({ order, action, stageOverride, extraOptions });
    setPipelineTransitionForm({ reason: '', note: '', blocked_reason: action === 'block' ? (order?.blocked_reason || '') : '' });
  };

  const closePipelineTransitionDialog = () => {
    setPipelineTransitionDialog(null);
    setPipelineTransitionForm({ reason: '', note: '', blocked_reason: '' });
  };

  const submitPipelineTransitionDialog = async () => {
    if (!pipelineTransitionDialog?.order) return;
    const { order, action, stageOverride, extraOptions } = pipelineTransitionDialog;
    const options = {
      ...extraOptions,
      reason: pipelineTransitionForm.reason,
      note: pipelineTransitionForm.note
    };
    if (stageOverride) options.target_stage = stageOverride;
    if (action === 'block') {
      options.blocked_reason = pipelineTransitionForm.blocked_reason;
      if (!options.blocked_reason?.trim()) {
        setError('Blocked reason is required.');
        return;
      }
    }
    await transitionSingleOrder(order, action, options);
    closePipelineTransitionDialog();
  };

  const handlePipelineTransition = async (order, action, stageOverride = null, extraOptions = {}) => {
    setError('');
    if (['next', 'back', 'set_stage', 'complete', 'cancel', 'block'].includes(action)) {
      openPipelineTransitionDialog(order, action, stageOverride, extraOptions);
      return;
    }
    const options = { ...extraOptions };
    if (stageOverride) options.target_stage = stageOverride;
    await transitionSingleOrder(order, action, options);
  };

  const getPipelineTransitionDialogConfig = (dialog) => {
    const fallback = {
      title: 'Confirm transition',
      message: 'Review details and confirm this workflow update.',
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'Reason',
      noteLabel: 'Note (optional)',
      notePlaceholder: 'Add detail',
      confirmLabel: 'Submit'
    };
    if (!dialog?.order) return fallback;
    const order = dialog.order;
    const currentStage = order.pipeline_stage || order._stageName || pipelineStages[0] || 'Current stage';
    const currentIndex = pipelineStages.indexOf(currentStage);
    const nextStage = currentIndex >= 0 && currentIndex < pipelineStages.length - 1
      ? pipelineStages[currentIndex + 1]
      : 'next stage';
    const previousStage = currentIndex > 0 ? pipelineStages[currentIndex - 1] : 'previous stage';

    if (dialog.action === 'next') {
      return {
        title: `Move Order #${order.order_number} to ${nextStage}?`,
        message: `This moves the order from "${currentStage}" to "${nextStage}".`,
        reasonLabel: 'Transition reason (optional)',
        reasonPlaceholder: `Why move to ${nextStage}?`,
        noteLabel: 'Handover note (optional)',
        notePlaceholder: 'Add context for the next stage owner',
        confirmLabel: `Move to ${nextStage}`
      };
    }
    if (dialog.action === 'back') {
      return {
        title: `Move Order #${order.order_number} back to ${previousStage}?`,
        message: `This moves the order from "${currentStage}" back to "${previousStage}".`,
        reasonLabel: 'Rework reason (optional)',
        reasonPlaceholder: `Why move back to ${previousStage}?`,
        noteLabel: 'Rework note (optional)',
        notePlaceholder: 'Add context on what needs correction',
        confirmLabel: `Move Back to ${previousStage}`
      };
    }
    if (dialog.action === 'complete') {
      return {
        title: `Complete Order #${order.order_number}?`,
        message: 'This marks the order as completed and removes it from active workflow stages.',
        reasonLabel: 'Completion reason (optional)',
        reasonPlaceholder: 'Reason for completion',
        noteLabel: 'Completion note (optional)',
        notePlaceholder: 'Add final remarks',
        confirmLabel: 'Mark Complete'
      };
    }
    if (dialog.action === 'cancel') {
      return {
        title: `Cancel Order #${order.order_number}?`,
        message: 'This marks the order as cancelled and removes it from active workflow stages.',
        reasonLabel: 'Cancellation reason (optional)',
        reasonPlaceholder: 'Reason for cancellation',
        noteLabel: 'Cancellation note (optional)',
        notePlaceholder: 'Add cancellation details',
        confirmLabel: 'Cancel Order'
      };
    }
    return fallback;
  };

  const handleViewPipelineHistory = async (order) => {
    if (!order?.id) return;
    setPipelineHistoryOrder(order);
    setPipelineHistoryLoading(true);
    try {
      const rows = await api.getOrderPipelineHistory(order.id);
      setPipelineHistoryRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err.message);
      setPipelineHistoryRows([]);
    } finally {
      setPipelineHistoryLoading(false);
    }
  };

  const applyPipelinePreset = (preset) => {
    setPipelinePreset(preset);
    if (preset === 'all') {
      setPipelineBlockedOnly(false);
      setPipelineUnassignedOnly(false);
      setPipelineOverdueOnly(false);
      return;
    }
    if (preset === 'blocked') {
      setPipelineBlockedOnly(true);
      setPipelineUnassignedOnly(false);
      return;
    }
    if (preset === 'unassigned') {
      setPipelineBlockedOnly(false);
      setPipelineUnassignedOnly(true);
      return;
    }
    if (preset === 'overdue_unassigned') {
      setPipelineOverdueOnly(true);
      setPipelineUnassignedOnly(true);
      setPipelineBlockedOnly(false);
    }
  };

  const handleBulkTransition = async (action) => {
    if (selectedPipelineOrderIds.length === 0) return;
    const selectedOrders = orders.filter((order) => selectedPipelineOrderIds.includes(order.id));
    for (const order of selectedOrders) {
      // eslint-disable-next-line no-await-in-loop
      await transitionSingleOrder(order, action);
    }
    setSelectedPipelineOrderIds([]);
  };

  const getCustomerDisplayName = useCallback((customer) => {
    if (!customer) return '';
    if (typeof customer.name === 'string' && customer.name.trim()) return customer.name.trim();
    const fallback = customerSchema.fields.find((field) => {
      const value = customer.custom_fields?.[field.key];
      return typeof value === 'string' && value.trim();
    });
    return fallback ? String(customer.custom_fields[fallback.key]).trim() : '';
  }, [customerSchema.fields]);

  const productCatalogWithPayload = useMemo(
    () => productCatalog.map((product) => ({ ...product, parsedPayload: parseProductPayload(product) })),
    [productCatalog]
  );

  const filteredProductCatalog = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    return productCatalogWithPayload.filter((product) => {
      const payloadFields = product.parsedPayload?.fields || {};
      const rawMaterialsList = Array.isArray(payloadFields.raw_materials)
        ? payloadFields.raw_materials
        : [];
      const hasRaw = rawMaterialsList.length > 0;
      if (productHasRawFilter === 'with' && !hasRaw) return false;
      if (productHasRawFilter === 'without' && hasRaw) return false;

      if (productMaterialFilter !== 'all') {
        const matchesMaterial = rawMaterialsList.some(
          (entry) => String(entry?.raw_material_name || entry?.type || '').toLowerCase() === productMaterialFilter
        );
        if (!matchesMaterial) return false;
      }

      if (!query) return true;
      const nameMatch = String(product.name || '').toLowerCase().includes(query);
      const rawMatch = rawMaterialsList.some((entry) => {
        const rawMaterial = String(entry?.raw_material_name || entry?.type || '').toLowerCase();
        const characteristicValues = Object.values(entry?.characteristics || {})
          .map((value) => String(value || '').toLowerCase())
          .join(' ');
        return rawMaterial.includes(query) || characteristicValues.includes(query);
      });
      const fieldMatch = Object.values(payloadFields || {}).some((value) =>
        String(value || '').toLowerCase().includes(query)
      );
      return nameMatch || rawMatch || fieldMatch;
    });
  }, [productCatalogWithPayload, productSearch, productMaterialFilter, productHasRawFilter]);

  const hasActiveProductFilters = Boolean(productSearch.trim() || productMaterialFilter !== 'all' || productHasRawFilter !== 'all');
  const productDisplayFields = useMemo(
    () => (productSchema.fields || []).filter((field) => field.key !== 'raw_materials'),
    [productSchema.fields]
  );
  const visibleProductCards = useMemo(
    () => filteredProductCatalog.map((product) => {
      const fields = product.parsedPayload?.fields || {};
      const rawMaterialsList = Array.isArray(fields.raw_materials)
        ? fields.raw_materials.map((entry) => normalizeProductRawMaterialEntry(entry))
        : [];
      const missingRequiredFields = productDisplayFields
        .filter((field) => field?.required && field.key !== 'raw_materials')
        .filter((field) => {
          const value = fields[field.key];
          if (field.type === 'checkbox') return value !== true;
          if (value === undefined || value === null) return true;
          if (typeof value === 'string') return value.trim() === '';
          return false;
        })
        .map((field) => field.label || field.key);
      const totalRawRequiredQty = rawMaterialsList.reduce((sum, entry) => sum + (parseFloat(entry.required_qty || 0) || 0), 0);
      return {
        ...product,
        displayFields: fields,
        rawMaterialsList,
        missingRequiredFields,
        totalRawRequiredQty
      };
    }),
    [filteredProductCatalog, productDisplayFields]
  );
  const sortedProductListRows = useMemo(() => {
    const rows = [...visibleProductCards];
    const { key, direction } = productListSort;
    const factor = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (key === 'rawCount') {
        return ((a.rawMaterialsList?.length || 0) - (b.rawMaterialsList?.length || 0)) * factor;
      }
      if (key === 'updated') {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime() || 0;
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime() || 0;
        return (aTime - bTime) * factor;
      }
      if (key === 'bom') {
        const aBom = (a.rawMaterialsList?.length || 0) > 0 ? 1 : 0;
        const bBom = (b.rawMaterialsList?.length || 0) > 0 ? 1 : 0;
        return (aBom - bBom) * factor;
      }
      return String(a.name || '').localeCompare(String(b.name || '')) * factor;
    });
    return rows;
  }, [visibleProductCards, productListSort]);
  const toggleProductListSort = (key) => {
    setProductListSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      const nextDirection = key === 'name' ? 'asc' : 'desc';
      return { key, direction: nextDirection };
    });
  };
  const getProductSortIndicator = (key) => {
    if (productListSort.key !== key) return '';
    return productListSort.direction === 'asc' ? '↑' : '↓';
  };
  const toggleProductListExpanded = (productId) => {
    setExpandedProductListIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };
  const formatProductDisplayFieldValue = (field, value) => {
    if (field?.type === 'checkbox') return value ? 'Yes' : 'No';
    if (value === undefined || value === null || value === '') return '—';
    const text = String(value);
    if (field?.type === 'number' && field?.unit) return `${text} ${field.unit}`;
    return text;
  };
  const productMetrics = useMemo(() => {
    const rows = productCatalogWithPayload.map((product) => {
      const fields = product.parsedPayload?.fields || {};
      const rawMaterialsList = Array.isArray(fields.raw_materials)
        ? fields.raw_materials
        : [];
      const missing = productDisplayFields
        .filter((field) => field?.required && field.key !== 'raw_materials')
        .some((field) => {
          const value = fields[field.key];
          if (field.type === 'checkbox') return value !== true;
          if (value === undefined || value === null) return true;
          if (typeof value === 'string') return value.trim() === '';
          return false;
        });
      return { hasBom: rawMaterialsList.length > 0, missing, rawCount: rawMaterialsList.length };
    });
    const total = rows.length;
    const withBom = rows.filter((row) => row.hasBom).length;
    const missing = rows.filter((row) => row.missing).length;
    const avgRaw = total > 0
      ? (rows.reduce((sum, row) => sum + row.rawCount, 0) / total)
      : 0;
    return { total, withBom, missing, avgRaw };
  }, [productCatalogWithPayload, productDisplayFields]);

  const customerOptions = useMemo(() => {
    return customers
      .map((customer) => ({ id: customer.id, label: getCustomerDisplayName(customer) }))
      .filter((item) => item.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers, getCustomerDisplayName]);

  const {
    orderProductSummaries,
    orderDraftTotals,
    handleAddOrderProductCard,
    handleRemoveOrderProductCard,
    updateOrderProduct,
    resetOrderForm
  } = useOrders({
    orderProducts,
    productCatalogWithPayload,
    setOrderProducts,
    setOrderFormErrors,
    setOrderForm,
    getTodayDateKey
  });

  const {
    enqueueOfflineAction,
    flushOfflineActionQueue,
    offlineQueueOpen,
    offlineQueueSnapshot,
    offlineSyncing,
    setOfflineActionQueue,
    setOfflineQueueOpen
  } = useOfflineQueue({
    api,
    loadData,
    setError,
    setSuccess,
    setIsOnline
  });

  useEffect(() => {
    if (!pipelineActionMenuOpenId) return undefined;
    const handleClick = (event) => {
      if (event.target.closest?.('.pipeline-action-menu')) return;
      setPipelineActionMenuOpenId(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [pipelineActionMenuOpenId]);

  useEffect(() => {
    if (!transactionActionMenuOpenId) return undefined;
    const handleClick = (event) => {
      if (event.target.closest?.('.transaction-action-menu')) return;
      setTransactionActionMenuOpenId(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [transactionActionMenuOpenId]);

  

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    const orderNumber = parseInt(orderForm.order_number, 10);
    if (!Number.isInteger(orderNumber) || orderNumber <= 0) {
      nextErrors.order_number = 'Enter a valid order number.';
    }
    if (!orderForm.buyer_name.trim()) {
      nextErrors.buyer_name = 'Buyer name is required.';
    }
    if (!orderForm.delivery_date) {
      nextErrors.delivery_date = 'Delivery date is required.';
    }
    const invalidProductIndex = orderProducts.findIndex((entry) => !entry.product_id || !(parseInt(entry.quantity, 10) > 0));
    if (invalidProductIndex >= 0) {
      nextErrors.products = `Complete Product Type and Units for card #${invalidProductIndex + 1}.`;
    }
    if (Object.keys(nextErrors).length > 0) {
      setOrderFormErrors(nextErrors);
      setError(Object.values(nextErrors)[0]);
      return;
    }
    setOrderFormErrors({});

    const productsPayload = orderProducts.map((entry) => {
      const selected = productCatalogWithPayload.find((product) => String(product.id) === String(entry.product_id));
      const rawMaterialsList = Array.isArray(selected?.parsedPayload?.fields?.raw_materials)
        ? selected.parsedPayload.fields.raw_materials
        : [];
      const items = rawMaterialsList.map((item) => ({
        raw_material_name: item?.raw_material_name || item?.type,
        characteristics: item?.characteristics && typeof item.characteristics === 'object'
          ? item.characteristics
          : (item?.category ? { Type: item.category } : {}),
        required_qty: parseFloat(item?.required_qty || 0)
      }));
      return {
        product_id: selected?.id,
        product_name: selected?.name || 'Unnamed Product',
        quantity: Math.max(1, parseInt(entry.quantity, 10) || 1),
        delivery_date: orderForm.delivery_date,
        items,
        fields: selected?.parsedPayload?.fields || {}
      };
    });
    const productWithoutBom = productsPayload.find((product) => {
      const items = Array.isArray(product?.items) ? product.items : [];
      return !items.some((item) => {
        const rawMaterialName = String(item?.raw_material_name || '').trim();
        const requiredQty = Number(item?.required_qty);
        return rawMaterialName && Number.isFinite(requiredQty) && requiredQty > 0;
      });
    });
    if (productWithoutBom) {
      const bomMessage = `"${productWithoutBom.product_name}" has no valid raw material mapping. Update this product before creating the order.`;
      setOrderFormErrors((prev) => ({ ...prev, bom: bomMessage }));
      setError(bomMessage);
      return;
    }

    const totalQty = productsPayload.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    const totalRequired = productsPayload.reduce(
      (sum, item) => sum + item.items.reduce((inner, raw) => inner + ((parseFloat(raw.required_qty) || 0) * item.quantity), 0),
      0
    );

    try {
      const payload = {
        order_number: orderNumber,
        buyer_name: orderForm.buyer_name.trim(),
        delivery_date: orderForm.delivery_date,
        products: productsPayload,
        product_count: productsPayload.length,
        total_qty: totalQty,
        total_required: totalRequired
      };
      if (!navigator.onLine) {
        enqueueOfflineAction('order_create', payload);
        setSuccess('You are offline. Order saved and will sync when your connection returns.');
        resetOrderForm();
        setActiveTab('orders');
        return;
      }
      const createdOrder = await api.createOrder(payload);
      const fgAllocated = Array.isArray(createdOrder?.finished_allocations)
        ? createdOrder.finished_allocations.reduce((sum, item) => sum + Number(item?.allocated_quantity || 0), 0)
        : 0;
      const rmPlannedBuckets = Array.isArray(createdOrder?.raw_shortfall_requirements)
        ? createdOrder.raw_shortfall_requirements.length
        : 0;
      setSuccess(`Order created successfully. Allocated from finished stock: ${fgAllocated.toFixed(2)}. Planned from raw materials: ${rmPlannedBuckets} bucket(s).`);
      resetOrderForm();
      await Promise.all([loadData({ silent: true }), loadNextOrderNumber()]);
      setActiveTab('orders');
    } catch (err) {
      const shortages = Array.isArray(err?.payload?.shortages) ? err.payload.shortages : [];
      if (shortages.length > 0 && err?.payload?.can_override) {
        setLowStockModal({
          payload: {
            order_number: orderNumber,
            buyer_name: orderForm.buyer_name.trim(),
            delivery_date: orderForm.delivery_date,
            products: productsPayload,
            product_count: productsPayload.length,
            total_qty: totalQty,
            total_required: totalRequired
          },
          shortages,
          reason: ''
        });
        setError('');
        return;
      }
      if (String(err.message || '').toLowerCase().includes('order number')) {
        setOrderFormErrors((prev) => ({
          ...prev,
          order_number: err.message
        }));
      }
      setError(err.message);
    }
  };

  const handleOverrideLowStockOrderCreate = async () => {
    if (!lowStockModal?.payload) return;
    if (!navigator.onLine) {
      setError('Device is offline. Please reconnect and try again.');
      return;
    }
    try {
      setLowStockOverrideLoading(true);
      const createdOrder = await api.createOrder({
        ...lowStockModal.payload,
        allow_low_stock_override: true,
        override_reason: String(lowStockModal.reason || '').trim()
      });
      setLowStockModal(null);
      const fgAllocated = Array.isArray(createdOrder?.finished_allocations)
        ? createdOrder.finished_allocations.reduce((sum, item) => sum + Number(item?.allocated_quantity || 0), 0)
        : 0;
      setSuccess(`Order created with low-stock override. FG allocated: ${fgAllocated.toFixed(2)}. Some raw-material buckets may be negative.`);
      resetOrderForm();
      await Promise.all([loadData({ silent: true }), loadNextOrderNumber()]);
      setActiveTab('orders');
    } catch (err) {
      setError(err.message);
    } finally {
      setLowStockOverrideLoading(false);
    }
  };

  const openCsvImport = (mode, inventoryKind = 'raw_material') => {
    setCsvImportModal({
      mode,
      inventoryKind,
      fileName: '',
      csvText: '',
      showTemplate: false,
      allowNegative: true,
      autoAddCharacteristics: inventoryKind === 'raw_material',
      summary: null,
      errors: [],
      characteristicCandidates: [],
      preview: []
    });
  };

  const handleCsvFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setCsvImportModal((prev) => (
        prev
          ? {
            ...prev,
            fileName: file.name,
            csvText: text,
            summary: null,
            errors: [],
            characteristicCandidates: [],
            preview: []
          }
          : prev
      ));
    } catch (_err) {
      setError('Unable to read CSV file.');
    }
  };

  const handleCsvImportPreview = async () => {
    if (!csvImportModal?.csvText?.trim()) {
      setError('Please choose a CSV file first.');
      return;
    }
    try {
      setCsvImportLoading(true);
      setError('');
      const result = await api.previewTransactionsImport({
        csv_text: csvImportModal.csvText,
        mode: csvImportModal.mode,
        inventory_kind: csvImportModal.inventoryKind || 'raw_material',
        allow_negative: Boolean(csvImportModal.allowNegative),
        auto_add_characteristics: Boolean(csvImportModal.autoAddCharacteristics)
      });
      setCsvImportModal((prev) => (
        prev
          ? {
            ...prev,
            summary: result?.summary || null,
            errors: Array.isArray(result?.errors) ? result.errors : [],
            characteristicCandidates: Array.isArray(result?.characteristic_candidates) ? result.characteristic_candidates : [],
            preview: Array.isArray(result?.preview) ? result.preview : []
          }
          : prev
      ));
    } catch (err) {
      setError(err.message);
      setCsvImportModal((prev) => (
        prev
          ? {
            ...prev,
            summary: err?.payload?.summary || null,
            errors: Array.isArray(err?.payload?.errors) ? err.payload.errors : [],
            preview: []
          }
          : prev
      ));
    } finally {
      setCsvImportLoading(false);
    }
  };

  const handleCsvImportCommit = async () => {
    if (!csvImportModal?.csvText?.trim()) {
      setError('Please choose a CSV file first.');
      return;
    }
    try {
      setCsvImportLoading(true);
      setError('');
      const result = await api.importTransactionsCsv({
        csv_text: csvImportModal.csvText,
        mode: csvImportModal.mode,
        inventory_kind: csvImportModal.inventoryKind || 'raw_material',
        allow_negative: Boolean(csvImportModal.allowNegative),
        auto_add_characteristics: Boolean(csvImportModal.autoAddCharacteristics)
      });
      const createdCount = Number(result?.characteristics_created_count || 0);
      const createdSuffix = createdCount > 0
        ? ` ${createdCount} characteristic value(s) added to lists.`
        : '';
      setSuccess(`CSV import completed. ${result?.inserted_count || 0} row(s) inserted.${createdSuffix}`);
      setCsvImportModal(null);
      await loadData({ silent: true });
      if (csvImportModal.mode === 'opening_stock') {
        setActiveTab('stock');
      } else {
        setActiveTab('transactions');
      }
    } catch (err) {
      setError(err.message);
      setCsvImportModal((prev) => (
        prev
          ? {
            ...prev,
            summary: err?.payload?.summary || prev.summary,
            errors: Array.isArray(err?.payload?.errors) ? err.payload.errors : prev.errors
          }
          : prev
      ));
    } finally {
      setCsvImportLoading(false);
    }
  };

  const buildCsvImportTemplate = (mode, inventoryKind = 'raw_material') => {
    if (inventoryKind === 'finished_goods') {
      const firstProductName = String(finishedGoodsOptions?.[0]?.name || productCatalog?.[0]?.name || 'Sample Product').trim();
      const headers = ['type', 'finished_product_name', 'quantity', 'date', 'notes'];
      const rows = [
        { type: 'in', finished_product_name: firstProductName, quantity: '50', date: getTodayDateKey(), notes: 'Finished goods inbound import sample' },
        { type: 'out', finished_product_name: firstProductName, quantity: '10', date: getTodayDateKey(), notes: 'Finished goods outbound import sample' }
      ];
      const csvEscape = (value) => {
        const text = String(value ?? '');
        if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
        return text;
      };
      const lines = [
        headers.join(','),
        ...rows.map((row) => headers.map((header) => csvEscape(row[header] || '')).join(','))
      ];
      return lines.join('\n');
    }

    const firstRawMaterial = rawMaterials[0];
    const firstRawMaterialName = String(firstRawMaterial?.name || 'Cotton').trim();
    const firstCharacteristics = (firstRawMaterial?.characteristics || [])
      .map((item) => String(item?.name || '').trim())
      .filter(Boolean);
    const characteristicHeaders = csvImportCharacteristicHeaders.length > 0
      ? csvImportCharacteristicHeaders
      : firstCharacteristics;

    const headers = mode === 'opening_stock'
      ? ['raw_material_type', ...characteristicHeaders, 'opening_quantity', 'date', 'notes']
      : ['type', 'raw_material_type', ...characteristicHeaders, 'quantity', 'date', 'unit_cost', 'notes'];

    const baseCharacteristics = {};
    characteristicHeaders.forEach((header, idx) => {
      baseCharacteristics[header] = idx === 0 ? 'Default' : '';
    });

    const rows = mode === 'opening_stock'
      ? [
        { raw_material_type: firstRawMaterialName, ...baseCharacteristics, opening_quantity: '100', date: getTodayDateKey(), notes: 'Opening balance import' }
      ]
      : [
        { type: 'in', raw_material_type: firstRawMaterialName, ...baseCharacteristics, quantity: '25', date: getTodayDateKey(), unit_cost: '120', notes: 'Inbound import sample' },
        { type: 'out', raw_material_type: firstRawMaterialName, ...baseCharacteristics, quantity: '5', date: getTodayDateKey(), unit_cost: '120', notes: 'Outbound import sample' }
      ];

    const csvEscape = (value) => {
      const text = String(value ?? '');
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const lines = [
      headers.join(','),
      ...rows.map((row) => headers.map((header) => csvEscape(row[header] || '')).join(','))
    ];
    return lines.join('\n');
  };

  const downloadCsvImportTemplate = (mode, inventoryKind = 'raw_material') => {
    const csvText = buildCsvImportTemplate(mode, inventoryKind);
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    if (inventoryKind === 'finished_goods') {
      link.setAttribute('download', 'Finished_Goods_Transactions_Import_Template.csv');
    } else {
      link.setAttribute('download', mode === 'opening_stock' ? 'Opening_Stock_Import_Template.csv' : 'Transactions_Import_Template.csv');
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const loadNextOrderNumber = async () => {
    try {
      setOrderNumberLoading(true);
      const data = await api.getOrderNumber();
      setOrderForm((prev) => ({ ...prev, order_number: String(data?.next_order_number || '') }));
    } catch (err) {
      setError(err.message);
    } finally {
      setOrderNumberLoading(false);
    }
  };

  const attendanceEligibleStaff = useMemo(
    () => staff
      .filter((member) => member.is_active && !member.anonymized_at)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [staff]
  );

  const buildStaffAttendanceDraft = useCallback((records = []) => {
    const next = {};
    attendanceEligibleStaff.forEach((member) => {
      next[member.id] = {
        staff_id: member.id,
        status: 'present',
        notes: ''
      };
    });
    records.forEach((record) => {
      const staffId = Number(record?.staff_id);
      if (!Number.isInteger(staffId) || !next[staffId]) return;
      next[staffId] = {
        staff_id: staffId,
        status: String(record?.status || 'present').trim() || 'present',
        notes: String(record?.notes || '')
      };
    });
    return next;
  }, [attendanceEligibleStaff]);

  const loadStaffAttendance = useCallback(async (dateValue) => {
    if (!dateValue) return;
    setStaffAttendanceLoading(true);
    try {
      const rows = await api.getStaffAttendance(dateValue);
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setStaffAttendanceEntries(buildStaffAttendanceDraft(normalizedRows));
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffAttendanceLoading(false);
    }
  }, [buildStaffAttendanceDraft]);

  const openStaffAttendanceModal = () => {
    const today = getTodayDateKey();
    setStaffAttendanceDate(today);
    setStaffAttendanceEntries(buildStaffAttendanceDraft([]));
    setShowStaffAttendanceModal(true);
  };

  const loadStaffAttendanceView = useCallback(async (dateValue) => {
    if (!dateValue) return;
    setStaffAttendanceViewLoading(true);
    try {
      const rows = await api.getStaffAttendance(dateValue);
      setStaffAttendanceViewRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffAttendanceViewLoading(false);
    }
  }, []);

  const openStaffAttendanceViewModal = () => {
    const today = getTodayDateKey();
    setStaffAttendanceViewDate(today);
    setStaffAttendanceViewRows([]);
    setShowStaffAttendanceViewModal(true);
  };

  const updateStaffAttendanceEntry = (staffId, updates) => {
    setStaffAttendanceEntries((prev) => ({
      ...prev,
      [staffId]: {
        staff_id: staffId,
        status: 'present',
        notes: '',
        ...(prev[staffId] || {}),
        ...updates
      }
    }));
  };

  const handleSaveStaffAttendance = async () => {
    if (!staffAttendanceDate) {
      setError('Attendance date is required.');
      return;
    }
    const entries = attendanceEligibleStaff.map((member) => {
      const current = staffAttendanceEntries[member.id] || {};
      return {
        staff_id: member.id,
        status: String(current.status || 'present'),
        notes: String(current.notes || '').trim()
      };
    });
    if (entries.length === 0) {
      setError('No active staff available for attendance.');
      return;
    }
    try {
      setStaffAttendanceSaving(true);
      await api.saveStaffAttendance(staffAttendanceDate, entries);
      setSuccess(`Attendance saved for ${staffAttendanceDate}.`);
      setShowStaffAttendanceModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffAttendanceSaving(false);
    }
  };

  useEffect(() => {
    if (!showStaffAttendanceModal) return;
    loadStaffAttendance(staffAttendanceDate);
  }, [showStaffAttendanceModal, staffAttendanceDate, loadStaffAttendance]);

  useEffect(() => {
    if (!showStaffAttendanceViewModal) return;
    loadStaffAttendanceView(staffAttendanceViewDate);
  }, [showStaffAttendanceViewModal, staffAttendanceViewDate, loadStaffAttendanceView]);

  const attendanceSummary = useMemo(() => {
    const totals = { present: 0, absent: 0, half_day: 0, leave: 0 };
    attendanceEligibleStaff.forEach((member) => {
      const status = String(staffAttendanceEntries[member.id]?.status || 'present');
      if (Object.prototype.hasOwnProperty.call(totals, status)) {
        totals[status] += 1;
      }
    });
    return totals;
  }, [attendanceEligibleStaff, staffAttendanceEntries]);

  const attendanceViewSummary = useMemo(() => {
    const totals = { present: 0, absent: 0, half_day: 0, leave: 0, total: 0 };
    (staffAttendanceViewRows || []).forEach((row) => {
      const status = String(row?.status || '');
      if (Object.prototype.hasOwnProperty.call(totals, status)) {
        totals[status] += 1;
      }
      totals.total += 1;
    });
    return totals;
  }, [staffAttendanceViewRows]);

  const getAttendanceStatusBadgeClass = (status) => {
    if (status === 'absent') return 'out';
    if (status === 'half_day') return 'hold';
    if (status === 'leave') return 'warn';
    return 'in';
  };

  const hasEntityNotes = (entity) => Boolean(String(entity?.notes || '').trim());
  const isRecentlyUpdated = (updatedAt) => {
    const updatedTime = new Date(updatedAt || '').getTime();
    if (!Number.isFinite(updatedTime)) return false;
    return (Date.now() - updatedTime) <= (7 * 24 * 60 * 60 * 1000);
  };
  const getEntityUpdatedTime = (entity) => {
    const timestamp = new Date(entity?.updated_at || entity?.created_at || '').getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  const getStatusSortWeight = (isActive) => (isActive ? 0 : 1);

  const activeStaffCount = staff.filter((member) => member.is_active && !member.anonymized_at).length;
  const inactiveStaffCount = staff.filter((member) => !member.is_active || member.anonymized_at).length;
  const staffWithNotesCount = staff.filter((member) => hasEntityNotes(member)).length;
  const staffUpdatedThisWeekCount = staff.filter((member) => isRecentlyUpdated(member.updated_at)).length;

  const activeCustomersCount = customers.filter((customer) => customer.is_active !== false).length;
  const inactiveCustomersCount = customers.filter((customer) => customer.is_active === false).length;
  const customersWithNotesCount = customers.filter((customer) => hasEntityNotes(customer)).length;
  const customersUpdatedThisWeekCount = customers.filter((customer) => isRecentlyUpdated(customer.updated_at)).length;

  const activeContractorsCount = contractors.filter((contractor) => contractor.is_active !== false).length;
  const inactiveContractorsCount = contractors.filter((contractor) => contractor.is_active === false).length;
  const contractorsWithNotesCount = contractors.filter((contractor) => hasEntityNotes(contractor)).length;
  const contractorsUpdatedThisWeekCount = contractors.filter((contractor) => isRecentlyUpdated(contractor.updated_at)).length;

  const filteredStaff = staff.filter((member) => {
    const query = staffSearch.toLowerCase().trim();
    const isActive = Boolean(member.is_active && !member.anonymized_at);
    const hasNotes = hasEntityNotes(member);
    if (staffStatusFilter === 'active' && !isActive) return false;
    if (staffStatusFilter === 'inactive' && isActive) return false;
    if (staffNotesFilter === 'with' && !hasNotes) return false;
    if (staffNotesFilter === 'without' && hasNotes) return false;
    if (!query) return true;
    const customValues = Object.values(member.custom_fields || {}).join(' ');
    return (
      String(member.name || '').toLowerCase().includes(query) ||
      String(member.skill_category || '').toLowerCase().includes(query) ||
      String(member.id_type || '').toLowerCase().includes(query) ||
      customValues.toLowerCase().includes(query)
    );
  }).sort((a, b) => {
    if (staffSortMode === 'az') return String(a.name || '').localeCompare(String(b.name || ''));
    if (staffSortMode === 'za') return String(b.name || '').localeCompare(String(a.name || ''));
    if (staffSortMode === 'status') {
      return getStatusSortWeight(Boolean(a.is_active && !a.anonymized_at))
        - getStatusSortWeight(Boolean(b.is_active && !b.anonymized_at));
    }
    return getEntityUpdatedTime(b) - getEntityUpdatedTime(a);
  });

  const filteredCustomers = customers.filter((customer) => {
    const query = customerSearch.toLowerCase().trim();
    const isActive = customer.is_active !== false;
    const hasNotes = hasEntityNotes(customer);
    if (customerStatusFilter === 'active' && !isActive) return false;
    if (customerStatusFilter === 'inactive' && isActive) return false;
    if (customerNotesFilter === 'with' && !hasNotes) return false;
    if (customerNotesFilter === 'without' && hasNotes) return false;
    if (!query) return true;
    const customValues = Object.values(customer.custom_fields || {}).join(' ');
    return customValues.toLowerCase().includes(query);
  }).sort((a, b) => {
    if (customerSortMode === 'az') return String(a.custom_fields?.[customerSchema.fields?.[0]?.key] || '').localeCompare(String(b.custom_fields?.[customerSchema.fields?.[0]?.key] || ''));
    if (customerSortMode === 'za') return String(b.custom_fields?.[customerSchema.fields?.[0]?.key] || '').localeCompare(String(a.custom_fields?.[customerSchema.fields?.[0]?.key] || ''));
    if (customerSortMode === 'status') return getStatusSortWeight(a.is_active !== false) - getStatusSortWeight(b.is_active !== false);
    return getEntityUpdatedTime(b) - getEntityUpdatedTime(a);
  });

  const filteredContractors = contractors.filter((contractor) => {
    const query = contractorSearch.toLowerCase().trim();
    const isActive = contractor.is_active !== false;
    const hasNotes = hasEntityNotes(contractor);
    if (contractorStatusFilter === 'active' && !isActive) return false;
    if (contractorStatusFilter === 'inactive' && isActive) return false;
    if (contractorNotesFilter === 'with' && !hasNotes) return false;
    if (contractorNotesFilter === 'without' && hasNotes) return false;
    if (!query) return true;
    const customValues = Object.values(contractor.custom_fields || {}).join(' ');
    return customValues.toLowerCase().includes(query);
  }).sort((a, b) => {
    if (contractorSortMode === 'az') return String(a.custom_fields?.[contractorSchema.fields?.[0]?.key] || '').localeCompare(String(b.custom_fields?.[contractorSchema.fields?.[0]?.key] || ''));
    if (contractorSortMode === 'za') return String(b.custom_fields?.[contractorSchema.fields?.[0]?.key] || '').localeCompare(String(a.custom_fields?.[contractorSchema.fields?.[0]?.key] || ''));
    if (contractorSortMode === 'status') return getStatusSortWeight(a.is_active !== false) - getStatusSortWeight(b.is_active !== false);
    return getEntityUpdatedTime(b) - getEntityUpdatedTime(a);
  });

  const selectedRawMaterialOption = rawMaterialOptions.find((item) => item.name === rawMaterialStockType);
  const rawMaterialUnitLabel = selectedRawMaterialOption?.unit_label || quantityUnit;
  const isFinishedGoodsStock = stockOverviewMode === 'finished_goods';
  const isRawMaterialStock = stockOverviewMode === 'raw_materials' && Boolean(rawMaterialStockType);
  const selectedTransactionRawMaterial = rawMaterialOptions.find((item) => item.name === rawMaterialType) || null;
  const selectedTransactionRawMaterialCharacteristics = Array.isArray(selectedTransactionRawMaterial?.characteristics)
    ? selectedTransactionRawMaterial.characteristics
    : [];
  const selectedEditingRawMaterial = findRawMaterialByName(editingRawMaterialType) || null;
  const selectedEditingRawMaterialCharacteristics = Array.isArray(selectedEditingRawMaterial?.characteristics)
    ? selectedEditingRawMaterial.characteristics
    : [];
  const selectedTransactionFilterMaterial = transactionLedgerMode === 'raw_material'
    ? (rawMaterialOptions.find((item) => item.name === transactionRawMaterialFilter) || null)
    : null;
  const transactionLedgerCharacteristics = Array.isArray(selectedTransactionFilterMaterial?.characteristics)
    ? selectedTransactionFilterMaterial.characteristics
    : [];
  const finishedGoodsLedgerOptions = useMemo(
    () => Array.from(new Set(
      transactions
        .map((transaction) => getTransactionRawMaterialContext(transaction).finishedProductName)
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b)),
    [transactions]
  );
  const finishedGoodsOptions = useMemo(
    () => productCatalogWithPayload
      .map((product) => ({ id: product.id, name: String(product.name || '').trim() }))
      .filter((item) => item.name)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [productCatalogWithPayload]
  );
  const { stockRows, stockCounts, totalStockQuantity } = useInventory({
    isFinishedGoodsStock,
    isRawMaterialStock,
    filteredFinishedGoodsStock,
    filteredRawMaterialStock,
    filteredStock,
    settings
  });
  const insightsRangeDays = useMemo(() => {
    if (insightsRange === '7d') return 7;
    if (insightsRange === '90d') return 90;
    return 30;
  }, [insightsRange]);
  const insightsDeadStockDaysValue = useMemo(() => {
    const parsed = Number(insightsDeadStockDays);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
  }, [insightsDeadStockDays]);
  const insightsRawMaterialOptions = useMemo(() => {
    const options = new Set();
    transactions.forEach((transaction) => {
      const rawMaterialName = String(getTransactionRawMaterialContext(transaction).rawMaterialName || '').trim();
      if (rawMaterialName) options.add(rawMaterialName);
    });
    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [transactions]);
  const insightsCharacteristicOptions = useMemo(() => {
    if (!insightsRawMaterialFilter) return [];
    const optionMap = new Map();
    transactions.forEach((transaction) => {
      const context = getTransactionRawMaterialContext(transaction);
      if (context.rawMaterialName !== insightsRawMaterialFilter) return;
      const key = getCharacteristicSelectionKey(context.characteristics);
      if (optionMap.has(key)) return;
      optionMap.set(key, {
        value: key,
        label: formatCharacteristicSelectionLabel(context.characteristics)
      });
    });
    return Array.from(optionMap.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [insightsRawMaterialFilter, transactions]);
  const insightsSelectionSummary = useMemo(() => {
    if (!insightsRawMaterialFilter) return 'All raw materials';
    if (!insightsCharacteristicFilter) return insightsRawMaterialFilter;
    const selectedOption = insightsCharacteristicOptions.find((option) => option.value === insightsCharacteristicFilter);
    if (!selectedOption) return insightsRawMaterialFilter;
    return `${insightsRawMaterialFilter} / ${selectedOption.label}`;
  }, [insightsCharacteristicFilter, insightsCharacteristicOptions, insightsRawMaterialFilter]);
  const matchesInsightsRawMaterialSelection = useCallback((context) => {
    const rawMaterialName = String(context?.rawMaterialName || '').trim();
    if (!rawMaterialName) return false;
    if (insightsRawMaterialFilter && rawMaterialName !== insightsRawMaterialFilter) return false;
    if (!insightsCharacteristicFilter) return true;
    return getCharacteristicSelectionKey(context?.characteristics || {}) === insightsCharacteristicFilter;
  }, [insightsCharacteristicFilter, insightsRawMaterialFilter]);
  useEffect(() => {
    if (!insightsRawMaterialFilter) {
      if (insightsCharacteristicFilter) setInsightsCharacteristicFilter('');
      return;
    }
    if (insightsCharacteristicOptions.some((option) => option.value === insightsCharacteristicFilter)) return;
    if (insightsCharacteristicFilter) setInsightsCharacteristicFilter('');
  }, [insightsCharacteristicFilter, insightsCharacteristicOptions, insightsRawMaterialFilter]);
  const insightsDateKeys = useMemo(() => {
    const keys = [];
    const today = new Date();
    for (let i = insightsRangeDays - 1; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      keys.push(toLocalDateKey(date));
    }
    return keys;
  }, [insightsRangeDays]);
  const insightsDateKeySet = useMemo(() => new Set(insightsDateKeys), [insightsDateKeys]);
  const insightsRawTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const context = getTransactionRawMaterialContext(transaction);
      return matchesInsightsRawMaterialSelection(context);
    });
  }, [matchesInsightsRawMaterialSelection, transactions]);
  const insightsMovementSeries = useMemo(() => {
    const map = new Map(insightsDateKeys.map((key) => [key, { date: key, inbound: 0, outbound: 0 }]));
    insightsRawTransactions.forEach((transaction) => {
      const dateKey = String(transaction.date || '').slice(0, 10);
      if (!map.has(dateKey)) return;
      const qty = Number(transaction.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const entry = map.get(dateKey);
      const type = String(transaction.type || '').toLowerCase();
      if (type === 'in') entry.inbound += qty;
      if (type === 'out') entry.outbound += qty;
    });
    return insightsDateKeys.map((dateKey) => map.get(dateKey) || { date: dateKey, inbound: 0, outbound: 0 });
  }, [insightsDateKeys, insightsRawTransactions]);
  const insightsTotalSeries = useMemo(
    () => insightsMovementSeries.map((item) => ({ date: item.date, total: item.inbound - item.outbound })),
    [insightsMovementSeries]
  );
  const insightsLineChart = useMemo(() => {
    if (insightsTotalSeries.length === 0) return null;
    const width = 560;
    const height = 200;
    const padding = { top: 16, right: 16, bottom: 28, left: 46 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const values = insightsTotalSeries.map((point) => Number(point.total || 0));
    const maxAbs = Math.max(1, ...values.map((value) => Math.abs(value)));
    const yForValue = (value) => padding.top + ((maxAbs - value) / (maxAbs * 2)) * plotHeight;
    const xForIndex = (index) => padding.left + (index / Math.max(1, values.length - 1)) * plotWidth;
    const points = values.map((value, index) => `${xForIndex(index).toFixed(2)},${yForValue(value).toFixed(2)}`).join(' ');
    const axisTicks = [maxAbs, 0, -maxAbs].map((value) => ({
      value,
      y: yForValue(value)
    }));
    const xAxisLabels = Array.from(new Set([
      0,
      Math.floor((insightsTotalSeries.length - 1) / 2),
      insightsTotalSeries.length - 1
    ]))
      .filter((index) => index >= 0)
      .map((index) => ({
        key: insightsTotalSeries[index]?.date || String(index),
        x: xForIndex(index),
        label: String(insightsTotalSeries[index]?.date || '').slice(5)
      }));
    return {
      width,
      height,
      points,
      axisTicks,
      xAxisLabels,
      zeroY: yForValue(0),
      maxAbs
    };
  }, [insightsTotalSeries]);
  const insightsTopConsumedRaw = useMemo(() => {
    const consumed = new Map();
    insightsRawTransactions.forEach((transaction) => {
      const dateKey = String(transaction.date || '').slice(0, 10);
      if (!insightsDateKeySet.has(dateKey)) return;
      const type = String(transaction.type || '').toLowerCase();
      if (type !== 'out') return;
      const ctx = getTransactionRawMaterialContext(transaction);
      const rawMaterialName = String(ctx.rawMaterialName || '').trim();
      if (!rawMaterialName) return;
      const qty = Number(transaction.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const isMaterialScoped = Boolean(insightsRawMaterialFilter);
      const key = isMaterialScoped
        ? `${rawMaterialName.toLowerCase()}::${getCharacteristicSelectionKey(ctx.characteristics)}`
        : rawMaterialName.toLowerCase();
      const current = consumed.get(key) || {
        label: isMaterialScoped ? formatCharacteristicSelectionLabel(ctx.characteristics) : rawMaterialName,
        quantity: 0
      };
      current.quantity += qty;
      consumed.set(key, current);
    });
    return Array.from(consumed.values())
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);
  }, [insightsDateKeySet, insightsRawMaterialFilter, insightsRawTransactions]);
  const insightsStageCounts = useMemo(() => {
    const map = new Map();
    (orders || []).forEach((order) => {
      const stage = String(order.current_stage || 'Created').trim() || 'Created';
      map.set(stage, Number(map.get(stage) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([stage, count]) => ({ stage, count }))
      .sort((a, b) => b.count - a.count);
  }, [orders]);
  const insightsLowCriticalSplit = useMemo(() => ([
    { label: 'Healthy', value: Math.max(0, stockCounts.total - stockCounts.low - stockCounts.critical) },
    { label: 'Low', value: stockCounts.low },
    { label: 'Critical', value: stockCounts.critical }
  ]), [stockCounts]);
  const insightsKpis = useMemo(() => {
    const totalIn = insightsMovementSeries.reduce((sum, item) => sum + item.inbound, 0);
    const totalOut = insightsMovementSeries.reduce((sum, item) => sum + item.outbound, 0);
    return {
      totalIn,
      totalOut,
      netFlow: totalIn - totalOut,
      ordersOpen: (orders || []).filter((order) => String(order.status || '').toLowerCase() === 'pending').length
    };
  }, [insightsMovementSeries, orders]);
  const rawMaterialRollup = useMemo(() => {
    const map = new Map();
    transactions.forEach((transaction) => {
      const ctx = getTransactionRawMaterialContext(transaction);
      if (!matchesInsightsRawMaterialSelection(ctx)) return;
      const rawMaterialName = String(ctx.rawMaterialName || '').trim();
      if (!rawMaterialName) return;
      const characteristicsKey = getCharacteristicSelectionKey(ctx.characteristics);
      const key = `${rawMaterialName.toLowerCase()}::${characteristicsKey}`;
      const current = map.get(key) || {
        name: rawMaterialName,
        characteristics: ctx.characteristics || {},
        characteristicsLabel: formatCharacteristicSelectionLabel(ctx.characteristics),
        characteristicsKey,
        currentStock: 0,
        lastMovementDate: null,
        outboundInRange: 0,
        movementCountInRange: 0,
        lastUnitCost: 0,
        inboundCostTotal: 0,
        inboundQuantityForCost: 0
      };
      const qty = Number(transaction.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const type = String(transaction.type || '').toLowerCase();
      if (type === 'in') {
        current.currentStock += qty;
        if (ctx.unitCost > 0) {
          current.inboundQuantityForCost += qty;
          current.inboundCostTotal += qty * ctx.unitCost;
        }
      }
      if (type === 'out' || type === 'hold') current.currentStock -= qty;
      if (ctx.unitCost > 0) current.lastUnitCost = ctx.unitCost;
      const dateKey = String(transaction.date || '').slice(0, 10);
      if (dateKey) {
        if (!current.lastMovementDate || dateKey > current.lastMovementDate) current.lastMovementDate = dateKey;
      }
      if (insightsDateKeySet.has(dateKey)) {
        current.movementCountInRange += 1;
        if (type === 'out') current.outboundInRange += qty;
      }
      map.set(key, current);
    });
    return Array.from(map.values())
      .map((entry) => {
        const averageUnitCost = entry.inboundQuantityForCost > 0
          ? entry.inboundCostTotal / entry.inboundQuantityForCost
          : (entry.lastUnitCost > 0 ? entry.lastUnitCost : null);
        return {
          ...entry,
          averageUnitCost,
          estimatedStockValue: averageUnitCost !== null
            ? Number((Number(entry.currentStock || 0) * averageUnitCost).toFixed(2))
            : null
        };
      })
      .filter((entry) => Number(entry.currentStock || 0) > 0);
  }, [insightsDateKeySet, matchesInsightsRawMaterialSelection, transactions]);
  const deadStockItems = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - insightsDeadStockDaysValue);
    const cutoffKey = toLocalDateKey(cutoffDate);
    return rawMaterialRollup
      .filter((entry) => {
        if (!entry.lastMovementDate) return true;
        return entry.lastMovementDate < cutoffKey;
      })
      .sort((a, b) => {
        const valueDelta = Number(b.estimatedStockValue || 0) - Number(a.estimatedStockValue || 0);
        if (valueDelta !== 0) return valueDelta;
        return Number(b.currentStock || 0) - Number(a.currentStock || 0);
      })
      .slice(0, 10);
  }, [insightsDeadStockDaysValue, rawMaterialRollup]);
  const coverageForecastRows = useMemo(() => {
    return rawMaterialRollup
      .map((entry) => {
        const avgDailyOut = Number(entry.outboundInRange || 0) / Math.max(1, insightsRangeDays);
        const coverageDays = avgDailyOut > 0 ? Number(entry.currentStock || 0) / avgDailyOut : null;
        const stockoutDate = coverageDays !== null
          ? (() => {
            const date = new Date();
            date.setDate(date.getDate() + Math.max(0, Math.ceil(coverageDays)));
            return toLocalDateKey(date);
          })()
          : null;
        return {
          ...entry,
          avgDailyOut,
          coverageDays,
          stockoutDate
        };
      })
      .sort((a, b) => {
        const aValue = a.coverageDays === null ? Number.POSITIVE_INFINITY : a.coverageDays;
        const bValue = b.coverageDays === null ? Number.POSITIVE_INFINITY : b.coverageDays;
        return aValue - bValue;
      })
      .slice(0, 12);
  }, [rawMaterialRollup, insightsRangeDays]);
  const reorderRecommendations = useMemo(() => {
    const lowThreshold = Number(settings?.stock_threshold_low || 0);
    const planningDays = 14;
    return coverageForecastRows
      .map((entry) => {
        const targetByUsage = Number(entry.avgDailyOut || 0) * planningDays;
        const targetQty = Math.max(lowThreshold, targetByUsage);
        const recommendedQty = Math.max(0, targetQty - Number(entry.currentStock || 0));
        return {
          ...entry,
          targetQty,
          recommendedQty
        };
      })
      .filter((entry) => entry.recommendedQty > 0.01)
      .sort((a, b) => b.recommendedQty - a.recommendedQty)
      .slice(0, 10);
  }, [coverageForecastRows, settings?.stock_threshold_low]);
  const deadStockTotals = useMemo(() => {
    return deadStockItems.reduce((summary, entry) => ({
      count: summary.count + 1,
      estimatedValue: summary.estimatedValue + Number(entry.estimatedStockValue || 0)
    }), { count: 0, estimatedValue: 0 });
  }, [deadStockItems]);
  const transactionActionFilterOptions = useMemo(() => (
    transactionLedgerMode === 'finished_goods'
      ? [
        { key: 'in', label: 'Stock In' },
        { key: 'out', label: 'Stock Out' },
        { key: 'hold', label: 'Hold' }
      ]
      : RAW_TRANSACTION_FILTER_ACTIONS
  ), [transactionLedgerMode]);

  const filteredTransactions = transactions.filter((transaction) => {
    const rawContext = getTransactionRawMaterialContext(transaction);
    if (transactionTypeFilter !== 'all' && String(transaction.type || '').toLowerCase() !== transactionTypeFilter) {
      return false;
    }
    if (transactionLedgerMode === 'finished_goods') {
      if (!rawContext.finishedProductName) {
        return false;
      }
      if (transactionRawActionFilter !== 'all' && String(transaction.type || '').toLowerCase() !== transactionRawActionFilter) {
        return false;
      }
      if (transactionFinishedProductFilter && rawContext.finishedProductName !== transactionFinishedProductFilter) {
        return false;
      }
    } else {
      if (!rawContext.rawMaterialName) {
        return false;
      }
      if (transactionRawActionFilter !== 'all' && rawContext.action !== transactionRawActionFilter) {
        return false;
      }
      if (!transactionRawMaterialFilter) {
        return false;
      }
      if (rawContext.rawMaterialName !== transactionRawMaterialFilter) {
        return false;
      }
    }
    if (transactionDateFromFilter || transactionDateToFilter) {
      const dateValue = String(transaction.date || '').slice(0, 10);
      if (transactionDateFromFilter && dateValue < transactionDateFromFilter) return false;
      if (transactionDateToFilter && dateValue > transactionDateToFilter) return false;
    }
    for (const characteristic of transactionLedgerCharacteristics) {
      const name = String(characteristic?.name || '').trim();
      if (!name) continue;
      const selectedValue = String(transactionRawCharacteristicFilters?.[name] || '').trim();
      if (!selectedValue) continue;
      if (String(rawContext.characteristics?.[name] || '').trim() !== selectedValue) return false;
    }
    return true;
  });
  const filteredTransactionSummary = useMemo(() => filteredTransactions.reduce((summary, transaction) => {
    const quantity = Number(transaction.quantity || 0);
    const safeQuantity = Number.isFinite(quantity) ? quantity : 0;
    const type = String(transaction.type || '').toLowerCase();
    if (type === 'in') summary.inbound += safeQuantity;
    if (type === 'out') summary.outbound += safeQuantity;
    if (type === 'hold') summary.hold += safeQuantity;
    if (transaction.notes) summary.withNotes += 1;
    if (transaction.challan?.id || transaction.challan_no) summary.withChallan += 1;
    return summary;
  }, {
    inbound: 0,
    outbound: 0,
    hold: 0,
    withNotes: 0,
    withChallan: 0
  }), [filteredTransactions]);
  const ledgerScopeTransactions = useMemo(() => transactions.filter((transaction) => {
    const rawContext = getTransactionRawMaterialContext(transaction);
    if (transactionLedgerMode === 'finished_goods') {
      if (!rawContext.finishedProductName) return false;
      if (transactionFinishedProductFilter && rawContext.finishedProductName !== transactionFinishedProductFilter) return false;
      return true;
    }
    if (!rawContext.rawMaterialName) return false;
    if (transactionRawMaterialFilter && rawContext.rawMaterialName !== transactionRawMaterialFilter) return false;
    return true;
  }), [transactionFinishedProductFilter, transactionLedgerMode, transactionRawMaterialFilter, transactions]);
  const activeLedgerFilterChips = useMemo(() => {
    const chips = [];
    if (transactionLedgerMode === 'finished_goods') {
      if (transactionFinishedProductFilter) chips.push(`Product: ${transactionFinishedProductFilter}`);
    } else if (transactionRawMaterialFilter) {
      chips.push(`Material: ${transactionRawMaterialFilter}`);
    }
    if (transactionTypeFilter !== 'all') chips.push(`Direction: ${String(transactionTypeFilter).toUpperCase()}`);
    if (transactionRawActionFilter !== 'all') {
      const matchedAction = transactionActionFilterOptions.find((item) => item.key === transactionRawActionFilter);
      chips.push(`Action: ${matchedAction?.label || transactionRawActionFilter}`);
    }
    if (transactionDateFromFilter || transactionDateToFilter) {
      chips.push(`Date: ${transactionDateFromFilter || 'Start'} to ${transactionDateToFilter || 'Today'}`);
    }
    Object.entries(transactionRawCharacteristicFilters || {}).forEach(([key, value]) => {
      if (!String(value || '').trim()) return;
      chips.push(`${key}: ${value}`);
    });
    return chips;
  }, [
    transactionDateFromFilter,
    transactionDateToFilter,
    transactionFinishedProductFilter,
    transactionLedgerMode,
    transactionRawActionFilter,
    transactionRawCharacteristicFilters,
    transactionRawMaterialFilter,
    transactionTypeFilter,
    transactionActionFilterOptions
  ]);
  const hasActiveLedgerFilters = activeLedgerFilterChips.length > 0;

  const totalTransactionPages = Math.max(1, Math.ceil(filteredTransactions.length / transactionsPageSize));
  const safeTransactionsPage = Math.min(transactionsPage, totalTransactionPages);
  const pagedTransactions = filteredTransactions.slice(
    (safeTransactionsPage - 1) * transactionsPageSize,
    safeTransactionsPage * transactionsPageSize
  );
  const filteredOrders = orders.filter((order) => {
    const query = orderSearch.toLowerCase().trim();
    if (!query) return true;
    return (
      String(order.order_number || '').toLowerCase().includes(query) ||
      String(order.buyer_name || '').toLowerCase().includes(query) ||
      String(order.pipeline_stage || '').toLowerCase().includes(query) ||
      String(order.status || '').toLowerCase().includes(query)
    );
  }).filter((order) => {
    if (orderStatusFilter === 'all') return true;
    const label = String(getOrderStatusLabel(order)).toLowerCase();
    if (orderStatusFilter === 'in_progress') {
      return label !== 'completed' && label !== 'cancelled' && label !== 'canceled';
    }
    if (orderStatusFilter === 'completed') return label === 'completed';
    if (orderStatusFilter === 'cancelled') return label === 'cancelled' || label === 'canceled';
    return true;
  }).filter((order) => {
    if (orderStageFilter === 'all') return true;
    return String(order.pipeline_stage || '') === orderStageFilter;
  }).filter((order) => {
    const buyerQuery = orderBuyerFilter.toLowerCase().trim();
    if (!buyerQuery) return true;
    return String(order.buyer_name || '').toLowerCase().includes(buyerQuery);
  }).filter((order) => {
    if (!orderDateFrom && !orderDateTo) return true;
    const created = parseUtcTimestamp(order.created_at);
    if (!created || Number.isNaN(created.getTime())) return false;
    const from = orderDateFrom ? new Date(`${orderDateFrom}T00:00:00Z`) : null;
    const to = orderDateTo ? new Date(`${orderDateTo}T23:59:59Z`) : null;
    if (from && created < from) return false;
    if (to && created > to) return false;
    return true;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aCreated = parseUtcTimestamp(a.created_at)?.getTime() || 0;
    const bCreated = parseUtcTimestamp(b.created_at)?.getTime() || 0;
    if (orderSort === 'created_asc') return aCreated - bCreated;
    if (orderSort === 'created_desc') return bCreated - aCreated;
    if (orderSort === 'order_number_asc') return (a.order_number || 0) - (b.order_number || 0);
    if (orderSort === 'order_number_desc') return (b.order_number || 0) - (a.order_number || 0);
    if (orderSort === 'buyer_asc') return String(a.buyer_name || '').localeCompare(String(b.buyer_name || ''));
    if (orderSort === 'buyer_desc') return String(b.buyer_name || '').localeCompare(String(a.buyer_name || ''));
    if (orderSort === 'total_qty_desc') return (b.total_qty || 0) - (a.total_qty || 0);
    if (orderSort === 'total_qty_asc') return (a.total_qty || 0) - (b.total_qty || 0);
    return 0;
  });

  const orderStatusCounts = sortedOrders.reduce(
    (acc, order) => {
      const label = String(getOrderStatusLabel(order)).toLowerCase();
      if (label === 'completed') acc.completed += 1;
      else if (label === 'cancelled' || label === 'canceled') acc.cancelled += 1;
      else acc.in_progress += 1;
      return acc;
    },
    { in_progress: 0, completed: 0, cancelled: 0 }
  );
  const orderFulfilmentSummary = (() => {
    const todayKey = toLocalDateKey(new Date());
    const dueSoonDate = new Date();
    dueSoonDate.setDate(dueSoonDate.getDate() + 3);
    const dueSoonKey = toLocalDateKey(dueSoonDate);

    return sortedOrders.reduce((summary, order) => {
      const payload = parseOrderPayload(order.payload) || {};
      const deliveryDate = String(payload.delivery_date || order.delivery_date || '').slice(0, 10);
      const statusLabel = String(getOrderStatusLabel(order)).toLowerCase();
      const isFinal = ['completed', 'cancelled', 'canceled'].includes(statusLabel);
      summary.holdRemaining += Number(order.hold_remaining_total || 0);
      summary.finishedRemaining += Number(order.finished_hold_remaining_total || 0);
      if (deliveryDate) {
        summary.withDelivery += 1;
        if (!isFinal && deliveryDate < todayKey) summary.overdue += 1;
        if (!isFinal && deliveryDate >= todayKey && deliveryDate <= dueSoonKey) summary.dueSoon += 1;
      }
      return summary;
    }, {
      overdue: 0,
      dueSoon: 0,
      withDelivery: 0,
      holdRemaining: 0,
      finishedRemaining: 0
    });
  })();
  const holdOverviewRows = useMemo(
    () => orders.flatMap((order) => {
      const holdBreakdown = Array.isArray(order?.hold_breakdown) ? order.hold_breakdown : [];
      return holdBreakdown
        .map((item) => {
          const remaining = Number(item?.remaining || 0);
          if (!(remaining > 0)) return null;
          const materialName = String(item?.raw_material_name || '').trim();
          return {
            order_id: order.id,
            order_number: order.order_number,
            buyer_name: order.buyer_name || '-',
            status: getOrderStatusLabel(order),
            raw_material_name: materialName,
            unit: rawMaterialUnitByName[materialName] || '',
            reserved: Number(item?.reserved || 0),
            consumed: Number(item?.consumed || 0),
            remaining
          };
        })
        .filter(Boolean);
    }),
    [orders, rawMaterialUnitByName]
  );

  // Bulk selection removed for now.

  const handleExportOrdersCsv = () => {
    const rows = [];
    rows.push(['Order #', 'Buyer', 'Status', 'Stage', 'Created', 'Updated', 'Total Qty', 'Total Required'].join(','));
    sortedOrders.forEach((order) => {
      rows.push([
        order.order_number ?? '',
        order.buyer_name ?? '',
        getOrderStatusLabel(order),
        order.pipeline_stage ?? '',
        order.created_at ?? '',
        order.updated_at ?? '',
        order.total_qty ?? '',
        order.total_required ?? ''
      ].join(','));
    });
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = getTodayDateKey();
    link.setAttribute('href', url);
    link.setAttribute('download', `Orders_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeOrderCount = useMemo(
    () => orders.filter((order) => !['Completed', 'Cancelled', 'Canceled'].includes(String(order.status || ''))).length,
    [orders]
  );
  const overdueOrderCount = useMemo(
    () => orders.filter((order) => !['Completed', 'Cancelled', 'Canceled'].includes(String(order.status || '')) && getStageAgeDays(order) >= 7).length,
    [orders]
  );
  const lowStockCount = stockRows.filter((item) => {
    const current = Number(item.current_stock || 0);
    const critical = Number(settings?.stock_threshold_critical || 0);
    const low = Number(settings?.stock_threshold_low || 0);
    return current <= Math.max(low, critical);
  }).length;

  const hasRawMaterialValueOptions = useMemo(
    () => rawMaterials.some((material) =>
      (material.characteristics || []).some((characteristic) => {
        const listKey = String(characteristic?.list_key || '').trim();
        if (!listKey) return false;
        const list = customListMap[listKey];
        return Array.isArray(list?.items) && list.items.length > 0;
      })
    ),
    [rawMaterials, customListMap]
  );

  const hasProductWithBom = useMemo(
    () => productCatalogWithPayload.some((product) => {
      const entries = Array.isArray(product?.parsedPayload?.fields?.raw_materials)
        ? product.parsedPayload.fields.raw_materials
        : [];
      return entries.some((entry) => {
        const rawMaterialName = String(entry?.raw_material_name || entry?.type || '').trim();
        const requiredQty = Number(entry?.required_qty);
        return rawMaterialName && Number.isFinite(requiredQty) && requiredQty > 0;
      });
    }),
    [productCatalogWithPayload]
  );

  const rawMaterialUsageByName = useMemo(() => {
    const usageMap = {};
    rawMaterials.forEach((material) => {
      usageMap[material.name] = {
        productCount: 0,
        openOrderCount: 0,
        transactionCount: 0,
        lastTransactionAt: null,
        currentStock: 0
      };
    });

    productCatalogWithPayload.forEach((product) => {
      const entries = Array.isArray(product?.parsedPayload?.fields?.raw_materials)
        ? product.parsedPayload.fields.raw_materials
        : [];
      entries.forEach((entry) => {
        const rawMaterialName = String(entry?.raw_material_name || entry?.type || '').trim();
        if (!rawMaterialName) return;
        if (!usageMap[rawMaterialName]) {
          usageMap[rawMaterialName] = {
            productCount: 0,
            openOrderCount: 0,
            transactionCount: 0,
            lastTransactionAt: null,
            currentStock: 0
          };
        }
        usageMap[rawMaterialName].productCount += 1;
      });
    });

    orders.forEach((order) => {
      const status = String(order.status || '').toLowerCase();
      if (['completed', 'cancelled', 'canceled'].includes(status)) return;
      const payload = parseOrderPayload(order?.payload) || {};
      const productIds = Array.isArray(payload?.items) ? payload.items.map((item) => String(item?.product_id || '')) : [];
      const rawMaterialNames = new Set();
      productIds.forEach((productId) => {
        const product = productCatalogWithPayload.find((entry) => String(entry.id) === productId);
        const entries = Array.isArray(product?.parsedPayload?.fields?.raw_materials)
          ? product.parsedPayload.fields.raw_materials
          : [];
        entries.forEach((entry) => {
          const rawMaterialName = String(entry?.raw_material_name || entry?.type || '').trim();
          if (rawMaterialName) rawMaterialNames.add(rawMaterialName);
        });
      });
      rawMaterialNames.forEach((rawMaterialName) => {
        if (!usageMap[rawMaterialName]) {
          usageMap[rawMaterialName] = {
            productCount: 0,
            openOrderCount: 0,
            transactionCount: 0,
            lastTransactionAt: null,
            currentStock: 0
          };
        }
        usageMap[rawMaterialName].openOrderCount += 1;
      });
    });

    transactions.forEach((transaction) => {
      const context = getTransactionRawMaterialContext(transaction);
      const rawMaterialName = String(context.rawMaterialName || '').trim();
      if (!rawMaterialName) return;
      if (!usageMap[rawMaterialName]) {
        usageMap[rawMaterialName] = {
          productCount: 0,
          openOrderCount: 0,
          transactionCount: 0,
          lastTransactionAt: null,
          currentStock: 0
        };
      }
      const quantity = Number(transaction.quantity || 0);
      usageMap[rawMaterialName].transactionCount += 1;
      usageMap[rawMaterialName].currentStock += transaction.type === 'in' ? quantity : -quantity;
      const currentLast = usageMap[rawMaterialName].lastTransactionAt
        ? new Date(usageMap[rawMaterialName].lastTransactionAt).getTime()
        : 0;
      const transactionTime = transaction.date ? new Date(transaction.date).getTime() : 0;
      if (transactionTime > currentLast) {
        usageMap[rawMaterialName].lastTransactionAt = transaction.date;
      }
    });

    return usageMap;
  }, [orders, parseOrderPayload, productCatalogWithPayload, rawMaterials, transactions]);

  const hasWorkflowProgress = useMemo(() => {
    if (!orders.length) return false;
    const firstStage = pipelineStages[0] || '';
    return orders.some((order) => {
      const status = String(order.status || '').toLowerCase();
      if (status === 'completed' || status === 'cancelled' || status === 'canceled') return true;
      const stage = String(order.pipeline_stage || '');
      return firstStage ? stage && stage !== firstStage : Boolean(stage);
    });
  }, [orders, pipelineStages]);

  const onboardingContext = useMemo(() => ({
    activeOrderCount,
    canAccessUsers: canAccessUsersTab,
    criticalStockCount: Number(stockCounts?.critical || 0),
    hasOrders: orders.length > 0,
    hasProductWithBom,
    hasRawMaterialValueOptions,
    hasRawMaterials: rawMaterials.length > 0,
    hasTransactions: transactions.length > 0,
    hasWorkflowProgress,
    lowStockCount,
    orderCount: orders.length,
    overdueOrderCount,
    pipelineStageCount: pipelineStages.length,
    rawMaterialCount: rawMaterials.length,
    stockGroupCount: Number(stockCounts?.total || 0),
    transactionCount: transactions.length,
    userCount: users.length
  }), [
    activeOrderCount,
    canAccessUsersTab,
    hasProductWithBom,
    hasRawMaterialValueOptions,
    hasWorkflowProgress,
    lowStockCount,
    orders.length,
    overdueOrderCount,
    pipelineStages.length,
    rawMaterials.length,
    stockCounts?.critical,
    stockCounts?.total,
    transactions.length,
    users.length
  ]);
  const onboardingChecklist = useMemo(() => buildDashboardChecklist({
    actions: onboardingActions,
    context: onboardingContext,
    ordersEnabled,
    role: user?.role,
    visibleTabIds
  }), [onboardingActions, onboardingContext, ordersEnabled, user?.role, visibleTabIds]);
  const onboardingProgress = onboardingChecklist.totalCount > 0
    ? `${onboardingChecklist.completedCount}/${onboardingChecklist.totalCount}`
    : '';
  const shouldShowOnboardingCard = !loading
    && onboardingChecklist.pendingSteps.length > 0
    && !setupCardHidden;
  const tourSteps = useMemo(
    () => buildDashboardTourSteps({
      context: onboardingContext,
      orderedTabs,
      ordersEnabled,
      role: user?.role,
      setActiveTab,
      setSettingsTab,
      visibleTabIds
    }),
    [onboardingContext, orderedTabs, ordersEnabled, user?.role, visibleTabIds]
  );
  const currentTourStep = isTourOpen ? tourSteps[tourStepIndex] : null;
  const positionTourTooltip = useCallback(() => {
    if (!isTourOpen || !currentTourStep) return;
    const targetEl = document.querySelector(currentTourStep.selector);
    if (!targetEl) {
      setTourTooltipStyle({
        left: Math.max(12, Math.round((window.innerWidth - 340) / 2)),
        top: Math.max(12, Math.round((window.innerHeight - 220) / 2))
      });
      return;
    }
    const rect = targetEl.getBoundingClientRect();
    const maxLeft = Math.max(12, window.innerWidth - 352);
    const nextLeft = Math.min(maxLeft, Math.max(12, Math.round(rect.left + (rect.width / 2) - 170)));
    let nextTop = Math.round(rect.bottom + 12);
    if (nextTop + 220 > window.innerHeight) {
      nextTop = Math.max(12, Math.round(rect.top - 232));
    }
    setTourTooltipStyle({ left: nextLeft, top: nextTop });
  }, [isTourOpen, currentTourStep]);
  const handleTourNext = useCallback(() => {
    if (tourStepIndex >= tourSteps.length - 1) {
      const completedAt = new Date().toISOString();
      closeTour();
      persistOnboardingState({
        tour_version: ONBOARDING_TOUR_VERSION,
        welcome_dismissed_at: completedAt,
        tour_completed_at: completedAt,
        tour_skipped_at: null,
        tour_snoozed_until: null
      }).catch(() => {});
      return;
    }
    setTourStepIndex((prev) => Math.min(prev + 1, tourSteps.length - 1));
  }, [tourStepIndex, tourSteps.length, closeTour, persistOnboardingState]);
  const handleTourBack = useCallback(() => {
    setTourStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);
  const handleTourSkip = useCallback(() => {
    const skippedAt = new Date().toISOString();
    closeTour();
    persistOnboardingState({
      tour_version: ONBOARDING_TOUR_VERSION,
      welcome_dismissed_at: skippedAt,
      tour_skipped_at: skippedAt,
      tour_completed_at: null,
      tour_snoozed_until: null
    }).catch(() => {});
  }, [closeTour, persistOnboardingState]);

  useEffect(() => {
    if (loading || !onboardingLoaded || tourSteps.length === 0 || isTourOpen) return;
    const activeVersion = Number(onboardingState?.tour_version || ONBOARDING_TOUR_VERSION);
    const isCompleted = activeVersion === ONBOARDING_TOUR_VERSION && Boolean(onboardingState?.tour_completed_at);
    const isSkipped = activeVersion === ONBOARDING_TOUR_VERSION && Boolean(onboardingState?.tour_skipped_at);
    const snoozeUntil = onboardingState?.tour_snoozed_until ? new Date(onboardingState.tour_snoozed_until) : null;
    const isSnoozed = snoozeUntil && !isNaN(snoozeUntil.getTime()) && snoozeUntil > new Date();
    if (!isCompleted && !isSkipped && !isSnoozed) {
      setWelcomeModalOpen(true);
    }
  }, [loading, onboardingLoaded, onboardingState, tourSteps.length, isTourOpen]);

  useEffect(() => {
    if (!isTourOpen || !currentTourStep?.prep) return;
    currentTourStep.prep();
  }, [isTourOpen, currentTourStep]);

  useEffect(() => {
    if (!isTourOpen || !currentTourStep) return undefined;

    let timeoutId = null;
    const applyStepTarget = (attemptsRemaining = 6) => {
      const targetEl = document.querySelector(currentTourStep.selector);
      const navTargetEl = currentTourStep.navSelector
        ? document.querySelector(currentTourStep.navSelector)
        : null;
      if (!targetEl && attemptsRemaining > 0) {
        timeoutId = window.setTimeout(() => applyStepTarget(attemptsRemaining - 1), 120);
        return;
      }
      if (tourTargetRef.current && tourTargetRef.current !== targetEl) {
        tourTargetRef.current.classList.remove('tour-target-active');
      }
      if (tourNavTargetRef.current && tourNavTargetRef.current !== navTargetEl) {
        tourNavTargetRef.current.classList.remove('tour-target-nav-active');
      }
      tourTargetRef.current = targetEl || null;
      tourNavTargetRef.current = navTargetEl || null;
      if (tourTargetRef.current) {
        tourTargetRef.current.classList.add('tour-target-active');
        tourTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
      if (tourNavTargetRef.current) {
        tourNavTargetRef.current.classList.add('tour-target-nav-active');
        tourNavTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      positionTourTooltip();
    };

    const rafId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => applyStepTarget(), 120);
    });
    const onReposition = () => positionTourTooltip();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      if (tourTargetRef.current) {
        tourTargetRef.current.classList.remove('tour-target-active');
        tourTargetRef.current = null;
      }
      if (tourNavTargetRef.current) {
        tourNavTargetRef.current.classList.remove('tour-target-nav-active');
        tourNavTargetRef.current = null;
      }
    };
  }, [isTourOpen, currentTourStep, positionTourTooltip]);

  const handleDismissSetupCard = () => {
    const nextChecklistState = {
      hidden: true,
      expanded: false,
      dismissed_once: true
    };
    applyChecklistState(nextChecklistState);
    persistOnboardingState({ checklist_state: nextChecklistState }).catch(() => {});
  };

  const handleOpenSetupCard = () => {
    const nextChecklistState = {
      hidden: false,
      expanded: setupCardDismissedOnce ? false : true,
      dismissed_once: setupCardDismissedOnce
    };
    applyChecklistState(nextChecklistState);
    persistOnboardingState({ checklist_state: nextChecklistState }).catch(() => {});
  };
  const handleToggleSetupCardExpanded = () => {
    const nextChecklistState = {
      hidden: false,
      expanded: !setupCardExpanded,
      dismissed_once: setupCardDismissedOnce
    };
    applyChecklistState(nextChecklistState);
    persistOnboardingState({ checklist_state: nextChecklistState }).catch(() => {});
  };

  const renderNextStepEmptyState = ({ title, description, actionLabel, onAction }) => (
    <div className="empty-state empty-state-action">
      <strong>{title}</strong>
      <p>{description}</p>
      {actionLabel && typeof onAction === 'function' && (
        <button type="button" className="btn-edit" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );

  return (
    <div className={`dashboard font-size-${selectedFontSize}`}>
      <AdminDashboardHeader
        canReturnToSuperAdmin={canReturnToSuperAdmin}
        onLogout={onLogout}
        onReturnToSuperAdmin={handleReturnToSuperAdmin}
        onOpenSetupCard={onboardingChecklist.pendingSteps.length > 0 ? handleOpenSetupCard : null}
        setupProgress={onboardingProgress}
        user={user}
      />
      <div className={`dashboard-main ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <AdminDashboardTabs
          activeTab={activeTab}
          isAdminUser={isAdminUser}
          isSidebarCollapsed={isSidebarCollapsed}
          onStartTour={startTour}
          onToggleSidebar={() => setIsSidebarCollapsed((prev) => !prev)}
          orderedTabs={orderedTabs}
          ordersEnabled={ordersEnabled}
          setSettingsTab={setSettingsTab}
          settingsTab={settingsTab}
          setActiveTab={setActiveTab}
          visibleTabIds={visibleTabIds}
        />

        <div className="dashboard-content">
          {/* Material toggle removed: single material view only */}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <OfflineQueueBanner
            isOnline={isOnline}
            offlineQueueSnapshot={offlineQueueSnapshot}
            offlineSyncing={offlineSyncing}
            onFlush={flushOfflineActionQueue}
            onOpenQueue={() => setOfflineQueueOpen(true)}
            onClearQueue={() => {
              setOfflineActionQueue([]);
              setSuccess('Offline queue cleared.');
            }}
          />

          <div className="tab-content">
        <OnboardingChecklist
          isVisible={shouldShowOnboardingCard}
          isExpanded={setupCardExpanded}
          completedCount={onboardingChecklist.completedCount}
          description={onboardingChecklist.description}
          totalCount={onboardingChecklist.totalCount}
          pendingSteps={onboardingChecklist.pendingSteps}
          onToggleExpanded={handleToggleSetupCardExpanded}
          onDismiss={handleDismissSetupCard}
          title={onboardingChecklist.title}
        />
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {(activeTab === 'add' || activeTab === 'transactions') && currentTabConfig?.mode !== 'custom_form' && (
              <div className="material-movements-shell">
                {activeTab === 'add' && (
                  <div className="section-header material-movements-header" style={{ marginBottom: '10px' }}>
                    <h2>Material Movements</h2>
                  </div>
                )}
                <div className="section-header inventory-mode-header" style={{ marginBottom: '12px' }}>
                  <div className="subtabs transaction-mode-switch" role="tablist" aria-label="Inventory type mode">
                    <button
                      type="button"
                      className={`raw ${transactionLedgerMode === 'raw_material' ? 'active' : ''}`}
                      onClick={() => handleSetInventoryMovementMode('raw_material')}
                    >
                      <Factory className="btn-icon" size={21} />
                      <span>Raw Materials</span>
                    </button>
                    <button
                      type="button"
                      className={`finished ${transactionLedgerMode === 'finished_goods' ? 'active' : ''}`}
                      onClick={() => handleSetInventoryMovementMode('finished_goods')}
                    >
                      <Package className="btn-icon" size={21} />
                      <span>Finished Goods</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'add' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="add-transaction-section" data-tour-id="add-movement-form">
                <div className="section-header">
                  <h2>Add Materials</h2>
                </div>
                <form onSubmit={handleSubmit} className="transaction-form">
                  <p className="helper-text calculation-note">
                    Stock impact guide: <strong>Inbound</strong> adds stock, <strong>Outbound</strong> removes stock,
                    and <strong>Hold</strong> reserves stock until released.
                  </p>
                  <div className="challan-toggle-card">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={movementHasChallan}
                        onChange={(e) => setMovementHasChallan(e.target.checked)}
                      />
                      <span>Create challan for this movement</span>
                    </label>
                    <small className="helper-text">
                      Challans are created from movements. Once issued, they can only be cancelled and reissued.
                    </small>
                  </div>
                  <div className="form-row">
                    {transactionLedgerMode === 'finished_goods' ? (
                      <>
                        <div className="form-group">
                          <label>Finished Goods Action *</label>
                          <select
                            value={finishedGoodsAction}
                            onChange={(e) => setFinishedGoodsAction(e.target.value)}
                            disabled={movementHasChallan}
                            required
                          >
                            {movementHasChallan ? (
                              <option value="out">Stock Out</option>
                            ) : (
                              <>
                                <option value="in">Stock In</option>
                                <option value="out">Stock Out</option>
                                <option value="hold">Hold</option>
                              </>
                            )}
                          </select>
                          {movementHasChallan && (
                            <small className="helper-text">Challan can only be issued for outbound movement.</small>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Finished Product *</label>
                          <select
                            value={finishedGoodsProductId}
                            onChange={(e) => setFinishedGoodsProductId(e.target.value)}
                            required
                          >
                            <option value="">Select product</option>
                            {finishedGoodsOptions.map((item) => (
                              <option key={`fg-product-${item.id}`} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Raw Material Action *</label>
                          <select
                            value={rawMaterialAction}
                            onChange={(e) => setRawMaterialAction(e.target.value)}
                            required
                          >
                            {(movementHasChallan ? RAW_OUTBOUND_ACTIONS : RAW_TRANSACTION_ACTIONS).map((action) => (
                              <option key={action.key} value={action.key}>
                                {action.label} ({action.type === 'out' ? 'Outbound' : 'Inbound'})
                              </option>
                            ))}
                          </select>
                          {movementHasChallan && (
                            <small className="helper-text">Challan can only be issued for outbound movement.</small>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Raw Material *</label>
                          <select
                            value={rawMaterialType}
                            onChange={(e) => {
                              setRawMaterialType(e.target.value);
                              setRawMaterialCost('');
                            }}
                            required
                          >
                            <option value="">Select raw material</option>
                            {rawMaterialOptions.map((item) => (
                              <option key={item.name} value={item.name}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        {selectedTransactionRawMaterialCharacteristics.map((characteristic) => {
                          const options = getCharacteristicValues(characteristic.list_key);
                          return (
                            <div key={`txn-char-${characteristic.list_key}`} className="form-group">
                              <label>{selectedTransactionRawMaterial?.name} {characteristic.name}{characteristic.mandatory ? ' *' : ''}</label>
                              <select
                                value={rawMaterialCharacteristics?.[characteristic.name] || ''}
                                onChange={(e) =>
                                  setRawMaterialCharacteristics((prev) => ({
                                    ...prev,
                                    [characteristic.name]: e.target.value
                                  }))
                                }
                                disabled={!rawMaterialType}
                                required={characteristic.mandatory}
                              >
                                <option value="">Select {characteristic.name}</option>
                                {options.map((value) => (
                                  <option key={`${characteristic.list_key}-${value}`} value={value}>{value}</option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                        <div className="form-group">
                          <label>{transactionStockField.label || 'Quantity'} *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={transactionValues[transactionStockField.key] || ''}
                            onChange={(e) => updateTransactionValue(transactionStockField.key, e.target.value)}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Unit Cost *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={rawMaterialCost}
                            onChange={(e) => setRawMaterialCost(e.target.value)}
                            placeholder="Enter cost per unit"
                            required
                          />
                        </div>
                        {RAW_TRANSACTION_ACTION_MAP[rawMaterialAction]?.type === 'in' && (
                          <div className="form-group">
                            <label>Linked Order # (optional)</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={rawMaterialLinkedOrderNumber}
                              onChange={(e) => setRawMaterialLinkedOrderNumber(e.target.value)}
                              placeholder="e.g. 1042"
                            />
                            <small className="helper-text">Use this when a purchase is specifically for one order.</small>
                          </div>
                        )}
                      </>
                    )}
                    {transactionExtraFields.map((field) => {
                      const value = transactionValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                      if (field.type === 'dropdown') {
                        return (
                          <div key={field.key} className="form-group">
                            <label>{renderDropdownLabel(field)}</label>
                            <select
                              value={value}
                              onChange={(e) => updateTransactionValue(field.key, e.target.value)}
                              required={field.required}
                            >
                              <option value="">Select {field.label}</option>
                              {getDropdownOptions(field).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        );
                      }
                      if (field.type === 'checkbox') {
                        return (
                          <div key={field.key} className="form-group">
                            <label className="toggle-label">
                              <input
                                type="checkbox"
                                checked={Boolean(value)}
                                onChange={(e) => updateTransactionValue(field.key, e.target.checked)}
                              />
                              <span>{field.label}</span>
                            </label>
                          </div>
                        );
                      }
                      return (
                        <div key={field.key} className="form-group">
                          <label>{field.label}{field.required ? ' *' : ''}</label>
                          <input
                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                            value={value}
                            onChange={(e) => updateTransactionValue(field.key, e.target.value)}
                            required={field.required}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {movementHasChallan && (
                    <div className="challan-form-shell">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Challan Type *</label>
                          <select value={movementChallanType} onChange={(e) => setMovementChallanType(e.target.value)} required>
                            <option value="delivery">Delivery</option>
                            <option value="material_issue">Material Issue</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Order # (optional)</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={movementChallanOrderNumber}
                            onChange={(e) => setMovementChallanOrderNumber(e.target.value)}
                            placeholder="Link to order number"
                          />
                        </div>
                        <div className="form-group">
                          <label>Party Name</label>
                          <input
                            type="text"
                            value={movementChallanPartyName}
                            onChange={(e) => setMovementChallanPartyName(e.target.value)}
                            placeholder="Customer / department / contractor"
                          />
                        </div>
                        <div className="form-group">
                          <label>Party Contact</label>
                          <input
                            type="text"
                            value={movementChallanPartyContact}
                            onChange={(e) => setMovementChallanPartyContact(e.target.value)}
                            placeholder="Contact number or person"
                          />
                        </div>
                        <div className="form-group">
                          <label>Party Address</label>
                          <input
                            type="text"
                            value={movementChallanPartyAddress}
                            onChange={(e) => setMovementChallanPartyAddress(e.target.value)}
                            placeholder="Address (optional)"
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label><Truck className="btn-icon" size={14} /> Transporter</label>
                          <input
                            type="text"
                            value={movementChallanTransporterName}
                            onChange={(e) => setMovementChallanTransporterName(e.target.value)}
                            placeholder="Transporter name"
                          />
                        </div>
                        <div className="form-group">
                          <label>Vehicle #</label>
                          <input
                            type="text"
                            value={movementChallanVehicleNumber}
                            onChange={(e) => setMovementChallanVehicleNumber(e.target.value)}
                            placeholder="Vehicle number"
                          />
                        </div>
                        <div className="form-group">
                          <label>LR / GR #</label>
                          <input
                            type="text"
                            value={movementChallanLrNumber}
                            onChange={(e) => setMovementChallanLrNumber(e.target.value)}
                            placeholder="LR / GR / docket"
                          />
                        </div>
                        <div className="form-group">
                          <label>Driver Name</label>
                          <input
                            type="text"
                            value={movementChallanDriverName}
                            onChange={(e) => setMovementChallanDriverName(e.target.value)}
                            placeholder="Driver name"
                          />
                        </div>
                        <div className="form-group">
                          <label>Driver Phone</label>
                          <input
                            type="text"
                            value={movementChallanDriverPhone}
                            onChange={(e) => setMovementChallanDriverPhone(e.target.value)}
                            placeholder="Driver phone"
                          />
                        </div>
                        <div className="form-group">
                          <label>Dispatch Date/Time</label>
                          <input
                            type="datetime-local"
                            value={movementChallanDispatchDateTime}
                            onChange={(e) => setMovementChallanDispatchDateTime(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label><Signature className="btn-icon" size={14} /> Signature (optional)</label>
                          <input type="file" accept="image/*" onChange={handleUploadChallanSignature} />
                          {movementChallanSignatureData && (
                            <div className="challan-signature-preview">
                              <img src={movementChallanSignatureData} alt="Signature preview" />
                              <button type="button" className="btn-edit" onClick={() => setMovementChallanSignatureData('')}>Clear</button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Additional Detail Fields</label>
                        <div className="challan-detail-rows">
                          {movementChallanCustomDetails.map((row, index) => (
                            <div key={`challan-detail-${index}`} className="challan-detail-row">
                              <input
                                type="text"
                                value={row.key}
                                onChange={(e) => {
                                  const next = movementChallanCustomDetails.map((item, idx) => (
                                    idx === index ? { ...item, key: e.target.value } : item
                                  ));
                                  setMovementChallanCustomDetails(next);
                                }}
                                placeholder="Field name"
                              />
                              <input
                                type="text"
                                value={row.value}
                                onChange={(e) => {
                                  const next = movementChallanCustomDetails.map((item, idx) => (
                                    idx === index ? { ...item, value: e.target.value } : item
                                  ));
                                  setMovementChallanCustomDetails(next);
                                }}
                                placeholder="Field value"
                              />
                              <button
                                type="button"
                                className="btn-delete"
                                onClick={() => {
                                  const next = movementChallanCustomDetails.filter((_, idx) => idx !== index);
                                  setMovementChallanCustomDetails(next.length > 0 ? next : [{ key: '', value: '' }]);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn-edit"
                          onClick={() => setMovementChallanCustomDetails((prev) => [...prev, { key: '', value: '' }])}
                        >
                          Add Detail Field
                        </button>
                      </div>

                    </div>
                  )}

                  {transactionSchema.include_date !== false && (
                    <div className="form-group">
                      <label htmlFor="date">Date *</label>
                      <input
                        type="date"
                        id="date"
                        value={transactionDate}
                        onChange={(e) => setTransactionDate(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {transactionSchema.include_notes !== false && (
                    <div className="form-group">
                      <label htmlFor="notes">Notes</label>
                      <textarea
                        id="notes"
                        value={transactionNotes}
                        onChange={(e) => setTransactionNotes(e.target.value)}
                        placeholder="Enter any additional notes (optional)"
                        rows="4"
                      />
                    </div>
                  )}

                  <button type="submit" className="submit-btn">
                    <Plus className="btn-icon" size={16} />
                    <span>Add Transaction</span>
                  </button>
                </form>
                <div className="empty-state" style={{ marginTop: '16px' }}>
                  Finished product transactions will be added in the next phase.
                </div>
              </div>
            )}

            {currentTabConfig?.mode === 'custom_form' && (
              <div className="add-transaction-section" data-tour-id={`custom-form-${currentTabConfig.id}`}>
                <h2>{currentTabConfig.label}</h2>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitCustomForm(currentTabConfig);
                  }}
                  className="transaction-form"
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        value={getCustomFormState(currentTabConfig.id).type}
                        onChange={(e) =>
                          updateCustomFormState(currentTabConfig.id, { type: e.target.value })
                        }
                        required
                      >
                        <option value="in">Stock In</option>
                        <option value="out">Stock Out</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Quantity ({transactionSchema.quantity_unit || quantityUnit}) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={getCustomFormState(currentTabConfig.id).quantity}
                        onChange={(e) =>
                          updateCustomFormState(currentTabConfig.id, { quantity: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Date *</label>
                      <input
                        type="date"
                        value={getCustomFormState(currentTabConfig.id).date}
                        onChange={(e) =>
                          updateCustomFormState(currentTabConfig.id, { date: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={getCustomFormState(currentTabConfig.id).notes}
                      onChange={(e) =>
                        updateCustomFormState(currentTabConfig.id, { notes: e.target.value })
                      }
                      rows="3"
                    />
                  </div>
                  {(currentTabConfig.fields || []).length > 0 && (
                    <div className="custom-fields">
                      {currentTabConfig.fields.map((field) => {
                        const value = getCustomFieldValues(currentTabConfig.id)[field.key] ?? (field.type === 'checkbox' ? false : '');
                        if (field.type === 'dropdown') {
                          return (
                            <div key={field.key} className="form-group">
                              <label>{renderDropdownLabel(field)}</label>
                              <select
                                value={value}
                                onChange={(e) => updateCustomFieldValue(currentTabConfig.id, field.key, e.target.value)}
                                required={field.required}
                              >
                                <option value="">Select {field.label}</option>
                                {getDropdownOptions(field).map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          );
                        }
                        if (field.type === 'checkbox') {
                          return (
                            <div key={field.key} className="form-group">
                              <label className="toggle-label">
                                <input
                                  type="checkbox"
                                  checked={Boolean(value)}
                                  onChange={(e) => updateCustomFieldValue(currentTabConfig.id, field.key, e.target.checked)}
                                />
                                <span>{field.label}</span>
                              </label>
                            </div>
                          );
                        }
                        return (
                          <div key={field.key} className="form-group">
                            <label>{field.label}{field.required ? ' *' : ''}</label>
                            <input
                              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                              value={value}
                              onChange={(e) => updateCustomFieldValue(currentTabConfig.id, field.key, e.target.value)}
                              required={field.required}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button type="submit" className="submit-btn">
                    Save
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'product_add' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="add-transaction-section product-add-shell" data-tour-id="product-bom-builder">
                <div className="section-header">
                  <h2>Add Product</h2>
                </div>
                {(() => {
                  const normalizedBomRows = productRawMaterials.map((entry) => normalizeProductRawMaterialEntry(entry));
                  const requiredFieldIssue = (productSchema.fields || []).find((field) => {
                    if (!field?.required) return false;
                    const value = productFormValues[field.key];
                    if (field.type === 'checkbox') return value !== true;
                    if (value === undefined || value === null) return true;
                    if (typeof value === 'string') return value.trim() === '';
                    return false;
                  });
                  const bomIssues = normalizedBomRows.flatMap((row, index) => {
                    const selectedRawMaterial = rawMaterials.find((item) => item.name === row.raw_material_name);
                    const requiredCharacteristics = (selectedRawMaterial?.characteristics || []).filter((item) => item.mandatory);
                    const missingCharacteristics = requiredCharacteristics
                      .filter((characteristic) => !String(row.characteristics?.[characteristic.name] || '').trim())
                      .map((characteristic) => characteristic.name);
                    const issues = [];
                    if (!row.raw_material_name) issues.push(`Row ${index + 1}: raw material type is required.`);
                    if (!(parseFloat(row.required_qty) > 0)) issues.push(`Row ${index + 1}: quantity per unit must be greater than 0.`);
                    if (missingCharacteristics.length > 0) {
                      issues.push(`Row ${index + 1}: missing ${missingCharacteristics.join(', ')}.`);
                    }
                    return issues;
                  });
                  const duplicateRawMaterialRows = (() => {
                    const counts = {};
                    normalizedBomRows.forEach((row) => {
                      const key = String(row.raw_material_name || '').trim().toLowerCase();
                      if (!key) return;
                      counts[key] = (counts[key] || 0) + 1;
                    });
                    return Object.values(counts).filter((count) => count > 1).length;
                  })();
                  const hasBasicDetails = Boolean(productFormName.trim()) && !requiredFieldIssue;
                  const totalRows = normalizedBomRows.length;
                  const validRows = normalizedBomRows.filter((row) => row.raw_material_name && parseFloat(row.required_qty) > 0).length;
                  const totalQtyPerUnit = normalizedBomRows.reduce((sum, row) => sum + Number(row.required_qty || 0), 0);
                  const bomRowsWithSelections = normalizedBomRows.filter((row) => Object.keys(row.characteristics || {}).length > 0).length;

                  return (
                    <>
                      <div className="product-builder-banner">
                        <div>
                          <strong>Build one product cleanly from top to bottom.</strong>
                          <p className="helper-text">Details define the product, BOM defines material usage, and review catches blockers before save.</p>
                        </div>
                        <span className={`status-pill ${(requiredFieldIssue || bomIssues.length > 0) ? 'warn' : 'ok'}`}>
                          {(requiredFieldIssue || bomIssues.length > 0) ? 'Needs review' : 'Ready to save'}
                        </span>
                      </div>
                      <p className="helper-text">Build products in three steps: details, BOM, and final review.</p>
                      <div className="product-builder-steps" role="tablist" aria-label="Product builder steps">
                        <button type="button" className={`product-builder-step ${productAddStep === 1 ? 'active' : ''}`} onClick={() => setProductAddStep(1)}>1. Details</button>
                        <button type="button" className={`product-builder-step ${productAddStep === 2 ? 'active' : ''}`} onClick={() => setProductAddStep(2)}>2. BOM</button>
                        <button type="button" className={`product-builder-step ${productAddStep === 3 ? 'active' : ''}`} onClick={() => setProductAddStep(3)}>3. Review</button>
                      </div>

                      <div className="product-builder-grid">
                        <section className="product-add-panel product-builder-main">
                          {productAddStep === 1 && (
                            <>
                              <h3>Product Details</h3>
                              <div className="product-add-fields">
                                <div className="form-group">
                                  <label>Product name *</label>
                                  <input
                                    type="text"
                                    value={productFormName}
                                    onChange={(e) => setProductFormName(e.target.value)}
                                    placeholder="e.g. Heritage Wool Rug"
                                  />
                                </div>
                                {(productSchema.fields || []).map((field) => (
                                  <div key={field.key} className="form-group">
                                    <label>
                                      {field.type === 'dropdown' ? renderDropdownLabel(field) : (
                                        <>
                                          {field.label || field.key}
                                          {field.required ? ' *' : ''}
                                        </>
                                      )}
                                    </label>
                                    {field.type === 'dropdown' ? (
                                      <select
                                        value={productFormValues[field.key] || ''}
                                        onChange={(e) => updateProductFormValue(field.key, e.target.value)}
                                      >
                                        <option value="">Select</option>
                                        {getDropdownOptions(field).map((opt) => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    ) : field.type === 'checkbox' ? (
                                      <input
                                        type="checkbox"
                                        checked={Boolean(productFormValues[field.key])}
                                        onChange={(e) => updateProductFormValue(field.key, e.target.checked)}
                                      />
                                    ) : (
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={productFormValues[field.key] || ''}
                                        onChange={(e) => updateProductFormValue(field.key, e.target.value)}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}

                          {productAddStep === 2 && (
                            <>
                              <div className="product-raw-toolbar">
                                <div>
                                  <h3>BOM Table</h3>
                                  <p className="helper-text">Add rows with raw material, quantity, and required characteristics.</p>
                                </div>
                                <button
                                  type="button"
                                  className="btn-edit"
                                  onClick={handleAddProductRawMaterialEntry}
                                  disabled={rawMaterials.length === 0}
                                >
                                  <Plus className="btn-icon" size={16} />
                                  <span>Add Row</span>
                                </button>
                              </div>
                              <div className="workflow-summary-pills product-bom-metrics">
                                <div className="workflow-summary-pill">
                                  <span>Rows</span>
                                  <strong>{totalRows}</strong>
                                </div>
                                <div className="workflow-summary-pill is-completed">
                                  <span>Valid</span>
                                  <strong>{validRows}</strong>
                                </div>
                                <div className="workflow-summary-pill">
                                  <span>With selections</span>
                                  <strong>{bomRowsWithSelections}</strong>
                                </div>
                                <div className="workflow-summary-pill is-overdue">
                                  <span>Issues</span>
                                  <strong>{bomIssues.length}</strong>
                                </div>
                              </div>
                              {rawMaterials.length === 0 ? (
                                <div className="empty-state">No raw materials configured yet. Add raw materials in Settings first.</div>
                              ) : normalizedBomRows.length === 0 ? (
                                <div className="empty-state">No BOM rows yet. Click Add Row to begin.</div>
                              ) : (
                                <div className="product-bom-table-wrap">
                                  <table className="product-bom-table">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Raw Material</th>
                                        <th>Characteristics</th>
                                        <th>Qty / Unit</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {normalizedBomRows.map((row, index) => {
                                        const selectedType = findRawMaterialByName(row.raw_material_name);
                                        const characteristics = Array.isArray(selectedType?.characteristics) ? selectedType.characteristics : [];
                                        return (
                                          <tr key={`product-bom-row-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>
                                              <select
                                                value={row.raw_material_name}
                                                onChange={(e) =>
                                                  updateProductRawMaterial(index, {
                                                    raw_material_name: e.target.value,
                                                    characteristics: {}
                                                  })
                                                }
                                              >
                                                <option value="">Select type</option>
                                                {getRawMaterialSelectOptions(row.raw_material_name).map((item) => (
                                                  <option key={item.name} value={item.name}>
                                                    {item.name}{item.archived ? ' (Archived)' : ''}
                                                  </option>
                                                ))}
                                              </select>
                                            </td>
                                            <td>
                                              {!row.raw_material_name ? (
                                                <span className="helper-text">Select raw material first.</span>
                                              ) : characteristics.length === 0 ? (
                                                <span className="helper-text">No characteristics</span>
                                              ) : (
                                                <div className="product-bom-characteristics-inline">
                                                  {characteristics.map((characteristic) => {
                                                    const options = getCharacteristicValues(characteristic.list_key);
                                                    return (
                                                      <div key={`bom-inline-${index}-${characteristic.name}`} className="product-bom-characteristic-field">
                                                        <label>
                                                          {characteristic.name}
                                                          {characteristic.mandatory ? ' *' : ''}
                                                        </label>
                                                        <select
                                                          value={row.characteristics?.[characteristic.name] || ''}
                                                          onChange={(e) =>
                                                            updateProductRawMaterial(index, {
                                                              characteristics: {
                                                                ...(row.characteristics || {}),
                                                                [characteristic.name]: e.target.value
                                                              }
                                                            })
                                                          }
                                                        >
                                                          <option value="">Select {characteristic.name}</option>
                                                          {options.map((option) => (
                                                            <option key={`${characteristic.list_key}-${option}`} value={option}>{option}</option>
                                                          ))}
                                                        </select>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={row.required_qty ?? ''}
                                                onChange={(e) => updateProductRawMaterial(index, { required_qty: e.target.value })}
                                                placeholder="0.00"
                                              />
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn-delete-small"
                                                onClick={() => handleRemoveProductRawMaterialEntry(index)}
                                                aria-label={`Remove raw material row ${index + 1}`}
                                                title="Remove row"
                                              >
                                                <X size={14} />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </>
                          )}

                          {productAddStep === 3 && (
                            <div className="product-review-panel">
                              <h3>Review Product</h3>
                              <div className="product-review-grid">
                                <div className="product-review-item">
                                  <span>Name</span>
                                  <strong>{productFormName || 'Not set'}</strong>
                                </div>
                                <div className="product-review-item">
                                  <span>BOM rows</span>
                                  <strong>{totalRows}</strong>
                                </div>
                                <div className="product-review-item">
                                  <span>Valid rows</span>
                                  <strong>{validRows}</strong>
                                </div>
                                <div className="product-review-item">
                                  <span>Total qty / unit</span>
                                  <strong>{totalQtyPerUnit.toFixed(2)}</strong>
                                </div>
                              </div>
                              <div className={`product-review-validation ${(requiredFieldIssue || bomIssues.length > 0) ? 'warn' : 'ok'}`}>
                                {requiredFieldIssue || bomIssues.length > 0
                                  ? `${(requiredFieldIssue ? 1 : 0) + bomIssues.length} issue(s) found.`
                                  : 'Ready to save.'}
                              </div>
                              <button type="button" className="btn-edit" onClick={handleCreateProduct}>
                                Save Product
                              </button>
                            </div>
                          )}
                        </section>

                        <aside className="product-add-panel product-builder-summary">
                          <h3>Live Summary</h3>
                          <div className="product-review-grid compact">
                            <div className="product-review-item">
                              <span>Details</span>
                              <strong>{hasBasicDetails ? 'Complete' : 'Pending'}</strong>
                            </div>
                            <div className="product-review-item">
                              <span>BOM rows</span>
                              <strong>{totalRows}</strong>
                            </div>
                            <div className="product-review-item">
                              <span>Duplicates</span>
                              <strong>{duplicateRawMaterialRows}</strong>
                            </div>
                            <div className="product-review-item">
                              <span>Qty / unit</span>
                              <strong>{totalQtyPerUnit.toFixed(2)}</strong>
                            </div>
                          </div>
                          <div className="product-builder-issues">
                            <strong>Issues</strong>
                            {requiredFieldIssue && (
                              <div className="product-builder-issue">Product detail required: {requiredFieldIssue.label || requiredFieldIssue.key}.</div>
                            )}
                            {bomIssues.length === 0 && !requiredFieldIssue ? (
                              <small className="helper-text">No blockers detected.</small>
                            ) : (
                              bomIssues.map((issue, index) => (
                                <div key={`bom-issue-${index}`} className="product-builder-issue">{issue}</div>
                              ))
                            )}
                          </div>
                        </aside>
                      </div>

                      <div className="product-add-sticky-actions">
                        <button type="button" className="btn-secondary" onClick={resetProductCreateForm}>
                          Reset
                        </button>
                        {productAddStep > 1 && (
                          <button type="button" className="btn-ghost" onClick={() => setProductAddStep((prev) => Math.max(1, prev - 1))}>
                            Back
                          </button>
                        )}
                        {productAddStep < 3 ? (
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => setProductAddStep((prev) => Math.min(3, prev + 1))}
                            disabled={(productAddStep === 1 && !hasBasicDetails) || (productAddStep === 2 && totalRows === 0)}
                          >
                            Next
                          </button>
                        ) : (
                          <button type="button" className="btn-edit" onClick={handleCreateProduct}>
                            Save Product
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {activeTab === 'products' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="transactions-section" data-tour-id="products-overview">
                <div className="section-header">
                  <h2>Products</h2>
                </div>
                <div className="product-toolbar">
                  <div className="product-toolbar-row product-toolbar-row-primary">
                    <div className="product-search">
                      <span className="product-search-icon" aria-hidden="true">
                        <Search size={14} />
                      </span>
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Search products, raw material, characteristics"
                      />
                    </div>
                  </div>
                  <div className="product-toolbar-row product-toolbar-row-actions">
                    <div className="product-view-switch" role="tablist" aria-label="Product views">
                      <button
                        type="button"
                        className={`btn-edit ${productViewMode === 'cards' ? 'active' : ''}`}
                        onClick={() => setProductViewMode('cards')}
                      >
                        <Kanban size={16} strokeWidth={1.8} />
                        Cards
                      </button>
                      <button
                        type="button"
                        className={`btn-edit ${productViewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setProductViewMode('list')}
                      >
                        <List size={16} strokeWidth={1.8} />
                        List
                      </button>
                    </div>
                    <button type="button" className="btn-edit" onClick={handleExportProductsCsv} disabled={filteredProductCatalog.length === 0}>
                      <FileDown className="btn-icon" size={16} />
                      <span>Export CSV (Excel)</span>
                    </button>
                  </div>
                </div>
                <div className="workflow-summary-pills product-metrics-row">
                  <div className="workflow-summary-pill">
                    <span>Total Products</span>
                    <strong>{productMetrics.total}</strong>
                  </div>
                  <div className="workflow-summary-pill is-completed">
                    <span>With BOM</span>
                    <strong>{productMetrics.withBom}</strong>
                  </div>
                  <div className="workflow-summary-pill is-cancelled">
                    <span>Missing Required Fields</span>
                    <strong>{productMetrics.missing}</strong>
                  </div>
                  <div className="workflow-summary-pill">
                    <span>Avg Raw / Product</span>
                    <strong>{productMetrics.avgRaw.toFixed(1)}</strong>
                  </div>
                </div>
                <div className="product-chip-filters">
                  <button
                    type="button"
                    className={`product-chip ${productHasRawFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setProductHasRawFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`product-chip ${productHasRawFilter === 'with' ? 'active' : ''}`}
                    onClick={() => setProductHasRawFilter('with')}
                  >
                    Has BOM
                  </button>
                  <button
                    type="button"
                    className={`product-chip ${productHasRawFilter === 'without' ? 'active' : ''}`}
                    onClick={() => setProductHasRawFilter('without')}
                  >
                    Missing BOM
                  </button>
                  {rawMaterials.map((item) => {
                    const key = String(item.name || '').toLowerCase();
                    return (
                      <button
                        key={`product-chip-${key}`}
                        type="button"
                        className={`product-chip ${productMaterialFilter === key ? 'active' : ''}`}
                        onClick={() => setProductMaterialFilter((prev) => (prev === key ? 'all' : key))}
                      >
                        Uses {item.name}
                      </button>
                    );
                  })}
                </div>
                <div className="product-filters">
                  <div className="product-filter">
                    <label>Raw Material</label>
                    <select
                      value={productMaterialFilter}
                      onChange={(e) => setProductMaterialFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      {rawMaterials.map((item) => (
                        <option key={item.name} value={String(item.name || '').toLowerCase()}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="product-filter">
                    <label>Raw Materials</label>
                    <select
                      value={productHasRawFilter}
                      onChange={(e) => setProductHasRawFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="with">With raw materials</option>
                      <option value="without">Without raw materials</option>
                    </select>
                  </div>
                </div>
                {hasActiveProductFilters && (
                  <div className="product-filter-clear-row">
                    <span className="helper-text">Filters active.</span>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        setProductSearch('');
                        setProductMaterialFilter('all');
                        setProductHasRawFilter('all');
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
                <p className="helper-text">View products created for this company.</p>
                {visibleProductCards.length === 0 ? (
                  productCatalogWithPayload.length === 0
                    ? renderNextStepEmptyState({
                      title: 'No products yet.',
                      description: 'No products are available yet. Use Add Product from the sidebar to create one.'
                    })
                    : (
                      <div className="empty-state empty-state-action">
                        <strong>No products match current filters.</strong>
                        <p>Try broadening your search or clear filters.</p>
                        <div className="order-summary-actions">
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => {
                              setProductSearch('');
                              setProductMaterialFilter('all');
                              setProductHasRawFilter('all');
                            }}
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    )
                ) : productViewMode === 'list' ? (
                  <>
                    <p className="helper-text product-list-helper">Click a product row to view full details and BOM breakdown.</p>
                    <div className="table-wrapper product-list-table-wrapper">
                    <table className="data-table product-list-table">
                      <thead>
                        <tr>
                          <th className="product-sticky-col">
                            <button type="button" className="table-sort-btn" onClick={() => toggleProductListSort('name')}>
                              Product {getProductSortIndicator('name')}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="table-sort-btn" onClick={() => toggleProductListSort('bom')}>
                              BOM {getProductSortIndicator('bom')}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="table-sort-btn" onClick={() => toggleProductListSort('rawCount')}>
                              Raw Count {getProductSortIndicator('rawCount')}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="table-sort-btn" onClick={() => toggleProductListSort('updated')}>
                              Updated {getProductSortIndicator('updated')}
                            </button>
                          </th>
                          <th className="product-actions-col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedProductListRows.map((product) => {
                          const fields = product.displayFields || {};
                          const rawList = product.rawMaterialsList || [];
                          const isExpanded = expandedProductListIds.includes(product.id);
                          return (
                            <React.Fragment key={`product-list-${product.id}`}>
                              <tr className={`product-list-row ${isExpanded ? 'is-expanded' : ''}`} onClick={() => toggleProductListExpanded(product.id)}>
                                <td className="product-sticky-col">
                                  <div className="product-list-name-cell">
                                    <strong>{product.name}</strong>
                                    <small>{isExpanded ? 'Hide details' : 'View details'}</small>
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-pill ${rawList.length > 0 ? 'ok' : 'warn'}`}>
                                    {rawList.length > 0 ? 'Has BOM' : 'Missing BOM'}
                                  </span>
                                </td>
                                <td>
                                  <span className="status-pill ok">{rawList.length}</span>
                                </td>
                                <td>{product.updated_at ? new Date(product.updated_at).toLocaleDateString() : (product.created_at ? new Date(product.created_at).toLocaleDateString() : '—')}</td>
                                <td className="product-row-actions product-actions-col" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="btn-edit-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartEditProduct(product);
                                    }}
                                    aria-label={`Edit ${product.name}`}
                                    title="Edit product"
                                  >
                                    <Pencil className="btn-icon" size={14} />
                                    <span>Edit</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-edit-small icon-only-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openProductNotes(product);
                                    }}
                                    aria-label={`Notes for ${product.name}`}
                                    title="Product notes"
                                  >
                                    <StickyNote className="btn-icon" size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-edit-small icon-only-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      requestDuplicateProduct(product);
                                    }}
                                    aria-label={`Duplicate ${product.name}`}
                                    title="Duplicate product"
                                  >
                                    <Copy className="btn-icon" size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-delete-small icon-only-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      requestDeleteProduct(product);
                                    }}
                                    aria-label={`Delete ${product.name}`}
                                    title="Delete product"
                                  >
                                    <Trash2 className="btn-icon" size={14} />
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="product-list-detail-row">
                                  <td colSpan={5}>
                                    <div className="product-list-detail-grid">
                                      <div className="product-list-detail-section">
                                        <strong>Product Details</strong>
                                        <div className="product-view-card-meta">
                                          {productDisplayFields.length === 0 ? (
                                            <div className="empty-state">No product fields configured.</div>
                                          ) : productDisplayFields.map((field) => (
                                            <div key={`${product.id}-detail-${field.key}`} className="product-view-meta-item">
                                              <span>{field.label || field.key}</span>
                                              <strong>{formatProductDisplayFieldValue(field, fields[field.key])}</strong>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="product-list-detail-section">
                                        <strong>BOM Breakdown</strong>
                                        {rawList.length === 0 ? (
                                          <div className="empty-state">No BOM mapped for this product.</div>
                                        ) : (
                                          <div className="product-bom-rows">
                                            {rawList.map((entry, index) => {
                                              const characteristicText = Object.entries(entry.characteristics || {})
                                                .map(([key, value]) => `${key}: ${value}`)
                                                .join(' • ');
                                              return (
                                                <div key={`${product.id}-detail-bom-${index}`} className="product-bom-row">
                                                  <div>
                                                    <strong>{entry.raw_material_name || 'Raw Material'}</strong>
                                                    <p>{characteristicText || 'No characteristic selected'}</p>
                                                  </div>
                                                  <span>{Number(entry.required_qty || 0).toFixed(2)}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="product-list-mobile-cards">
                    {sortedProductListRows.map((product) => {
                      const rawList = product.rawMaterialsList || [];
                      const fields = product.displayFields || {};
                      const isExpanded = expandedProductListIds.includes(product.id);
                      return (
                        <article key={`product-mobile-${product.id}`} className="product-list-mobile-card">
                          <div className="product-list-mobile-header" onClick={() => toggleProductListExpanded(product.id)}>
                            <h3>{product.name}</h3>
                            <span>{isExpanded ? 'Hide' : 'Details'}</span>
                          </div>
                          <div className="product-view-card-chips">
                            <span className={`status-pill ${rawList.length > 0 ? 'ok' : 'warn'}`}>
                              {rawList.length > 0 ? 'Has BOM' : 'Missing BOM'}
                            </span>
                            <span className="status-pill ok">Raw: {rawList.length}</span>
                            <span className="status-pill ok">
                              Updated: {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : (product.created_at ? new Date(product.created_at).toLocaleDateString() : '—')}
                            </span>
                          </div>
                          <div className="product-row-actions">
                            <button type="button" className="btn-edit-small" onClick={() => handleStartEditProduct(product)}>
                              <Pencil className="btn-icon" size={14} />
                              <span>Edit</span>
                            </button>
                            <button type="button" className="btn-edit-small icon-only-btn" onClick={() => openProductNotes(product)} title="Product notes">
                              <StickyNote className="btn-icon" size={14} />
                            </button>
                            <button type="button" className="btn-edit-small icon-only-btn" onClick={() => requestDuplicateProduct(product)} title="Duplicate product">
                              <Copy className="btn-icon" size={14} />
                            </button>
                            <button type="button" className="btn-delete-small icon-only-btn" onClick={() => requestDeleteProduct(product)} title="Delete product">
                              <Trash2 className="btn-icon" size={14} />
                            </button>
                          </div>
                          {isExpanded && (
                            <div className="product-list-mobile-details">
                              <div className="product-view-card-meta">
                                {productDisplayFields.map((field) => (
                                  <div key={`${product.id}-mobile-${field.key}`} className="product-view-meta-item">
                                    <span>{field.label || field.key}</span>
                                    <strong>{formatProductDisplayFieldValue(field, fields[field.key])}</strong>
                                  </div>
                                ))}
                              </div>
                              <div className="product-view-bom">
                                <div className="product-view-bom-header"><strong>BOM Breakdown</strong></div>
                                {rawList.length === 0 ? (
                                  <div className="empty-state">No BOM mapped for this product.</div>
                                ) : (
                                  <div className="product-bom-rows">
                                    {rawList.map((entry, index) => {
                                      const characteristicText = Object.entries(entry.characteristics || {})
                                        .map(([key, value]) => `${key}: ${value}`)
                                        .join(' • ');
                                      return (
                                        <div key={`${product.id}-mobile-bom-${index}`} className="product-bom-row">
                                          <div>
                                            <strong>{entry.raw_material_name || 'Raw Material'}</strong>
                                            <p>{characteristicText || 'No characteristic selected'}</p>
                                          </div>
                                          <span>{Number(entry.required_qty || 0).toFixed(2)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                  </>
                ) : (
                  <div className="product-cards-grid">
                    {visibleProductCards.map((product) => {
                      const rawList = product.rawMaterialsList;
                      const bomExpanded = expandedProductBomIds.includes(product.id);
                      const visibleBomRows = bomExpanded ? rawList : rawList.slice(0, 3);
                      const initials = String(product.name || 'P')
                        .split(' ')
                        .map((item) => item[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();
                      return (
                        <article key={`product-card-${product.id}`} className="product-view-card">
                          <header className="product-view-card-header">
                            <div className="product-avatar">{initials}</div>
                            <div className="product-view-card-title">
                              <h3>{product.name}</h3>
                              <p>
                                Created {product.created_at ? new Date(product.created_at).toLocaleDateString() : '—'}
                              </p>
                            </div>
                          </header>

                          <div className="product-view-card-meta">
                            {productDisplayFields.map((field) => {
                              const value = product.displayFields?.[field.key];
                              const formatted = field.type === 'checkbox'
                                ? (value ? 'Yes' : 'No')
                                : (value !== undefined && value !== null && value !== '' ? String(value) : '—');
                              return (
                                <div key={`${product.id}-meta-${field.key}`} className="product-view-meta-item">
                                  <span>{field.label || field.key}</span>
                                  <strong>{formatted}</strong>
                                </div>
                              );
                            })}
                          </div>

                          <div className="product-view-card-chips">
                            <span className={`status-pill ${rawList.length > 0 ? 'ok' : 'warn'}`}>
                              {rawList.length > 0 ? 'Has BOM' : 'Missing BOM'}
                            </span>
                            <span className="status-pill ok">Raw items: {rawList.length}</span>
                            <span className="status-pill ok">Total req: {product.totalRawRequiredQty.toFixed(2)}</span>
                            {product.missingRequiredFields.length > 0 && (
                              <span className="status-pill warn">Missing fields: {product.missingRequiredFields.join(', ')}</span>
                            )}
                          </div>

                          <div className="product-view-bom">
                            <div className="product-view-bom-header">
                              <strong>BOM Preview</strong>
                              {rawList.length > 3 && (
                                <button
                                  type="button"
                                  className="btn-ghost"
                                  onClick={() => toggleProductBomExpanded(product.id)}
                                >
                                  {bomExpanded ? 'Collapse' : `View all (${rawList.length})`}
                                </button>
                              )}
                            </div>
                            {rawList.length === 0 ? (
                              <div className="empty-state">No raw materials mapped yet.</div>
                            ) : (
                              <div className="product-bom-rows">
                                {visibleBomRows.map((entry, index) => {
                                  const characteristicText = Object.entries(entry.characteristics || {})
                                    .map(([key, value]) => `${key}: ${value}`)
                                    .join(' • ');
                                  return (
                                    <div key={`${product.id}-bom-${index}`} className="product-bom-row">
                                      <div>
                                        <strong>{entry.raw_material_name || 'Raw Material'}</strong>
                                        <p>{characteristicText || 'No characteristic selected'}</p>
                                      </div>
                                      <span>{Number(entry.required_qty || 0).toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <footer className="product-view-card-actions">
                            <button
                              type="button"
                              className="btn-edit-small"
                              onClick={() => handleStartEditProduct(product)}
                            >
                              <Pencil className="btn-icon" size={14} />
                              <span>View / Edit</span>
                            </button>
                            <div className="product-view-card-actions-secondary">
                              <button
                                type="button"
                                className="btn-edit-small icon-only-btn"
                                onClick={() => openProductNotes(product)}
                                title="Product notes"
                                aria-label="Product notes"
                              >
                                <StickyNote className="btn-icon" size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn-edit-small icon-only-btn"
                                onClick={() => requestDuplicateProduct(product)}
                                title="Duplicate product"
                                aria-label="Duplicate product"
                              >
                                <Copy className="btn-icon" size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn-delete-small icon-only-btn"
                                onClick={() => requestDeleteProduct(product)}
                                title="Delete product"
                                aria-label="Delete product"
                              >
                                <Trash2 className="btn-icon" size={14} />
                              </button>
                            </div>
                          </footer>
                        </article>
                      );
                    })}
                  </div>
                )}
                {productNotesTarget && (
                  <div className="modal-overlay" onClick={() => setProductNotesTarget(null)}>
                    <div className="modal" role="dialog" aria-modal="true" aria-label="Transaction notes" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
                      <h3>Product Notes: {productNotesTarget.name}</h3>
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          rows={5}
                          value={productNotesText}
                          onChange={(e) => setProductNotesText(e.target.value)}
                          placeholder="Add notes for this product"
                        />
                      </div>
                      <div className="modal-actions modal-actions-cancel-order">
                        <button type="button" onClick={() => setProductNotesTarget(null)}>
                          <X className="btn-icon" size={16} />
                          <span>Cancel</span>
                        </button>
                        <button type="button" className="btn-primary" onClick={handleSaveProductNotes}>
                          <CheckCircle2 className="btn-icon" size={16} />
                          <span>Save Notes</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pipeline_board' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="stock-section workflow-section" data-tour-id="workflow-board">
                <div className="section-header">
                  <h2>Order Workflow</h2>
                  <button
                    type="button"
                    className="btn-edit"
                    onClick={() => {
                      setSettingsTab('system');
                      setActiveTab('settings');
                    }}
                  >
                    Manage Stages
                  </button>
                </div>

                <div className="workflow-summary-bar">
                  {(() => {
                    const activeCount = orders.filter((o) => !['Completed', 'Cancelled', 'Canceled'].includes(o.status)).length;
                    const overdueCount = orders.filter((o) => getStageAgeDays(o) >= 7 && !['Completed', 'Cancelled', 'Canceled'].includes(o.status)).length;
                    const completedCount = orders.filter((o) => o.status === 'Completed').length;
                    const cancelledCount = orders.filter((o) => ['Cancelled', 'Canceled'].includes(o.status)).length;
                    return (
                      <div className="workflow-summary-pills">
                        <div className="workflow-summary-pill">
                          <span>Active</span>
                          <strong>{activeCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-overdue">
                          <span>Overdue</span>
                          <strong>{overdueCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-completed">
                          <span>Completed</span>
                          <strong>{completedCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-cancelled">
                          <span>Cancelled</span>
                          <strong>{cancelledCount}</strong>
                        </div>
                        <button
                          type="button"
                          className="workflow-summary-pill summary-pill-button"
                          onClick={() => setHoldOverviewOpen(true)}
                        >
                          <span>Total In Hold</span>
                          <strong>View</strong>
                        </button>
                      </div>
                    );
                  })()}
                </div>

                <div className="stock-toolbar workflow-toolbar">
                  <div className="workflow-toolbar-row workflow-toolbar-primary">
                    <div className="workflow-search">
                      <span className="workflow-search-icon" aria-hidden="true">
                        <Search size={14} />
                      </span>
                      <input
                        className="workflow-search-input"
                        placeholder="Search order # or buyer"
                        value={pipelineSearch}
                        onChange={(e) => setPipelineSearch(e.target.value)}
                      />
                      {pipelineSearch && (
                        <button
                          type="button"
                          className="workflow-search-clear"
                          onClick={() => setPipelineSearch('')}
                          aria-label="Clear workflow search"
                          title="Clear"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <select
                      value={pipelineAssigneeFilter}
                      onChange={(e) => setPipelineAssigneeFilter(e.target.value)}
                      className="select-input"
                    >
                      <option value="all">All assignees</option>
                      {Object.entries(usersById).map(([id, u]) => (
                        <option key={id} value={id}>{u.username}</option>
                      ))}
                    </select>
                    <label className="toggle-inline">
                      <input
                        type="checkbox"
                        checked={pipelineOverdueOnly}
                        onChange={(e) => setPipelineOverdueOnly(e.target.checked)}
                      />
                      Overdue only (&gt;= 7d)
                    </label>
                    {selectedPipelineOrderIds.length > 0 && (
                      <>
                        <button type="button" className="btn-edit" onClick={() => handleBulkTransition('next')}>
                          Bulk next ({selectedPipelineOrderIds.length})
                        </button>
                        <button type="button" className="btn-edit" onClick={() => handleBulkTransition('back')}>
                          Bulk back
                        </button>
                        <button type="button" className="btn-edit" onClick={() => handleBulkTransition('complete')}>
                          Bulk complete
                        </button>
                      </>
                    )}
                    <div className="workflow-view-switch" role="tablist" aria-label="Workflow views">
                      <button
                        type="button"
                        className={`btn-edit ${pipelineView === 'board' ? 'active' : ''}`}
                        onClick={() => setPipelineView('board')}
                      >
                        <Kanban size={16} strokeWidth={1.8} />
                        Board
                      </button>
                      <button
                        type="button"
                        className={`btn-edit ${pipelineView === 'list' ? 'active' : ''}`}
                        onClick={() => setPipelineView('list')}
                      >
                        <List size={16} strokeWidth={1.8} />
                        List
                      </button>
                      <button
                        type="button"
                        className={`btn-edit ${pipelineView === 'calendar' ? 'active' : ''}`}
                        onClick={() => setPipelineView('calendar')}
                      >
                        <CalendarDays size={16} strokeWidth={1.8} />
                        Calendar
                      </button>
                    </div>
                  </div>
                  <div className="workflow-toolbar-row workflow-toolbar-secondary workflow-toolbar-filters">
                    <div className="workflow-filter-group">
                      <label className="workflow-filter-label">Preset</label>
                      <select
                        className="select-input compact"
                        value={pipelinePreset}
                        onChange={(e) => applyPipelinePreset(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="blocked">Blocked</option>
                        <option value="unassigned">Unassigned</option>
                        <option value="overdue_unassigned">Overdue + Unassigned</option>
                      </select>
                    </div>
                    <label className="toggle-inline"><input type="checkbox" checked={pipelineBlockedOnly} onChange={(e) => setPipelineBlockedOnly(e.target.checked)} /> Blocked only</label>
                    <label className="toggle-inline"><input type="checkbox" checked={pipelineUnassignedOnly} onChange={(e) => setPipelineUnassignedOnly(e.target.checked)} /> Unassigned only</label>
                    {(pipelineBlockedOnly || pipelineUnassignedOnly || pipelineOverdueOnly || pipelineSearch) && (
                      <span className="badge">Active filters</span>
                    )}
                  </div>
                </div>

                {pipelineStages.length === 0 ? (
                  renderNextStepEmptyState({
                    title: 'No workflow stages configured.',
                    description: 'Add pipeline stages in Settings so orders can be tracked from start to finish.',
                    actionLabel: 'Open Settings',
                    onAction: () => {
                      setActiveTab('settings');
                      setSettingsTab('system');
                    }
                  })
                ) : pipelineView === 'list' ? (
                  filteredPipelineOrders.length === 0 ? (
                    orders.length === 0
                      ? renderNextStepEmptyState({
                        title: 'No active orders yet.',
                        description: 'Create your first order to begin workflow tracking.',
                        actionLabel: 'Create Order',
                        onAction: () => setActiveTab('order')
                      })
                      : <div className="empty-state">No orders match the current filters.</div>
                  ) : (
                    <div className="table-wrapper workflow-table-wrapper">
                      <table className="data-table workflow-list-table">
                        <thead>
                          <tr>
                            <th>Order</th>
                            <th>Buyer</th>
                            <th>Stage</th>
                            <th>Delivery</th>
                            <th>Assignee</th>
                            <th>Stage Age</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPipelineOrders.map((order) => {
                            const stageIndex = pipelineStages.indexOf(order._stageName);
                            const busy = pipelineTransitioningIds.includes(order.id);
                            return (
                              <tr key={`pipeline-list-${order.id}`}>
                                <td>#{order.order_number}</td>
                                <td>{order.buyer_name || 'Unnamed buyer'}</td>
                                <td>{order._stageName}</td>
                                <td>{formatDateOnly(order._deliveryDate, getCurrentTimeZone())}</td>
                                <td>{order.assigned_user_id ? (usersById[String(order.assigned_user_id)]?.username || 'Assigned') : 'Unassigned'}</td>
                                <td>{getStageAgeDays(order)}d</td>
                                <td>{order.blocked_reason ? `Blocked: ${order.blocked_reason}` : 'Active'}</td>
                                <td>
                                  <div className="workflow-inline-actions">
                                    <button type="button" className="btn-link" disabled={busy || stageIndex === 0} onClick={() => handlePipelineTransition(order, 'back')}>Back</button>
                                    <button type="button" className="btn-link" disabled={busy || stageIndex === pipelineStages.length - 1} onClick={() => handlePipelineTransition(order, 'next')}>Next</button>
                                    <button type="button" className="btn-link" disabled={busy} onClick={() => handlePipelineTransition(order, 'complete')}>Complete</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : pipelineView === 'calendar' ? (
                  filteredPipelineOrders.length === 0 ? (
                    orders.length === 0
                      ? renderNextStepEmptyState({
                        title: 'No active orders yet.',
                        description: 'Create your first order to begin workflow tracking.',
                        actionLabel: 'Create Order',
                        onAction: () => setActiveTab('order')
                      })
                      : <div className="empty-state">No orders match the current filters.</div>
                  ) : (
                    <div className="workflow-calendar">
                      <div className="workflow-calendar-header">
                        <div className="workflow-calendar-nav" aria-label="Calendar month controls">
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => {
                              const d = new Date(
                                pipelineCalendarMonthStart.getFullYear(),
                                pipelineCalendarMonthStart.getMonth() - 1,
                                1
                              );
                              setPipelineCalendarCursor(toLocalMonthKey(d));
                              setExpandedCalendarDayKey(null);
                            }}
                          >
                            Prev
                          </button>
                          <div className="workflow-calendar-month-label">{pipelineCalendarMonthLabel}</div>
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => {
                              const d = new Date(
                                pipelineCalendarMonthStart.getFullYear(),
                                pipelineCalendarMonthStart.getMonth() + 1,
                                1
                              );
                              setPipelineCalendarCursor(toLocalMonthKey(d));
                              setExpandedCalendarDayKey(null);
                            }}
                          >
                            Next
                          </button>
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => {
                              setPipelineCalendarCursor(toLocalMonthKey(new Date()));
                              setExpandedCalendarDayKey(null);
                            }}
                          >
                            Today
                          </button>
                        </div>
                      </div>
                      <div className="workflow-calendar-weekdays">
                        {pipelineCalendarWeekdayLabels.map((weekday) => (
                          <div key={`weekday-${weekday}`} className="workflow-calendar-weekday">{weekday}</div>
                        ))}
                      </div>
                      <div className="workflow-calendar-grid">
                        {pipelineCalendarDays.map((day) => {
                          const hasMore = day.orders.length > 3;
                          const isExpanded = expandedCalendarDayKey === day.key;
                          const visibleOrders = isExpanded ? day.orders : day.orders.slice(0, 3);
                          return (
                            <section
                              key={`calendar-day-${day.key}`}
                              className={`workflow-calendar-day ${day.inCurrentMonth ? '' : 'is-muted'} ${day.isToday ? 'is-today' : ''}`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                const orderId = Number(e.dataTransfer.getData('text/order-id'));
                                if (!Number.isFinite(orderId)) return;
                                const order = filteredPipelineOrders.find((item) => item.id === orderId);
                                if (!order) return;
                                if (String(order._deliveryDate || '') === day.key) return;
                                handlePipelineTransition(order, 'reschedule', null, { delivery_date: day.key });
                              }}
                            >
                              <header className="workflow-calendar-day-header">
                                <span className="workflow-calendar-day-number">{day.dayNumber}</span>
                                {day.orders.length > 0 && <span className="workflow-calendar-day-count">{day.orders.length}</span>}
                              </header>
                              <div className="workflow-calendar-day-items">
                                {visibleOrders.map((order) => {
                                  const busy = pipelineTransitioningIds.includes(order.id);
                                  const stageColors = getStageColors(order._stageName);
                                  const stageAge = getStageAgeDays(order);
                                  return (
                                    <article
                                      key={`calendar-order-${order.id}`}
                                      className={`workflow-calendar-item ${stageAge >= 7 ? 'is-overdue' : ''}`}
                                      style={{ borderLeftColor: stageColors.fg }}
                                      draggable={!busy}
                                      onDragStart={(e) => e.dataTransfer.setData('text/order-id', String(order.id))}
                                      onClick={() => setViewingOrder(order)}
                                    >
                                      <div className="workflow-calendar-item-title">
                                        <strong>#{order.order_number}</strong> {order.buyer_name || 'Unnamed buyer'}
                                      </div>
                                      <div className="workflow-calendar-item-meta">Stage: {order._stageName}</div>
                                    </article>
                                  );
                                })}
                                {hasMore && !isExpanded && (
                                  <button
                                    type="button"
                                    className="workflow-calendar-more"
                                    onClick={() => setExpandedCalendarDayKey(day.key)}
                                  >
                                    +{day.orders.length - 3} more
                                  </button>
                                )}
                                {hasMore && isExpanded && (
                                  <button
                                    type="button"
                                    className="workflow-calendar-more"
                                    onClick={() => setExpandedCalendarDayKey(null)}
                                  >
                                    Show less
                                  </button>
                                )}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                      {pipelineUnscheduledOrders.length > 0 && (
                        <section className="workflow-calendar-unscheduled">
                          <h4>Unscheduled</h4>
                          <div
                            className="workflow-calendar-unscheduled-items"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const orderId = Number(e.dataTransfer.getData('text/order-id'));
                              if (!Number.isFinite(orderId)) return;
                              const order = filteredPipelineOrders.find((item) => item.id === orderId);
                              if (!order) return;
                              if (!order._deliveryDate) return;
                              handlePipelineTransition(order, 'reschedule', null, { delivery_date: '' });
                            }}
                          >
                            {pipelineUnscheduledOrders.map((order) => {
                              const busy = pipelineTransitioningIds.includes(order.id);
                              return (
                                <article
                                  key={`calendar-unscheduled-${order.id}`}
                                  className="workflow-calendar-item unscheduled"
                                  draggable={!busy}
                                  onDragStart={(e) => e.dataTransfer.setData('text/order-id', String(order.id))}
                                  onClick={() => setViewingOrder(order)}
                                >
                                  <div className="workflow-calendar-item-title">
                                    <strong>#{order.order_number}</strong> {order.buyer_name || 'Unnamed buyer'}
                                  </div>
                                  <div className="workflow-calendar-item-meta">Stage: {order._stageName}</div>
                                </article>
                              );
                            })}
                          </div>
                        </section>
                      )}
                    </div>
                  )
                ) : (
                  <div className="pipeline-board" data-tour-id="workflow-board">
                    {pipelineStages.map((stage) => {
                      const stageOrders = filteredPipelineOrdersByStage[stage] || [];
                      return (
                        <div
                          key={stage}
                          className="pipeline-column"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const orderId = Number(e.dataTransfer.getData('text/order-id'));
                            const order = orders.find((item) => item.id === orderId);
                            if (order) {
                              handlePipelineTransition(order, 'set_stage', stage);
                            }
                          }}
                        >
                          <div className="pipeline-column-header">
                            <div className="pipeline-column-title">
                              <h4>{stage}</h4>
                            </div>
                          </div>
                          <div className="pipeline-column-count">
                            {stageOrders.length} orders
                          </div>
                          <div className="pipeline-card-list">
                            {stageOrders.length === 0 && <div className="pipeline-empty">No orders</div>}
                            {stageOrders.map((order) => {
                              const busy = pipelineTransitioningIds.includes(order.id);
                              const stageIndex = pipelineStages.indexOf(stage);
                              const canBack = stageIndex > 0;
                              const canNext = stageIndex >= 0 && stageIndex < pipelineStages.length - 1;
                              const stageAge = getStageAgeDays(order);
                              const overdue = stageAge >= 7;
                              const statusLabel = String(getOrderStatusLabel(order)).toLowerCase();
                              const isFinal = ['completed', 'cancelled', 'canceled'].includes(statusLabel);
                              const isEditingAssignee = pipelineEditingAssigneeId === order.id;
                              return (
                                <article
                                  key={order.id}
                                  className={`pipeline-card ${overdue ? 'pipeline-card-overdue' : ''} ${isFinal ? 'pipeline-card-muted' : ''}`}
                                  draggable={!busy}
                                  onDragStart={(e) => e.dataTransfer.setData('text/order-id', String(order.id))}
                                  onClickCapture={(e) => {
                                    if (e.target.closest('button,select,input,textarea')) return;
                                    setViewingOrder(order);
                                  }}
                                >
                                  <div className="pipeline-card-header">
                                    <div className="pipeline-card-title">Order #{order.order_number}</div>
                                    <div className="pipeline-card-header-actions">
                                      <label className="toggle-inline pipeline-select">
                                        <input
                                          type="checkbox"
                                          checked={selectedPipelineOrderIds.includes(order.id)}
                                          onChange={(e) => {
                                            setSelectedPipelineOrderIds((prev) => e.target.checked
                                              ? [...prev, order.id]
                                              : prev.filter((id) => id !== order.id));
                                          }}
                                        />
                                        Select
                                      </label>
                                      <div className="pipeline-action-menu" onClick={(e) => e.stopPropagation()}>
                                        <button
                                          type="button"
                                          className="btn-edit pipeline-btn pipeline-action-trigger"
                                          aria-label="More actions"
                                          title="More actions"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPipelineActionMenuOpenId((prev) => (prev === order.id ? null : order.id));
                                          }}
                                        >
                                          <MoreHorizontal className="btn-icon" size={16} />
                                        </button>
                                        {pipelineActionMenuOpenId === order.id && (
                                          <div className="pipeline-action-dropdown">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setPipelineActionMenuOpenId(null);
                                                handlePipelineTransition(order, 'block');
                                              }}
                                            >
                                              Block
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setPipelineActionMenuOpenId(null);
                                                handlePipelineTransition(order, 'unblock');
                                              }}
                                            >
                                              Unblock
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setPipelineActionMenuOpenId(null);
                                                handleViewPipelineHistory(order);
                                              }}
                                            >
                                              History
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {overdue && (
                                    <div className="pipeline-card-badges">
                                      <span className="pipeline-badge pipeline-badge-overdue">{stageAge}d overdue</span>
                                    </div>
                                  )}
                                  {(() => {
                                    const payload = parseOrderPayload(order.payload) || {};
                                    const delivery = payload.delivery_date || order.delivery_date || '';
                                    return (
                                      <div className="pipeline-card-meta-grid">
                                        <div>
                                          <span className="pipeline-card-meta-label">Buyer</span>
                                          <span className="pipeline-card-meta-value">{order.buyer_name || 'Unnamed buyer'}</span>
                                        </div>
                                        <div>
                                          <span className="pipeline-card-meta-label">Delivery</span>
                                          <span className="pipeline-card-meta-value">
                                            {delivery ? (
                                              <span className="pipeline-chip">{formatDateOnly(delivery, getCurrentTimeZone())}</span>
                                            ) : '—'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="pipeline-card-meta-label">Qty</span>
                                          <span className="pipeline-card-meta-value">{order.total_qty ?? 0}</span>
                                        </div>
                                        <div>
                                          <span className="pipeline-card-meta-label">Total</span>
                                          <span className="pipeline-card-meta-value">
                                            {Number(order.total_required || 0).toFixed(2)} kg
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  {order.blocked_reason && (
                                    <details className="pipeline-card-blocked" onClick={(e) => e.stopPropagation()}>
                                      <summary>
                                        <span className="pipeline-badge pipeline-badge-blocked">Blocked</span>
                                      </summary>
                                      <div className="pipeline-card-meta">{order.blocked_reason}</div>
                                    </details>
                                  )}
                                  <div className="pipeline-card-assignee">
                                    <span className="pipeline-card-meta-label">Assignee</span>
                                    {isEditingAssignee ? (
                                      <select
                                        className="select-input compact"
                                        value={order.assigned_user_id ?? ''}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const nextAssignee = e.target.value ? Number(e.target.value) : null;
                                          handlePipelineTransition(order, 'assign', null, { assigned_user_id: nextAssignee });
                                          setPipelineEditingAssigneeId(null);
                                        }}
                                        onBlur={() => setPipelineEditingAssigneeId(null)}
                                      >
                                        <option value="">Unassigned</option>
                                        {users.map((member) => (
                                          <option key={member.id} value={member.id}>{member.real_name || member.username}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className="pipeline-assignee-pill">
                                        <span className="pipeline-assignee-initials">
                                          {(() => {
                                            const name = usersById[String(order.assigned_user_id || '')]?.real_name
                                              || usersById[String(order.assigned_user_id || '')]?.username
                                              || 'Unassigned';
                                            return name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
                                          })()}
                                        </span>
                                        <span className="pipeline-assignee-name">
                                          {order.assigned_user_id
                                            ? (usersById[String(order.assigned_user_id)]?.real_name || usersById[String(order.assigned_user_id)]?.username || 'Assigned')
                                            : 'Unassigned'}
                                        </span>
                                        <button
                                          type="button"
                                          className="pipeline-assignee-edit"
                                          aria-label="Edit assignee"
                                          title="Edit assignee"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPipelineEditingAssigneeId(order.id);
                                          }}
                                        >
                                          <Pencil size={14} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="pipeline-card-actions" onClick={(e) => e.stopPropagation()}>
                                    <div className="pipeline-card-action-bar">
                                      <button
                                        type="button"
                                        className="pipeline-action-icon"
                                        disabled={busy || !canBack}
                                        aria-label="Previous stage"
                                        title="Previous stage"
                                        onClick={() => handlePipelineTransition(order, 'back')}
                                      >
                                        <ArrowLeft size={16} />
                                      </button>
                                      <button
                                        type="button"
                                        className="pipeline-action-primary"
                                        disabled={busy || !canNext}
                                        onClick={() => handlePipelineTransition(order, 'next')}
                                      >
                                        <span>Next</span>
                                        <ArrowRight size={16} />
                                      </button>
                                      <button
                                        type="button"
                                        className="pipeline-action-icon"
                                        disabled={busy}
                                        aria-label="Complete"
                                        title="Complete"
                                        onClick={() => handlePipelineTransition(order, 'complete')}
                                      >
                                        <CheckCircle2 size={16} />
                                      </button>
                                      <button
                                        type="button"
                                        className="pipeline-action-icon danger"
                                        disabled={busy}
                                        aria-label="Cancel"
                                        title="Cancel"
                                        onClick={() => handlePipelineTransition(order, 'cancel')}
                                      >
                                        <XCircle size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'insights' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="insights-section" role="tabpanel" id="panel-insights" aria-labelledby="tab-insights" data-tour-id="insights-overview">
                <div className="stock-toolbar stock-toolbar-header">
                  <h2>Insights</h2>
                  <div className="stock-header-actions">
                    <select
                      className="select-input"
                      value={insightsRange}
                      onChange={(e) => setInsightsRange(e.target.value)}
                    >
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                    </select>
                  </div>
                </div>
                <div className="insights-filter-bar">
                  <select
                    className="select-input"
                    value={insightsRawMaterialFilter}
                    onChange={(e) => {
                      setInsightsRawMaterialFilter(e.target.value);
                      setInsightsCharacteristicFilter('');
                    }}
                  >
                    <option value="">All raw materials</option>
                    {insightsRawMaterialOptions.map((option) => (
                      <option key={`insight-material-${option}`} value={option}>{option}</option>
                    ))}
                  </select>
                  <select
                    className="select-input"
                    value={insightsCharacteristicFilter}
                    disabled={!insightsRawMaterialFilter}
                    onChange={(e) => setInsightsCharacteristicFilter(e.target.value)}
                  >
                    <option value="">All characteristic combinations</option>
                    {insightsCharacteristicOptions.map((option) => (
                      <option key={`insight-characteristics-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    className="select-input"
                    value={insightsDeadStockDays}
                    onChange={(e) => setInsightsDeadStockDays(e.target.value)}
                  >
                    <option value="30">Dead stock: 30+ days</option>
                    <option value="60">Dead stock: 60+ days</option>
                    <option value="90">Dead stock: 90+ days</option>
                    <option value="120">Dead stock: 120+ days</option>
                  </select>
                </div>
                <p className="helper-text insights-scope-note">
                  Scope: <strong>{insightsSelectionSummary}</strong>
                </p>

                <div className="summary-cards insights-kpi-cards">
                  <div className="summary-card">
                    <div className="summary-value">{insightsKpis.totalIn.toFixed(2)}</div>
                    <div className="summary-label">Inbound ({insightsRange})</div>
                  </div>
                  <div className="summary-card summary-out">
                    <div className="summary-value">{insightsKpis.totalOut.toFixed(2)}</div>
                    <div className="summary-label">Outbound ({insightsRange})</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">{insightsKpis.netFlow.toFixed(2)}</div>
                    <div className="summary-label">Net Flow</div>
                  </div>
                  <div className="summary-card summary-low">
                    <div className="summary-value">{formatInsightsCurrency(deadStockTotals.estimatedValue)}</div>
                    <div className="summary-label">Dead Stock Value</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">{deadStockTotals.count}</div>
                    <div className="summary-label">Dead Stock Items</div>
                  </div>
                  <div className="summary-card summary-low">
                    <div className="summary-value">{insightsKpis.ordersOpen}</div>
                    <div className="summary-label">Open Orders</div>
                  </div>
                </div>

                <div className="insights-grid">
                  <section className="section-card insights-card">
                    <div className="card-title-row">
                      <h3>Stock Flow Trend</h3>
                    </div>
                    {insightsTotalSeries.length === 0 ? (
                      <div className="empty-state">No movement data in selected range.</div>
                    ) : (
                      <div className="insights-line-wrap">
                        {insightsLineChart && (
                          <>
                            <svg
                              className="insights-line-chart"
                              viewBox={`0 0 ${insightsLineChart.width} ${insightsLineChart.height}`}
                              preserveAspectRatio="none"
                              role="img"
                              aria-label="Stock flow trend"
                            >
                              {insightsLineChart.axisTicks.map((tick) => (
                                <g key={`insight-line-tick-${tick.value}`}>
                                  <line
                                    className={tick.value === 0 ? 'insights-line-grid zero' : 'insights-line-grid'}
                                    x1="46"
                                    x2={String(insightsLineChart.width - 16)}
                                    y1={tick.y}
                                    y2={tick.y}
                                  />
                                  <text x="8" y={tick.y + 4} className="insights-line-axis-label">
                                    {tick.value.toFixed(0)}
                                  </text>
                                </g>
                              ))}
                              {insightsLineChart.xAxisLabels.map((label) => (
                                <text
                                  key={`insight-line-label-${label.key}`}
                                  x={label.x}
                                  y={insightsLineChart.height - 8}
                                  textAnchor="middle"
                                  className="insights-line-axis-label"
                                >
                                  {label.label}
                                </text>
                              ))}
                              <polyline points={insightsLineChart.points} />
                            </svg>
                            <div className="helper-text">
                              Latest net flow on {insightsTotalSeries[insightsTotalSeries.length - 1]?.date}: <strong>{Number(insightsTotalSeries[insightsTotalSeries.length - 1]?.total || 0).toFixed(2)}</strong>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </section>

                  <section className="section-card insights-card">
                    <div className="card-title-row">
                      <h3>Top Raw Material Consumption</h3>
                    </div>
                    {insightsTopConsumedRaw.length === 0 ? (
                      <div className="empty-state">No raw material outbound activity in selected range.</div>
                    ) : (
                      <div className="insights-bars">
                        {(() => {
                          const maxValue = Math.max(1, ...insightsTopConsumedRaw.map((item) => item.quantity));
                          return insightsTopConsumedRaw.map((item) => (
                            <div key={`insight-rm-${item.label}`} className="insights-bar-row compact">
                              <span className="insights-bar-label">{item.label}</span>
                              <div className="insights-bar-track">
                                <div className="insights-bar rm" style={{ width: `${(item.quantity / maxValue) * 100}%` }} />
                              </div>
                              <strong className="insights-bar-value">{item.quantity.toFixed(2)}</strong>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </section>

                  <section className="section-card insights-card">
                    <div className="card-title-row">
                      <h3>Fulfilment Snapshot</h3>
                    </div>
                    {insightsStageCounts.length === 0 ? (
                      <div className="empty-state">No orders available.</div>
                    ) : (
                      <div className="insights-bars">
                        {(() => {
                          const maxValue = Math.max(1, ...insightsStageCounts.map((item) => item.count));
                          return insightsStageCounts.map((item) => (
                            <div key={`insight-stage-${item.stage}`} className="insights-bar-row">
                              <span className="insights-bar-label">{item.stage}</span>
                              <div className="insights-bar-track">
                                <div className="insights-bar stage" style={{ width: `${(item.count / maxValue) * 100}%` }} />
                              </div>
                              <strong className="insights-bar-value">{item.count}</strong>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                    <div className="insights-split">
                      {insightsLowCriticalSplit.map((item) => (
                        <div key={`insight-split-${item.label}`} className="insights-split-pill">
                          <span>{item.label}</span>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="section-card insights-card">
                    <div className="card-title-row">
                      <h3>Action Queue</h3>
                    </div>
                    <div className="insights-action-stack">
                      <div className="insights-action-card">
                        <strong>Reorder now</strong>
                        {reorderRecommendations.length === 0 ? (
                          <span className="helper-text">No immediate reorder recommendations.</span>
                        ) : (
                          <div className="insights-bars">
                            {reorderRecommendations.slice(0, 5).map((item) => (
                              <div key={`reorder-${item.name}-${item.characteristicsKey}`} className="insights-bar-row compact">
                                <span className="insights-bar-label">{item.name}</span>
                                <strong className="insights-bar-value">{Number(item.recommendedQty || 0).toFixed(2)}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="insights-action-card">
                        <strong>Dead stock watch</strong>
                        {deadStockItems.length === 0 ? (
                          <span className="helper-text">No dead stock found for the selected threshold.</span>
                        ) : (
                          <div className="insights-action-list">
                            {deadStockItems.slice(0, 3).map((item) => (
                              <div key={`dead-stock-${item.name}-${item.characteristicsKey}`} className="insights-action-row">
                                <span>{item.name}</span>
                                <strong>{item.lastMovementDate || 'No movement'}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="insights-action-card">
                        <strong>Coverage risk</strong>
                        {coverageForecastRows.length === 0 ? (
                          <span className="helper-text">No coverage data available.</span>
                        ) : (
                          <div className="insights-action-list">
                            {coverageForecastRows.slice(0, 3).map((item) => (
                              <div key={`coverage-${item.name}-${item.characteristicsKey}`} className="insights-action-row">
                                <span>{item.name}</span>
                                <strong>{item.coverageDays === null ? 'No outflow' : `${item.coverageDays.toFixed(1)}d`}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeTab === 'stock' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="stock-section" role="tabpanel" id="panel-stock" aria-labelledby="tab-stock" data-tour-id="stock-overview-panel">
                <div className="stock-toolbar stock-toolbar-header">
                  <h2>Stock Overview</h2>
                  <div className="stock-header-actions">
                    <button
                      onClick={() => handleExportStock(stockRows, {
                        mode: isFinishedGoodsStock ? 'finished_goods' : (isRawMaterialStock ? 'raw_material' : 'main')
                      })}
                      className="btn-edit"
                      style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                      disabled={stockRows.length === 0}
                    >
                      <FileDown className="btn-icon" size={16} />
                      <span>Export CSV (Excel)</span>
                    </button>
                    {!isFinishedGoodsStock && (
                      <button
                        type="button"
                        className="btn-edit"
                        style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={() => openCsvImport('opening_stock', 'raw_material')}
                      >
                        <Plus className="btn-icon" size={16} />
                        <span>Import Opening Stock CSV</span>
                      </button>
                    )}
                  </div>
                </div>
                <div className={`summary-cards stock-summary-cards ${stockSummaryExpanded ? 'is-open' : ''}`}>
                  <div className="summary-card summary-total">
                    <div className="summary-value">{totalStockQuantity.toFixed(2)}</div>
                    <div className="summary-label">
                      Total Quantity ({isRawMaterialStock ? rawMaterialUnitLabel : (isFinishedGoodsStock ? finishedGoodsQuantityUnit : quantityUnit)})
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">{stockCounts.total}</div>
                    <div className="summary-label">
                      {isFinishedGoodsStock ? 'Total Products' : (isRawMaterialStock ? 'Total Combinations' : 'Total Groups')}
                    </div>
                  </div>
                  <div className="summary-card summary-low">
                    <div className="summary-value">{stockCounts.low}</div>
                    <div className="summary-label">Low</div>
                  </div>
                  <div className="summary-card summary-critical">
                    <div className="summary-value">{stockCounts.critical}</div>
                    <div className="summary-label">Critical</div>
                  </div>
                  <div className="summary-card summary-out">
                    <div className="summary-value">{stockCounts.outOfStock}</div>
                    <div className="summary-label">Out of Stock</div>
                  </div>
                </div>
                <div className="stock-controls-sticky">
                  <div className="section-header inventory-mode-header stock-mode-header">
                    <div className="stock-mode-switch subtabs transaction-mode-switch" role="tablist" aria-label="Stock type">
                      <button
                        type="button"
                        className={`raw ${stockOverviewMode === 'raw_materials' ? 'active' : ''}`}
                        onClick={() => setStockOverviewMode('raw_materials')}
                      >
                        <Factory className="btn-icon" size={21} />
                        <span>Raw Materials</span>
                      </button>
                      <button
                        type="button"
                        className={`finished ${stockOverviewMode === 'finished_goods' ? 'active' : ''}`}
                        onClick={() => setStockOverviewMode('finished_goods')}
                      >
                        <Package className="btn-icon" size={21} />
                        <span>Finished Goods</span>
                      </button>
                    </div>
                  </div>
                  <div className="stock-filters">
                    {stockOverviewMode !== 'finished_goods' && rawMaterialOptions.length > 0 && (
                      <select
                        value={rawMaterialStockType}
                        onChange={(e) => setRawMaterialStockType(e.target.value)}
                        className="select-input"
                      >
                        {rawMaterialOptions.map((item) => (
                          <option key={item.name} value={item.name}>
                            {item.is_main ? `Main: ${item.name}` : item.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {!isFinishedGoodsStock && !isRawMaterialStock && groupingFields.map((field) => {
                      const options = Array.from(
                        new Set(
                          stock.map((row) => row.group?.[field.key] || 'Unassigned')
                        )
                      ).sort();
                      return (
                        <select
                          key={field.key}
                          value={stockFilters[field.key] || ''}
                          onChange={(e) =>
                            setStockFilters((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          className="select-input"
                        >
                          <option value="">All {field.label}</option>
                          {options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      );
                    })}
                    <select
                      value={stockSort}
                      onChange={(e) => setStockSort(e.target.value)}
                      className="select-input"
                    >
                      <option value="none">Sort: Default</option>
                      <option value="high">Sort: Stock high → low</option>
                      <option value="low">Sort: Stock low → high</option>
                    </select>
                    <div className="search-field">
                      <span className="search-field-icon" aria-hidden="true">
                        <Search size={14} />
                      </span>
                      <input
                        type="text"
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        placeholder={
                          isFinishedGoodsStock
                            ? 'Search finished goods...'
                            : (isRawMaterialStock ? 'Search combinations...' : 'Search stock groups...')
                        }
                        className="list-search"
                      />
                      {stockSearch && (
                        <button
                          type="button"
                          className="search-clear"
                          onClick={() => setStockSearch('')}
                          aria-label="Clear search"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {(isFinishedGoodsStock || isRawMaterialStock) && (
                      <label className="toggle-inline">
                        <input
                          type="checkbox"
                          checked={includeSetupOnlyStock}
                          onChange={(e) => setIncludeSetupOnlyStock(e.target.checked)}
                        />
                        Include setup-only items
                      </label>
                    )}
                    </div>
                  </div>
                {isFinishedGoodsStock ? (
                  filteredFinishedGoodsStock.length === 0 ? (
                    renderNextStepEmptyState({
                      title: 'No finished goods stock yet.',
                      description: 'Complete orders to generate finished goods stock entries.',
                      actionLabel: 'View Orders',
                      onAction: () => setActiveTab('orders')
                    })
                  ) : (
                    <div className="table-wrapper">
                      <table className="data-table stock-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Current Stock ({finishedGoodsQuantityUnit})</th>
                            <th>Completed Orders</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFinishedGoodsStock.map((row, rowIndex) => (
                            <tr key={`fg-stock-${rowIndex}`}>
                              <td>{row.group?.product_name || 'Unnamed Product'}</td>
                              <td style={getStockLevelStyle(row.current_stock, settings)}>
                                {Number(row.current_stock || 0).toFixed(2)}
                              </td>
                              <td>{Number(row.completed_orders || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : isRawMaterialStock ? (
                  rawMaterialStockData.length === 0 ? (
                    renderNextStepEmptyState({
                      title: 'No raw material stock data yet.',
                      description: 'Add inbound transactions for this raw material type to start stock tracking.',
                      actionLabel: 'Add Transaction',
                      onAction: () => setActiveTab('add')
                    })
                  ) : (
                    <div className="table-wrapper">
                      <table className="data-table stock-table">
                        <thead>
                          <tr>
                            {selectedRawMaterialOption?.characteristics?.map((characteristic) => (
                              <th key={`stock-char-${characteristic.list_key}`}>{characteristic.name}</th>
                            ))}
                            <th>Current Stock ({rawMaterialUnitLabel})</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRawMaterialStock.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {selectedRawMaterialOption?.characteristics?.map((characteristic) => (
                                <td key={`stock-char-value-${rowIndex}-${characteristic.list_key}`}>
                                  {row.group?.characteristics?.[characteristic.name] || '-'}
                                </td>
                              ))}
                              <td style={getStockLevelStyle(row.current_stock, settings)}>
                                {Number(row.current_stock || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : groupingFields.length === 0 ? (
                  <div className="empty-state">No grouping fields selected in the company setup.</div>
                ) : stock.length === 0 ? (
                  renderNextStepEmptyState({
                    title: 'No stock data yet.',
                    description: 'Record your first stock movement to populate inventory totals.',
                    actionLabel: 'Add Transaction',
                    onAction: () => setActiveTab('add')
                  })
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table stock-table">
                      <thead>
                        <tr>
                          {groupingFields.map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                          <th>Current Stock ({quantityUnit})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStock.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {groupingFields.map((field) => (
                              <td key={field.key}>{row.group?.[field.key] ?? 'Unassigned'}</td>
                            ))}
                            <td style={getStockLevelStyle(row.current_stock, settings)}>
                              {Number(row.current_stock || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'order' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="card" data-tour-id="create-order-form">
                <div className="section-header">
                  <h2>Create Order</h2>
                  <div>
                    <button type="button" className="btn-edit" onClick={handleAddOrderProductCard}>
                      <Plus className="btn-icon" size={16} />
                      <span>Add Product</span>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleCreateOrder}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Order Number *</label>
                      <input
                        type="number"
                        min="1"
                        className={orderFormErrors.order_number ? 'field-error' : ''}
                        value={orderForm.order_number}
                        onChange={(e) => {
                          setOrderForm((prev) => ({ ...prev, order_number: e.target.value }));
                          setOrderFormErrors((prev) => {
                            const next = { ...prev };
                            delete next.order_number;
                            return next;
                          });
                        }}
                        placeholder={orderNumberLoading ? 'Loading...' : 'Order number'}
                        required
                      />
                      {orderFormErrors.order_number && <small className="form-error-text">{orderFormErrors.order_number}</small>}
                    </div>
                    <div className="form-group">
                      <label>Buyer *</label>
                      <input
                        type="text"
                        list="customer-options"
                        className={orderFormErrors.buyer_name ? 'field-error' : ''}
                        value={orderForm.buyer_name}
                        onChange={(e) => {
                          setOrderForm((prev) => ({ ...prev, buyer_name: e.target.value }));
                          setOrderFormErrors((prev) => {
                            const next = { ...prev };
                            delete next.buyer_name;
                            return next;
                          });
                        }}
                        placeholder="Search or type buyer"
                        required
                      />
                      {orderFormErrors.buyer_name && <small className="form-error-text">{orderFormErrors.buyer_name}</small>}
                      <datalist id="customer-options">
                        {customerOptions.map((customer) => (
                          <option key={customer.id} value={customer.label} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group">
                      <label>Delivery Date *</label>
                      <input
                        type="date"
                        className={orderFormErrors.delivery_date ? 'field-error' : ''}
                        value={orderForm.delivery_date}
                        onChange={(e) => {
                          setOrderForm((prev) => ({ ...prev, delivery_date: e.target.value }));
                          setOrderFormErrors((prev) => {
                            const next = { ...prev };
                            delete next.delivery_date;
                            return next;
                          });
                        }}
                        required
                      />
                      {orderFormErrors.delivery_date && <small className="form-error-text">{orderFormErrors.delivery_date}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Products</label>
                    {orderFormErrors.products && <small className="form-error-text">{orderFormErrors.products}</small>}
                    {orderFormErrors.bom && <small className="form-error-text">{orderFormErrors.bom}</small>}
                    <div className="draft-list">
                      {orderProducts.map((entry, index) => {
                        const summary = orderProductSummaries[index];
                        return (
                          <div key={`order-product-${index}`} className="draft-card" style={{ padding: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div className="form-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
                                <div className="form-group">
                                  <label>Product Type *</label>
                                  <input
                                    type="text"
                                    list={`product-options-${index}`}
                                    className={orderFormErrors.products ? 'field-error' : ''}
                                    value={
                                      productCatalogWithPayload.find((product) => String(product.id) === String(entry.product_id))?.name
                                      || entry.product_id
                                      || ''
                                    }
                                    onChange={(e) => {
                                      const selected = productCatalogWithPayload.find(
                                        (product) => product.name.toLowerCase() === e.target.value.toLowerCase().trim()
                                      );
                                      updateOrderProduct(index, { product_id: selected ? String(selected.id) : '' });
                                    }}
                                    placeholder="Search product"
                                    required
                                  />
                                  <datalist id={`product-options-${index}`}>
                                    {productCatalogWithPayload.map((product) => (
                                      <option key={product.id} value={product.name} />
                                    ))}
                                  </datalist>
                                </div>
                                <div className="form-group">
                                  <label>Units *</label>
                                  <input
                                    type="number"
                                    min="1"
                                    className={orderFormErrors.products ? 'field-error' : ''}
                                    value={entry.quantity}
                                    onChange={(e) => updateOrderProduct(index, { quantity: e.target.value })}
                                    required
                                  />
                                </div>
                              </div>

                              {summary?.selected ? (
                                <div className="availability-panel" style={{ marginBottom: 0 }}>
                                  <strong>Raw Materials for {summary.selected.name}</strong>
                                  <p className="helper-text calculation-note">
                                    Line math: <strong>Total Required = Per Unit Qty x Units</strong>.
                                  </p>
                                  {summary.rawMaterials.length === 0 ? (
                                    <div className="draft-meta" style={{ marginTop: '8px' }}>No raw material mapping found for this product.</div>
                                  ) : (
                                    <div className="table-wrapper" style={{ marginTop: '10px' }}>
                                      <table className="data-table">
                                        <thead>
                                          <tr>
                                            <th>Raw Material</th>
                                            <th>Selections</th>
                                            <th>Per Unit</th>
                                            <th>Total ({summary.quantity} units)</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {summary.rawMaterials.map((raw) => (
                                            <tr key={raw.key}>
                                              <td>{raw.raw_material_name}</td>
                                              <td>
                                                {Object.entries(raw.characteristics || {}).map(([key, value]) => `${key}: ${value}`).join(', ') || '—'}
                                              </td>
                                              <td>{Number(raw.perProductQty || 0).toFixed(2)}</td>
                                              <td>{Number(raw.totalQty || 0).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="draft-meta">Select a product to view required raw materials.</div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="draft-delete"
                              onClick={() => handleRemoveOrderProductCard(index)}
                              disabled={orderProducts.length === 1}
                              aria-label="Remove product"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="calculation-summary">
                      <strong>Order calculation</strong>
                      <p>
                        Order Total Required = Sum of all product raw material line totals.
                      </p>
                      <p>
                        Draft total: {orderDraftTotals.totalUnits} units, {orderDraftTotals.totalRequired.toFixed(2)} kg required.
                      </p>
                    </div>
                    <button type="submit" className="btn-primary">
                      <Plus className="btn-icon" size={16} />
                      <span>Create Order</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="orders-section" data-tour-id="orders-overview">
                <div className="section-header">
                  <h2>Orders</h2>
                  <div className="order-header-actions">
                    <div className="order-search">
                      <span className="order-search-icon" aria-hidden="true">
                        <Search size={14} />
                      </span>
                      <input
                        type="text"
                        value={orderSearch}
                        onChange={(e) => setOrderSearch(e.target.value)}
                        placeholder="Search orders"
                      />
                    </div>
                  </div>
                </div>
                <div className="order-summary-bar">
                  <div className="order-summary-pill">
                    <span>In Progress</span>
                    <strong>{orderStatusCounts.in_progress}</strong>
                  </div>
                  <div className="order-summary-pill is-overdue">
                    <span>Overdue Delivery</span>
                    <strong>{orderFulfilmentSummary.overdue}</strong>
                  </div>
                  <div className="order-summary-pill">
                    <span>Due in 3 Days</span>
                    <strong>{orderFulfilmentSummary.dueSoon}</strong>
                  </div>
                  <div className="order-summary-pill is-completed">
                    <span>Completed</span>
                    <strong>{orderStatusCounts.completed}</strong>
                  </div>
                  <div className="order-summary-pill is-cancelled">
                    <span>Cancelled</span>
                    <strong>{orderStatusCounts.cancelled}</strong>
                  </div>
                  <div className="order-summary-pill">
                    <span>FG Remaining</span>
                    <strong>{orderFulfilmentSummary.finishedRemaining.toFixed(2)}</strong>
                  </div>
                  <button
                    type="button"
                    className="summary-pill-action"
                    onClick={() => setHoldOverviewOpen(true)}
                  >
                    <span>Hold Remaining</span>
                    <strong>{orderFulfilmentSummary.holdRemaining.toFixed(2)}</strong>
                  </button>
                  <div className="order-summary-actions">
                    <button type="button" className="btn-ghost" onClick={handleExportOrdersCsv}>
                      <Download className="btn-icon" size={16} />
                      <span>Export CSV (Excel)</span>
                    </button>
                  </div>
                </div>
                <div className="order-filters">
                  <div className="order-filter">
                    <label>Status</label>
                    <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="order-filter">
                    <label>Stage</label>
                    <select value={orderStageFilter} onChange={(e) => setOrderStageFilter(e.target.value)}>
                      <option value="all">All</option>
                      {pipelineStages.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="order-filter">
                    <label>Buyer</label>
                    <input
                      type="text"
                      value={orderBuyerFilter}
                      onChange={(e) => setOrderBuyerFilter(e.target.value)}
                      placeholder="Search buyer"
                    />
                  </div>
                  <div className="order-filter">
                    <label>Date From</label>
                    <input type="date" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)} />
                  </div>
                  <div className="order-filter">
                    <label>Date To</label>
                    <input type="date" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)} />
                  </div>
                  <div className="order-filter">
                    <label>Sort By</label>
                    <select value={orderSort} onChange={(e) => setOrderSort(e.target.value)}>
                      <option value="created_desc">Created (Newest)</option>
                      <option value="created_asc">Created (Oldest)</option>
                      <option value="order_number_desc">Order # (High → Low)</option>
                      <option value="order_number_asc">Order # (Low → High)</option>
                      <option value="buyer_asc">Buyer (A → Z)</option>
                      <option value="buyer_desc">Buyer (Z → A)</option>
                      <option value="total_qty_desc">Total Qty (High → Low)</option>
                      <option value="total_qty_asc">Total Qty (Low → High)</option>
                    </select>
                  </div>
                </div>
                {cancelOrderTarget && (
                  <div className="modal-overlay" onClick={() => setCancelOrderTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Cancel Order #{cancelOrderTarget.order_number}?</h3>
                      <p className="modal-text">This action cannot be undone.</p>
                      <div className="modal-actions">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => setCancelOrderTarget(null)}
                          style={{ background: '#e3f6ec', borderColor: '#b7e6c9', color: '#1f7a3a' }}
                        >
                          <CheckCircle2 className="btn-icon" size={16} />
                          <span>Keep Order</span>
                        </button>
                        <button
                          type="button"
                          className="btn-danger"
                          onClick={confirmCancelOrder}
                          style={{ background: '#ffe4e6', borderColor: '#f5b3b8', color: '#b42318' }}
                        >
                          <XCircle className="btn-icon" size={16} />
                          <span>Cancel Order</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {completeOrderTarget && (
                  <div className="modal-overlay" onClick={() => setCompleteOrderTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Mark Order #{completeOrderTarget.order_number} as completed?</h3>
                      <p className="modal-text">This will finalize the order.</p>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setCompleteOrderTarget(null)}>
                          <X className="btn-icon" size={16} />
                          <span>Keep Order</span>
                        </button>
                        <button type="button" className="btn-primary" onClick={confirmCompleteOrder}>
                          <CheckCircle2 className="btn-icon" size={16} />
                          <span>Mark Completed</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {orderNotesTarget && (
                  <div className="modal-overlay" onClick={() => setOrderNotesTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Order Notes #{orderNotesTarget.order_number}</h3>
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          rows={5}
                          value={orderNotesText}
                          onChange={(e) => setOrderNotesText(e.target.value)}
                          placeholder="Add notes for this order"
                        />
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setOrderNotesTarget(null)}>
                          <X className="btn-icon" size={16} />
                          <span>Cancel</span>
                        </button>
                        <button type="button" className="btn-primary" onClick={handleSaveOrderNotes}>
                          <CheckCircle2 className="btn-icon" size={16} />
                          <span>Save Notes</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {sortedOrders.length === 0 ? (
                  renderNextStepEmptyState({
                    title: 'No orders yet.',
                    description: 'No orders are available yet. Use Create Order from the sidebar to start workflow and delivery tracking.'
                  })
                ) : (
                  <>
                    <p className="helper-text orders-table-note">
                      This view is now fulfillment-first. Open an order row for cost and hold detail; use the board when you need active stage control.
                    </p>
                    <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Buyer</th>
                          <th>Stage</th>
                          <th>Delivery</th>
                          <th>Status</th>
                          <th>Total Qty</th>
                          <th>Fulfilment</th>
                          <th>Updated</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedOrders.map((order) => {
                          const statusLabel = getOrderStatusLabel(order);
                          const isFinal = ['completed', 'cancelled', 'canceled'].includes(String(statusLabel).toLowerCase());
                          const currentStage = order.pipeline_stage || '';
                          const stageIndex = pipelineStages.indexOf(currentStage);
                          const canNext = !isFinal && stageIndex >= 0 && stageIndex < pipelineStages.length - 1;
                          const payload = parseOrderPayload(order.payload) || {};
                          const deliveryDate = payload.delivery_date || order.delivery_date || '';
                          const todayKey = toLocalDateKey(new Date());
                          const deliveryKey = String(deliveryDate).slice(0, 10);
                          const deliveryIsLate = deliveryKey && !isFinal && deliveryKey < todayKey;
                          return (
                          <tr
                            key={order.id}
                            onClick={() => setViewingOrder(order)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{order.order_number}</td>
                            <td>{order.buyer_name || '-'}</td>
                            <td>
                              <span className="status-pill ok">{currentStage || 'Not set'}</span>
                            </td>
                            <td>
                              {deliveryDate ? (
                                <span className={`status-pill ${deliveryIsLate ? 'warn' : 'ok'}`}>
                                  {formatDateOnly(deliveryDate, getCurrentTimeZone())}
                                </span>
                              ) : '—'}
                            </td>
                            <td>
                              {(() => {
                                const statusLabel = getOrderStatusLabel(order);
                                const palette = getStageColors(statusLabel);
                                return (
                                  <span
                                    className="order-stage-chip"
                                    style={{ background: palette.bg, color: palette.fg }}
                                  >
                                    {statusLabel}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              {Number(order.total_qty || 0).toFixed(2)}
                            </td>
                            <td>
                              <div className="order-fulfilment-cell">
                                <span>FG {Number(order.finished_hold_remaining_total || 0).toFixed(2)}</span>
                                <span>Hold {Number(order.hold_remaining_total || 0).toFixed(2)}</span>
                              </div>
                            </td>
                            <td>{formatDateTime(order.updated_at, getCurrentTimeZone())}</td>
                            <td>
                              <div className="order-row-actions">
                                {canNext && (
                                  <button
                                    type="button"
                                    className="btn-link order-action-next"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePipelineTransition(order, 'next');
                                    }}
                                  >
                                    <ArrowRight className="btn-icon" size={14} />
                                    <span>Next Stage</span>
                                  </button>
                                )}
                                {!isFinal && (
                                  <button
                                    type="button"
                                    className="btn-link order-action-complete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCompleteOrder(order);
                                    }}
                                  >
                                    <CheckCircle2 className="btn-icon" size={14} />
                                    <span>Complete</span>
                                  </button>
                                )}
                                {!isFinal && (
                                  <button
                                    type="button"
                                    className="btn-link order-action-cancel"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelOrder(order);
                                    }}
                                  >
                                    <XCircle className="btn-icon" size={14} />
                                    <span>Cancel</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'transactions' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="transactions-section" data-tour-id="movement-history-table">
                <div className="section-header movement-ledger-header">
                  <div>
                    <h2>Material Movement Ledger</h2>
                    <p className="movement-ledger-subtitle">Verification-first ledger for stock entries, challans, and operator activity.</p>
                  </div>
                  <div className="movement-ledger-utility-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => openCsvImport('transactions', transactionLedgerMode === 'finished_goods' ? 'finished_goods' : 'raw_material')}
                    >
                      <Plus className="btn-icon" size={16} />
                      <span>Import CSV</span>
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleExportTransactionsCsv} disabled={filteredTransactions.length === 0}>
                      <FileDown className="btn-icon" size={16} />
                      <span>Export CSV</span>
                    </button>
                  </div>
                </div>
                <div className="movement-ledger-summary">
                  <div className="workflow-summary-pills history-summary-pills">
                    <div className="workflow-summary-pill is-completed">
                      <span>Inbound</span>
                      <strong>{filteredTransactionSummary.inbound.toFixed(2)}</strong>
                    </div>
                    <div className="workflow-summary-pill">
                      <span>Outbound</span>
                      <strong>{filteredTransactionSummary.outbound.toFixed(2)}</strong>
                    </div>
                    <div className="workflow-summary-pill is-overdue">
                      <span>Hold</span>
                      <strong>{filteredTransactionSummary.hold.toFixed(2)}</strong>
                    </div>
                    <div className="workflow-summary-pill">
                      <span>Entries</span>
                      <strong>{filteredTransactions.length}</strong>
                    </div>
                  </div>
                  <div className="workflow-summary-pills history-summary-pills movement-ledger-meta-pills">
                    <div className="workflow-summary-pill">
                      <span>With Challan</span>
                      <strong>{filteredTransactionSummary.withChallan}</strong>
                    </div>
                    <div className="workflow-summary-pill">
                      <span>Notes</span>
                      <strong>{filteredTransactionSummary.withNotes}</strong>
                    </div>
                  </div>
                </div>
                <div className="movement-ledger-filter-shell">
                  <div className="movement-ledger-filter-grid">
                    {transactionLedgerMode === 'finished_goods' ? (
                      <div className="form-group">
                        <label>Finished Product</label>
                        <select
                          value={transactionFinishedProductFilter}
                          onChange={(e) => setTransactionFinishedProductFilter(e.target.value)}
                        >
                          <option value="">All finished products</option>
                          {finishedGoodsLedgerOptions.map((name) => (
                            <option key={`txn-finished-filter-${name}`} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Raw Material Type</label>
                        <select
                          value={transactionRawMaterialFilter}
                          onChange={(e) => {
                            setTransactionRawMaterialFilter(e.target.value);
                            setTransactionRawCharacteristicFilters({});
                          }}
                        >
                          <option value="">Select raw material type</option>
                          {rawMaterialOptions.map((item) => (
                            <option key={`txn-filter-${item.name}`} value={item.name}>{item.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Direction</label>
                      <select value={transactionTypeFilter} onChange={(e) => setTransactionTypeFilter(e.target.value)}>
                        <option value="all">All</option>
                        <option value="in">IN</option>
                        <option value="out">OUT</option>
                        <option value="hold">HOLD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Action</label>
                      <select value={transactionRawActionFilter} onChange={(e) => setTransactionRawActionFilter(e.target.value)}>
                        <option value="all">All actions</option>
                        {transactionActionFilterOptions.map((action) => (
                          <option key={`txn-action-filter-${action.key}`} value={action.key}>{action.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date From</label>
                      <input
                        type="date"
                        value={transactionDateFromFilter}
                        onChange={(e) => setTransactionDateFromFilter(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date To</label>
                      <input
                        type="date"
                        value={transactionDateToFilter}
                        onChange={(e) => setTransactionDateToFilter(e.target.value)}
                      />
                    </div>
                    <div className="form-group movement-ledger-reset-cell">
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => {
                          setTransactionTypeFilter('all');
                          setTransactionRawActionFilter('all');
                          setTransactionFinishedProductFilter('');
                          setTransactionRawMaterialFilter('');
                          setTransactionDateFromFilter('');
                          setTransactionDateToFilter('');
                          setTransactionRawCharacteristicFilters({});
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                  {transactionLedgerCharacteristics.length > 0 && (
                    <div className="movement-ledger-filter-grid movement-ledger-filter-grid-secondary">
                      {transactionLedgerCharacteristics.map((characteristic) => (
                        <div key={`txn-filter-char-${characteristic.list_key}`} className="form-group">
                          <label>{characteristic.name}</label>
                          <select
                            value={transactionRawCharacteristicFilters[characteristic.name] || ''}
                            onChange={(e) =>
                              setTransactionRawCharacteristicFilters((prev) => ({
                                ...prev,
                                [characteristic.name]: e.target.value
                              }))
                            }
                          >
                            <option value="">All {characteristic.name}</option>
                            {getCharacteristicValues(characteristic.list_key).map((value) => (
                              <option key={`txn-filter-${characteristic.list_key}-${value}`} value={value}>{value}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {activeLedgerFilterChips.length > 0 && (
                  <div className="movement-ledger-active-filters">
                    {activeLedgerFilterChips.map((chip) => (
                      <span key={chip} className="movement-ledger-filter-chip">{chip}</span>
                    ))}
                  </div>
                )}
                {viewingNote && (
                  <div className="modal-overlay" role="presentation" onClick={() => setViewingNote(null)}>
                    <div className="modal" role="dialog" aria-modal="true" aria-label="Transaction notes" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
                      <h3>Transaction Notes</h3>
                      <div className="form-group">
                        <p style={{ whiteSpace: 'pre-wrap', padding: '10px', background: '#f5f5f5', borderRadius: '4px', minHeight: '100px' }}>
                          {viewingNote || 'No notes'}
                        </p>
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setViewingNote(null)}>
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {editingTransaction && (
                  <div className="modal-overlay" onClick={() => setEditingTransaction(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Edit Transaction</h3>
                      <form onSubmit={handleUpdateTransaction}>
                        {editingTransactionLedgerMode === 'finished_goods' ? (
                          <>
                            <div className="form-group">
                              <label>Finished Goods Action *</label>
                              <select
                                value={editingFinishedGoodsAction}
                                onChange={(e) => setEditingFinishedGoodsAction(e.target.value)}
                                required
                              >
                                <option value="in">Stock In</option>
                                <option value="out">Stock Out</option>
                                <option value="hold">Hold</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Finished Product *</label>
                              <select
                                value={editingFinishedGoodsProductId}
                                onChange={(e) => setEditingFinishedGoodsProductId(e.target.value)}
                                required
                              >
                                <option value="">Select product</option>
                                {finishedGoodsOptions.map((item) => (
                                  <option key={`edit-fg-product-${item.id}`} value={item.id}>{item.name}</option>
                                ))}
                              </select>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="form-group">
                              <label>Raw Material Action *</label>
                              <select
                                value={editingRawMaterialAction}
                                onChange={(e) => setEditingRawMaterialAction(e.target.value)}
                                required
                              >
                                {RAW_TRANSACTION_ACTIONS.map((action) => (
                                  <option key={`edit-raw-action-${action.key}`} value={action.key}>{action.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Raw Material *</label>
                              <select
                                value={editingRawMaterialType}
                                onChange={(e) => {
                                  setEditingRawMaterialType(e.target.value);
                                  setEditingRawMaterialCharacteristics({});
                                  setEditingRawMaterialCost('');
                                }}
                                required
                              >
                                <option value="">Select raw material</option>
                                {getRawMaterialSelectOptions(editingRawMaterialType).map((item) => (
                                  <option key={`edit-raw-type-${item.name}`} value={item.name}>
                                    {item.name}{item.archived ? ' (Archived)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {selectedEditingRawMaterialCharacteristics.map((characteristic) => {
                              const options = getCharacteristicValues(characteristic.list_key);
                              return (
                                <div key={`edit-txn-char-${characteristic.list_key}`} className="form-group">
                                  <label>
                                    {selectedEditingRawMaterial?.name} {characteristic.name}{characteristic.mandatory ? ' *' : ''}
                                  </label>
                                  <select
                                    value={editingRawMaterialCharacteristics?.[characteristic.name] || ''}
                                    onChange={(e) =>
                                      setEditingRawMaterialCharacteristics((prev) => ({
                                        ...prev,
                                        [characteristic.name]: e.target.value
                                      }))
                                    }
                                    disabled={!editingRawMaterialType}
                                    required={characteristic.mandatory}
                                  >
                                    <option value="">Select {characteristic.name}</option>
                                    {options.map((opt) => (
                                      <option key={`edit-${characteristic.list_key}-${opt}`} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                            <div className="form-group">
                              <label>Unit Cost *</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingRawMaterialCost}
                                onChange={(e) => setEditingRawMaterialCost(e.target.value)}
                                required
                              />
                            </div>
                          </>
                        )}
                        <div className="form-group">
                          <label>{transactionStockField.label || 'Quantity'} *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingTransactionValues[transactionStockField.key] ?? ''}
                            onChange={(e) =>
                              setEditingTransactionValues((prev) => ({
                                ...prev,
                                [transactionStockField.key]: e.target.value
                              }))
                            }
                            required
                          />
                        </div>
                        {transactionExtraFields.map((field) => {
                          const value = editingTransactionValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                          if (field.type === 'dropdown') {
                            const options = getDropdownOptions(field);
                            return (
                              <div key={field.key} className="form-group">
                                <label>{renderDropdownLabel(field)}</label>
                                <select
                                  value={value}
                                  onChange={(e) =>
                                    setEditingTransactionValues((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value
                                    }))
                                  }
                                  required={field.required}
                                >
                                  <option value="">Select {field.label}</option>
                                  {options.map((opt) => (
                                    <option key={`edit-${field.key}-${opt}`} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          }
                          if (field.type === 'checkbox') {
                            return (
                              <div key={field.key} className="form-group">
                                <label className="toggle-label">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(value)}
                                    onChange={(e) =>
                                      setEditingTransactionValues((prev) => ({
                                        ...prev,
                                        [field.key]: e.target.checked
                                      }))
                                    }
                                  />
                                  <span>{field.label}</span>
                                </label>
                              </div>
                            );
                          }
                          return (
                            <div key={field.key} className="form-group">
                              <label>{field.label}{field.required ? ' *' : ''}</label>
                              <input
                                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                value={value}
                                onChange={(e) =>
                                  setEditingTransactionValues((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value
                                  }))
                                }
                                required={field.required}
                              />
                            </div>
                          );
                        })}
                        {transactionSchema.include_date !== false && (
                          <div className="form-group">
                            <label>Date</label>
                            <input
                              type="date"
                              value={editingTransactionDate}
                              onChange={(e) => setEditingTransactionDate(e.target.value)}
                              required
                            />
                          </div>
                        )}
                        {transactionSchema.include_notes !== false && (
                          <div className="form-group">
                            <label>Notes</label>
                            <textarea
                              value={editingTransactionNotes}
                              onChange={(e) => setEditingTransactionNotes(e.target.value)}
                              rows="3"
                            />
                          </div>
                        )}
                        <div className="modal-actions">
                          <button type="submit">Update</button>
                          <button type="button" onClick={() => setEditingTransaction(null)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {filteredTransactions.length === 0 ? (
                  transactionLedgerMode === 'raw_material' && !transactionRawMaterialFilter
                    ? renderNextStepEmptyState({
                      title: 'Select a material type.',
                      description: 'Choose a raw material above to view ledger entries.',
                      actionLabel: 'Add Transaction',
                      onAction: () => setActiveTab('add')
                    })
                    : renderNextStepEmptyState({
                      title: hasActiveLedgerFilters ? 'No matching entries.' : 'No transactions yet.',
                      description: hasActiveLedgerFilters
                        ? 'Adjust the current filters or reset them to widen the ledger view.'
                        : ledgerScopeTransactions.length > 0
                          ? 'This view has entries, but none match the current filters.'
                          : 'Add a stock movement to start this ledger.',
                      actionLabel: 'Add Transaction',
                      onAction: () => setActiveTab('add')
                    })
                ) : (
                  <>
                    <div className="table-wrapper movement-ledger-table-wrapper">
                    <table className="data-table movement-ledger-table">
                      <thead>
                        <tr>
                          {transactionSchema.include_date !== false && <th className="movement-ledger-sticky-col movement-ledger-sticky-col-date">Date</th>}
                          <th className="movement-ledger-sticky-col movement-ledger-sticky-col-name">Material / Product</th>
                          <th>Direction</th>
                          <th className="movement-ledger-number-col">{transactionStockField.label || 'Quantity'}</th>
                          <th>Action</th>
                          <th>Specs</th>
                          <th>Unit Cost</th>
                          <th>Challan</th>
                          {transactionExtraFields.map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                          {transactionSchema.include_notes !== false && <th>Notes</th>}
                          <th>Created By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedTransactions.map((transaction) => {
                          const challan = transaction.challan;
                          const hasChallan = Boolean(challan?.id);
                          const challanStatus = String(challan?.status || '').toLowerCase();
                          return (
                          <tr key={transaction.id}>
                            {(() => {
                              const rawContext = getTransactionRawMaterialContext(transaction);
                              const characteristicSummary = formatCharacteristicSelectionLabel(rawContext.characteristics, '-');
                              const entryName = rawContext.rawMaterialName || rawContext.finishedProductName || '-';
                              return (
                                <>
                                  {transactionSchema.include_date !== false && (
                                    <td className="movement-ledger-sticky-col movement-ledger-sticky-col-date">
                                      {formatDateOnly(transaction.date, getCurrentTimeZone())}
                                    </td>
                                  )}
                                  <td className="movement-ledger-sticky-col movement-ledger-sticky-col-name">{entryName}</td>
                                  <td>
                                    <span className={`badge ${transaction.type}`}>
                                      {String(transaction.type || '-').toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="movement-ledger-number-col">{Number(transaction.quantity || 0).toFixed(2)}</td>
                                  <td>{getTransactionActionLabel(transaction, rawContext)}</td>
                                  <td className="movement-ledger-specs-cell">{characteristicSummary}</td>
                                  <td className="movement-ledger-number-col">{rawContext.unitCost > 0 ? rawContext.unitCost.toFixed(2) : '-'}</td>
                                  <td>
                                    {hasChallan ? (
                                      <div className="challan-cell">
                                        <span className="challan-number">{challan.challan_number || transaction.challan_no}</span>
                                        <span className={`status-pill ${challanStatus === 'cancelled' ? 'warn' : 'ok'}`}>
                                          {challanStatus === 'cancelled' ? 'Cancelled' : 'Issued'}
                                        </span>
                                      </div>
                                    ) : (transaction.challan_no || '-')}
                                  </td>
                                  {transactionExtraFields.map((field) => {
                                    const value = transaction.custom_fields?.[field.key];
                                    return (
                                      <td key={field.key}>{value !== undefined && value !== '' ? String(value) : '-'}</td>
                                    );
                                  })}
                                  {transactionSchema.include_notes !== false && (
                                    <td>
                                      <button
                                        type="button"
                                        onClick={() => openNotesModal('transaction', transaction, 'Transaction')}
                                        className={`btn-edit icon-only-btn movement-ledger-note-btn ${transaction.notes ? 'has-note' : ''}`}
                                        title={transaction.notes ? 'View or edit notes' : 'Add notes'}
                                        aria-label={transaction.notes ? 'View or edit notes' : 'Add notes'}
                                      >
                                        <StickyNote className="btn-icon" size={14} />
                                      </button>
                                    </td>
                                  )}
                                  <td>{transaction.created_by_username}</td>
                                  <td className="transaction-action-cell">
                                    <div className="transaction-action-row">
                                      {!hasChallan && (
                                        <button
                                          type="button"
                                          onClick={() => handleEditTransaction(transaction)}
                                          className="btn-edit icon-only-btn"
                                          title="Edit transaction"
                                          aria-label="Edit transaction"
                                        >
                                          <Pencil className="btn-icon" size={14} />
                                        </button>
                                      )}
                                      <div className="pipeline-action-menu transaction-action-menu" onClick={(e) => e.stopPropagation()}>
                                        <button
                                          type="button"
                                          className="btn-edit pipeline-btn pipeline-action-trigger"
                                          aria-label="More actions"
                                          title="More actions"
                                          onClick={() => setTransactionActionMenuOpenId((prev) => (prev === transaction.id ? null : transaction.id))}
                                        >
                                          <MoreHorizontal className="btn-icon" size={16} />
                                        </button>
                                        {transactionActionMenuOpenId === transaction.id && (
                                          <div className="pipeline-action-dropdown">
                                            {hasChallan && (
                                              <button type="button" onClick={() => {
                                                setTransactionActionMenuOpenId(null);
                                                handlePrintChallan(challan);
                                              }}
                                              >
                                                Print challan
                                              </button>
                                            )}
                                            {hasChallan && (
                                              <button type="button" onClick={() => {
                                                setTransactionActionMenuOpenId(null);
                                                handleDownloadChallanPdf(challan);
                                              }}
                                              >
                                                Save challan as PDF
                                              </button>
                                            )}
                                            {hasChallan && challanStatus === 'issued' && (
                                              <button type="button" onClick={() => {
                                                setTransactionActionMenuOpenId(null);
                                                handleCancelChallan(challan);
                                              }}
                                              >
                                                Cancel challan
                                              </button>
                                            )}
                                            {hasChallan && challanStatus === 'cancelled' && (
                                              <button type="button" onClick={() => {
                                                setTransactionActionMenuOpenId(null);
                                                handleReissueChallan(challan);
                                              }}
                                              >
                                                Reissue challan
                                              </button>
                                            )}
                                            {!hasChallan && (
                                              <button type="button" onClick={() => {
                                                setTransactionActionMenuOpenId(null);
                                                openConfirmDialog({
                                                  type: 'transaction',
                                                  id: transaction.id,
                                                  title: 'Delete transaction?',
                                                  message: 'This will permanently delete this transaction.'
                                                });
                                              }}
                                              >
                                                Delete transaction
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </>
                              );
                            })()}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
                {(transactionLedgerMode === 'finished_goods' || transactionRawMaterialFilter) && filteredTransactions.length > 0 && (
                  <div className="pagination-bar">
                    <div className="pagination-info">
                      Page {safeTransactionsPage} of {totalTransactionPages} ({filteredTransactions.length} records)
                    </div>
                    <div className="pagination-controls">
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setTransactionsPage((p) => Math.max(1, p - 1))}
                        disabled={safeTransactionsPage === 1}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setTransactionsPage((p) => Math.min(totalTransactionPages, p + 1))}
                        disabled={safeTransactionsPage === totalTransactionPages}
                      >
                        Next
                      </button>
                      <select
                        className="page-size-select"
                        value={transactionsPageSize}
                        onChange={(e) => {
                          setTransactionsPageSize(Number(e.target.value));
                          setTransactionsPage(1);
                        }}
                      >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'staff' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="transactions-section people-secondary-section" data-tour-id="staff-overview">
                <div className="section-header">
                  <h2>People · Staff</h2>
                </div>
                <p className="helper-text people-secondary-note">Secondary workspace for maintaining staff records, ID details, and status changes.</p>
                <>
                    <div className="people-summary-bar">
                      <div className="workflow-summary-pills">
                        <div className="workflow-summary-pill">
                          <span>Total</span>
                          <strong>{staff.length}</strong>
                        </div>
                        <div className="workflow-summary-pill is-completed">
                          <span>Active</span>
                          <strong>{activeStaffCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-cancelled">
                          <span>Inactive</span>
                          <strong>{inactiveStaffCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>With Notes</span>
                          <strong>{staffWithNotesCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>Updated 7d</span>
                          <strong>{staffUpdatedThisWeekCount}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="filters-row" style={{ marginBottom: '16px' }}>
                      <div className="search-field">
                        <span className="search-field-icon" aria-hidden="true">
                          <Search size={14} />
                        </span>
                        <input
                          type="text"
                          value={staffSearch}
                          onChange={(e) => setStaffSearch(e.target.value)}
                          placeholder="Search staff..."
                          className="list-search"
                        />
                        {staffSearch && (
                          <button
                            type="button"
                            className="search-clear"
                            onClick={() => setStaffSearch('')}
                            aria-label="Clear search"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <select
                        className="select-input"
                        value={staffStatusFilter}
                        onChange={(e) => setStaffStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <select
                        className="select-input"
                        value={staffNotesFilter}
                        onChange={(e) => setStaffNotesFilter(e.target.value)}
                      >
                        <option value="all">All Notes</option>
                        <option value="with">With Notes</option>
                        <option value="without">Without Notes</option>
                      </select>
                      <select
                        className="select-input"
                        value={staffSortMode}
                        onChange={(e) => setStaffSortMode(e.target.value)}
                      >
                        <option value="recent">Sort: Recently Updated</option>
                        <option value="az">Sort: Name A-Z</option>
                        <option value="za">Sort: Name Z-A</option>
                        <option value="status">Sort: Active first</option>
                      </select>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={openStaffAttendanceModal}
                        disabled={attendanceEligibleStaff.length === 0}
                      >
                        <CalendarDays className="btn-icon" size={16} />
                        <span>Take Attendance</span>
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={openStaffAttendanceViewModal}
                      >
                        <CalendarDays className="btn-icon" size={16} />
                        <span>View Attendance</span>
                      </button>
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => {
                          resetStaffForm();
                          setShowCreateStaff(true);
                        }}
                      >
                        <Plus className="btn-icon" size={16} />
                        <span>Add New Staff</span>
                      </button>
                    </div>
                    <p className="helper-text calculation-note">
                      Showing {filteredStaff.length} of {staff.length} staff records.
                    </p>

                    {showCreateStaff && (
                      <div className="modal-overlay" onClick={() => setShowCreateStaff(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                          <h3>Add Staff Member</h3>
                          <form onSubmit={handleAddStaff}>
                            <div className="form-group">
                              <label>Name *</label>
                              <input
                                type="text"
                                value={staffForm.name}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, name: e.target.value }))}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>{renderDropdownLabel({ key: 'staff_skill_category', label: 'Skill Category', required: true })}</label>
                              <select
                                value={staffForm.skill_category}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, skill_category: e.target.value }))}
                                required
                              >
                                <option value="">Select skill category</option>
                                {getDropdownOptions({ key: 'staff_skill_category', options: [] }).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              <small className="helper-text">Manage options in Settings → Manage Lists.</small>
                            </div>
                            <div className="form-group">
                              <label>{renderDropdownLabel({ key: 'staff_id_type', label: 'ID Type', required: true })}</label>
                              <select
                                value={staffForm.id_type}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, id_type: e.target.value }))}
                                required
                              >
                                <option value="">Select ID type</option>
                                {getDropdownOptions({
                                  key: 'staff_id_type',
                                  options: ['PAN', 'Aadhaar', 'Passport', 'Driving License', 'Other']
                                }).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>ID Value *</label>
                              <input
                                type="text"
                                value={staffForm.id_value}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, id_value: e.target.value }))}
                                placeholder="Alphanumeric"
                                required
                              />
                              <small className="helper-text">Only letters and numbers are allowed.</small>
                            </div>

                            {staffSchema.fields.length > 0 && (
                              <div className="section-card">
                                <h3>Additional Fields</h3>
                                {staffSchema.fields.map((field) => {
                                  const value = staffCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                    <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="modal-actions">
                              <button type="submit">Save</button>
                              <button type="button" onClick={() => setShowCreateStaff(false)}>
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {showStaffAttendanceModal && (
                      <div className="modal-overlay" onClick={() => !staffAttendanceSaving && setShowStaffAttendanceModal(false)}>
                        <div className="modal attendance-modal" onClick={(e) => e.stopPropagation()}>
                          <h3>Take Staff Attendance</h3>
                          <div className="attendance-toolbar">
                            <div className="form-group">
                              <label>Date</label>
                              <input
                                type="date"
                                value={staffAttendanceDate}
                                onChange={(e) => setStaffAttendanceDate(e.target.value)}
                                disabled={staffAttendanceLoading || staffAttendanceSaving}
                              />
                            </div>
                            <div className="workflow-summary-pills">
                              <div className="workflow-summary-pill"><span>Present</span><strong>{attendanceSummary.present}</strong></div>
                              <div className="workflow-summary-pill is-cancelled"><span>Absent</span><strong>{attendanceSummary.absent}</strong></div>
                              <div className="workflow-summary-pill"><span>Half Day</span><strong>{attendanceSummary.half_day}</strong></div>
                              <div className="workflow-summary-pill"><span>Leave</span><strong>{attendanceSummary.leave}</strong></div>
                            </div>
                          </div>

                          {attendanceEligibleStaff.length === 0 ? (
                            <div className="empty-state">No active staff available for attendance.</div>
                          ) : staffAttendanceLoading ? (
                            <div className="empty-state">Loading attendance...</div>
                          ) : (
                            <div className="table-wrapper attendance-table-wrapper">
                              <table className="data-table attendance-table">
                                <thead>
                                  <tr>
                                    <th>Staff</th>
                                    <th>Status</th>
                                    <th>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {attendanceEligibleStaff.map((member) => {
                                    const current = staffAttendanceEntries[member.id] || { status: 'present', notes: '' };
                                    return (
                                      <tr key={`attendance-${member.id}`}>
                                        <td>{member.name}</td>
                                        <td>
                                          <select
                                            value={current.status || 'present'}
                                            onChange={(e) => updateStaffAttendanceEntry(member.id, { status: e.target.value })}
                                            disabled={staffAttendanceSaving}
                                          >
                                            {STAFF_ATTENDANCE_STATUS_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            value={current.notes || ''}
                                            onChange={(e) => updateStaffAttendanceEntry(member.id, { notes: e.target.value })}
                                            placeholder="Optional note"
                                            disabled={staffAttendanceSaving}
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="modal-actions">
                            <button
                              type="button"
                              onClick={() => setShowStaffAttendanceModal(false)}
                              disabled={staffAttendanceSaving}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={handleSaveStaffAttendance}
                              disabled={staffAttendanceSaving || staffAttendanceLoading || attendanceEligibleStaff.length === 0}
                            >
                              {staffAttendanceSaving ? 'Saving...' : 'Save Attendance'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {showStaffAttendanceViewModal && (
                      <div className="modal-overlay" onClick={() => setShowStaffAttendanceViewModal(false)}>
                        <div className="modal attendance-modal" onClick={(e) => e.stopPropagation()}>
                          <h3>View Staff Attendance</h3>
                          <div className="attendance-toolbar">
                            <div className="form-group">
                              <label>Date</label>
                              <input
                                type="date"
                                value={staffAttendanceViewDate}
                                onChange={(e) => setStaffAttendanceViewDate(e.target.value)}
                                disabled={staffAttendanceViewLoading}
                              />
                            </div>
                            {!staffAttendanceViewLoading && (
                              <div className="workflow-summary-pills">
                                <div className="workflow-summary-pill"><span>Total</span><strong>{attendanceViewSummary.total}</strong></div>
                                <div className="workflow-summary-pill is-completed"><span>Present</span><strong>{attendanceViewSummary.present}</strong></div>
                                <div className="workflow-summary-pill is-cancelled"><span>Absent</span><strong>{attendanceViewSummary.absent}</strong></div>
                                <div className="workflow-summary-pill"><span>Half Day</span><strong>{attendanceViewSummary.half_day}</strong></div>
                                <div className="workflow-summary-pill"><span>Leave</span><strong>{attendanceViewSummary.leave}</strong></div>
                              </div>
                            )}
                          </div>
                          {staffAttendanceViewLoading ? (
                            <div className="empty-state">Loading attendance...</div>
                          ) : staffAttendanceViewRows.length === 0 ? (
                            <div className="empty-state">No attendance records found for this date.</div>
                          ) : (
                            <div className="table-wrapper attendance-table-wrapper">
                              <table className="data-table attendance-table">
                                <thead>
                                  <tr>
                                    <th>Staff</th>
                                    <th>Status</th>
                                    <th>Notes</th>
                                    <th>Updated</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {staffAttendanceViewRows.map((row) => {
                                    const statusLabel = STAFF_ATTENDANCE_STATUS_OPTIONS.find((item) => item.value === row.status)?.label || row.status;
                                    return (
                                      <tr key={`attendance-view-${row.id}`}>
                                        <td>{row.staff_name || row.staff_id}</td>
                                        <td><span className={`badge ${getAttendanceStatusBadgeClass(row.status)}`}>{statusLabel}</span></td>
                                        <td>{row.notes || '—'}</td>
                                        <td>{formatDateTime(row.updated_at, getCurrentTimeZone())}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="modal-actions">
                            <button type="button" onClick={() => setShowStaffAttendanceViewModal(false)}>
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {viewingStaff && (
                      <div className="modal-overlay" onClick={() => setViewingStaff(null)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                          <h3>Staff Details</h3>
                          <div className="details-list">
                            <div><strong>Name:</strong> {viewingStaff.name || '-'}</div>
                            <div><strong>Skill Category:</strong> {viewingStaff.skill_category || '-'}</div>
                            <div><strong>ID Type:</strong> {viewingStaff.id_type || '-'}</div>
                            <div><strong>ID Value:</strong> {viewingStaff.has_id_value ? viewingStaff.id_masked : '-'}</div>
                            <div><strong>Status:</strong> {viewingStaff.is_active ? 'Active' : 'Inactive'}</div>
                          </div>
                          {staffSchema.fields.length > 0 && (
                            <div className="section-card">
                              <h3>Additional Fields</h3>
                              {staffSchema.fields.map((field) => (
                                <div key={field.key} className="form-group">
                                  <label>{field.label}</label>
                                  <div className="read-only">
                                    {viewingStaff.custom_fields?.[field.key] !== undefined
                                      ? String(viewingStaff.custom_fields[field.key])
                                      : '-'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="modal-actions">
                            <button type="button" className="btn-ghost" onClick={() => openNotesModal('staff', viewingStaff, 'Staff')}>
                              <StickyNote className="btn-icon" size={14} />
                              <span>{viewingStaff.notes ? 'Edit Notes' : 'Add Notes'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setViewingStaff(null)}
                              aria-label="Close"
                              title="Close"
                            >
                              <X className="btn-icon" size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {editingStaff && (
                      <div className="modal-overlay" onClick={() => setEditingStaff(null)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                          <h3>Edit Staff Member</h3>
                          <form onSubmit={handleUpdateStaff}>
                            <div className="form-group">
                              <label>Name *</label>
                              <input
                                type="text"
                                value={staffForm.name}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, name: e.target.value }))}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>{renderDropdownLabel({ key: 'staff_skill_category', label: 'Skill Category', required: true })}</label>
                              <select
                                value={staffForm.skill_category}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, skill_category: e.target.value }))}
                                required
                              >
                                <option value="">Select skill category</option>
                                {getDropdownOptions({ key: 'staff_skill_category', options: [] }).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>{renderDropdownLabel({ key: 'staff_id_type', label: 'ID Type', required: true })}</label>
                              <select
                                value={staffForm.id_type}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, id_type: e.target.value }))}
                                required
                              >
                                <option value="">Select ID type</option>
                                {getDropdownOptions({
                                  key: 'staff_id_type',
                                  options: ['PAN', 'Aadhaar', 'Passport', 'Driving License', 'Other']
                                }).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>ID Value *</label>
                              <div className="id-field-row">
                                <input
                                  type="text"
                                  value={staffForm.id_value}
                                  onChange={(e) => setStaffForm((prev) => ({ ...prev, id_value: e.target.value }))}
                                  placeholder={editingStaff.id_masked ? `Masked: ${editingStaff.id_masked}` : 'Alphanumeric'}
                                  disabled={!staffForm.id_value && Boolean(editingStaff.id_masked)}
                                  required
                                />
                                {editingStaff.id_masked && !staffForm.id_value && (
                                  <button
                                    type="button"
                                    className="btn-edit"
                                    onClick={() => handleRevealStaffId(editingStaff.id)}
                                  >
                                    Reveal
                                  </button>
                                )}
                              </div>
                              <small className="helper-text">Only letters and numbers are allowed.</small>
                            </div>
                            <label className="toggle-inline">
                              <input
                                type="checkbox"
                                checked={staffForm.is_active}
                                onChange={(e) => setStaffForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                              />
                              Active
                            </label>

                            {staffSchema.fields.length > 0 && (
                              <div className="section-card">
                                <h3>Additional Fields</h3>
                                {staffSchema.fields.map((field) => {
                                  const value = staffCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                    <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setStaffCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="modal-actions">
                              <button type="submit">Update</button>
                              <button type="button" onClick={() => setEditingStaff(null)}>
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {filteredStaff.length === 0 ? (
                      <div className="empty-state">No staff found.</div>
                    ) : (
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Skill Category</th>
                              <th>ID Type</th>
                              <th>ID Value</th>
                              <th>Status</th>
                              <th>Updated</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredStaff.map((member) => {
                              const status = member.anonymized_at
                                ? 'Anonymized'
                                : member.is_active
                                  ? 'Active'
                                  : 'Unemployed';
                              const statusClass = member.anonymized_at || !member.is_active ? 'out' : 'in';
                              return (
                                <tr
                                  key={member.id}
                                  className="table-row-click"
                                  onClick={() => handleViewStaff(member)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleViewStaff(member);
                                    }
                                  }}
                                >
                                  <td>{member.name}</td>
                                  <td>{member.skill_category || '-'}</td>
                                  <td>{member.id_type || '-'}</td>
                                  <td>{member.has_id_value ? member.id_masked : '-'}</td>
                                  <td>
                                    <span className={`badge ${statusClass}`}>{status}</span>
                                  </td>
                                  <td>{formatDateOnly(member.updated_at, getCurrentTimeZone())}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn-edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditStaff(member);
                                      }}
                                    >
                                      <Pencil className="btn-icon" size={14} />
                                      <span>Edit</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openNotesModal('staff', member, 'Staff');
                                      }}
                                    >
                                      <StickyNote className="btn-icon" size={14} />
                                      <span>{member.notes ? 'Notes' : 'Add Notes'}</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openConfirmDialog({
                                          type: 'staff-status',
                                          id: member.id,
                                          is_active: !member.is_active,
                                          title: member.is_active ? 'Mark staff as unemployed?' : 'Mark staff as active?',
                                          message: member.is_active
                                            ? 'This will keep the record but mark the staff member as unemployed.'
                                            : 'This will mark the staff member as active again.'
                                          ,
                                          actionLabel: member.is_active ? 'Mark Unemployed' : 'Mark Active'
                                        });
                                      }}
                                    >
                                      {member.is_active ? (
                                        <>
                                          <UserMinus className="btn-icon" size={14} />
                                          <span>Mark Unemployed</span>
                                        </>
                                      ) : (
                                        <>
                                          <UserCheck className="btn-icon" size={14} />
                                          <span>Mark Active</span>
                                        </>
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openConfirmDialog({
                                          type: 'staff-delete',
                                          id: member.id,
                                          title: 'Delete staff?',
                                          message: `This will permanently delete "${member.name}".`
                                        });
                                      }}
                                    >
                                      <Trash2 className="btn-icon" size={14} />
                                      <span>Delete</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                </>
              </div>
            )}

            {activeTab === 'customers' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="transactions-section people-secondary-section" data-tour-id="customers-overview">
                <div className="section-header">
                  <h2>People · Customers</h2>
                </div>
                <p className="helper-text people-secondary-note">Secondary workspace for maintaining customer profiles used in orders and records.</p>
                <>
                    <div className="people-summary-bar">
                      <div className="workflow-summary-pills">
                        <div className="workflow-summary-pill">
                          <span>Total</span>
                          <strong>{customers.length}</strong>
                        </div>
                        <div className="workflow-summary-pill is-completed">
                          <span>Active</span>
                          <strong>{activeCustomersCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-cancelled">
                          <span>Inactive</span>
                          <strong>{inactiveCustomersCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>With Notes</span>
                          <strong>{customersWithNotesCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>Updated 7d</span>
                          <strong>{customersUpdatedThisWeekCount}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="filters-row" style={{ marginBottom: '16px' }}>
                      <div className="search-field">
                        <span className="search-field-icon" aria-hidden="true">
                          <Search size={14} />
                        </span>
                        <input
                          type="text"
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          placeholder="Search customers..."
                          className="list-search"
                        />
                        {customerSearch && (
                          <button
                            type="button"
                            className="search-clear"
                            onClick={() => setCustomerSearch('')}
                            aria-label="Clear search"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <select
                        className="select-input"
                        value={customerStatusFilter}
                        onChange={(e) => setCustomerStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <select
                        className="select-input"
                        value={customerNotesFilter}
                        onChange={(e) => setCustomerNotesFilter(e.target.value)}
                      >
                        <option value="all">All Notes</option>
                        <option value="with">With Notes</option>
                        <option value="without">Without Notes</option>
                      </select>
                      <select
                        className="select-input"
                        value={customerSortMode}
                        onChange={(e) => setCustomerSortMode(e.target.value)}
                      >
                        <option value="recent">Sort: Recently Updated</option>
                        <option value="az">Sort: Name A-Z</option>
                        <option value="za">Sort: Name Z-A</option>
                        <option value="status">Sort: Active first</option>
                      </select>
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => {
                          setCustomerCustomValues({});
                          setShowCreateCustomer(true);
                        }}
                      >
                        <Plus className="btn-icon" size={16} />
                        <span>Add New Customer</span>
                      </button>
                    </div>
                    <p className="helper-text calculation-note">
                      Showing {filteredCustomers.length} of {customers.length} customer records.
                    </p>

                    {customerSchema.fields.length === 0 ? (
                      <div className="empty-state">No customer fields configured in the wizard yet.</div>
                    ) : (
                      <>
                        {showCreateCustomer && (
                          <div className="modal-overlay" onClick={() => setShowCreateCustomer(false)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Add Customer</h3>
                              <form onSubmit={handleAddCustomer}>
                                {customerSchema.fields.map((field) => {
                                  const value = customerCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                                <div className="modal-actions">
                                  <button type="submit">Save</button>
                                  <button type="button" onClick={() => setShowCreateCustomer(false)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        )}

                        {viewingCustomer && (
                          <div className="modal-overlay" onClick={() => setViewingCustomer(null)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Customer Details</h3>
                              <div className="details-list">
                                {customerSchema.fields.map((field) => (
                                  <div key={field.key}>
                                    <strong>{field.label}:</strong>{' '}
                                    {viewingCustomer.custom_fields?.[field.key] !== undefined
                                      ? String(viewingCustomer.custom_fields[field.key])
                                      : '-'}
                                  </div>
                                ))}
                                <div>
                                  <strong>Status:</strong>{' '}
                                  {viewingCustomer.is_active ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                              <div className="modal-actions">
                                <button type="button" className="btn-ghost" onClick={() => openNotesModal('customer', viewingCustomer, 'Customer')}>
                                  <StickyNote className="btn-icon" size={14} />
                                  <span>{viewingCustomer.notes ? 'Edit Notes' : 'Add Notes'}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setViewingCustomer(null)}
                                  aria-label="Close"
                                  title="Close"
                                >
                                  <X className="btn-icon" size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {editingCustomer && (
                          <div className="modal-overlay" onClick={() => setEditingCustomer(null)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Edit Customer</h3>
                              <form onSubmit={handleUpdateCustomer}>
                                {customerSchema.fields.map((field) => {
                                  const value = customerCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setCustomerCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                                <label className="toggle-inline">
                                  <input
                                    type="checkbox"
                                    checked={editingCustomer.is_active !== false}
                                    onChange={(e) =>
                                      setEditingCustomer((prev) => ({ ...prev, is_active: e.target.checked }))
                                    }
                                  />
                                  Active
                                </label>
                                <div className="modal-actions">
                                  <button type="submit">Update</button>
                                  <button type="button" onClick={() => setEditingCustomer(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        )}

                        {filteredCustomers.length === 0 ? (
                          <div className="empty-state">No customers found.</div>
                        ) : (
                          <div className="table-wrapper">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  {customerSchema.fields.slice(0, 4).map((field) => (
                                    <th key={field.key}>{field.label}</th>
                                  ))}
                                  <th>Status</th>
                                  <th>Updated</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCustomers.map((customer) => (
                                <tr
                                  key={customer.id}
                                  className="table-row-click"
                                  onClick={() => handleViewCustomer(customer)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleViewCustomer(customer);
                                    }
                                  }}
                                >
                                    {customerSchema.fields.slice(0, 4).map((field) => (
                                      <td key={field.key}>
                                        {customer.custom_fields?.[field.key] !== undefined
                                          ? String(customer.custom_fields[field.key])
                                          : '-'}
                                      </td>
                                    ))}
                                    <td>
                                      <span className={`badge ${customer.is_active ? 'in' : 'out'}`}>
                                        {customer.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td>{formatDateOnly(customer.updated_at, getCurrentTimeZone())}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditCustomer(customer);
                                        }}
                                      >
                                        <Pencil className="btn-icon" size={14} />
                                        <span>Edit</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openNotesModal('customer', customer, 'Customer');
                                        }}
                                      >
                                        <StickyNote className="btn-icon" size={14} />
                                        <span>{customer.notes ? 'Notes' : 'Add Notes'}</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openConfirmDialog({
                                            type: 'customer-delete',
                                            id: customer.id,
                                            title: 'Delete customer?',
                                            message: 'This will permanently delete this customer.'
                                          });
                                        }}
                                      >
                                        <Trash2 className="btn-icon" size={14} />
                                        <span>Delete</span>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                </>
              </div>
            )}

            {activeTab === 'contractors' && currentTabConfig?.mode !== 'custom_form' && (
              <div className="transactions-section people-secondary-section" data-tour-id="contractors-overview">
                <div className="section-header">
                  <h2>People · Contractors</h2>
                </div>
                <p className="helper-text people-secondary-note">Secondary workspace for contractor profiles, availability, and notes.</p>
                <>
                    <div className="people-summary-bar">
                      <div className="workflow-summary-pills">
                        <div className="workflow-summary-pill">
                          <span>Total</span>
                          <strong>{contractors.length}</strong>
                        </div>
                        <div className="workflow-summary-pill is-completed">
                          <span>Active</span>
                          <strong>{activeContractorsCount}</strong>
                        </div>
                        <div className="workflow-summary-pill is-cancelled">
                          <span>Inactive</span>
                          <strong>{inactiveContractorsCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>With Notes</span>
                          <strong>{contractorsWithNotesCount}</strong>
                        </div>
                        <div className="workflow-summary-pill">
                          <span>Updated 7d</span>
                          <strong>{contractorsUpdatedThisWeekCount}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="filters-row" style={{ marginBottom: '16px' }}>
                      <div className="search-field">
                        <span className="search-field-icon" aria-hidden="true">
                          <Search size={14} />
                        </span>
                        <input
                          type="text"
                          value={contractorSearch}
                          onChange={(e) => setContractorSearch(e.target.value)}
                          placeholder="Search contractors..."
                          className="list-search"
                        />
                        {contractorSearch && (
                          <button
                            type="button"
                            className="search-clear"
                            onClick={() => setContractorSearch('')}
                            aria-label="Clear search"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <select
                        className="select-input"
                        value={contractorStatusFilter}
                        onChange={(e) => setContractorStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <select
                        className="select-input"
                        value={contractorNotesFilter}
                        onChange={(e) => setContractorNotesFilter(e.target.value)}
                      >
                        <option value="all">All Notes</option>
                        <option value="with">With Notes</option>
                        <option value="without">Without Notes</option>
                      </select>
                      <select
                        className="select-input"
                        value={contractorSortMode}
                        onChange={(e) => setContractorSortMode(e.target.value)}
                      >
                        <option value="recent">Sort: Recently Updated</option>
                        <option value="az">Sort: Name A-Z</option>
                        <option value="za">Sort: Name Z-A</option>
                        <option value="status">Sort: Active first</option>
                      </select>
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => {
                          setContractorCustomValues({});
                          setShowCreateContractor(true);
                        }}
                      >
                        <Plus className="btn-icon" size={16} />
                        <span>Add New Contractor</span>
                      </button>
                    </div>
                    <p className="helper-text calculation-note">
                      Showing {filteredContractors.length} of {contractors.length} contractor records.
                    </p>

                    {contractorSchema.fields.length === 0 ? (
                      <div className="empty-state">No contractor fields configured in the wizard yet.</div>
                    ) : (
                      <>
                        {showCreateContractor && (
                          <div className="modal-overlay" onClick={() => setShowCreateContractor(false)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Add Contractor</h3>
                              <form onSubmit={handleAddContractor}>
                                {contractorSchema.fields.map((field) => {
                                  const value = contractorCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                                <div className="modal-actions">
                                  <button type="submit">Save</button>
                                  <button type="button" onClick={() => setShowCreateContractor(false)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        )}

                        {viewingContractor && (
                          <div className="modal-overlay" onClick={() => setViewingContractor(null)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Contractor Details</h3>
                              <div className="details-list">
                                {contractorSchema.fields.map((field) => (
                                  <div key={field.key}>
                                    <strong>{field.label}:</strong>{' '}
                                    {viewingContractor.custom_fields?.[field.key] !== undefined
                                      ? String(viewingContractor.custom_fields[field.key])
                                      : '-'}
                                  </div>
                                ))}
                                <div>
                                  <strong>Status:</strong>{' '}
                                  {viewingContractor.is_active ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                              <div className="modal-actions">
                                <button type="button" className="btn-ghost" onClick={() => openNotesModal('contractor', viewingContractor, 'Contractor')}>
                                  <StickyNote className="btn-icon" size={14} />
                                  <span>{viewingContractor.notes ? 'Edit Notes' : 'Add Notes'}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setViewingContractor(null)}
                                  aria-label="Close"
                                  title="Close"
                                >
                                  <X className="btn-icon" size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {editingContractor && (
                          <div className="modal-overlay" onClick={() => setEditingContractor(null)}>
                            <div className="modal" onClick={(e) => e.stopPropagation()}>
                              <h3>Edit Contractor</h3>
                              <form onSubmit={handleUpdateContractor}>
                                {contractorSchema.fields.map((field) => {
                                  const value = contractorCustomValues[field.key] ?? (field.type === 'checkbox' ? false : '');
                                  if (field.type === 'dropdown') {
                                    const options = getDropdownOptions(field);
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>{renderDropdownLabel(field)}</label>
                                        <select
                                          value={value}
                                          onChange={(e) =>
                                            setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                          }
                                          required={field.required}
                                        >
                                          <option value="">Select {field.label}</option>
                                          {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (field.type === 'checkbox') {
                                    return (
                                      <div key={field.key} className="form-group">
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) =>
                                              setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                            }
                                          />
                                          {field.label}
                                        </label>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field.key} className="form-group">
                                      <label>{field.label}{field.required ? ' *' : ''}</label>
                                      <input
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={value}
                                        onChange={(e) =>
                                          setContractorCustomValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                                        }
                                        required={field.required}
                                      />
                                    </div>
                                  );
                                })}
                                <label className="toggle">
                                  <input
                                    type="checkbox"
                                    checked={editingContractor.is_active !== false}
                                    onChange={(e) =>
                                      setEditingContractor((prev) => ({ ...prev, is_active: e.target.checked }))
                                    }
                                  />
                                  Active
                                </label>
                                <div className="modal-actions">
                                  <button type="submit">Update</button>
                                  <button type="button" onClick={() => setEditingContractor(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        )}

                        {filteredContractors.length === 0 ? (
                          <div className="empty-state">No contractors found.</div>
                        ) : (
                          <div className="table-wrapper">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  {contractorSchema.fields.slice(0, 4).map((field) => (
                                    <th key={field.key}>{field.label}</th>
                                  ))}
                                  <th>Status</th>
                                  <th>Updated</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredContractors.map((contractor) => (
                                  <tr
                                    key={contractor.id}
                                    className="table-row-click"
                                    onClick={() => handleViewContractor(contractor)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleViewContractor(contractor);
                                      }
                                    }}
                                  >
                                    {contractorSchema.fields.slice(0, 4).map((field) => (
                                      <td key={field.key}>
                                        {contractor.custom_fields?.[field.key] !== undefined
                                          ? String(contractor.custom_fields[field.key])
                                          : '-'}
                                      </td>
                                    ))}
                                    <td>
                                      <span className={`badge ${contractor.is_active ? 'in' : 'out'}`}>
                                        {contractor.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td>{formatDateOnly(contractor.updated_at, getCurrentTimeZone())}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditContractor(contractor);
                                        }}
                                      >
                                        <Pencil className="btn-icon" size={14} />
                                        <span>Edit</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openNotesModal('contractor', contractor, 'Contractor');
                                        }}
                                      >
                                        <StickyNote className="btn-icon" size={14} />
                                        <span>{contractor.notes ? 'Notes' : 'Add Notes'}</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openConfirmDialog({
                                            type: 'contractor-delete',
                                            id: contractor.id,
                                            title: 'Delete contractor?',
                                            message: 'This will permanently delete this contractor.'
                                          });
                                        }}
                                      >
                                        <Trash2 className="btn-icon" size={14} />
                                        <span>Delete</span>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                </>
              </div>
            )}

            {activeTab === 'settings' && currentTabConfig?.mode !== 'custom_form' && settingsTab === 'customLists' && (
              <div className="settings-shell" data-tour-id="manage-lists-settings">
                <div className="settings-content">
                  {renderSettingsHeader({
                    title: 'Manage Lists',
                    description: 'Manage dropdown values through a catalog and focused editor.',
                    dirty: false,
                    actions: (
                      <div className="settings-header-buttons">
                        <button
                          type="button"
                          className="btn-ghost settings-link-button"
                          onClick={() => setSettingsTab('system')}
                        >
                          Go to System Settings
                        </button>
                      </div>
                    )
                  })}
                  {renderSettingsSummary()}
                  {(() => {
                    if (manageListCatalog.length === 0) {
                      return <div className="empty-state">No dropdown fields are defined in the wizard.</div>;
                    }
                    const sectionOptions = [
                      { id: 'all', title: 'All Sections' },
                      ...Array.from(
                        new Map(manageListCatalog.map((entry) => [entry.sectionId, entry.sectionTitle])).entries()
                      ).map(([id, title]) => ({ id, title }))
                    ];
                    const filteredCatalog = manageListCatalog.filter((entry) => {
                      const list = customListMap[entry.key];
                      const items = list?.items || [];
                      const matchesText =
                        !settingsSearchQuery
                        || [entry.label, entry.key, entry.sectionTitle, ...(entry.usages || [])]
                          .some((value) => matchesSettingsSearch(value))
                        || items.some((item) => matchesSettingsSearch(item.value));
                      const matchesSection = manageListCategoryFilter === 'all' || entry.sectionId === manageListCategoryFilter;
                      return matchesText && matchesSection;
                    });

                    if (filteredCatalog.length === 0) {
                      return <div className="empty-state">No lists match your search.</div>;
                    }

                    const selectedCatalog =
                      filteredCatalog.find((entry) => entry.key === selectedManageListKey)
                      || filteredCatalog[0];
                    const selectedList = customListMap[selectedCatalog?.key];
                    const selectedItemsRaw = selectedList?.items || [];
                    const selectedCatalogMetaValues = [
                      selectedCatalog?.label,
                      selectedCatalog?.key,
                      selectedCatalog?.sectionTitle,
                      ...((selectedCatalog?.usages || []))
                    ];
                    const selectedCatalogMatchesMeta = !settingsSearchQuery
                      || selectedCatalogMetaValues.some((value) => matchesSettingsSearch(value));
                    const selectedItems = settingsSearchQuery && !selectedCatalogMatchesMeta
                      ? selectedItemsRaw.filter((item) => matchesSettingsSearch(item.value))
                      : selectedItemsRaw;
                    const valueSearchActive = Boolean(settingsSearchQuery && !selectedCatalogMatchesMeta);
                    const listValueCounts = selectedItemsRaw.reduce((map, item) => {
                      const key = String(item?.value || '').trim().toLowerCase();
                      if (!key) return map;
                      map[key] = (map[key] || 0) + 1;
                      return map;
                    }, {});
                    const duplicateCount = Object.values(listValueCounts).filter((count) => count > 1).length;
                    const blankCount = selectedItemsRaw.filter((item) => !String(item?.value || '').trim()).length;

                    return (
                      <div className="list-manager-shell">
                        <aside className="list-manager-catalog">
                          <div className="list-manager-catalog-head">
                            <h3>List Catalog</h3>
                            <span>{filteredCatalog.length} list(s)</span>
                          </div>
                          <div className="list-manager-filter">
                            <select
                              value={manageListCategoryFilter}
                              onChange={(e) => setManageListCategoryFilter(e.target.value)}
                            >
                              {sectionOptions.map((option) => (
                                <option key={option.id} value={option.id}>{option.title}</option>
                              ))}
                            </select>
                          </div>
                          <div className="list-manager-catalog-list">
                            {filteredCatalog.map((entry) => {
                              const list = customListMap[entry.key];
                              const count = list?.items?.length || 0;
                              const initialized = Boolean(list);
                              const isSelected = selectedCatalog?.key === entry.key;
                              return (
                                <button
                                  key={entry.key}
                                  type="button"
                                  className={`list-manager-catalog-item ${isSelected ? 'active' : ''}`}
                                  onClick={() => setSelectedManageListKey(entry.key)}
                                >
                                  <div className="list-manager-catalog-item-head">
                                    <strong>{entry.label}</strong>
                                    <span className={`list-manager-status-pill ${initialized ? 'ready' : 'empty'}`}>
                                      {initialized ? `${count}` : '0'}
                                    </span>
                                  </div>
                                  <small>{entry.sectionTitle}</small>
                                </button>
                              );
                            })}
                          </div>
                        </aside>

                        <section className="list-manager-editor">
                          <div className="list-manager-editor-head">
                            <div>
                              <h3>{selectedCatalog.label}</h3>
                              <p className="helper-text">
                                {selectedCatalog.sectionTitle} · Used in {(selectedCatalog.usages || []).join(', ')}
                              </p>
                            </div>
                            <span className="helper-text">{selectedCatalog.key}</span>
                          </div>
                          <div className="list-manager-add-row">
                            <input
                              type="text"
                              value={customListInputs[selectedCatalog.key] || ''}
                              onChange={(e) =>
                                setCustomListInputs((prev) => ({
                                  ...prev,
                                  [selectedCatalog.key]: e.target.value
                                }))
                              }
                              placeholder={`Add value to ${selectedCatalog.label}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomListItem(selectedCatalog.key);
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn-edit"
                              onClick={() => handleAddCustomListItem(selectedCatalog.key)}
                            >
                              Add Value
                            </button>
                          </div>
                          {!selectedList && (
                            <p className="helper-text">
                              This list will be initialized automatically when you add the first value.
                            </p>
                          )}
                          {selectedItems.length === 0 ? (
                            <div className="empty-state">
                              {valueSearchActive ? 'No values match your search.' : 'No values in this list yet.'}
                            </div>
                          ) : (
                            <div className="list-manager-values">
                              {selectedItems.map((item) => {
                                const editingValue = editingListItems[item.id];
                                return (
                                  <div key={item.id} className="list-manager-value-item">
                                    {editingValue !== undefined ? (
                                      <>
                                        <input
                                          type="text"
                                          value={editingValue}
                                          onChange={(e) =>
                                            setEditingListItems((prev) => ({
                                              ...prev,
                                              [item.id]: e.target.value
                                            }))
                                          }
                                        />
                                        <div className="list-manager-value-actions">
                                          <button
                                            type="button"
                                            className="btn-edit"
                                            onClick={() => handleUpdateCustomListItem(item.id, editingValue)}
                                          >
                                            Save
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-delete"
                                            onClick={() =>
                                              setEditingListItems((prev) => {
                                                const next = { ...prev };
                                                delete next[item.id];
                                                return next;
                                              })
                                            }
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <span className="list-manager-value-text" title={item.value}>{item.value}</span>
                                        <div className="list-manager-value-actions">
                                          <button
                                            type="button"
                                            className="btn-edit"
                                            onClick={() =>
                                              setEditingListItems((prev) => ({
                                                ...prev,
                                                [item.id]: item.value
                                              }))
                                            }
                                          >
                                            <Pencil className="btn-icon" size={14} />
                                            <span>Edit</span>
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-delete-small"
                                            onClick={() => handleDeleteCustomListItem(item.id)}
                                            aria-label={`Delete ${item.value}`}
                                          >
                                            <Trash2 className="btn-icon" size={14} />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </section>

                        <aside className="list-manager-usage">
                          <div className="list-manager-usage-card">
                            <h4>Usage</h4>
                            <p className="helper-text">{selectedCatalog.sectionTitle}</p>
                            <div className="list-manager-chip-row">
                              {(selectedCatalog.usages || []).map((usage) => (
                                <span key={usage} className="list-manager-chip">{usage}</span>
                              ))}
                            </div>
                          </div>
                          <div className="list-manager-usage-card">
                            <h4>Validation</h4>
                            <div className={`list-manager-validation ${duplicateCount > 0 || blankCount > 0 ? 'warn' : 'ok'}`}>
                              {duplicateCount > 0 || blankCount > 0
                                ? `${duplicateCount} duplicate(s), ${blankCount} blank value(s)`
                                : 'No structural issues found'}
                            </div>
                            <small className="helper-text">
                              Duplicate or blank values can cause dropdown confusion in forms.
                            </small>
                          </div>
                        </aside>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'settings' && currentTabConfig?.mode !== 'custom_form' && settingsTab === 'system' && (
              <div className="settings-shell" data-tour-id="system-settings">
                <div className="settings-content">
                  {renderSettingsHeader({
                    title: 'System Settings',
                    description: 'Tune thresholds, colors, and defaults for stock health.',
                    dirty: settingsDirty,
                    actions: (
                      <div className="settings-header-buttons">
                        {settingsDirty && (
                          <button
                            type="button"
                            className="btn-delete"
                            onClick={() => setSettings(savedSettings)}
                          >
                            Undo Changes
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-edit"
                          onClick={() => {
                            if (!window.confirm('Reset settings to defaults?')) return;
                            setSettings(DEFAULT_SETTINGS);
                          }}
                        >
                          Reset Defaults
                        </button>
                        <button
                          type="button"
                          className="btn-ghost settings-link-button"
                          onClick={() => setSettingsTab('customLists')}
                        >
                          Open Manage Lists
                        </button>
                      </div>
                    )
                  })}
                  {(() => {
                  const colored = settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true;
                  const validOrder = thresholdsValid;
                  const dirty = settingsDirty;
                  const showDisplay = matchesSettingsSection('Display', ['colors', 'indicator', 'stock levels', 'font', 'text size']);
                  const showPipeline = matchesSettingsSection(
                    'Process Pipeline',
                    ['pipeline', 'stages', 'process', ...pipelineStages]
                  );
                  const showChallanBranding = matchesSettingsSection(
                    'Challan Branding',
                    ['challan', 'branding', 'logo', 'legal name', 'address', 'gst', 'pan', 'contact', 'generated by', 'qr', 'print', 'pdf']
                  );
                  const showThresholds = matchesSettingsSection('Stock Thresholds', ['critical', 'low', 'medium', 'levels']);
                  const showDefaults = matchesSettingsSection('Defaults', ['reset', 'factory', 'recommended']);
                  const showThresholdDefaults = showThresholds || showDefaults;
                  const visibleCards = [showDisplay, showPipeline, showChallanBranding, showThresholdDefaults].filter(Boolean).length;
                  const brandingShowQr = settings.challan_branding_show_qr === 'true' || settings.challan_branding_show_qr === true;

                  const setPipeline = (nextStages) => {
                    setSettings({ ...settings, material_pipeline: JSON.stringify(nextStages) });
                  };
                  const addPipelineStage = () => {
                    const value = newPipelineStage.trim();
                    if (!value) return;
                    setPipeline([...pipelineStages, value]);
                    setNewPipelineStage('');
                  };

                  const updatePipelineStage = (index, value) => {
                    const next = pipelineStages.map((stage, idx) => (idx === index ? value : stage));
                    setPipeline(next);
                  };

                  const movePipelineStage = (index, direction) => {
                    const targetIndex = direction === 'up' ? index - 1 : index + 1;
                    if (targetIndex < 0 || targetIndex >= pipelineStages.length) return;
                    const next = [...pipelineStages];
                    const [item] = next.splice(index, 1);
                    next.splice(targetIndex, 0, item);
                    setPipeline(next);
                  };

                  const removePipelineStage = (index) => {
                    const next = pipelineStages.filter((_, idx) => idx !== index);
                    setPipeline(next);
                  };

                  const handleSave = async () => {
                    if (!validOrder) return;
                    try {
                      await api.updateSettings(settings);
                      setSuccess('Settings updated successfully!');
                      setSavedSettings(settings);
                      loadData();
                    } catch (err) {
                      setError(err.message);
                    }
                  };

                  return (
                    <div className="settings-grid settings-system-grid">
                      {visibleCards === 0 && (
                        <div className="empty-state settings-span-2">No settings match your search.</div>
                      )}

                      {showDisplay && (
                        <div className="section-card settings-system-card settings-display-card">
                          <div className="card-title-row">
                            <h3>Display & Labels</h3>
                            <span className="settings-inline-count">Interface</span>
                          </div>
                          <p className="helper-text">Control how information appears across the app.</p>
                          <div className="settings-toggle-stack">
                          <div className="toggle-row settings-toggle-row">
                            <label className="settings-switch-row">
                              <div className="settings-switch-copy">
                                <strong>Enable color indicators</strong>
                                <small>{colored ? 'Stock levels are color-coded based on thresholds.' : 'Stock levels are shown in plain text.'}</small>
                              </div>
                              <span className="settings-switch-control">
                                <input
                                  type="checkbox"
                                  checked={colored}
                                  onChange={(e) => setSettings({ ...settings, color_indicators_enabled: e.target.checked })}
                                  aria-label="Enable color indicators"
                                />
                                <span className="settings-switch-slider" />
                              </span>
                            </label>
                          </div>
                          <div className="settings-font-size-row">
                            <div className="settings-font-size-copy">
                              <strong>Font size</strong>
                              <small>Choose how large text appears throughout the dashboard.</small>
                            </div>
                            <div className="font-size-toggle" role="group" aria-label="Select dashboard font size">
                              {FONT_SIZE_OPTIONS.map((option) => {
                                const isActive = selectedFontSize === option.value;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className={`font-size-toggle-btn ${isActive ? 'active' : ''}`}
                                    onClick={() => setSettings({ ...settings, font_size: option.value })}
                                    aria-pressed={isActive}
                                    aria-label={`${option.label} font size`}
                                  >
                                    <span className={`font-size-toggle-icon ${option.value}`}>{option.iconLabel}</span>
                                    <span>{option.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          </div>
                          <div className="tooltip-row settings-tooltip-row">
                            <div className="tooltip">
                              What do these colors mean?
                              <div className="tooltip-content">
                                <div className="legend-item">
                                  <span className="legend-swatch legend-critical" /> Critical (≤ critical)
                                </div>
                                <div className="legend-item">
                                  <span className="legend-swatch legend-low" /> Low (≤ low)
                                </div>
                                <div className="legend-item">
                                  <span className="legend-swatch legend-medium" /> Medium (≤ medium)
                                </div>
                                <div className="legend-item">
                                  <span className="legend-swatch legend-high" /> High (&gt; medium)
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {showPipeline && (
                        <div className="section-card settings-system-card settings-pipeline-card">
                          <div className="card-title-row">
                            <h3>Manufacturing Pipeline</h3>
                            <span className="settings-inline-count">{pipelineStages.length} stages</span>
                          </div>
                          <p className="helper-text">Define the stages in your workflow.</p>
                          {pipelineStages.length === 0 ? (
                            <p className="helper-text">No stages yet. Add steps like “Warehouse → Dye House → Contractor”.</p>
                          ) : (
                            <div className="pipeline-settings">
                              <div className="pipeline-settings-header">
                                <span>Stage</span>
                                <span />
                              </div>
                              <div className="pipeline-list">
                                {pipelineStages.map((stage, index) => (
                                  <div key={`${stage}-${index}`} className="pipeline-row">
                                    <div className="pipeline-order">
                                      <button
                                        type="button"
                                        className="btn-edit pipeline-move"
                                        onClick={() => movePipelineStage(index, 'up')}
                                        disabled={index === 0}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-edit pipeline-move"
                                        onClick={() => movePipelineStage(index, 'down')}
                                        disabled={index === pipelineStages.length - 1}
                                      >
                                        ↓
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      value={stage}
                                      onChange={(e) => updatePipelineStage(index, e.target.value)}
                                      placeholder="Stage name"
                                    />
                                    <button
                                      type="button"
                                      className="btn-delete-small"
                                      onClick={() => removePipelineStage(index)}
                                      aria-label={`Remove ${stage}`}
                                      title="Remove stage"
                                    >
                                      <Trash2 className="btn-icon" size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="pipeline-add">
                            <input
                              type="text"
                              value={newPipelineStage}
                              onChange={(e) => setNewPipelineStage(e.target.value)}
                              placeholder="Add a stage (e.g. Warehouse)"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addPipelineStage();
                                }
                              }}
                            />
                            <button type="button" className="btn-edit" onClick={addPipelineStage}>
                              <Plus className="btn-icon" size={16} />
                              <span>Add Stage</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {showChallanBranding && (
                        <div className="section-card settings-system-card settings-challan-branding-card">
                          <div className="card-title-row">
                            <h3>Challan Branding</h3>
                            <span className="settings-inline-count">Print/PDF</span>
                          </div>
                          <p className="helper-text">Used in challan print preview and Save as PDF output.</p>
                          <div className="form-group">
                            <label>Logo URL (optional)</label>
                            <input
                              type="text"
                              value={settings.challan_branding_logo_url || ''}
                              onChange={(e) => setSettings({ ...settings, challan_branding_logo_url: e.target.value })}
                              placeholder="https://your-domain.com/logo.png"
                            />
                            <small className="helper-text">Use a direct public image URL or existing hosted logo path.</small>
                          </div>
                          <div className="form-group">
                            <label>Legal Name</label>
                            <input
                              type="text"
                              value={settings.challan_branding_legal_name || ''}
                              onChange={(e) => setSettings({ ...settings, challan_branding_legal_name: e.target.value })}
                              placeholder="Falls back to company name if blank"
                            />
                          </div>
                          <div className="form-group">
                            <label>Address</label>
                            <textarea
                              value={settings.challan_branding_address || ''}
                              onChange={(e) => setSettings({ ...settings, challan_branding_address: e.target.value })}
                              rows={3}
                              placeholder="Registered address"
                            />
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>GST (optional)</label>
                              <input
                                type="text"
                                value={settings.challan_branding_gst || ''}
                                onChange={(e) => setSettings({ ...settings, challan_branding_gst: e.target.value })}
                                placeholder="GSTIN"
                              />
                            </div>
                            <div className="form-group">
                              <label>PAN (optional)</label>
                              <input
                                type="text"
                                value={settings.challan_branding_pan || ''}
                                onChange={(e) => setSettings({ ...settings, challan_branding_pan: e.target.value })}
                                placeholder="PAN"
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Contact</label>
                            <input
                              type="text"
                              value={settings.challan_branding_contact || ''}
                              onChange={(e) => setSettings({ ...settings, challan_branding_contact: e.target.value })}
                              placeholder="Phone / email"
                            />
                          </div>
                          <div className="form-group">
                            <label>Generated By Label</label>
                            <input
                              type="text"
                              value={settings.challan_branding_generated_by || ''}
                              onChange={(e) => setSettings({ ...settings, challan_branding_generated_by: e.target.value })}
                              placeholder="Generated by Inventory Management Software"
                            />
                          </div>
                          <div className="form-group">
                            <label className="toggle-label">
                              <input
                                type="checkbox"
                                checked={brandingShowQr}
                                onChange={(e) => setSettings({ ...settings, challan_branding_show_qr: e.target.checked })}
                              />
                              <span>Enable QR block by default on challans</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {showThresholdDefaults && (
                        <div className="section-card settings-span-2 settings-system-card settings-threshold-card settings-threshold-defaults-card">
                          <div className="card-title-row">
                            <h3>Stock Thresholds & Defaults</h3>
                            <span className={`settings-inline-count ${validOrder ? 'valid' : 'invalid'}`}>
                              {validOrder ? 'Valid order' : 'Needs fix'}
                            </span>
                          </div>
                          <p className="helper-text">
                            Tune stock-level bands and quickly reset to the recommended baseline when needed.
                          </p>

                          {showThresholds && (
                            <div className="settings-threshold-section">
                              <div className="settings-threshold-section-head">
                                <strong>Threshold Controls</strong>
                                <small>Set the levels that drive stock status colors.</small>
                              </div>
                              <div className="threshold-row">
                                <div className="threshold-label">
                                  <strong>Critical</strong>
                                  <small>Stock at or below this level is critical.</small>
                                </div>
                                <div className="threshold-inputs">
                                  <input
                                    type="range"
                                    min="0"
                                    max="1000"
                                    step="1"
                                    value={settings.stock_threshold_critical}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_critical: e.target.value })}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={settings.stock_threshold_critical}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_critical: e.target.value })}
                                  />
                                </div>
                              </div>

                              <div className="threshold-row">
                                <div className="threshold-label">
                                  <strong>Low</strong>
                                  <small>Stock at or below this level is low.</small>
                                </div>
                                <div className="threshold-inputs">
                                  <input
                                    type="range"
                                    min="0"
                                    max="1000"
                                    step="1"
                                    value={settings.stock_threshold_low}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_low: e.target.value })}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={settings.stock_threshold_low}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_low: e.target.value })}
                                  />
                                </div>
                              </div>

                              <div className="threshold-row">
                                <div className="threshold-label">
                                  <strong>Medium</strong>
                                  <small>Stock at or below this level is good.</small>
                                </div>
                                <div className="threshold-inputs">
                                  <input
                                    type="range"
                                    min="0"
                                    max="1000"
                                    step="1"
                                    value={settings.stock_threshold_medium}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_medium: e.target.value })}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={settings.stock_threshold_medium}
                                    onChange={(e) => setSettings({ ...settings, stock_threshold_medium: e.target.value })}
                                  />
                                </div>
                              </div>

                              <div className={`validation-row ${validOrder ? 'valid' : 'invalid'}`}>
                                {validOrder ? 'Thresholds look good (Critical < Low < Medium).' : 'Fix order: Critical must be lower than Low, and Low lower than Medium.'}
                              </div>
                            </div>
                          )}

                          <div className="setting-preview-card">
                            <strong>Current Values</strong>
                            <div className="settings-value-grid">
                              <div className="settings-value-chip critical">
                                <span>Critical</span>
                                <strong>≤ {settings.stock_threshold_critical} Kg</strong>
                              </div>
                              <div className="settings-value-chip low">
                                <span>Low</span>
                                <strong>≤ {settings.stock_threshold_low} Kg</strong>
                              </div>
                              <div className="settings-value-chip medium">
                                <span>Medium</span>
                                <strong>≤ {settings.stock_threshold_medium} Kg</strong>
                              </div>
                              <div className="settings-value-chip high">
                                <span>High</span>
                                <strong>&gt; {settings.stock_threshold_medium} Kg</strong>
                              </div>
                            </div>
                          </div>
                          <div className="settings-threshold-section challan-format-section">
                            <div className="settings-threshold-section-head">
                              <strong>Challan Numbering</strong>
                              <small>Select numbering pattern for all new challans.</small>
                            </div>
                            <div className="form-group">
                              <label>Format</label>
                              <select
                                value={settings.challan_number_format || 'calendar'}
                                onChange={(e) => setSettings({ ...settings, challan_number_format: e.target.value })}
                              >
                                <option value="calendar">CH-YYYY-#### (Calendar Year)</option>
                                <option value="financial_year">CH/YY-YY/#### (Financial Year)</option>
                              </select>
                            </div>
                          </div>
                          {showDefaults && (
                            <div className="settings-defaults-section">
                              <div className="settings-threshold-section-head">
                                <strong>Recommended Baseline</strong>
                                <small>Apply these defaults anytime for a clean reset.</small>
                              </div>
                              <div className="defaults-box settings-defaults-grid">
                                <div className="settings-value-chip critical">
                                  <span>Critical</span>
                                  <strong>{DEFAULT_SETTINGS.stock_threshold_critical} Kg</strong>
                                </div>
                                <div className="settings-value-chip low">
                                  <span>Low</span>
                                  <strong>{DEFAULT_SETTINGS.stock_threshold_low} Kg</strong>
                                </div>
                                <div className="settings-value-chip medium">
                                  <span>Medium</span>
                                  <strong>{DEFAULT_SETTINGS.stock_threshold_medium} Kg</strong>
                                </div>
                                <div className="settings-value-chip neutral">
                                  <span>Color Indicators</span>
                                  <strong>{DEFAULT_SETTINGS.color_indicators_enabled}</strong>
                                </div>
                                <div className="settings-value-chip neutral">
                                  <span>Font Size</span>
                                  <strong>{DEFAULT_SETTINGS.font_size}</strong>
                                </div>
                                <div className="settings-value-chip neutral">
                                  <span>Challan Format</span>
                                  <strong>{DEFAULT_SETTINGS.challan_number_format}</strong>
                                </div>
                              </div>
                              <div className="settings-defaults-actions">
                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => {
                                    if (!window.confirm('Reset settings to defaults?')) return;
                                    setSettings(DEFAULT_SETTINGS);
                                  }}
                                >
                                  Reset to Defaults
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {dirty && (
                        <div className="settings-savebar settings-span-2">
                          <span>Unsaved changes</span>
                          <div className="savebar-actions">
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => setSettings(savedSettings)}
                            >
                              Discard
                            </button>
                            <button
                              type="button"
                              className="btn-edit"
                              disabled={!validOrder}
                              onClick={handleSave}
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'settings' && currentTabConfig?.mode !== 'custom_form' && settingsTab === 'rawMaterials' && (
              <div className="settings-shell" data-tour-id="raw-materials-settings">
                <div className="settings-content">
                  {renderSettingsHeader({
                    title: 'Raw Materials',
                    description: 'Define Level 1 materials and Level 2 characteristics. Level 3 values are managed in Manage Lists.',
                    dirty: settingsDirty,
                    actions: (
                      <div className="settings-header-buttons">
                        {settingsDirty && (
                          <button
                            type="button"
                            className="btn-delete"
                            onClick={() => setSettings(savedSettings)}
                          >
                            Undo Changes
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {renderSettingsSummary()}
                  {(() => {
                    const validOrder = thresholdsValid;
                    const dirty = settingsDirty;
                    const filteredRawMaterials = settingsSearchQuery
                      ? rawMaterials
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) =>
                          matchesSettingsSection(item.name, [
                            item.unit_label || '',
                            item.consume_stage || '',
                            ...(item.characteristics || []).map((entry) => entry.name)
                          ])
                        )
                      : rawMaterials.map((item, index) => ({ item, index }));
                    const selectedRawMaterial = rawMaterials[selectedRawMaterialIndex] || null;
                    const selectedCategories = selectedRawMaterial?.characteristics || [];

                    const handleSave = async () => {
                      try {
                        await api.updateSettings(settings);
                        setSuccess('Settings updated successfully!');
                        setSavedSettings(settings);
                        loadData();
                      } catch (err) {
                        setError(err.message);
                      }
                    };

                    return (
                      <div className="settings-grid">
                        <div className="raw-materials-card settings-span-2">
                          <div className="raw-materials-card-header">
                            <div>
                              <h3>Raw Materials</h3>
                              <p className="helper-text">Manage material types and characteristics in one workspace. Values are maintained in Manage Lists.</p>
                            </div>
                            <div className="settings-header-buttons">
                              <button
                                type="button"
                                className="btn-edit"
                                onClick={openRawMaterialsImportModal}
                              >
                                <FileDown className="btn-icon" size={16} />
                                <span>Import CSV</span>
                              </button>
                              <button
                                type="button"
                                className="btn-edit"
                                onClick={downloadRawMaterialsTemplate}
                              >
                                <Download className="btn-icon" size={16} />
                                <span>Download Template</span>
                              </button>
                              <button
                                type="button"
                                className="btn-edit"
                                onClick={downloadRawMaterialsCsv}
                              >
                                <Download className="btn-icon" size={16} />
                                <span>Export CSV</span>
                              </button>
                            </div>
                          </div>

                          {(() => {
                            const activeMaterials = rawMaterials.filter((item) => !item.archived);
                            const archivedMaterialCount = rawMaterials.filter((item) => item.archived).length;
                            const mainMaterial = activeMaterials.find((item) => item.is_main)?.name || 'Not set';
                            const activeMaterialCount = activeMaterials.length;
                            const characteristicCount = rawMaterials.reduce(
                              (sum, item) => sum + (Array.isArray(item.characteristics) ? item.characteristics.length : 0),
                              0
                            );
                            const missingRequiredLists = rawMaterials.reduce(
                              (sum, item) => sum + (item.characteristics || []).filter(
                                (entry) => entry.mandatory && (!entry.list_key || !customListMap[entry.list_key])
                              ).length,
                              0
                            );
                            const duplicateMaterialNames = new Set();
                            const seenMaterialNames = new Set();
                            rawMaterials.forEach((item) => {
                              const name = String(item?.name || '').trim().toLowerCase();
                              if (!name) return;
                              if (seenMaterialNames.has(name)) duplicateMaterialNames.add(name);
                              seenMaterialNames.add(name);
                            });
                            const validationIssues = [];
                            rawMaterials.forEach((item, index) => {
                              const name = String(item?.name || '').trim();
                              if (!name) {
                                validationIssues.push({ key: `material-name-${index}`, text: `Material #${index + 1} needs a name.`, index });
                              } else if (duplicateMaterialNames.has(name.toLowerCase())) {
                                validationIssues.push({ key: `material-duplicate-${index}`, text: `"${name}" is duplicated.`, index });
                              }
                              const characteristics = Array.isArray(item.characteristics) ? item.characteristics : [];
                              const localNames = new Set();
                              characteristics.forEach((entry, categoryIndex) => {
                                const categoryName = String(entry?.name || '').trim();
                                const normalizedCategory = categoryName.toLowerCase();
                                if (!categoryName) {
                                  validationIssues.push({
                                    key: `category-name-${index}-${categoryIndex}`,
                                    text: `A characteristic in "${item.name || `Material #${index + 1}`}" has no name.`,
                                    index
                                  });
                                } else if (localNames.has(normalizedCategory)) {
                                  validationIssues.push({
                                    key: `category-duplicate-${index}-${categoryIndex}`,
                                    text: `"${item.name || `Material #${index + 1}`}" has duplicate characteristic "${categoryName}".`,
                                    index
                                  });
                                } else {
                                  localNames.add(normalizedCategory);
                                }
                              });
                            });
                            const selectedUsage = selectedRawMaterial ? rawMaterialUsageByName[selectedRawMaterial.name] || {
                              productCount: 0,
                              openOrderCount: 0,
                              transactionCount: 0,
                              lastTransactionAt: null,
                              currentStock: 0
                            } : null;

                            return (
                              <>
                                <div className="raw-materials-phase-strip" aria-label="Raw material setup phases">
                                  <div className="raw-materials-phase-pill is-active">
                                    <span>1</span>
                                    <div>
                                      <strong>Add Types</strong>
                                      <small>Create the material master list.</small>
                                    </div>
                                  </div>
                                  <div className={`raw-materials-phase-pill ${selectedRawMaterial ? 'is-active' : ''}`}>
                                    <span>2</span>
                                    <div>
                                      <strong>Configure Material</strong>
                                      <small>{selectedRawMaterial ? `Editing ${selectedRawMaterial.name || `Material #${selectedRawMaterialIndex + 1}`}` : 'Select a type to configure fields.'}</small>
                                    </div>
                                  </div>
                                  <div className={`raw-materials-phase-pill ${validationIssues.length + missingRequiredLists === 0 && rawMaterials.length > 0 ? 'is-complete' : ''}`}>
                                    <span>3</span>
                                    <div>
                                      <strong>Validate</strong>
                                      <small>{validationIssues.length + missingRequiredLists === 0 ? 'Ready to save.' : 'Resolve validation issues before saving.'}</small>
                                    </div>
                                  </div>
                                </div>
                                <div className="raw-materials-summary-strip">
                                  <div className="raw-materials-summary-card">
                                    <span>Total Types</span>
                                    <strong>{rawMaterials.length}</strong>
                                  </div>
                                  <div className="raw-materials-summary-card">
                                    <span>Active Types</span>
                                    <strong>{activeMaterialCount}</strong>
                                  </div>
                                  <div className="raw-materials-summary-card">
                                    <span>Main Material</span>
                                    <strong>{mainMaterial}</strong>
                                  </div>
                                  <div className="raw-materials-summary-card">
                                    <span>Characteristics</span>
                                    <strong>{characteristicCount}</strong>
                                  </div>
                                  <div className="raw-materials-summary-card">
                                    <span>Archived</span>
                                    <strong>{archivedMaterialCount}</strong>
                                  </div>
                                  <div className={`raw-materials-summary-card ${validationIssues.length > 0 || missingRequiredLists > 0 ? 'is-warning' : 'is-positive'}`}>
                                    <span>Validation</span>
                                    <strong>{validationIssues.length + missingRequiredLists} issue(s)</strong>
                                  </div>
                                </div>

                                <div className="raw-materials-top-composer" aria-label="Add raw material type">
                                  <div className="raw-materials-top-composer-head">
                                    <strong>Add New Type</strong>
                                    <small>Always available for quick entry</small>
                                  </div>
                                  <div className="raw-materials-top-composer-grid">
                                    <input
                                      type="text"
                                      value={newRawMaterial.name}
                                      onChange={(e) => setNewRawMaterial({ ...newRawMaterial, name: e.target.value })}
                                      placeholder="Material name"
                                    />
                                    <input
                                      type="text"
                                      value={newRawMaterial.unitLabel}
                                      onChange={(e) => setNewRawMaterial({ ...newRawMaterial, unitLabel: e.target.value })}
                                      placeholder="Unit label (Kg, pcs, meter)"
                                    />
                                    <select
                                      value={newRawMaterial.consumeStage}
                                      onChange={(e) => setNewRawMaterial({ ...newRawMaterial, consumeStage: e.target.value })}
                                    >
                                      <option value="">Not auto-consumed</option>
                                      {pipelineStages.map((stage) => (
                                        <option key={`new-rm-stage-${stage}`} value={stage}>{stage}</option>
                                      ))}
                                    </select>
                                    <button type="button" className="btn-edit" onClick={addRawMaterial}>
                                      <Plus className="btn-icon" size={16} />
                                      <span>Add Type</span>
                                    </button>
                                  </div>
                                </div>

                                <div className="raw-materials-workspace">
                                  <div className="raw-materials-types">
                                    <div className="raw-materials-subheader">
                                      <h4>Material Types</h4>
                                      <p className="helper-text">Select a type to edit on the right.</p>
                                    </div>
                                    {rawMaterials.length === 0 ? (
                                      <div className="empty-state">No raw material types yet.</div>
                                    ) : filteredRawMaterials.length === 0 ? (
                                      <p className="helper-text">No materials match your search.</p>
                                    ) : (
                                      <div className="raw-materials-type-list">
                                        {filteredRawMaterials.map(({ item, index }) => {
                                          const isSelected = index === selectedRawMaterialIndex;
                                          const hasIssue = validationIssues.some((issue) => issue.index === index);
                                          return (
                                            <button
                                              key={`${item.name}-${index}`}
                                              type="button"
                                              className={`raw-materials-type-row ${isSelected ? 'active' : ''} ${hasIssue ? 'has-issue' : ''} ${item.archived ? 'is-archived' : ''}`}
                                              onClick={() => setSelectedRawMaterialIndex(index)}
                                            >
                                              <div className="raw-materials-type-info">
                                                <div className="raw-materials-type-title">{item.name || `Material #${index + 1}`}</div>
                                                <div className="raw-materials-type-meta">
                                                  {item.unit_label || 'Unit'} · {Array.isArray(item.characteristics) ? item.characteristics.length : 0} characteristics · {item.consume_stage || 'No auto stage'}
                                                </div>
                                              </div>
                                              <div className="raw-materials-type-actions">
                                                {item.is_main && <span className="raw-materials-pill">Main</span>}
                                                {item.archived && <span className="raw-materials-pill is-archived">Archived</span>}
                                                <button
                                                  type="button"
                                                  className="btn-edit"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateRawMaterial(index, { is_main: true });
                                                  }}
                                                  disabled={item.archived}
                                                >
                                                  <CheckCircle2 className="btn-icon" size={14} />
                                                  <span>Set Main</span>
                                                </button>
                                                <button
                                                  type="button"
                                                  className={item.archived ? 'btn-edit' : 'btn-delete-small'}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleRawMaterialArchived(index);
                                                  }}
                                                  aria-label={`${item.archived ? 'Restore' : 'Archive'} ${item.name}`}
                                                  title={item.archived ? 'Restore raw material type' : 'Archive raw material type'}
                                                >
                                                  {item.archived ? <ArrowLeft className="btn-icon" size={14} /> : <Trash2 className="btn-icon" size={14} />}
                                                </button>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>

                                  <div className="raw-materials-categories">
                                    {!selectedRawMaterial ? (
                                      <div className="empty-state">Select a material type to edit details and characteristics.</div>
                                    ) : (
                                      <>
                                        <div className="raw-materials-subheader">
                                          <h4>Material Editor</h4>
                                          <p className="helper-text">Editing: {selectedRawMaterial.name || `Material #${selectedRawMaterialIndex + 1}`}</p>
                                        </div>

                                        {selectedRawMaterial.archived && (
                                          <div className="raw-materials-archive-banner">
                                            This material is archived. It stays available in historical products and transactions, but new setup flows should avoid it.
                                          </div>
                                        )}

                                        <div className="raw-materials-usage-panel">
                                          <div className="raw-materials-usage-card">
                                            <span>Products Using It</span>
                                            <strong>{selectedUsage?.productCount || 0}</strong>
                                          </div>
                                          <div className="raw-materials-usage-card">
                                            <span>Open Orders</span>
                                            <strong>{selectedUsage?.openOrderCount || 0}</strong>
                                          </div>
                                          <div className="raw-materials-usage-card">
                                            <span>Transactions</span>
                                            <strong>{selectedUsage?.transactionCount || 0}</strong>
                                          </div>
                                          <div className="raw-materials-usage-card">
                                            <span>Current Stock</span>
                                            <strong>{Number(selectedUsage?.currentStock || 0).toFixed(2)}</strong>
                                          </div>
                                          <div className="raw-materials-usage-card">
                                            <span>Last Movement</span>
                                            <strong>{selectedUsage?.lastTransactionAt ? formatDateOnly(selectedUsage.lastTransactionAt, getCurrentTimeZone()) : 'Never'}</strong>
                                          </div>
                                        </div>

                                        <div className="raw-materials-editor-grid">
                                          <div className="form-group">
                                            <label>Name</label>
                                            <input
                                              type="text"
                                              value={selectedRawMaterial.name || ''}
                                              onChange={(e) => updateRawMaterial(selectedRawMaterialIndex, { name: e.target.value })}
                                              placeholder="Material name"
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label>Unit Label</label>
                                            <input
                                              type="text"
                                              value={selectedRawMaterial.unit_label || ''}
                                              onChange={(e) => updateRawMaterial(selectedRawMaterialIndex, { unit_label: e.target.value })}
                                              placeholder="Kg, pcs, meter"
                                            />
                                          </div>
                                          <div className="form-group">
                                            <label>Consume Stage</label>
                                            <select
                                              value={selectedRawMaterial.consume_stage || ''}
                                              onChange={(e) => updateRawMaterial(selectedRawMaterialIndex, { consume_stage: e.target.value })}
                                            >
                                              <option value="">Not auto-consumed</option>
                                              {pipelineStages.map((stage) => (
                                                <option key={`rm-stage-${stage}`} value={stage}>{stage}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="form-group">
                                            <label>Status</label>
                                            <div className="raw-materials-editor-actions">
                                              <button
                                                type="button"
                                                className={`btn-edit raw-materials-main-toggle ${selectedRawMaterial.is_main ? 'active' : ''}`}
                                                onClick={() => updateRawMaterial(selectedRawMaterialIndex, { is_main: !selectedRawMaterial.is_main })}
                                                disabled={selectedRawMaterial.archived}
                                              >
                                                {selectedRawMaterial.is_main ? 'Main enabled' : 'Set as main'}
                                              </button>
                                              <button
                                                type="button"
                                                className={selectedRawMaterial.archived ? 'btn-edit' : 'btn-delete'}
                                                onClick={() => toggleRawMaterialArchived(selectedRawMaterialIndex)}
                                              >
                                                {selectedRawMaterial.archived ? 'Restore Material' : 'Archive Material'}
                                              </button>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="raw-materials-subheader raw-materials-subheader-table">
                                          <h4>Characteristics</h4>
                                          <p className="helper-text">Add and manage characteristic fields used in forms. Value options live in Manage Lists.</p>
                                        </div>
                                        <div className="raw-materials-inline-form raw-materials-inline-form-compact">
                                          <input
                                            type="text"
                                            value={newRawMaterialCategory.name}
                                            onChange={(e) =>
                                              setNewRawMaterialCategory((prev) => ({ ...prev, name: e.target.value }))
                                            }
                                            placeholder="Characteristic name (e.g. Color)"
                                          />
                                          <label className="toggle-inline">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(newRawMaterialCategory.mandatory)}
                                              onChange={(e) =>
                                                setNewRawMaterialCategory((prev) => ({ ...prev, mandatory: e.target.checked }))
                                              }
                                            />
                                            Mandatory
                                          </label>
                                          <button
                                            type="button"
                                            className="btn-edit"
                                            onClick={() => addRawMaterialCategory(selectedRawMaterialIndex)}
                                          >
                                            <Plus className="btn-icon" size={16} />
                                            <span>Add</span>
                                          </button>
                                        </div>

                                        {selectedCategories.length === 0 ? (
                                          <div className="empty-state">No characteristics yet for this material.</div>
                                        ) : (
                                          <div className="raw-materials-characteristics-table-wrap">
                                            <table className="raw-materials-characteristics-table">
                                              <thead>
                                                <tr>
                                                  <th>Characteristic</th>
                                                  <th>Required</th>
                                                  {user?.is_super_admin && <th>List Key</th>}
                                                  <th>Values</th>
                                                  <th>Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {selectedCategories.map((category, categoryIndex) => (
                                                  <tr key={`${category.name}-${categoryIndex}`}>
                                                    <td>
                                                      <input
                                                        type="text"
                                                        value={category.name}
                                                        onChange={(e) =>
                                                          updateRawMaterialCategory(selectedRawMaterialIndex, categoryIndex, { name: e.target.value })
                                                        }
                                                        placeholder="Characteristic name"
                                                      />
                                                    </td>
                                                    <td>
                                                      <label className="toggle-inline">
                                                        <input
                                                          type="checkbox"
                                                          checked={Boolean(category.mandatory)}
                                                          onChange={(e) =>
                                                            updateRawMaterialCategory(selectedRawMaterialIndex, categoryIndex, { mandatory: e.target.checked })
                                                          }
                                                        />
                                                        <span>{category.mandatory ? 'Yes' : 'No'}</span>
                                                      </label>
                                                    </td>
                                                    {user?.is_super_admin && (
                                                      <td>
                                                        <code>{category.list_key}</code>
                                                      </td>
                                                    )}
                                                    <td>
                                                      <button
                                                        type="button"
                                                        className="btn-edit"
                                                        onClick={() => {
                                                          setSelectedManageListKey(category.list_key);
                                                          setManageListCategoryFilter('all');
                                                          setSettingsSearch(category.name);
                                                          setSettingsTab('customLists');
                                                        }}
                                                      >
                                                        Manage Values
                                                      </button>
                                                    </td>
                                                    <td>
                                                      <button
                                                        type="button"
                                                        className="btn-delete-small"
                                                        onClick={() => removeRawMaterialCategory(selectedRawMaterialIndex, categoryIndex)}
                                                        aria-label={`Remove ${category.name}`}
                                                        title="Remove characteristic"
                                                      >
                                                        <Trash2 className="btn-icon" size={14} />
                                                      </button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>

                                {(validationIssues.length + missingRequiredLists) > 0 && (
                                  <div className="raw-materials-validation-panel">
                                    <div className="raw-materials-validation-head">
                                      <strong>Validation</strong>
                                      <span>{validationIssues.length + missingRequiredLists} issue(s)</span>
                                    </div>
                                    <div className="raw-materials-validation-list">
                                      {validationIssues.map((issue) => (
                                        <button
                                          key={issue.key}
                                          type="button"
                                          className="raw-materials-validation-item"
                                          onClick={() => setSelectedRawMaterialIndex(issue.index)}
                                        >
                                          <XCircle className="btn-icon" size={14} />
                                          <span>{issue.text}</span>
                                        </button>
                                      ))}
                                      {missingRequiredLists > 0 && (
                                        <div className="raw-materials-validation-item is-warning">
                                          <XCircle className="btn-icon" size={14} />
                                          <span>{missingRequiredLists} mandatory characteristic(s) are missing a linked values list.</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {dirty && (
                          <div className="settings-savebar settings-span-2">
                            <span>Unsaved changes</span>
                            <div className="savebar-actions">
                              <button
                                type="button"
                                className="btn-delete"
                                onClick={() => setSettings(savedSettings)}
                              >
                                Discard
                              </button>
                              <button
                                type="button"
                                className="btn-edit"
                                disabled={!validOrder}
                                onClick={handleSave}
                              >
                                Save Changes
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'settings' && currentTabConfig?.mode !== 'custom_form' && settingsTab === 'users' && (
              <div className="settings-shell" data-tour-id="user-access-table">
                <div className="settings-content">
                  {renderSettingsHeader({
                    title: 'Manage Users',
                    description: 'Control access, roles, and timezones.',
                    dirty: settingsDirty,
                    actions: (
                      <div className="settings-header-buttons">
                        {settingsDirty && (
                          <>
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => setSettings(savedSettings)}
                            >
                              Discard
                            </button>
                            <button
                              type="button"
                              className="btn-edit"
                              onClick={handleSaveSettings}
                            >
                              Save Access Changes
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="btn-edit"
                          onClick={() => setShowCreateUser(true)}
                        >
                          <Plus className="btn-icon" size={16} />
                          <span>Create User</span>
                        </button>
                      </div>
                    )
                  })}
                  {renderSettingsSummary()}
                  {editingUser && (
                  <div className="modal-overlay" onClick={() => setEditingUser(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Edit User</h3>
                      <form onSubmit={handleUpdateUser}>
                        <div className="form-group">
                          <label>Username *</label>
                          <input
                            type="text"
                            value={editingUser.username}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                username: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>New Password (leave empty to keep current)</label>
                          <input
                            type="password"
                            value={editingUser.password}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                password: e.target.value,
                              })
                            }
                            placeholder="Enter new password (must meet complexity policy) or leave empty"
                          />
                        </div>
                        <div className="form-group">
                          <label>Role *</label>
                          <select
                            value={editingUser.role}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                role: e.target.value,
                              })
                            }
                            required
                          >
                            {roleOptions.map((role) => (
                              <option key={role} value={role}>
                                {formatRoleLabel(role)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="modal-actions">
                          <button type="submit">Update</button>
                          <button type="button" onClick={() => setEditingUser(null)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {showCreateUser && (
                  <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>Create User</h3>
                      <form
                        onSubmit={(e) => {
                          handleAddUser(e);
                          setShowCreateUser(false);
                        }}
                      >
                        <div className="form-group">
                          <label>Username *</label>
                          <input
                            type="text"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder="Username"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Password * <small>{PASSWORD_REQUIREMENTS_TEXT}</small></label>
                          <input
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            placeholder="Password"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Role</label>
                          <select
                            value={newUser.role}
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                          >
                            {roleOptions.map((role) => (
                              <option key={role} value={role}>
                                {formatRoleLabel(role)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="modal-actions">
                          <button type="submit">
                            <Plus className="btn-icon" size={16} />
                            <span>Create User</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowCreateUser(false)}
                          >
                            <X className="btn-icon" size={16} />
                            <span>Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                  {(() => {
                    const filteredUsers = settingsSearchQuery
                      ? users.filter((usr) => {
                        const lockedUntil = usr.locked_until ? new Date(usr.locked_until) : null;
                        const isLocked = lockedUntil && !isNaN(lockedUntil.getTime()) && lockedUntil > new Date();
                        return matchesSettingsSection(usr.username, [
                          usr.role,
                          usr.timezone || '',
                          usr.real_name || '',
                          isLocked ? 'locked' : 'active'
                        ]);
                      })
                      : users;
                    if (users.length === 0) {
                      return <div className="empty-state">No users found.</div>;
                    }
                    if (filteredUsers.length === 0) {
                      return <div className="empty-state">No users match your search.</div>;
                    }
                    return (
                      <div className="table-wrapper">
                        <table className="data-table" style={{ marginTop: '20px' }}>
                          <thead>
                            <tr>
                              <th>Username</th>
                              <th>Role</th>
                              <th>Status</th>
                              <th>Timezone</th>
                              <th>Last Login</th>
                              <th>Created At</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredUsers.map((usr) => {
                              const lockedUntil = usr.locked_until ? new Date(usr.locked_until) : null;
                              const isLocked = lockedUntil && !isNaN(lockedUntil.getTime()) && lockedUntil > new Date();
                              const timeZone = usr.timezone || getBrowserTimeZone();
                              const lastLoginDate = parseUtcTimestamp(usr.last_login_at);
                              const lastLogin = lastLoginDate
                                ? new Intl.DateTimeFormat('en-US', {
                                    timeZone,
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                  }).format(lastLoginDate)
                                : 'Never Logged In';
                              const createdAt = formatDateOnly(usr.created_at, timeZone);
                              return (
                                <tr key={usr.id}>
                                  <td>{usr.username}</td>
                                  <td>
                                    <span className={`badge ${usr.role === 'admin' || usr.role === 'owner' ? 'in' : 'out'}`}>
                                      {usr.role.toUpperCase()}
                                    </span>
                                  </td>
                                  <td>
                                    {isLocked ? (
                                      <span className="badge out">LOCKED</span>
                                    ) : (
                                      <span className="badge in">ACTIVE</span>
                                    )}
                                  </td>
                                  <td>
                                    {user.username === 'admin' ? (
                                      <select
                                        className="select-input compact"
                                        value={timeZone}
                                        onChange={(e) => handleTimezoneChange(usr.id, e.target.value)}
                                      >
                                        <option value="Asia/Kolkata">IST</option>
                                        <option value="America/Chicago">CST</option>
                                      </select>
                                    ) : (
                                      <span>{timeZone}</span>
                                    )}
                                  </td>
                                  <td>{lastLogin}</td>
                                  <td>{createdAt}</td>
                                  <td>
                                    <button
                                      onClick={() => handleEditUser(usr)}
                                      className="btn-edit"
                                    >
                                      <Pencil className="btn-icon" size={14} />
                                      <span>{usr.id === user.id ? 'Edit Profile' : 'Edit'}</span>
                                    </button>
                                    {user.username === 'admin' && isLocked && (
                                      <button
                                        onClick={() =>
                                          openConfirmDialog({
                                            type: 'unlock',
                                            id: usr.username,
                                            title: 'Unlock user?',
                                            message: `This will remove the lock for "${usr.username}".`
                                          })
                                        }
                                        className="btn-edit"
                                      >
                                        <UserCheck className="btn-icon" size={14} />
                                        <span>Unlock</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() =>
                                        openConfirmDialog({
                                          type: 'user',
                                          id: usr.id,
                                          title: 'Delete user?',
                                          message: `This will permanently delete "${usr.username}".`
                                        })
                                      }
                                      className="btn-delete"
                                      disabled={usr.id === user.id || usr.username === 'admin'}
                                    >
                                      <Trash2 className="btn-icon" size={14} />
                                      <span>Delete</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                  <div className={`card role-access-shell ${roleAccessExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="role-access-header role-access-header-toggle">
                      <div>
                        <h3>Role & Action Access</h3>
                        <p className="helper-text">Assign section-level actions for each role. Actions: view, create, edit, delete.</p>
                      </div>
                      <button
                        type="button"
                        className="role-access-toggle-icon"
                        onClick={() => setRoleAccessExpanded((prev) => !prev)}
                        aria-label={roleAccessExpanded ? 'Collapse role access setup' : 'Expand role access setup'}
                        title={roleAccessExpanded ? 'Collapse role access setup' : 'Expand role access setup'}
                      >
                        {roleAccessExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                    {roleAccessExpanded && (
                      <>
                        <div className="role-access-create">
                          <label className="role-access-create-field">
                            <span>Role Name</span>
                            <input
                              type="text"
                              value={newUserRoleName}
                              onChange={(e) => setNewUserRoleName(e.target.value)}
                              placeholder="Example: procurement"
                            />
                          </label>
                          <label className="role-access-create-field">
                            <span>Parent Role (Optional)</span>
                            <select
                              value={newUserRoleParent}
                              onChange={(e) => setNewUserRoleParent(e.target.value)}
                            >
                              <option value="">No parent role</option>
                              {roleNameOptions.map((roleName) => (
                                <option key={`parent-role-${roleName}`} value={roleName}>
                                  {formatRoleLabel(roleName)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={handleAddUserRole}
                          >
                            Add Role
                          </button>
                        </div>
                        <div className="role-access-body">
                          <div className="role-access-list">
                            <div className="role-access-list-search">
                              <span className="role-access-search-icon" aria-hidden="true">
                                <Search size={14} />
                              </span>
                              <input
                                type="text"
                                value={roleSearchQuery}
                                onChange={(e) => setRoleSearchQuery(e.target.value)}
                                placeholder="Search roles..."
                              />
                            </div>
                            {userRoleDefinitions.length === 0 ? (
                              <div className="empty-state">No custom roles yet.</div>
                            ) : filteredRoleDefinitions.length === 0 ? (
                              <div className="empty-state">No roles match your search.</div>
                            ) : filteredRoleDefinitions.map((role) => {
                              const permissionCount = Object.values(resolvedRolePermissionsMap[role.name] || {}).filter((set) => hasAnyPermission(set)).length;
                              const isSelected = selectedRoleDefinition?.name === role.name;
                              const assignedUsers = Number(roleAssignmentCounts[role.name] || 0);
                              return (
                                <button
                                  key={`role-list-${role.name}`}
                                  type="button"
                                  className={`role-access-list-item ${isSelected ? 'active' : ''}`}
                                  onClick={() => setSelectedRoleName(role.name)}
                                >
                                  <div>
                                    <strong>{formatRoleLabel(role.name)}</strong>
                                    {role.inherits_from && <span>Inherits: {formatRoleLabel(role.inherits_from)}</span>}
                                    <span>{assignedUsers} user{assignedUsers === 1 ? '' : 's'}</span>
                                  </div>
                                  <span className="badge">{permissionCount} sections</span>
                                </button>
                              );
                            })}
                          </div>
                          <div className="role-access-editor">
                            {!selectedRoleDefinition ? (
                              <div className="empty-state">Select a role to edit action access.</div>
                            ) : (
                              <>
                                <div className="role-access-editor-header">
                                  <div>
                                    <h4>{formatRoleLabel(selectedRoleDefinition.name)}</h4>
                                    <p className="helper-text">Configure permissions by section. Leave unchecked to deny action.</p>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-delete"
                                    onClick={() => handleRemoveUserRole(selectedRoleIndex)}
                                  >
                                    Remove Role
                                  </button>
                                </div>
                                <div className="role-access-editor-controls">
                                  <label>
                                    Parent Role
                                    <select
                                      value={selectedRoleDefinition.inherits_from || ''}
                                      onChange={(e) => handleSetRoleInheritance(selectedRoleIndex, e.target.value)}
                                    >
                                      <option value="">No parent role</option>
                                      {roleNameOptions
                                        .filter((roleName) => roleName !== selectedRoleDefinition.name)
                                        .map((roleName) => (
                                          <option key={`inherit-${selectedRoleDefinition.name}-${roleName}`} value={roleName}>
                                            {formatRoleLabel(roleName)}
                                          </option>
                                        ))}
                                    </select>
                                  </label>
                                  <div className="role-access-bulk-actions">
                                    <button
                                      type="button"
                                      className="btn-edit-small"
                                      onClick={() => handleApplyRoleBulkPermissions(selectedRoleIndex, 'grant_view')}
                                    >
                                      Grant View All
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-edit-small"
                                      onClick={() => handleApplyRoleBulkPermissions(selectedRoleIndex, 'grant_crud')}
                                    >
                                      Grant CRUD All
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-delete role-access-bulk-clear"
                                      onClick={() => handleApplyRoleBulkPermissions(selectedRoleIndex, 'clear_all')}
                                    >
                                      Clear All
                                    </button>
                                  </div>
                                </div>
                                <div className="role-access-legend">
                                  <span><em className="dot dot-direct" />Direct</span>
                                  <span><em className="dot dot-inherited" />Inherited</span>
                                  <span><em className="dot dot-overridden" />Overridden</span>
                                </div>
                                <div className="table-wrapper role-access-table-wrap">
                                  <table className="data-table role-access-table">
                                    <thead>
                                      <tr>
                                        <th>Section</th>
                                        <th>View</th>
                                        <th>Create</th>
                                        <th>Edit</th>
                                        <th>Delete</th>
                                        <th>Source</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {roleSectionGroups.map((group) => (
                                        <React.Fragment key={`role-group-${group.id}`}>
                                          <tr className="role-access-group-row">
                                            <td colSpan={6}>{group.label}</td>
                                          </tr>
                                          {group.sections.map((tab) => {
                                            const effectivePermissions = createPermissionSet(resolvedRolePermissionsMap[selectedRoleDefinition.name]?.[tab.id] || {});
                                            const hasOwnOverride = Boolean(selectedRoleDefinition.permissions?.[tab.id]);
                                            const sourceClassName = hasOwnOverride
                                              ? 'is-overridden'
                                              : selectedRoleDefinition.inherits_from
                                                ? 'is-inherited'
                                                : 'is-direct';
                                            return (
                                              <tr key={`role-access-row-${selectedRoleDefinition.name}-${tab.id}`}>
                                                <td>{tab.label}</td>
                                                {ROLE_PERMISSION_ACTIONS.map((action) => (
                                                  <td key={`role-access-${tab.id}-${action}`}>
                                                    <input
                                                      type="checkbox"
                                                      checked={effectivePermissions[action]}
                                                      onChange={() =>
                                                        handleToggleUserRolePermission(
                                                          selectedRoleIndex,
                                                          tab.id,
                                                          action,
                                                          effectivePermissions
                                                        )
                                                      }
                                                    />
                                                  </td>
                                                ))}
                                                <td>
                                                  <span className={`role-access-source ${sourceClassName}`}>
                                                    {hasOwnOverride ? 'Overridden' : selectedRoleDefinition.inherits_from ? 'Inherited' : 'Direct'}
                                                  </span>
                                                  {hasOwnOverride && (
                                                    <button
                                                      type="button"
                                                      className="btn-edit-small"
                                                      onClick={() => handleClearRolePermissionOverride(selectedRoleIndex, tab.id)}
                                                      title="Clear override and inherit from parent"
                                                    >
                                                      Clear Override
                                                    </button>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
        </div>
      </div>

      <PipelineTransitionModal
        dialog={pipelineTransitionDialog}
        form={pipelineTransitionForm}
        onClose={closePipelineTransitionDialog}
        onFormChange={setPipelineTransitionForm}
        onSubmit={submitPipelineTransitionDialog}
        getConfig={getPipelineTransitionDialogConfig}
      />

      <PipelineHistoryModal
        order={pipelineHistoryOrder}
        loading={pipelineHistoryLoading}
        rows={pipelineHistoryRows}
        onClose={() => setPipelineHistoryOrder(null)}
        formatDateTime={formatDateTime}
        getCurrentTimeZone={getCurrentTimeZone}
      />

      {editingProduct && (
        <div className="modal-overlay" onClick={() => setEditingProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Product</h3>
            <div className="form-group">
              <label>Product Name</label>
              <input
                type="text"
                value={editProductName}
                onChange={(e) => setEditProductName(e.target.value)}
                placeholder="Enter product name"
              />
            </div>
            {(productSchema.fields || []).filter((field) => field.key !== 'raw_materials').map((field) => {
              const value = editProductValues[field.key];
              const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
              return (
                <div className="form-group" key={`edit-product-${field.key}`}>
                  <label>{field.label || field.key}</label>
                  {field.type === 'dropdown' ? (
                    <select
                      value={value || ''}
                      onChange={(e) => setEditProductValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {(field.options || []).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(e) => setEditProductValues((prev) => ({ ...prev, [field.key]: e.target.checked }))}
                      />
                      <span>Yes</span>
                    </label>
                  ) : (
                    <input
                      type={inputType}
                      value={value ?? ''}
                      onChange={(e) => setEditProductValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  )}
                </div>
              );
            })}
            <div className="form-group">
              <label>Raw Material Entries</label>
              <input
                type="number"
                min="0"
                value={editProductRawMaterialCount}
                onChange={(e) => {
                  const count = Math.max(0, parseInt(e.target.value || '0', 10));
                  setEditProductRawMaterialCount(count);
                  setEditProductRawMaterials((prev) => {
                    const next = prev.slice(0, count);
                    while (next.length < count) {
                      next.push({ raw_material_name: '', characteristics: {}, required_qty: '' });
                    }
                    return next;
                  });
                }}
              />
            </div>
            {editProductRawMaterialCount > 0 && (
              <div className="raw-materials-inline-form">
                {Array.from({ length: editProductRawMaterialCount }).map((_, index) => {
                  const entry = normalizeProductRawMaterialEntry(editProductRawMaterials[index]);
                  const characteristics = findRawMaterialByName(entry.raw_material_name)?.characteristics || [];
                  return (
                    <div key={`edit-rm-${index}`} className="raw-materials-inline-row">
                      <select
                        value={entry.raw_material_name}
                        onChange={(e) => updateEditProductRawMaterial(index, { raw_material_name: e.target.value, characteristics: {} })}
                      >
                        <option value="">Select type</option>
                        {getRawMaterialSelectOptions(entry.raw_material_name).map((item) => (
                          <option key={item.name} value={item.name}>
                            {item.name}{item.archived ? ' (Archived)' : ''}
                          </option>
                        ))}
                      </select>
                      {characteristics.map((characteristic) => (
                        <select
                          key={`${characteristic.list_key}-${index}`}
                          value={entry.characteristics?.[characteristic.name] || ''}
                          onChange={(e) =>
                            updateEditProductRawMaterial(index, {
                              characteristics: {
                                ...(entry.characteristics || {}),
                                [characteristic.name]: e.target.value
                              }
                            })
                          }
                          disabled={!entry.raw_material_name}
                        >
                          <option value="">Select {characteristic.name}{characteristic.mandatory ? ' *' : ''}</option>
                          {getCharacteristicValues(characteristic.list_key).map((value) => (
                            <option key={`${characteristic.list_key}-${value}`} value={value}>{value}</option>
                          ))}
                        </select>
                      ))}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={entry.required_qty ?? ''}
                        onChange={(e) => updateEditProductRawMaterial(index, { required_qty: e.target.value })}
                        placeholder="Quantity"
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setEditingProduct(null)}>Cancel</button>
              <button type="submit" onClick={handleSaveEditedProduct}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <OrderDetailsModal
        order={viewingOrder}
        costBreakdown={orderCostBreakdown}
        costBreakdownLoading={orderCostBreakdownLoading}
        costForm={orderCostForm}
        onCostFormChange={(key, value) => setOrderCostForm((prev) => ({ ...prev, [key]: value }))}
        onSaveCostComponents={handleSaveOrderCostComponents}
        parseOrderPayload={parseOrderPayload}
        getOrderStatusLabel={getOrderStatusLabel}
        getStageColors={getStageColors}
        rawMaterialUnitByName={rawMaterialUnitByName}
        onClose={() => setViewingOrder(null)}
        onOpenNotes={openOrderNotes}
        onReplaceOrder={handleReplaceOrder}
        onCancelOrder={(order) => {
          setViewingOrder(null);
          handleCancelOrder(order);
        }}
        onDownloadCsv={handleDownloadOrderCsv}
      />

      {holdOverviewOpen && (
        <div className="modal-overlay" onClick={() => setHoldOverviewOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Total In Hold</h3>
            <p className="modal-text">Remaining hold by order and raw material category.</p>
            {holdOverviewRows.length === 0 ? (
              <div className="empty-state">No remaining hold in active orders.</div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Buyer</th>
                      <th>Status</th>
                      <th>Raw Material</th>
                      <th>Reserved</th>
                      <th>Consumed</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdOverviewRows.map((row) => (
                      <tr key={`hold-overview-${row.order_id}-${row.raw_material_name}`}>
                        <td>{row.order_number}</td>
                        <td>{row.buyer_name}</td>
                        <td>{row.status}</td>
                        <td>{row.raw_material_name || '-'}</td>
                        <td>{row.reserved.toFixed(2)}{row.unit ? ` ${row.unit}` : ''}</td>
                        <td>{row.consumed.toFixed(2)}{row.unit ? ` ${row.unit}` : ''}</td>
                        <td>{row.remaining.toFixed(2)}{row.unit ? ` ${row.unit}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setHoldOverviewOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {lowStockModal && (
        <div className="modal-overlay" onClick={() => !lowStockOverrideLoading && setLowStockModal(null)}>
          <div className="modal low-stock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Low Raw Material Stock</h3>
            <p className="modal-text">
              This order requires more stock than currently available for the materials below.
              You can override and continue; those stock buckets will become negative.
            </p>
            <div className="low-stock-list">
              {(lowStockModal.shortages || []).map((item, idx) => {
                const characteristicsText = Object.entries(item.characteristics || {})
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(' • ');
                return (
                  <div key={`low-stock-${idx}`} className="low-stock-item">
                    <div className="low-stock-item-header">
                      <strong>{item.raw_material_name || 'Raw Material'}</strong>
                      <span>Short by {Number(item.shortage || 0).toFixed(2)}</span>
                    </div>
                    <p>{characteristicsText || 'No characteristic selected'}</p>
                    <div className="low-stock-item-meta">
                      <span>Required: {Number(item.required || 0).toFixed(2)}</span>
                      <span>Available: {Number(item.available || 0).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Override reason (optional)</label>
              <textarea
                rows={3}
                value={lowStockModal.reason || ''}
                onChange={(e) => setLowStockModal((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Why are you overriding low stock?"
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setLowStockModal(null)}
                disabled={lowStockOverrideLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleOverrideLowStockOrderCreate}
                disabled={lowStockOverrideLoading}
              >
                {lowStockOverrideLoading ? 'Overriding...' : 'Override & Create Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rawMaterialsImportModal && (
        <div className="modal-overlay" onClick={() => !rawMaterialsImportLoading && setRawMaterialsImportModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Import Raw Materials CSV</h3>
            <p className="modal-text">
              Use the template format for best results. Matching is based on material name plus unit label, and matched rows require an explicit review choice before import.
            </p>
            <div className="form-group">
              <label>Expected columns</label>
              <div className="raw-material-import-columns">
                <span className="raw-material-import-column-pill">name</span>
                <span className="raw-material-import-column-pill">unit_label</span>
                <span className="raw-material-import-column-pill">consume_stage</span>
                <span className="raw-material-import-column-pill">is_main</span>
                <span className="raw-material-import-column-pill">archived</span>
                <span className="raw-material-import-column-pill">characteristic_1_name</span>
                <span className="raw-material-import-column-pill">characteristic_1_required</span>
                <span className="raw-material-import-column-pill">characteristic_2_name</span>
                <span className="raw-material-import-column-pill">characteristic_2_required</span>
              </div>
              <small className="helper-text">
                Preferred format is multi-column characteristics. Legacy CSVs with a single <code>characteristics</code> column still import.
              </small>
            </div>
            <div className="form-group">
              <label>CSV File</label>
              <input type="file" accept=".csv,text/csv" onChange={handleRawMaterialsImportFileSelected} />
              {rawMaterialsImportModal.fileName && (
                <small className="helper-text">Selected: {rawMaterialsImportModal.fileName}</small>
              )}
              <label className="csv-import-option">
                <input
                  type="radio"
                  name="raw-material-import-mode"
                  checked={rawMaterialsImportModal.importMode === 'block'}
                  onChange={() => setRawMaterialsImportModal((prev) => (
                    prev
                      ? {
                        ...prev,
                        importMode: 'block',
                        result: null
                      }
                      : prev
                  ))}
                  disabled={rawMaterialsImportLoading}
                />
                <span>Block import if any row has errors</span>
              </label>
              <label className="csv-import-option">
                <input
                  type="radio"
                  name="raw-material-import-mode"
                  checked={rawMaterialsImportModal.importMode === 'valid_only'}
                  onChange={() => setRawMaterialsImportModal((prev) => (
                    prev
                      ? {
                        ...prev,
                        importMode: 'valid_only',
                        result: null
                      }
                      : prev
                  ))}
                  disabled={rawMaterialsImportLoading}
                />
                <span>Import valid rows only and skip invalid rows</span>
              </label>
              <div className="csv-import-template-actions">
                <button
                  type="button"
                  className="btn-edit"
                  onClick={downloadRawMaterialsTemplate}
                  disabled={rawMaterialsImportLoading}
                >
                  Download Template
                </button>
                <button
                  type="button"
                  onClick={() => setRawMaterialsImportModal((prev) => (prev ? { ...prev, showTemplate: !prev.showTemplate } : prev))}
                  disabled={rawMaterialsImportLoading}
                >
                  {rawMaterialsImportModal.showTemplate ? 'Hide Template' : 'View Template'}
                </button>
              </div>
            </div>
            {rawMaterialsImportModal.showTemplate && (
              <div className="csv-template-preview">
                <div className="csv-template-preview-header">
                  <strong>Raw Materials Template</strong>
                  <span>Preferred multi-column format</span>
                </div>
                <pre>{buildRawMaterialsImportTemplate()}</pre>
              </div>
            )}
            {rawMaterialsImportModal.summary && (
              <div className="workflow-summary-pills" style={{ marginBottom: '10px' }}>
                <div className="workflow-summary-pill">
                  <span>Total Rows</span>
                  <strong>{rawMaterialsImportModal.summary.total_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill is-completed">
                  <span>Valid</span>
                  <strong>{rawMaterialsImportModal.summary.valid_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill is-cancelled">
                  <span>Errors</span>
                  <strong>{rawMaterialsImportModal.summary.error_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill">
                  <span>Matches Found</span>
                  <strong>{rawMaterialsImportModal.summary.duplicate_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill is-overdue">
                  <span>Needs Review</span>
                  <strong>{rawMaterialsImportModal.summary.needs_review_rows || 0}</strong>
                </div>
              </div>
            )}
            {Array.isArray(rawMaterialsImportModal.rows) && rawMaterialsImportModal.rows.length > 0 && (
              <div className="table-wrapper raw-material-import-table-wrapper">
                <table className="data-table raw-material-import-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Incoming</th>
                      <th>Characteristics</th>
                      <th>Match</th>
                      <th>Validation</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawMaterialsImportModal.rows.map((row) => {
                      const matchLabel = !row.existingMaterial
                        ? 'New material'
                        : `${row.existingMaterial.archived ? 'Archived' : 'Active'} match found`;
                      const decisionOptions = !row.existingMaterial
                        ? [
                          { value: 'create', label: 'Create new' },
                          { value: 'skip', label: 'Skip row' }
                        ]
                        : row.existingMaterial.archived
                          ? [
                            { value: 'review', label: 'Choose action' },
                            { value: 'update', label: 'Update existing' },
                            { value: 'restore_update', label: 'Restore and update' },
                            { value: 'skip', label: 'Skip row' }
                          ]
                          : [
                            { value: 'review', label: 'Choose action' },
                            { value: 'update', label: 'Update existing' },
                            { value: 'skip', label: 'Skip row' }
                          ];
                      return (
                        <tr key={row.id}>
                          <td>{row.rowNumber}</td>
                          <td>
                            <div className="raw-material-import-primary">
                              <strong>{row.material.name || 'Unnamed material'}</strong>
                              <span>{row.material.unit_label || 'No unit'}{row.material.consume_stage ? ` • ${row.material.consume_stage}` : ''}</span>
                            </div>
                          </td>
                          <td>
                            {row.material.characteristics.length > 0 ? (
                              <div className="raw-material-import-characteristics">
                                {row.material.characteristics.map((item) => (
                                  <span key={`${row.id}-${item.name}`} className="raw-material-import-characteristic-pill">
                                    {item.name}{item.mandatory ? ' *' : ''}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="helper-text">No characteristics</span>
                            )}
                          </td>
                          <td>
                            <div className="raw-material-import-primary">
                              <strong>{matchLabel}</strong>
                              {row.existingMaterial && (
                                <span>
                                  {row.existingMaterial.consume_stage || 'No stage'}
                                  {row.existingMaterial.archived ? ' • archived' : ''}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {row.errors.length > 0 ? (
                              <div className="raw-material-import-errors">
                                {row.errors.map((message, index) => (
                                  <div key={`${row.id}-error-${index}`} className="raw-material-import-error">
                                    {message}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="raw-material-import-valid">Ready</span>
                            )}
                          </td>
                          <td>
                            <select
                              value={row.decision}
                              onChange={(e) => updateRawMaterialsImportRowDecision(row.id, e.target.value)}
                              disabled={rawMaterialsImportLoading || row.errors.length > 0}
                            >
                              {decisionOptions.map((option) => (
                                <option key={`${row.id}-${option.value}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {rawMaterialsImportModal.result && (
              <div className="raw-material-import-result">
                <div className="workflow-summary-pills">
                  <div className="workflow-summary-pill is-completed">
                    <span>Created</span>
                    <strong>{rawMaterialsImportModal.result.created}</strong>
                  </div>
                  <div className="workflow-summary-pill">
                    <span>Updated</span>
                    <strong>{rawMaterialsImportModal.result.updated}</strong>
                  </div>
                  <div className="workflow-summary-pill">
                    <span>Restored</span>
                    <strong>{rawMaterialsImportModal.result.restored}</strong>
                  </div>
                  <div className="workflow-summary-pill is-cancelled">
                    <span>Skipped</span>
                    <strong>{rawMaterialsImportModal.result.skipped}</strong>
                  </div>
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setRawMaterialsImportModal(null)} disabled={rawMaterialsImportLoading}>
                {rawMaterialsImportModal.result ? 'Close' : 'Cancel'}
              </button>
              <button
                type="button"
                className="btn-edit"
                onClick={handleRawMaterialsImportPreview}
                disabled={rawMaterialsImportLoading || !rawMaterialsImportModal.csvText?.trim()}
              >
                {rawMaterialsImportLoading ? 'Processing...' : 'Preview'}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleRawMaterialsImportCommit}
                disabled={rawMaterialsImportLoading || !rawMaterialsImportModal.rows?.length}
              >
                {rawMaterialsImportLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {csvImportModal && (
        <div className="modal-overlay" onClick={() => !csvImportLoading && setCsvImportModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {csvImportModal.inventoryKind === 'finished_goods'
                ? 'Import Finished Goods Transactions CSV'
                : (csvImportModal.mode === 'opening_stock' ? 'Import Opening Stock CSV' : 'Import Transactions CSV')}
            </h3>
            <p className="modal-text">
              {csvImportModal.inventoryKind === 'finished_goods'
                ? 'Required columns: type, finished_product_name, quantity. Optional: date, notes.'
                : (csvImportModal.mode === 'opening_stock'
                ? 'Required columns: raw_material_type, opening_quantity (or quantity), optional date, notes, characteristic columns.'
                : 'Required columns: type, raw_material_type, quantity, optional date, notes, unit_cost, characteristic columns.')}
            </p>
            <div className="form-group">
              <label>CSV File</label>
              <input type="file" accept=".csv,text/csv" onChange={handleCsvFileSelected} />
              {csvImportModal.fileName && <small className="helper-text">Selected: {csvImportModal.fileName}</small>}
              <label className="csv-import-option">
                <input
                  type="checkbox"
                  checked={Boolean(csvImportModal.allowNegative)}
                  onChange={(e) => setCsvImportModal((prev) => (
                    prev
                      ? {
                        ...prev,
                        allowNegative: e.target.checked,
                        summary: null,
                        errors: [],
                        preview: []
                      }
                      : prev
                  ))}
                  disabled={csvImportLoading}
                />
                <span>Allow negative stock during import (recommended for historical backfill)</span>
              </label>
              {csvImportModal.inventoryKind === 'raw_material' && (
                <label className="csv-import-option">
                  <input
                    type="checkbox"
                    checked={Boolean(csvImportModal.autoAddCharacteristics)}
                    onChange={(e) => setCsvImportModal((prev) => (
                      prev
                        ? {
                          ...prev,
                          autoAddCharacteristics: e.target.checked,
                          summary: null,
                          errors: [],
                          characteristicCandidates: [],
                          preview: []
                        }
                        : prev
                    ))}
                    disabled={csvImportLoading}
                  />
                  <span>Auto-add missing characteristic values to dropdown lists</span>
                </label>
              )}
              <div className="csv-import-template-actions">
                <button
                  type="button"
                  className="btn-edit"
                  onClick={() => downloadCsvImportTemplate(csvImportModal.mode, csvImportModal.inventoryKind || 'raw_material')}
                  disabled={csvImportLoading}
                >
                  Download Template
                </button>
                <button
                  type="button"
                  onClick={() => setCsvImportModal((prev) => (prev ? { ...prev, showTemplate: !prev.showTemplate } : prev))}
                  disabled={csvImportLoading}
                >
                  {csvImportModal.showTemplate ? 'Hide Template' : 'View Template'}
                </button>
              </div>
            </div>
            {csvImportModal.showTemplate && (
              <div className="csv-template-preview">
                <div className="csv-template-preview-header">
                  <strong>CSV Template Preview</strong>
                  <span>
                    {csvImportModal.inventoryKind === 'finished_goods'
                      ? 'Finished Goods'
                      : (csvImportModal.mode === 'opening_stock' ? 'Opening Stock' : 'Raw Material Transactions')}
                  </span>
                </div>
                <pre>{buildCsvImportTemplate(csvImportModal.mode, csvImportModal.inventoryKind || 'raw_material')}</pre>
              </div>
            )}
            {csvImportModal.summary && (
              <div className="workflow-summary-pills" style={{ marginBottom: '10px' }}>
                <div className="workflow-summary-pill">
                  <span>Total Rows</span>
                  <strong>{csvImportModal.summary.total_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill is-completed">
                  <span>Valid Rows</span>
                  <strong>{csvImportModal.summary.valid_rows || 0}</strong>
                </div>
                <div className="workflow-summary-pill is-cancelled">
                  <span>Error Rows</span>
                  <strong>{csvImportModal.summary.error_rows || 0}</strong>
                </div>
                {csvImportModal.inventoryKind === 'raw_material' && Boolean(csvImportModal.autoAddCharacteristics) && (
                  <div className="workflow-summary-pill">
                    <span>Characteristic Values To Add</span>
                    <strong>{Array.isArray(csvImportModal.characteristicCandidates) ? csvImportModal.characteristicCandidates.length : 0}</strong>
                  </div>
                )}
              </div>
            )}
            {Array.isArray(csvImportModal.errors) && csvImportModal.errors.length > 0 && (
              <div className="table-wrapper" style={{ marginBottom: '10px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvImportModal.errors.slice(0, 50).map((row, index) => (
                      <tr key={`csv-import-error-${index}`}>
                        <td>{row.row || '-'}</td>
                        <td>{row.message || 'Validation error'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {Array.isArray(csvImportModal.preview) && csvImportModal.preview.length > 0 && (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>{csvImportModal.inventoryKind === 'finished_goods' ? 'Finished Product' : 'Raw Material'}</th>
                      <th>Quantity</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvImportModal.preview.map((row, index) => (
                      <tr key={`csv-import-preview-${index}`}>
                        <td>{row.type}</td>
                        <td>{csvImportModal.inventoryKind === 'finished_goods' ? row.finished_product_name : row.raw_material_type}</td>
                        <td>{Number(row.quantity || 0).toFixed(2)}</td>
                        <td>{row.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setCsvImportModal(null)} disabled={csvImportLoading}>
                Close
              </button>
              <button
                type="button"
                className="btn-edit"
                onClick={handleCsvImportPreview}
                disabled={csvImportLoading || !csvImportModal.csvText?.trim()}
              >
                {csvImportLoading ? 'Processing...' : 'Preview'}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCsvImportCommit}
                disabled={csvImportLoading || !csvImportModal.csvText?.trim() || (csvImportModal.errors || []).length > 0}
              >
                {csvImportLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      <WelcomeModal
        isOpen={welcomeModalOpen}
        isSaving={onboardingSaving}
        roleLabel={formatRoleLabel(user?.role || 'user')}
        onStart={handleWelcomeStart}
        onLater={handleWelcomeLater}
        onSkip={handleWelcomeSkip}
      />

      <GuidedTour
        isOpen={isTourOpen}
        currentStep={currentTourStep}
        stepIndex={tourStepIndex}
        totalSteps={tourSteps.length}
        tooltipStyle={tourTooltipStyle}
        onBack={handleTourBack}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
      />

      {challanOutputDialog && (
        <div className="modal-overlay" onClick={() => setChallanOutputDialog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{challanOutputDialog.intent === 'download' ? 'Save Challan as PDF' : 'Print Challan'}</h3>
            <p className="modal-text">
              {challanOutputDialog.challanNumber
                ? `Choose output options for challan ${challanOutputDialog.challanNumber}.`
                : 'Choose output options for this challan.'}
            </p>
            <div className="form-group">
              <label>Layout Preset</label>
              <select
                value={challanOutputLayout}
                onChange={(e) => applyChallanOutputPreferences({ layout: e.target.value })}
              >
                <option value="internal">Internal (Detailed)</option>
                <option value="customer">Customer (Clean)</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={challanOutputIncludeUnitCost}
                    onChange={(e) => setChallanOutputIncludeUnitCost(e.target.checked)}
                  />
                  <span>Include Unit Cost</span>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={challanOutputIncludeEstimatedCost}
                    onChange={(e) => setChallanOutputIncludeEstimatedCost(e.target.checked)}
                  />
                  <span>Include Estimated Cost</span>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={challanOutputIncludeProductId}
                    onChange={(e) => setChallanOutputIncludeProductId(e.target.checked)}
                  />
                  <span>Include Product ID</span>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={challanOutputIncludeOrderIds}
                    onChange={(e) => setChallanOutputIncludeOrderIds(e.target.checked)}
                  />
                  <span>Include Order IDs / Refs</span>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={challanOutputIncludeQr}
                    onChange={(e) => setChallanOutputIncludeQr(e.target.checked)}
                  />
                  <span>Include QR</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setChallanOutputDialog(null)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleConfirmChallanOutput}>
                {challanOutputDialog.intent === 'download' ? 'Open Save as PDF' : 'Open Print'}
              </button>
            </div>
          </div>
        </div>
      )}

      <NotesModal
        target={notesModalTarget}
        text={notesModalText}
        setText={setNotesModalText}
        onClose={() => setNotesModalTarget(null)}
        onSave={handleSaveNotesModal}
      />

      <OfflineQueueModal
        isOpen={offlineQueueOpen}
        entries={offlineQueueSnapshot}
        isOnline={isOnline}
        offlineSyncing={offlineSyncing}
        onClose={() => setOfflineQueueOpen(false)}
        onFlush={flushOfflineActionQueue}
        onClear={() => {
          setOfflineActionQueue([]);
          setSuccess('Offline queue cleared.');
          setOfflineQueueOpen(false);
        }}
        formatDateTime={formatDateTime}
        getCurrentTimeZone={getCurrentTimeZone}
      />

      <ConfirmDialogModal
        dialog={confirmDialog}
        onClose={closeConfirmDialog}
        onConfirm={executeConfirmAction}
      />
    </div>
  );
}

export default AdminDashboard;
